import { askDshNotesWithLocalNames } from './ask-dsh-notes.js'
import { useAskDsh } from './use-ask-dsh.js'
import { useProfileRevision } from './profile-change-store.js'
import { AskDshIcon } from './AskDshIcon.js'
import { conversationWindowRequested, navigateConversationWindow } from './conversation-window.js'
import { ArkmeCommonGroupsPanel } from './ArkmeCommonGroupsPanel.js'
import { afterReactionLayout, afterMessageVisible } from './reaction-locate-layout.js'
import { reactionNotifications } from './reaction-notifications.js'
import { ArkmeReactionPreview, ReactionAddIcon, ArkmeReactionSelections } from './ArkmeReactionPreview.js'
import { reactionPreview } from './reaction-preview-store.js'
import { CONVERSATION_HEADER_COLUMNS } from './conversation-header-layout.js'
import { conversationSending, withConversationSend } from './conversation-window-sync.js'
import { DeepSeekLogoMark } from './ArkmeDshAgentInputMarker.js'
import { createNativeCopyLinkEntry, NATIVE_COPY_LINK_ENTRY, type NativeCopyLinkWindow } from './native-copy-link-entry.js'
import { NATIVE_FORWARD_ENTRY, isNativeForwardCaller, nativeForwardPreview, type NativeForwardDelivery, type NativeForwardWindow, type NativeForwardEntry, type NativeForwardResult } from './native-forward-entry.js'
import { longArticleWindowBridge, openLongArticleWindow } from './long-article-window.js'
import { attachmentPreviewBridge } from './attachment-preview-window.js'
import { openSessionAttachmentPreview } from './ArkmeRichContent.js'
import { tr, useArkmeLocale, arkmeIntlLocale } from './locale.js'
import { ArkmeActionMenu } from './ArkmeDshMenu.js'
import { useComposerPasteFocus } from './composer-paste-focus.js'
import { ArkmeComposerScreenshotButton } from './ArkmeComposerScreenshotButton.js'
import { ArkmeComposerToolButton } from './ArkmeComposerToolButton.js'
import { ArkmeSelfRolePicker } from './ArkmeSelfRolePicker.js'
import { arkmePersonalAvatarRef, arkmeSelfRoleAvatarFallback, arkmeSelfRoleForPresentation } from './self-role-presentation.js'
import { ArkmeComposerPlusIcon } from './ArkmeComposerToolIcon.js'
import { useSelfCalendarNavigation } from './use-self-calendar-navigation.js'
import { ArkmeCalendarNavigationStatus } from './ArkmeCalendarNavigationStatus.js'
import { ArkmeChatCalendar } from './ArkmeChatCalendar.js'
import type { ConversationCalendarInteractionState } from './conversation-calendar.js'
import { arkmeConversationTargetRow } from './conversation-viewport.js'
import { ArkmeForwardSearch } from './ArkmeForwardSearch.js'
import { ArkmeRightPanelHeader } from './ArkmeRightPanelHeader.js'
import { compareTimelineMessages } from './timeline-message-order.js'
import { ArkmeLivePhotoBadge } from './ArkmeLivePhotoBadge.js'
import { selfTopicDirectory } from './self-topic-directory-cache.js'
import { recordOwnerId } from '../record-owner-id.js'
import { ArkmeBotIdentityStyles, ArkmeBotSenderName } from './ArkmeBotIdentity.js'
import { ArkmePinnedCorner } from './ArkmePinnedCorner.js'
import { ArkmeMembershipBadge } from './ArkmeMembershipBadge.js'
import { useForwardTargetDirectory } from './forward-target-directory.js'
import { ArkmeRecordDeletionDialog } from './ArkmeRecordDeletionDialog.js'
import { Trash } from '@phosphor-icons/react/dist/icons/Trash'
import { CalendarBlank } from '@phosphor-icons/react/dist/icons/CalendarBlank'
import { messageSelectionStyles, ArkmeMessageSelectionControl, ArkmeSelectActionIcon } from './message-selection-presentation.js'
import { RegionMarquee } from './selection/RegionMarquee.js'
import { arkmeDetailExtensionComposerStyles } from './detail-extension-composer-style.js'
import { FileTextIcon } from '@phosphor-icons/react/dist/csr/FileText'
import { ArkmeRecordTopicAssignmentDialog } from './ArkmeRecordTopicAssignmentDialog.js'
import { retainNewerArkmeChatPolicy } from '../chat-policy-projection.js'
import { arkmeMarkdownPlainText, arkmeMarkdownTextRanges } from '../markdown.js'
import { arkmeCallRecordBubbleStyle } from './ArkmeCallRecordContent.js'
import { arkmeSourceAllowsUserWrite, isArkmeDSHInputTopic, arkmeTopicDisplayName } from '../topic-policy.js'
import { ArkmeTopicReadOnlyNotice } from './ArkmeTopicReadOnlyNotice.js'
import { ArkmeTopicSourceIcon } from './ArkmeDetailSourceBadgeVisuals.js'
import { arkmeTopicPathNames } from './source-tree.js'
import { withArkmeReadDeadline } from './read-deadline.js'
import { ArkmeCallDetailDrawer } from './ArkmeCallDetailDrawer.js'
import {
  Fragment, memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore,
  type CSSProperties, type ReactNode, type SetStateAction,
} from 'react'
import { createPortal } from 'react-dom'
import {
  IconCheckOutline16, IconLoadingOutline16, IconWarningOutline16, IconPaperclipOutline16, IconEditOutline16, Toast,
} from '@deepseek-ai/dsh-client-ui-primitives'
import { invalidateDirectMessageAdmission, requireDirectMessageSendAllowed, useDirectMessageAdmission } from './direct-message-admission.js'
import {
  ConversationActionsMenu, conversationExportActionItem, privateChatActionItems, usePrivateChatActions,
} from './PrivateChatActions.js'
import qrcode from 'qrcode-generator'
import { retainPartialTimelineMedia } from './timeline-media.js'
import { ArkmeRichText } from './ArkmeRichText.js'
import { ArkmeArticlePicker } from './ArkmeArticlePicker.js'
import { composerArticleKey, composerArticleStore, useComposerArticle } from './composer-article-store.js'
import { arkmeEmojiPlainText } from './arkme-emoji.js'
import type {
  ArkmeGroupNotificationResult, ArkmeAuthSnapshot, ArkmeGroupAiPolishNotice, ArkmeGroupAiPolishSnapshot, ArkmeSourceReadResult,
  ArkmeRelatedRecordingItem, ArkmeRelatedRecordingMonthBucket, ArkmeRelatedRecordingPage,
  ArkmeRelatedRecordingPageState, ArkmeSourceItem, ArkmeSourceSendResult, ArkmeTimelineAroundPage, ArkmeTimelineCursor, ArkmeTimelineItem, ArkmeTimelineMentionTarget, ArkmeTimelinePage, ArkmeMessageSnapshotDetail,
  ArkmeInterwovenBootstrap, ArkmeInterwovenDetail, ArkmeInterwovenMention, ArkmePluginResponse,
  ArkmeRelatedQuickNoteDetail, ArkmeRelatedQuickNoteItem, ArkmeRelatedQuickNoteList,
  ArkmeMessageCopyLinkExtendResult, ArkmeMessageCopyLinkExtensionItem, ArkmeMessageCopyLinkResolveResult, ArkmeMessageCopyLinkResult, ArkmeMessageCopyLinkSnapshotItem,
  ArkmeUploadedAsset, ArkmeSourceList, ArkmeSourceMessageExtendResult, ArkmeTimelineExtensionParent, ArkmeUserProfile, ArkmeUserProfileSnapshot,
  ArkmeConversationMemberItem, ArkmeConversationMemberRecordMode,
  ArkmeConversationMemberJoinEvent, ArkmeConversationMemberJoinPerson, ArkmeOpenPrivateChatResult,
  ArkmeHumanMentionInput,
  ArkmeTopicHierarchyMoveResult,
  ArkmeTopicDissolveTask,
  ArkmeBotList, ArkmeGroupBotCandidateList,
  ArkmeBotSummary,
  ArkmeSharedRecordingPreview,
  ArkmeBackgroundSoundPreference, ArkmeBackgroundSoundEligibilityReason, ArkmeProviderCapabilities,
  ArkmeRecordTagItem, ArkmeRecordTagList,
  ArkmeCalendarRecordItem, ArkmeGroupAvatarFallback, ArkmeSelfRole,
} from '../types.js'
import {
  arkmeHashTagMatches, arkmeHashTagTrigger, arkmeMergeHashTagSuggestions,
  arkmeReconcileHashTagSuggestionSnapshots, type ArkmeHashTagTrigger,
} from '../hashtag.js'
import { callArkme, ArkmeClientError } from './api.js'
import type { ArkmeMemberEvent } from '../types.js'
import { useMemberEventTimeline } from './use-member-event-timeline.js'
import { ArkmeMemberEventProfileDialog } from './ArkmeMemberEventProfile.js'
import { createArkmeSdk } from '../sdk/index.js'
import type { ArkmeContentBlock } from '../types.js'
import type { ArkmeFileSendTask } from '../file-transfer-contract.js'
import type {
  ArkmeRecordReeditAttachmentView,
  ArkmeRecordReeditSubmissionView,
  ArkmeRecordReeditEditorSnapshot,
} from '../record-reedit-contract.js'
import { projectRecordReedit, useRecordReeditSubmissions } from './record-reedit-submissions.js'
import { useRecordReeditEditors, type ArkmeRecordReeditComposerState } from './record-reedit-editors.js'
import { readConversationTimelineWindow } from './conversation-timeline-refresh.js'
import { useScrollBottomVisibility } from './use-scroll-bottom-visibility.js'
import { ArkmeConversationBottomControl } from './ArkmeConversationBottomControl.js'
import { projectArkmeChatAttentionFromMuted } from '../chat-attention.js'
import { bindSentFileTaskLocals, fileTaskShowsInlineStatus, fileTaskTimelineItem, localFileBlock, useArkmeFileSendTasks } from './file-send-tasks.js'
import { isArkmeRequestAbort, retryArkmeRead } from './read-retry.js'
import { arkmeAwaitVisibleReadIntent, arkmeVisibleReadIntentAllowed, subscribeArkmeReadIntentAvailability } from './read-intent-visibility.js'
import { verifyPhoneCaptcha } from './geetest.js'
import { ArkmeDirectorySourceAvatar, ArkmeUserAvatar } from './ArkmeAvatar.js'
import {
  ARKME_CONVERSATION_HEADER_ACTIONS_STYLE,
  ArkmeConversationHeaderIconButton,
  ArkmeGroupChatControls,
} from './ArkmeGroupChatControls.js'
import {
  ArkmeGroupMemberRemoveDialog, ArkmeLoadedBotRecordsPanel, ArkmeMemberActionMenu, ArkmeMemberProfileCard, ArkmeMemberRecordsPanel,
  arkmeMemberConversationAction,
  type ArkmeMemberMenuPosition,
} from './ArkmeChatMemberActions.js'
import { ArkmeLogin, type ArkmeLoginMode } from './ArkmeLogin.js'
import { ArkmeMuteIcon } from './ArkmeMuteIcon.js'
import { ArkmeMessageReadReceiptLine } from './ArkmeMessageReadReceipt.js'
import { ArkmeMessagePreparingIndicator } from './ArkmeMessagePreparingIndicator.js'
import { useMessagePreparing } from './use-message-preparing.js'
import {
  ArkmeMessageWithdrawalDialog, arkmeCanWithdrawTimelineMessage,
} from './ArkmeMessageWithdrawalDialog.js'
import { ArkmeArkoSurface } from './ArkmeArkoSurface.js'
import { ArkmePrivateCallMenu } from './ArkmePrivateCallMenu.js'
import { ArkmeLongArticleDialog } from './ArkmeLongArticleDialog.js'
import { ArkmeRecordingSurface } from './ArkmeRecordingSurface.js'
import { ArkmeRecognizedSpeakersSurface } from './ArkmeRecognizedSpeakersSurface.js'
import { ArkmeDirectRecordingStatus, useDirectRecordingOwner } from './recordings/ArkmeDirectRecording.js'
import { ArkmeSelfCalendarPopover } from './ArkmeCalendarSurface.js'
import { ArkmeRecordingImportDialog, type ArkmeRecordingImportDialogHandle, type RecordingImportButtonStatus } from './recordings/ArkmeRecordingImportDialog.js'
import { ArkmeRetainedCallPage } from './ArkmeRetainedCallPage.js'
import { ArkmeWorldSurface } from './ArkmeWorldSurface.js'
import {
  ArkmeMediaPreview, ArkmeMessageContent, arkmeContentMediaUrl, arkmeRelatedRecordingItemFromSharedRecording,
  arkmeRelatedRecordingItemFromSharedRecordingPreview,
} from './ArkmeRichContent.js'
import { ArkmeAttachmentStrip, ArkmeFilePreparingIndicator } from './ArkmeAttachmentStrip.js'
import { ArkmeConfirmDialog } from './ArkmeConfirmDialog.js'
import { ArkmeRichComposerInput, type ArkmeRichComposerHandle } from './ArkmeRichComposerInput.js'
import type { ArkmeComposerSelectionRequest } from './composer-selection-request.js'
import { useResizableComposer } from './use-resizable-composer.js'
import { ArkmeWideConversation } from './ArkmeWideConversation.js'
import { useConversationResizeAnchor } from './conversation-resize-anchor.js'
import { useResizableNoteDetail } from './use-resizable-note-detail.js'
import { ArkmeEmojiPicker } from './ArkmeEmojiPicker.js'
import type { ArkmeEmoji } from './arkme-emoji.js'
import { ArkmeSearchSurface } from './ArkmeSearchSurface.js'
import { ArkmeConversationSearch } from './ArkmeConversationSearch.js'
import { ArkmeContactAddSurface } from './ArkmeContactAddSurface.js'
import { ArkmeBotConversationSurface } from './ArkmeBotConversationSurface.js'
import { ArkmeBotAvatarActionMenu, ArkmeBotAvatarProfileCard, ArkmeBotRemoveDialog, arkmeInvalidateBotAvatarResolution,
  type ArkmeBotAvatarIdentity } from './ArkmeBotAvatarActions.js'
import { arkmeGroupBotsChanged } from './use-group-bots.js'
import { arkmeLoadedBotRecords } from './bot-records.js'
import type { ArkmeGroupBotItem } from '../tools/ports/bots.js'
import { ARKME_DEFAULT_SHARE_WEBSITE } from '../types.js'
import { ArkmeMarketplace } from './ArkmeMarketplace.js'
import {
  ArkmeSourceBreadcrumb,
} from './ArkmeSourceBreadcrumb.js'
import { SELF_TOPIC_MENU_OPEN } from './self-topic-menu-bridge.js'
import { useSendToSelfTour } from './ArkmeSendToSelfTour.js'
import {
  ArkmeTopicDirectoryPopover, type ArkmeSelfSourcesResolution, type ArkmeTopicCreateOpener,
} from './ArkmeTopicDirectoryPopover.js'
import { arkmeTheme } from './arkme-theme.js'
import { ArkmeProductNavigation } from './ArkmeProductNavigation.js'
import {
  arkmeConversationExportFileName, arkmeConversationExportMarkdown,
  collectArkmeConversationExportItems, collectArkmeConversationExportTopics,
  downloadArkmeConversationMarkdown,
} from './conversation-export.js'
import { ArkmeVoiceprintSurface } from './ArkmeVoiceprintSurface.js'
import { ArkmeNavigation, type ArkmeNavigationProps } from './ArkmeVirtualWorkspace.js'
import { arkmeAuthStore } from './auth-store.js'
import { betaCommunityWelcomeStore, welcomeDraft, welcomeMatchesSource } from './beta-community-welcome.js'
import { ArkmeBetaCommunityWelcome } from './ArkmeBetaCommunityWelcome.js'
import { arkmeConversationMembers } from './conversation-members-store.js'
import { useConversationMembers } from './use-conversation-members.js'
import { arkmeMessageReadReceipts } from './message-read-receipt-store.js'
import {
  arkmeChatDirectory, arkmeChatTimelineDelta, arkmeInterwovenInvalidation,
  type ArkmeChatDirectorySnapshot,
  type ArkmeChatTimelineSourceDeltaSnapshot,
  type ArkmeInterwovenInvalidationSnapshot,
} from './chat-directory-store.js'
import {
  ArkmeConversationMemoryCache,
  arkmeConversationTimelineDeltaItems,
  ARKME_CONVERSATION_TIMELINE_FRESH_MILLIS,
  arkmeConversationTimelineSequenceRange,
  arkmeConversationTimelineContentEqual,
  arkmeShouldRefreshChatTimeline,
  arkmeShouldRefreshRecordTimeline,
  type ArkmeConversationTimelineSnapshot,
  type ArkmeConversationTimelineSequenceRange,
} from './conversation-memory-cache.js'
import {
  arkmeConversationViewport, arkmeConversationAnchorOffset, arkmeConversationReadingDate, arkmeConversationTargetScrollTop, useConversationViewport,
  type ArkmeConversationViewportRestore,
} from './conversation-viewport.js'
import {
  insertArkmeComposerMentionToken, reconcileArkmeComposerMentions, arkmeComposerAtomicDeletion,
  arkmeComposerCanSend,
  arkmeComposerDraftStore,
  arkmeAttachmentId,
  arkmeSourceComposerDraftKey,
  releaseArkmeComposerDraft,
  serializeArkmeComposerDraft,
  type ArkmeComposerAttachment,
  type ArkmeComposerMention,
} from './composer-draft-store.js'
import { arkmeConversationComposerBorder, arkmeConversationComposerLayout } from './conversation-composer-presentation.js'
import { focusArkmeComposerFromClick, restoreArkmeComposerFocus } from './composer-focus.js'
import { arkmeDesktopNotifications } from './desktop-notification-runtime.js'
import {
  arkmeNotificationActivation,
  type ArkmeNotificationActivationSnapshot,
  type ArkmeNotificationActivationStore,
} from './notification-activation-store.js'
import { arkmeSourceIdentityKey, type ArkmeSourceIdentityFacts } from './source-identity.js'
import {
  arkmeComposerGroupMemberCount, arkmeComposerPlaceholderText,
  type ArkmeComposerPlaceholderTarget,
} from './composer-placeholder.js'
import {
  arkmeComposerMentionTrigger,
  arkmeGroupMentionCandidates,
  arkmeMemberForMention,
  arkmeMentionCandidateKey,
  arkmePrivateMentionCandidates,
  type ArkmeComposerMentionTrigger,
  type ArkmeMentionCandidate,
} from './mention-candidates.js'
export {
  arkmeComposerMentionTrigger,
  arkmeGroupMentionCandidates,
  arkmeMemberForMention,
  arkmeMemberForMentionTarget,
  arkmeMemberForVisibleMention,
  arkmeMentionCandidateMatches,
  arkmeMentionCandidateKey,
  arkmeMentionCandidatePrimaryText,
  arkmePrivateMentionCandidates,
} from './mention-candidates.js'
export type { ArkmeComposerMentionTrigger, ArkmeMentionCandidate } from './mention-candidates.js'
import { ArkmeComposerSendButton } from './ArkmeComposerSendButton.js'
import { ArkmeInterwovenReadProvider } from './ArkmeInterwovenReadReceipt.js'
import { ArkmeMentionSuggestionRow, ArkmeMentionSuggestionThemeStyles } from './ArkmeMentionSuggestionRow.js'
import {
  ArkmeInterwovenDetailAside, ArkmeInterwovenMentionCard,
  mergeConversationRows, projectInterwovenWindow, ArkmeInterwovenPrelude, resolveInterwovenGroupTarget,
  type ArkmeConversationRow, type ArkmeInterwovenDetailViewState,
} from './interwoven-moments.js'
import { ARKME_CONVERSATION_HEADER_HEIGHT } from './arkme-layout.js'
import {
  relatedDrawerBackTarget,
  type ArkmeRelatedDrawerView,
  type ArkmeRelatedQuickNoteDetailState,
  type ArkmeRelatedQuickNotesLoadState,
} from './ArkmeRelatedQuickNotes.js'
import { arkmeUi } from './ui-controller.js'
import { arkmeWechatRequestStartedAfterAuthStatus } from './arkme-auth-flow.js'
import {
  isCurrentRelatedRecordingRequest, mergeRelatedRecordingItems, RelatedRecordingDetail,
  RelatedRecordingsPanel, shouldShowPrivateChatActions,
} from './related-recordings.js'
import { isArkmeChatDirectorySource, isArkmeSelfWorkspaceSource } from './source-list.js'
import { ArkmeOfficialAuthorGuide, isArkmeOfficialAuthor } from './ArkmeOfficialAuthorGuide.js'
import { ArkmeTopicTagBadge } from './ArkmeTopicTagBadge.js'
import { arkmeLoginErrorMessage, arkmeStoredLoginErrorMessage } from './arkme-login-errors.js'
import {
  defaultArkmeLoginTranslate, type ArkmeLoginTranslate,
} from './arkme-login-locales.js'

import { ArkmeTimelineDetailDrawer, ForwardRecordsDetail } from './ArkmeNoteDetails.js'
import { usePersonalExtensionParents } from './use-personal-extension-parents.js'
import { ArkmeMessageSnapshotDialog, arkmeCanOpenMessageSnapshot } from './ArkmeMessageSnapshotDialog.js'
import { ArkmeMessageReportDialog, arkmeCanReportTimelineMessage } from './ArkmeMessageReportDialog.js'
import {
  arkmeSourceSupportsLocationCapture,
  captureArkmeRecordLocationForSend,
  type ArkmeRecordLocationForSendResult,
} from './record-capture-location.js'
import { ArkmeBackgroundSoundWaveform } from './ArkmeBackgroundSoundWaveform.js'
import {
  ArkmeRecordInputCaptureOwner,
  applyArkmeBackgroundSoundOwnerSnapshot,
  arkmeBackgroundSoundCaptureEnabled,
  arkmeBackgroundSoundServerPreferenceRuntime,
  arkmeMicrophoneCaptureReady,
  arkmeSourceSupportsRecordInputCapture,
  subscribeArkmeBackgroundSoundCapturePreference,
  type ArkmeBackgroundSoundFailure,
  type ArkmeBackgroundSoundRecorder,
  type ArkmeRecordInputCaptureResult,
} from './record-input-capture.js'
export { ArkmeTimelineDetailDrawer, arkmeTimelineDetailSenderText } from './ArkmeNoteDetails.js'

const ARKME_COMPOSER_SHOW_INPUT_TIME_STORAGE_KEY = 'arkme.composer.show-input-time'
const ARKME_RECORD_REEDIT_AUTOSAVE_MILLIS = 10_000
const ARKME_RECORD_REEDIT_HIGHLIGHT_MILLIS = 3_000

export function arkmeBackgroundSoundCaptureFailureFeedback(
  failure: ArkmeBackgroundSoundFailure | undefined,
): string {
  if (failure === 'permission-denied') return '未获得麦克风权限'
  if (failure === 'unavailable') return '当前设备无法录制'
  if (failure === 'start-failed') return '录音启动失败'
  if (failure === 'stop-failed') return '录音结束失败'
  if (failure === 'retention-evicted') return '切换会话较多，较早草稿的背景音已释放'
  if (failure === 'limit-reached') return '录音达到上限且未生成可用片段'
  return ''
}

function arkmeComposerShowsInputTime(): boolean {
  try { return window.localStorage.getItem(ARKME_COMPOSER_SHOW_INPUT_TIME_STORAGE_KEY) !== 'hidden' } catch { return true }
}

function setArkmeComposerShowsInputTime(value: boolean): void {
  try { window.localStorage.setItem(ARKME_COMPOSER_SHOW_INPUT_TIME_STORAGE_KEY, value ? 'shown' : 'hidden') } catch { /* storage is optional */ }
}

function ArkmeComposerInputTimeIcon({ visible }: { visible: boolean }) {
  return visible
    ? <svg width="14" height="15" viewBox="0 0 14 15" fill="none" aria-hidden>
      <path d="M0 7.5C.85 4.43 3.66 2.18 7 2.18S13.15 4.43 14 7.5C13.15 10.57 10.34 12.83 7 12.83S.85 10.57 0 7.5Z" stroke="currentColor" strokeWidth="1.4" />
      <circle cx="7" cy="7.5" r="1.92" stroke="currentColor" strokeWidth="1.2" />
    </svg>
    : <svg width="14" height="15" viewBox="0 0 14 15" fill="none" aria-hidden>
      <path d="M1.11 4.95C1.69 5.72 2.53 6.38 3.57 6.84C4.61 7.31 5.79 7.55 7 7.55S9.39 7.31 10.42 6.84C11.46 6.38 12.31 5.72 12.89 4.95M3.21 6.77L1.03 9.53M10.76 6.77L13 9.79M8.55 7.55L9.17 11.03M5.83 7.55L5.21 11.03" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
}

const ArkmeComposerInputStats = memo(function ArkmeComposerInputStats({
  active,
  visible,
  textLength,
  startedAtMillis,
  showsInputTime,
  onToggle,
}: {
  active: boolean
  visible: boolean
  textLength: number
  startedAtMillis?: number
  showsInputTime: boolean
  onToggle(): void
}) {
  useArkmeLocale()
  const [nowMillis, setNowMillis] = useState(() => Date.now())
  const [foreground, setForeground] = useState(() => typeof document === 'undefined' || document.visibilityState !== 'hidden')
  useEffect(() => {
    if (typeof document === 'undefined') return
    const update = () => { setForeground(document.visibilityState !== 'hidden') }
    document.addEventListener('visibilitychange', update)
    return () => { document.removeEventListener('visibilitychange', update) }
  }, [])
  useEffect(() => {
    setNowMillis(Date.now())
    if (!active || !foreground || !visible || !showsInputTime || startedAtMillis === undefined) return
    const timer = globalThis.setInterval(() => { setNowMillis(Date.now()) }, 1_000)
    return () => { globalThis.clearInterval(timer) }
  }, [active, foreground, showsInputTime, startedAtMillis, visible])
  if (!active || !visible) return null
  const durationSeconds = startedAtMillis === undefined
    ? 0
    : Math.max(0, Math.floor((nowMillis - startedAtMillis) / 1_000))
  return <div data-arkme-composer-stats="true" style={styles.composerStats} aria-label={`已输入 ${String(textLength)} 字，编辑 ${String(durationSeconds)} 秒`}>
    <span>{String(textLength)}{tr("字")}</span>
    <button data-arkme-feedback="neutral"
      type="button"
      style={styles.composerTimeToggle}
      aria-label={showsInputTime ? tr("隐藏输入时长") : tr("显示输入时长")}
      title={showsInputTime ? tr("隐藏输入时长") : tr("显示输入时长")}
      onMouseDown={event => { event.preventDefault() }}
      onClick={onToggle}
    >
      <span>{showsInputTime ? tr("{v0}秒", { v0: String(durationSeconds) }) : tr("思考中…")}</span>
      <ArkmeComposerInputTimeIcon visible={showsInputTime} />
    </button>
  </div>
})

export interface ArkmeSurfaceProps {
  t?: ArkmeLoginTranslate
  floating?: boolean
  productNavigation?: boolean
  initialAuth?: ArkmeAuthSnapshot | undefined
  currentSessionId?: string | undefined
  renderSlot?: ArkmeNavigationProps['renderSlot']
  productChrome?: boolean
  directoryLead?: ReactNode
  onCreateTask?: () => void
  onActivateSurface?: () => void
  ownsQrLogin?: boolean
  /** Whether the visible surface owns foreground requests, timers and polling. */
  active?: boolean
}

export type ArkmeAuthView = 'login' | 'content'

const EMPTY_SELF_SOURCES: readonly ArkmeSourceItem[] = []

const EMPTY_CHAT_DIRECTORY_SNAPSHOT: ArkmeChatDirectorySnapshot = {
  revision: 0,
  sources: [],
  baselineReady: false,
  isRefreshing: false,
}
const EMPTY_CHAT_DELTA_SNAPSHOT: ArkmeChatTimelineSourceDeltaSnapshot = {
  revision: 0, items: [], removedItemKeys: [], invalidationRevision: 0,
}
const EMPTY_INTERWOVEN_INVALIDATION: ArkmeInterwovenInvalidationSnapshot = Object.freeze({ revision: 0 })
const NOOP_SUBSCRIBE = (): (() => void) => () => undefined
const INACTIVE_RECORD_INPUT_RECORDER: ArkmeBackgroundSoundRecorder = Object.freeze({
  async start() {},
  async stop() { return undefined },
  async cancel() {},
  async dispose() {},
  subscribeAmplitude() { return () => undefined },
})
export function arkmeShouldDismissAnchoredMenu(
  target: Node | null,
  menu: Pick<Node, 'contains'> | null,
  trigger: Pick<Node, 'contains'> | null,
): boolean {
  return target !== null && menu?.contains(target) !== true && trigger?.contains(target) !== true
}

export function arkmeShouldToggleMessageSelectFromRowClick(target: EventTarget | null): boolean {
  return !(typeof Element !== 'undefined' && target instanceof Element && target.closest('[data-arkme-select-check]') !== null)
}

function AgentAssistantIcon({ size = 12 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="2 1.4 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      data-arkme-agent-source-icon="assistant"
    >
      <path d="M3.25 6.72C3.25 5.67 3.67 4.78 4.38 4.18L4.25 2.7C4.22 2.4 4.57 2.22 4.82 2.42L6.2 3.5C6.76 3.33 7.37 3.24 8 3.24C8.63 3.24 9.24 3.33 9.8 3.5L11.18 2.42C11.43 2.22 11.78 2.4 11.75 2.7L11.62 4.18C12.33 4.78 12.75 5.67 12.75 6.72V8.78C12.75 11.11 10.63 12.76 8 12.76C5.37 12.76 3.25 11.11 3.25 8.78V6.72Z" fill="#FFFDF4" stroke="#252525" strokeWidth="0.7" strokeLinejoin="round" />
      <path d="M4.38 4.18L4.25 2.7C4.22 2.4 4.57 2.22 4.82 2.42L6.2 3.5C6.55 3.4 6.93 3.32 7.32 3.28C7.26 4.12 7.28 4.79 7.16 5.42C7.04 6.1 6.84 6.65 6.68 7.18C6.5 7.78 6.13 8.19 5.57 8.35C4.75 8.58 3.9 8.23 3.26 7.67V6.72C3.26 5.67 3.67 4.78 4.38 4.18Z" fill="#252525" />
      <path d="M8.68 3.28C9.07 3.32 9.45 3.4 9.8 3.5L11.18 2.42C11.43 2.22 11.78 2.4 11.75 2.7L11.62 4.18C12.33 4.78 12.74 5.67 12.74 6.72V7.84C12.12 8.38 11.29 8.63 10.51 8.34C9.9 8.11 9.5 7.66 9.34 7.08C9.19 6.54 8.97 5.99 8.84 5.34C8.72 4.72 8.74 4.08 8.68 3.28Z" fill="#252525" />
      <ellipse cx="5.92" cy="6.76" rx="0.66" ry="0.72" fill="#FFFDF4" />
      <ellipse cx="10.08" cy="6.76" rx="0.66" ry="0.72" fill="#FFFDF4" />
      <ellipse cx="5.98" cy="6.7" rx="0.32" ry="0.43" fill="#252525" />
      <ellipse cx="10.14" cy="6.7" rx="0.32" ry="0.43" fill="#252525" />
      <circle cx="5.86" cy="6.46" r="0.14" fill="#FFFDF4" />
      <circle cx="10.02" cy="6.46" r="0.14" fill="#FFFDF4" />
      <path d="M7.58 8.24C7.8 8.09 8.2 8.09 8.42 8.24C8.54 8.32 8.51 8.49 8.38 8.56L8 8.75L7.62 8.56C7.49 8.49 7.46 8.32 7.58 8.24Z" fill="#EFA7A2" />
      <path d="M8 8.76V8.92M8 8.92C7.83 9.1 7.55 9.08 7.34 8.9M8 8.92C8.17 9.1 8.45 9.08 8.66 8.9" stroke="#252525" strokeWidth="0.44" strokeLinecap="round" />
    </svg>
  )
}

const colors = {
  panel: arkmeTheme.base,
  text: arkmeTheme.text,
  secondary: arkmeTheme.secondary,
  border: arkmeTheme.border,
  danger: arkmeTheme.danger,
}

const MESSAGE_ACTION_REQUEST_TIMEOUT_MS = 12_000
const MESSAGE_FORWARD_REQUEST_TIMEOUT_MS = 30_000
const MESSAGE_ACTION_NOTICE_MS = 1_800
const FORWARD_TARGET_LIMIT = 80
const MAX_FORWARD_TARGET_SELECTION = 5

const ARKME_MESSAGE_AVATAR_SIZE = 34

const styles: Record<string, CSSProperties> = {
  surface: {
    position: 'relative', overflow: 'hidden', width: '100%', height: '100%', minWidth: 0,
    display: 'flex', background: '#ffffff', color: colors.text,
  },
  compactSurface: { flexDirection: 'column' },
  floatingSurface: { background: '#ffffff' },
  directoryPane: {
    width: 300, minWidth: 300, height: '100%', minHeight: 0, flex: 'none', overflow: 'hidden',
    borderRight: `1px solid ${colors.border}`, background: '#ffffff', boxSizing: 'border-box',
  },
  compactDirectoryPane: {
    width: '100%', minWidth: 0, height: 'min(292px, 36vh)', flex: 'none',
    borderRight: 0, borderBottom: `1px solid ${colors.border}`,
  },
  panel: { flex: 1, width: '100%', height: '100%', minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' },
  conversationExportProgress: {
    position: 'absolute', zIndex: 72, top: 76, right: 16, maxWidth: 'calc(100% - 32px)', minHeight: 36,
    boxSizing: 'border-box', padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 8,
    border: '1px solid var(--dsw-alias-border-l1, rgba(31, 35, 41, 0.12))', borderRadius: 12,
    background: arkmeTheme.menu, color: arkmeTheme.text,
    boxShadow: '0 8px 24px rgba(31, 35, 41, 0.12)', pointerEvents: 'none',
    fontSize: 13, lineHeight: '18px',
  },
  conversationExportProgressName: {
    minWidth: 0, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600,
  },
  conversationExportProgressCount: {
    flex: 'none', color: 'var(--dsw-alias-label-secondary, #646a73)', fontVariantNumeric: 'tabular-nums',
  },
  contactBackdrop: {
    position: 'fixed', inset: 0, zIndex: 1000, padding: 16, boxSizing: 'border-box',
    display: 'grid', placeItems: 'center',
    background: 'var(--dsw-alias-bg-mask-1, rgba(19, 22, 26, 0.34))',
    backdropFilter: 'var(--dsw-mask-blur, blur(2px))', WebkitBackdropFilter: 'var(--dsw-mask-blur, blur(2px))',
  },
  contactDialog: {
    width: 'min(620px, 100%)', height: 'min(620px, calc(100% - 4px))', minHeight: 0,
    display: 'flex', flexDirection: 'column', overflow: 'hidden', border: `1px solid ${colors.border}`,
    borderRadius: 14, background: arkmeTheme.base, boxShadow: '0 22px 64px rgba(20, 23, 31, .22)',
  },
  contactDialogHeader: {
    height: 58, minHeight: 58, padding: '0 16px 0 20px', boxSizing: 'border-box', display: 'flex',
    alignItems: 'center', borderBottom: `1px solid ${colors.border}`, background: arkmeTheme.base,
  },
  contactDialogTitle: { flex: 1, minWidth: 0, margin: 0, color: colors.text, fontSize: 18, lineHeight: '24px', fontWeight: 600 },
  contactDialogClose: {
    width: 32, height: 32, padding: 0, border: 0, borderRadius: 8, background: 'transparent',
    color: colors.secondary, cursor: 'pointer', fontSize: 25, lineHeight: 1,
  },
  contactDialogBody: { flex: 1, minHeight: 0, overflow: 'hidden' },
  header: {
    flex: 'none', height: 68, display: 'flex', alignItems: 'center', padding: '12px 16px 12px 20px',
    boxSizing: 'border-box', borderBottom: `1px solid ${colors.border}`, position: 'relative', gap: 4,
  },
  titleGroup: { flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' },
  headerAvatar: { flex: 'none', display: 'grid', placeItems: 'center', marginRight: 6 },
  titleBlock: { flex: '0 1 auto', minWidth: 0, maxWidth: '100%', padding: '2px 0', display: 'flex', flexDirection: 'column', justifyContent: 'center' },
  titleLine: { minWidth: 0, display: 'flex', alignItems: 'center', gap: 6 },
  title: { flex: '0 1 auto', minWidth: 0, margin: 0, fontSize: 15, lineHeight: '21px', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  headerSubtitle: { color: colors.secondary, fontSize: 11, lineHeight: '15px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  titleMuteIcon: { width: 16, height: 16, flex: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: colors.secondary },

  messageActionToast: {
    position: 'absolute', left: '50%', bottom: 132, zIndex: 95, transform: 'translateX(-50%)',
    maxWidth: 'calc(100% - 48px)', padding: '8px 12px', borderRadius: 6, background: 'rgba(25,27,35,.94)',
    color: '#fff', boxShadow: '0 8px 24px rgba(20,23,31,.18)', fontSize: 12, lineHeight: '18px',
    textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', pointerEvents: 'none',
  },
  forwardSuccessBannerWrap: {
    position: 'absolute', left: 0, right: 0, bottom: 118, zIndex: 85, display: 'flex', justifyContent: 'center',
    padding: '0 16px', pointerEvents: 'none',
  },
  forwardSuccessBanner: {
    width: 'min(380px, 100%)', minHeight: 48, display: 'flex', alignItems: 'center', gap: 9,
    padding: '9px 12px', border: 0, borderRadius: 12, boxSizing: 'border-box',
    background: 'color-mix(in srgb, var(--dsw-alias-bg-layer-3, #fff) 80%, transparent)',
    color: colors.text, boxShadow: '0 5px 14px rgba(20,23,31,.16)', backdropFilter: 'blur(40px)',
    cursor: 'pointer', pointerEvents: 'auto', textAlign: 'left',
  },
  forwardSuccessAvatarStack: { width: 30, height: 30, flex: 'none', position: 'relative' },
  forwardSuccessText: { minWidth: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13, lineHeight: '18px', fontWeight: 600 },
  forwardSuccessAction: { flex: 'none', fontSize: 13, lineHeight: '18px', fontWeight: 700 },
  body: { flex: 1, minHeight: 0, overflowX: 'hidden', overflowY: 'auto', padding: '22px 22px 12px', background: arkmeTheme.base },
  utilityBody: { flex: 1, minHeight: 0, overflowY: 'auto' },
  error: { padding: '10px 12px', borderRadius: 9, background: arkmeTheme.dangerSoft, color: colors.danger, fontSize: 13 },
  records: { width: '100%', minWidth: 0, listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 0 },
  timelineSkeleton: { display: 'flex', flexDirection: 'column', gap: 24, padding: '4px 0 20px' },
  timelineSkeletonRow: { display: 'flex', alignItems: 'flex-start', gap: 11 },
  timelineSkeletonRowMe: { flexDirection: 'row-reverse' },
  timelineSkeletonAvatar: { width: 34, height: 34, flex: 'none', borderRadius: '50%', background: arkmeTheme.subtle },
  timelineSkeletonBubble: { height: 54, borderRadius: 14, background: arkmeTheme.subtle },
  date: { alignSelf: 'center', marginBottom: 18, color: arkmeTheme.caption, fontSize: 10 },
  row: { width: '100%', minWidth: 0, display: 'flex', background: 'transparent', transition: 'background-color .3s ease' },
  rowSearchTarget: { position: 'relative', isolation: 'isolate' },
  rowSearchTargetBackdrop: {
    position: 'absolute', top: -6, right: -6, bottom: 12, left: -6, zIndex: -1,
    background: arkmeTheme.active, pointerEvents: 'none',
  },
  rowMe: { justifyContent: 'flex-end' },
  rowOther: { justifyContent: 'flex-start' },
  sharedRecordingRow: { justifyContent: 'center' },
  ...messageSelectionStyles,
  messageLine: { maxWidth: '100%', display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 18 },
  messageLineSelectAvatarMode: { minWidth: 0, marginBottom: 0 },
  messageLineMe: { flexDirection: 'row-reverse' },
  forwardMessageLine: { width: 'auto' },
  sharedRecordingMessageLine: { width: 'min(600px, 100%)', justifyContent: 'center', marginBottom: 42 },
  messageLineSelectCardCenterMode: { gridColumn: '2', minWidth: 0, justifySelf: 'center', marginBottom: 0 },
  messageBody: { minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 7 },
  messageBodyMe: { alignItems: 'flex-end' },
  extensionMessageGroup: { width: '100%', minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0 },
  extensionMessageGroupMe: { alignItems: 'flex-end' },
  extensionChildLine: { width: '100%', minWidth: 0, display: 'flex', alignItems: 'flex-start', gap: 10 },
  extensionChildLineMe: { flexDirection: 'row-reverse' },
  extensionChildBody: { minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 7 },
  extensionChildBodyMe: { alignItems: 'flex-end' },
  forwardMessageBody: { flex: 1 },
  sharedRecordingMessageBody: { width: '100%', flex: 'none', alignItems: 'stretch' },
  messageAvatar: {
    width: ARKME_MESSAGE_AVATAR_SIZE, height: ARKME_MESSAGE_AVATAR_SIZE, flex: 'none', overflow: 'hidden', borderRadius: 999,
    display: 'grid', placeItems: 'center', background: 'transparent', color: arkmeTheme.secondary, fontSize: 11, fontWeight: 600,
  },
  messageAvatarImage: { width: '100%', height: '100%', display: 'block', objectFit: 'cover' },
  sender: { color: colors.text, fontSize: 12, fontWeight: 600 },
  messageHeader: { display: 'flex', alignItems: 'center', gap: 7 },
  agentSource: {
    marginTop: 4, maxWidth: '100%', display: 'inline-flex', alignItems: 'center', gap: 2,
    color: colors.secondary, fontSize: 12, lineHeight: '14.4px', fontWeight: 400,
  },
  agentSourceIcon: { flex: 'none', width: 12, height: 12, display: 'grid', placeItems: 'center', overflow: 'hidden' },
  agentSourceText: { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  selfTopicBadge: {
    maxWidth: 'min(600px, 100%)', minWidth: 0, height: 24, display: 'inline-flex', alignItems: 'center', gap: 3,
    padding: '0 6px', border: `1px solid ${colors.border}`, borderRadius: 8, background: colors.panel, color: arkmeTheme.secondary,
    font: 'inherit', fontSize: 11, lineHeight: '14px', cursor: 'pointer',
  },
  selfTopicBadgeText: { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  selfTopicBadgeChevron: { flex: 'none', color: arkmeTheme.tertiary, fontSize: 15, lineHeight: 1 },
  bubble: { maxWidth: 'min(600px, 100%)', minWidth: 0, padding: '10px 13px', overflow: 'hidden', overflowWrap: 'anywhere', wordBreak: 'break-word', borderRadius: '5px 16px 16px 16px', boxSizing: 'border-box', cursor: 'pointer', border: '1px solid rgba(29,32,40,.035)' },
  bubbleMe: { background: arkmeTheme.messageOwn, borderColor: 'rgba(83,97,145,.045)', borderRadius: '16px 5px 16px 16px', '--arkme-bubble-fade': arkmeTheme.messageOwn } as CSSProperties,
  bubbleOther: { background: arkmeTheme.messageOther, '--arkme-bubble-fade': arkmeTheme.messageOther } as CSSProperties,
  extensionParentPreview: {
    maxWidth: 'min(600px, 100%)', minWidth: 30, display: 'flex', alignItems: 'center', gap: 8,
    padding: 8, boxSizing: 'border-box', overflow: 'hidden', borderRadius: '12px 12px 0 0',
    background: `linear-gradient(180deg, ${arkmeTheme.subtle} 0%, transparent 68.06%)`,
    color: arkmeTheme.tertiary, fontSize: 12, lineHeight: '16px',
  },
  extensionParentPreviewMe: { marginRight: ARKME_MESSAGE_AVATAR_SIZE + 10 },
  extensionParentPreviewOther: { marginLeft: ARKME_MESSAGE_AVATAR_SIZE + 10 },
  extensionParentText: { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  extensionParentMedia: { display: 'flex', flex: 'none', gap: 4 },
  extensionParentImage: { width: 32, height: 32, display: 'block', objectFit: 'cover', borderRadius: 5, background: arkmeTheme.subtle },
  extensionParentFile: { maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: arkmeTheme.secondary },
  forwardBubble: { width: 354, maxWidth: '100%', minWidth: 0, padding: '13px 15px' },
  sharedRecordingBubble: {
    width: '100%', maxWidth: '100%', minWidth: 0, padding: '18px 21px',
    borderRadius: 12, background: arkmeTheme.base, borderColor: arkmeTheme.border,
    boxShadow: 'none', '--arkme-bubble-fade': arkmeTheme.base,
  } as CSSProperties,
  text: { margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12, lineHeight: '20px' },
  meta: { color: arkmeTheme.tertiary, fontSize: 11 },
  polishMeta: { minHeight: 14, marginBottom: 2, color: colors.secondary, fontSize: 10, lineHeight: '14px', display: 'flex', gap: 8, alignItems: 'center' },
  retry: { border: 0, padding: 0, background: 'transparent', color: arkmeTheme.danger, cursor: 'pointer', fontSize: 11 },
  notice: { alignSelf: 'center', maxWidth: 520, padding: '8px 12px 0', color: colors.secondary, textAlign: 'center', fontSize: 13, lineHeight: '16px' },
  memberJoinNotice: { alignSelf: 'center', width: '100%', boxSizing: 'border-box', color: arkmeTheme.caption, textAlign: 'center' },
  memberJoinTime: {
    width: 'fit-content', margin: '0 auto', padding: '4px 6px', boxSizing: 'border-box',
    color: arkmeTheme.caption, fontSize: 12, lineHeight: '14.4px', textAlign: 'center',
  },
  memberJoinLine: {
    width: 'clamp(240px, calc(100% - 120px), 640px)', maxWidth: 'calc(100% - 24px)', margin: '0 auto',
    paddingTop: 8, overflow: 'hidden', color: arkmeTheme.caption, fontSize: 13, lineHeight: '15.6px',
    textAlign: 'center', textOverflow: 'ellipsis', whiteSpace: 'nowrap', boxSizing: 'border-box',
  },
  memberJoinLink: { display: 'inline', padding: 0, border: 0, background: 'transparent', color: arkmeTheme.info, cursor: 'pointer', font: 'inherit', fontWeight: 500, lineHeight: 'inherit' },
  sentinel: { width: '100%', height: 1 },
  loading: { textAlign: 'center', color: colors.secondary, fontSize: 12, padding: 6 },
  composer: { ...arkmeConversationComposerLayout.composer, background: '#fff' },
  composerInfoRow: { display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, flex: 'none' },
  composerDestinationHint: {
    flex: '1 1 auto', minWidth: 0, overflow: 'hidden', paddingLeft: 4, display: 'flex', alignItems: 'center', gap: 0,
    color: arkmeTheme.tertiary, fontSize: 11, lineHeight: 'normal', whiteSpace: 'pre',
    transition: 'opacity 150ms linear, height 150ms linear', pointerEvents: 'none',
  },
  composerDestinationHintIcon: { flexShrink: 0, marginRight: 6, lineHeight: 'normal' },
  composerDestinationHintText: { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  composerDestinationHintName: { color: arkmeTheme.secondary, fontWeight: 500 },
  composerExtensionTarget: {
    margin: 0, padding: 8, display: 'flex', alignItems: 'flex-start', gap: 10,
    borderRadius: '12px 12px 0 0', border: `1px solid ${colors.border}`, borderBottom: 0,
    background: arkmeTheme.extensionSource,
    boxShadow: `inset 0 -1px 0 ${colors.border}`,
  },
  composerExtensionTargetBody: { flex: 1, minWidth: 0 },
  composerReeditLabel: { color: arkmeTheme.warning, fontSize: 12, lineHeight: '18px' },
  composerExtensionTargetText: { overflow: 'hidden', color: arkmeTheme.tertiary, fontSize: 12, lineHeight: '18px', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  composerExtensionTargetFiles: { marginTop: 5, display: 'flex', gap: 5, overflow: 'hidden', color: arkmeTheme.tertiary, fontSize: 10, lineHeight: '14px' },
  composerExtensionTargetFile: { width: 42, height: 34, display: 'grid', placeItems: 'center', overflow: 'hidden', borderRadius: 5, background: arkmeTheme.elevated, textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  composerExtensionTargetImage: { width: '100%', height: '100%', display: 'block', objectFit: 'cover' },
  composerExtensionTargetCancel: { flex: 'none', width: 24, height: 24, padding: 0, border: 0, borderRadius: 6, background: 'transparent', color: arkmeTheme.tertiary, cursor: 'pointer', fontSize: 18, lineHeight: '22px' },
  selectBar: { ...messageSelectionStyles.selectBar, position: 'absolute', inset: 0, zIndex: 35 },
  forwardTargetBackdrop: {
    position: 'absolute', inset: 0, zIndex: 70, display: 'grid', placeItems: 'center',
    padding: 48, boxSizing: 'border-box', background: 'rgba(19,22,26,.30)',
  },
  forwardTargetDialog: {
    width: 'min(520px, 100%)', height: 'min(660px, 100%)', minHeight: 'min(520px, 100%)',
    display: 'flex', flexDirection: 'column', overflow: 'hidden',
    border: `1px solid ${colors.border}`, borderRadius: 16, background: arkmeTheme.base,
    boxShadow: '0 22px 64px rgba(20,23,31,.24)',
  },
  forwardTargetHeader: {
    height: 54, flex: 'none', display: 'grid', gridTemplateColumns: '54px 1fr 54px', alignItems: 'center', padding: 0,
    boxSizing: 'border-box',
  },
  forwardTargetTitle: { minWidth: 0, margin: 0, textAlign: 'center', color: colors.text, fontSize: 16, lineHeight: '22px', fontWeight: 600 },
  forwardTargetClose: { width: 44, height: 44, border: 0, borderRadius: 10, background: 'transparent', color: arkmeTheme.tertiary, cursor: 'pointer', fontSize: 30, lineHeight: '30px' },
  forwardTargetSearchWrap: {
    margin: '4px 18px 12px', height: 38, flex: 'none',
  },
  forwardTargetBody: { flex: 1, minHeight: 0, overflowY: 'auto' },
  forwardTargetList: { margin: 0, padding: '4px 18px 18px', listStyle: 'none' },
  forwardTargetRow: {
    position: 'relative',
    width: '100%', minHeight: 56, display: 'grid', gridTemplateColumns: '20px 36px minmax(0, 1fr) auto', alignItems: 'center', gap: 10, padding: '9px 8px',
    boxSizing: 'border-box', border: 0, borderRadius: 8, background: 'transparent', color: colors.text,
    cursor: 'pointer', textAlign: 'left', font: 'inherit',
  },
  forwardTargetRowSelected: {},
  forwardTargetCheck: {
    width: 20, height: 20, display: 'grid', placeItems: 'center', borderRadius: 999, border: `1px solid ${arkmeTheme.tertiary}`,
    color: 'transparent', boxSizing: 'border-box', fontSize: 15, lineHeight: 1,
    opacity: .4,
  },
  forwardTargetCheckSelected: { borderColor: arkmeTheme.text, background: arkmeTheme.text, color: arkmeTheme.base, opacity: 1 },
  forwardTargetText: { minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 },
  forwardTargetName: { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 14, lineHeight: '22px', fontWeight: 500 },
  forwardTargetMeta: { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: colors.secondary, fontSize: 12, lineHeight: '18px' },
  forwardTargetTime: { color: arkmeTheme.caption, fontSize: 12, lineHeight: '18px', whiteSpace: 'nowrap' },
  forwardTargetStatus: { padding: '16px 18px 28px', color: colors.secondary, textAlign: 'center', fontSize: 12, lineHeight: '18px' },
  forwardTargetFooter: {
    flex: 'none', padding: '10px 16px 14px', borderTop: `1px solid ${colors.border}`, boxSizing: 'border-box',
    background: arkmeTheme.base,
  },
  forwardTargetRecipients: { display: 'flex', alignItems: 'center', gap: 10, minHeight: 30, padding: '0 2px', color: colors.text, fontSize: 14, lineHeight: '20px', fontWeight: 600 },
  forwardTargetAvatarStack: { minWidth: 0, display: 'flex', alignItems: 'center', gap: 8, overflow: 'hidden' },
  forwardTargetFooterDivider: { height: 1, margin: '8px 2px 0', background: colors.border },
  forwardTargetPreview: {
    position: 'relative', minHeight: 34, display: 'grid', gridTemplateColumns: '26px minmax(0, 1fr) 28px', alignItems: 'center', gap: 8,
    padding: '0 2px 2px', marginTop: 6, boxSizing: 'border-box',
  },
  forwardTargetPreviewIcon: { width: 26, height: 32, color: colors.secondary, display: 'grid', placeItems: 'center' },
  forwardTargetPreviewText: { minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 },
  forwardTargetPreviewTitle: { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: colors.text, fontSize: 12, lineHeight: '16px', fontWeight: 400 },
  forwardTargetPreviewSubtitle: { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: arkmeTheme.tertiary, fontSize: 12, lineHeight: '16px' },
  forwardTargetPreviewClose: {
    width: 28, height: 28, borderRadius: 14, border: 0, background: 'transparent',
    color: arkmeTheme.tertiary, display: 'grid', placeItems: 'center', cursor: 'pointer', padding: 0,
  },
  forwardTargetComposer: {
    position: 'relative', minHeight: 54, marginTop: 6, boxSizing: 'border-box', borderRadius: 12, background: arkmeTheme.layer2,
  },
  forwardTargetCommentInput: {
    width: '100%', minWidth: 0, minHeight: 54, maxHeight: 102, resize: 'none', border: 0, outline: 'none',
    padding: '17px 66px 17px 16px', boxSizing: 'border-box', background: 'transparent', color: colors.text,
    fontSize: 14, lineHeight: '20px', fontFamily: 'inherit',
  },
  forwardTargetSend: {
    position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
    width: 40, height: 40, border: 0, borderRadius: 999, display: 'grid', placeItems: 'center',
    background: arkmeTheme.primaryAction, color: arkmeTheme.onPrimaryAction, cursor: 'pointer', padding: 0,
  },
  forwardTargetSendDisabled: { opacity: .45, cursor: 'default' },
  forwardTargetSendError: { margin: '8px 2px 0', color: colors.danger, fontSize: 12, lineHeight: '16px' },
  copyLinkDetailPanel: {
    position: 'absolute', top: ARKME_CONVERSATION_HEADER_HEIGHT, right: 0, bottom: 0, zIndex: 10,
    minWidth: 0, boxSizing: 'border-box', display: 'flex', flexDirection: 'column',
    background: arkmeTheme.base, color: arkmeTheme.text, borderLeft: `1px solid ${arkmeTheme.borderSoft}`,
    boxShadow: '-12px 0 28px rgba(29,32,40,.055)',
  },
  copyLinkDetailHeader: {
    height: 58, flex: 'none', position: 'relative', display: 'grid', placeItems: 'center',
    padding: '0 92px 0 58px', boxSizing: 'border-box',
  },
  copyLinkDetailHeaderButton: {
    width: 40, height: 44, border: 0, padding: 0, borderRadius: 10, display: 'grid', placeItems: 'center',
    background: 'transparent', color: arkmeTheme.text, cursor: 'pointer',
  },
  copyLinkDetailBody: {
    flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', padding: '18px 30px 8px', boxSizing: 'border-box',
    display: 'flex', flexDirection: 'column',
  },
  copyLinkDetailRecord: { display: 'grid', gridTemplateColumns: '46px minmax(0, 1fr)', alignItems: 'flex-start', gap: 14 },
  copyLinkDetailPlaceholderAvatar: {
    width: 46, height: 46, flex: 'none', borderRadius: 999, display: 'grid', placeItems: 'center',
    background: arkmeTheme.subtle, color: arkmeTheme.secondary, fontSize: 15, fontWeight: 600, overflow: 'hidden',
  },
  copyLinkDetailAvatarImage: { width: '100%', height: '100%', display: 'block', objectFit: 'cover' },
  copyLinkDetailRecordMeta: { minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1, paddingTop: 2 },
  copyLinkDetailRecordSender: {
    minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    color: arkmeTheme.text, fontSize: 14, lineHeight: '20px', fontWeight: 600,
  },
  copyLinkDetailRecordTime: { color: arkmeTheme.tertiary, fontSize: 12, lineHeight: '18px' },
  copyLinkDetailRecordContent: {
    gridColumn: '1 / 3', marginTop: 10, minWidth: 0, color: arkmeTheme.text, textAlign: 'left',
    fontSize: 17, lineHeight: '27px', wordBreak: 'break-word', overflowWrap: 'anywhere',
  },
  copyLinkDetailDivider: { flex: 'none', height: 1, background: arkmeTheme.borderSoft, margin: '8px 0 14px' },
  copyLinkDetailExtensionTitle: { margin: '0 0 14px', color: arkmeTheme.secondary, fontSize: 14, lineHeight: '20px' },
  copyLinkDetailExtensionList: { display: 'flex', flexDirection: 'column' },
  copyLinkDetailExtensionItem: {
    display: 'grid', gridTemplateColumns: '36px minmax(0, 1fr)', gap: 10, alignItems: 'flex-start',
    padding: '10px 0 14px', marginBottom: 12, borderBottom: `1px solid ${arkmeTheme.borderSoft}`,
  },
  copyLinkDetailExtensionHead: { minWidth: 0, display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 },
  copyLinkDetailExtensionSender: {
    minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    color: arkmeTheme.text, fontSize: 12, lineHeight: '18px', fontWeight: 600,
  },
  copyLinkDetailExtensionTime: { flex: 'none', color: arkmeTheme.tertiary, fontSize: 12, lineHeight: '18px' },
  copyLinkDetailExtensionContent: {
    marginTop: 4, minWidth: 0, color: arkmeTheme.text, fontSize: 14, lineHeight: '22px',
    wordBreak: 'break-word', overflowWrap: 'anywhere',
  },
  copyLinkDetailMultiList: { display: 'flex', flexDirection: 'column', gap: 24 },
  copyLinkDetailMultiRow: { display: 'grid', gridTemplateColumns: '36px minmax(0, 1fr)', alignItems: 'flex-start', gap: 10 },
  copyLinkDetailMultiContent: { minWidth: 0, display: 'flex', flexDirection: 'column', gap: 5 },
  copyLinkDetailFooter: {
    flex: 'none', boxSizing: 'border-box', background: arkmeTheme.base,
  },
  copyLinkDetailGeneratedAt: {
    minHeight: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '2px 16px', boxSizing: 'border-box', color: arkmeTheme.tertiary, textAlign: 'center', fontSize: 11, lineHeight: '16px',
  },
  copyLinkDetailInputArea: arkmeDetailExtensionComposerStyles.bar,
  copyLinkDetailInputBar: arkmeDetailExtensionComposerStyles.shell,
  copyLinkDetailInputIcon: {
    width: 18, height: 28, flex: 'none', alignSelf: 'flex-start',
    display: 'grid', placeItems: 'center', color: arkmeTheme.tertiary,
  },
  copyLinkDetailInput: arkmeDetailExtensionComposerStyles.input,
  copyLinkDetailSendError: {
    margin: '8px 2px 0', color: colors.danger, fontSize: 12, lineHeight: '16px',
  },
  copyLinkDetailStatus: {
    flex: 1, minHeight: 0, display: 'grid', placeItems: 'center', padding: 28,
    color: arkmeTheme.secondary, fontSize: 13, lineHeight: '20px', textAlign: 'center',
  },
  copyLinkDetailError: { display: 'inline-flex', alignItems: 'center', gap: 10 },
  copyLinkDetailRetry: { border: 0, padding: 0, background: 'transparent', color: arkmeTheme.info, cursor: 'pointer', font: 'inherit' },
  copyLinkDetailContentLabel: { margin: '6px 0 0', color: arkmeTheme.tertiary, fontSize: 12, lineHeight: '18px' },
  composerInner: {
    ...arkmeConversationComposerLayout.composerInner,
    cursor: 'text',
    background: 'var(--arkme-primary-composer-idle, #f6f6f6)', boxShadow: 'none',
    transition: 'background-color 140ms ease',
  },
  composerInnerFocused: {
    background: 'var(--arkme-primary-composer-focused, #ffffff)',
    boxShadow: 'none',
  },
  composerStack: { width: '100%', minWidth: 0, display: 'flex', flexDirection: 'column', containerType: 'inline-size', containerName: 'arkme-composer' },
  textarea: {
    ...arkmeConversationComposerLayout.textarea,
    background: 'transparent', color: colors.text, boxShadow: 'none', appearance: 'none', WebkitAppearance: 'none',
    caretColor: 'var(--dsw-alias-state-business-primary, #3964fe)',
  },
  tools: { ...arkmeConversationComposerLayout.tools },
  toolGroup: { display: 'flex', alignItems: 'center', gap: 2 },
  composerSendArea: { display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 },
  composerStats: { display: 'flex', flexShrink: 0, alignItems: 'center', gap: 10, marginLeft: 'auto', marginRight: 4, color: arkmeTheme.tertiary, fontSize: 12, lineHeight: '16px', whiteSpace: 'nowrap' },
  composerTimeToggle: { height: 18, display: 'flex', alignItems: 'center', gap: 4, border: 0, padding: 0, background: 'transparent', color: arkmeTheme.tertiary, cursor: 'pointer', font: 'inherit' },
  attachments: { display: 'flex', flexWrap: 'wrap', gap: 6, padding: '0 12px' },
  uploadStatus: { padding: '0 14px', color: colors.secondary, fontSize: 12 },
  mentionSuggestions: {
    position: 'absolute', left: 12, right: 12, bottom: 'calc(100% + 8px)', zIndex: 22,
    maxHeight: 252, overflowY: 'auto', padding: 6, boxSizing: 'border-box',
    border: `1px solid ${colors.border}`, borderRadius: 12, background: colors.panel,
    boxShadow: '0 12px 32px rgba(0,0,0,.15)',
  },
  mentionSuggestionRow: {
    width: '100%', minWidth: 0, height: 40, padding: '6px 8px', boxSizing: 'border-box',
    display: 'flex', alignItems: 'center', gap: 8, border: 0, borderRadius: 8,
    background: 'transparent', color: colors.text, cursor: 'pointer', textAlign: 'left',
  },
  mentionSuggestionRowActive: { background: arkmeTheme.hover },
  mentionSuggestionAvatar: { width: 28, height: 28, flex: 'none', overflow: 'hidden', borderRadius: 999, display: 'grid', placeItems: 'center' },
  mentionSuggestionBotAvatar: {
    width: 28, height: 28, display: 'grid', placeItems: 'center', borderRadius: 999,
    background: arkmeTheme.subtle, color: colors.text,
    border: `1px solid ${colors.border}`, boxSizing: 'border-box',
  },
  mentionSuggestionText: { minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column' },
  mentionSuggestionName: { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13, lineHeight: '17px', fontWeight: 500 },
  mentionSuggestionSecondary: { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: colors.secondary, fontSize: 11, lineHeight: '15px' },
  mentionSuggestionsEmpty: { padding: '8px 10px', color: colors.secondary, fontSize: 12, lineHeight: '18px' },
  forwardDrawerDismiss: { position: 'absolute', top: ARKME_CONVERSATION_HEADER_HEIGHT, right: 0, bottom: 0, left: 0, zIndex: 9, background: 'transparent' },
  loginBody: { flex: 1, minHeight: 0, overflowY: 'auto' },
}

export function arkmeAuthView(auth: ArkmeAuthSnapshot | undefined): ArkmeAuthView {
  return auth?.status === 'authenticated' ? 'content' : 'login'
}

export function arkmeLoginNeedsPhoneBinding(auth: ArkmeAuthSnapshot | undefined): boolean {
  return auth?.status === 'binding-required'
}

export function arkmeAuthenticatedAccountChanged(
  previous: ArkmeAuthSnapshot | undefined,
  next: ArkmeAuthSnapshot,
): boolean {
  return next.status === 'authenticated'
    && (previous?.status !== 'authenticated' || previous.userId !== next.userId || previous.environment !== next.environment)
}

function arkmeAuthenticatedAccountKey(auth: ArkmeAuthSnapshot | undefined): string | undefined {
  return auth?.status === 'authenticated' ? `${auth.environment}:${String(auth.userId)}` : undefined
}

export function arkmeArkoSurfaceKey(auth: ArkmeAuthSnapshot | undefined): number | 'authenticated' | 'logged-out' {
  if (auth?.status !== 'authenticated') return 'logged-out'
  return auth.userId ?? 'authenticated'
}

export function ArkmeTimelineAgentSourceBadge({ item }: { item: ArkmeTimelineItem }) {
  if (item.agentSource === undefined) return null
  return <span style={styles.agentSource} data-arkme-agent-source={item.agentSource.kind}>
    <span style={styles.agentSourceIcon} aria-hidden><AgentAssistantIcon size={12} /></span>
    <span style={styles.agentSourceText}>{item.agentSource.label}</span>
  </span>
}

export function arkmeTimelineSelfTopicSource(
  item: ArkmeTimelineItem,
  sources: readonly ArkmeSourceItem[],
): ArkmeSourceItem | undefined {
  const topic = item.selfTopic
  if (topic === undefined) return undefined
  const resolved = sources.find(source => source.kind === 'topic' && source.topicHierarchyKey === topic.topicHierarchyKey)
  if (resolved !== undefined) return resolved
  if (topic.sourceRef === undefined || topic.title === undefined) return undefined
  return {
    sourceRef: topic.sourceRef,
    kind: 'topic',
    displayName: topic.title,
    activeAtMillis: 0,
    unreadCount: 0,
  }
}

export function arkmeTimelineSelfTopicPresentation(
  item: ArkmeTimelineItem,
  currentSource: ArkmeSourceItem | undefined,
  sources: readonly ArkmeSourceItem[],
): { topic: ArkmeSourceItem; displayLabel?: string } | undefined {
  if (currentSource?.kind !== 'send_to_self' && currentSource?.kind !== 'topic') return undefined
  const topic = arkmeTimelineSelfTopicSource(item, sources)
  if (topic === undefined) return undefined
  if (currentSource.kind === 'topic' && (topic.sourceRef === currentSource.sourceRef
    || topic.topicHierarchyKey !== undefined && topic.topicHierarchyKey === currentSource.topicHierarchyKey)) return undefined
  const displayLabel = arkmeTopicPathNames(topic, sources).join(' / ') || topic.displayName
  return { topic, displayLabel }
}

type ArkmeDetailBackTarget = {
  source: ArkmeSourceItem
  itemUid: string
  sendAtMillis: number
}

type ArkmePendingExtensionParentDetail =
  | { revision: number; kind: 'push'; from: ArkmeDetailBackTarget }
  | { revision: number; kind: 'restore'; stack: ArkmeDetailBackTarget[] }

export function ArkmeTimelineSelfTopicBadge({
  topic,
  displayLabel,
  onSelect,
}: {
  topic: ArkmeSourceItem
  displayLabel?: string
  onSelect: (source: ArkmeSourceItem) => void
}) {
  const label = displayLabel?.trim() || topic.displayName
  return <button data-arkme-feedback="neutral"
    type="button"
    data-arkme-self-topic-badge={label}
    aria-label={`查看主题「${label}」`}
    title={label}
    style={styles.selfTopicBadge}
    onClick={event => {
      event.stopPropagation()
      onSelect(topic)
    }}
  ><ArkmeTopicSourceIcon size={13} /><span style={styles.selfTopicBadgeText}>{label}</span><span aria-hidden style={styles.selfTopicBadgeChevron}>›</span>
  </button>
}

export function arkmeShouldBeginWechat(
  auth: ArkmeAuthSnapshot | undefined,
  authView: ArkmeAuthView,
  loginMode: ArkmeLoginMode,
  agreed: boolean,
  qr: string,
  qrRequestStarted: boolean,
  ownsQrLogin = true,
): boolean {
  return ownsQrLogin
    && authView === 'login'
    && auth !== undefined
    && ['logged-out', 'expired'].includes(auth.status)
    && loginMode === 'wechat'
    && agreed
    && qr === ''
    && !qrRequestStarted
}

function errorMessage(error: unknown): string {
  if (error instanceof ArkmeClientError) return error.body.message
  return error instanceof Error ? error.message : String(error)
}

function locationCaptureFeedback(result: ArkmeRecordLocationForSendResult): string | undefined {
  if (result.state === 'permission-required') {
    return result.permission === 'denied'
      ? '消息已发送，但系统已拒绝位置权限；请在系统定位设置中允许后再试'
      : result.permission === 'unavailable'
        ? '消息已发送，但当前浏览器不支持位置采集'
        : '消息已发送，但位置尚未授权；请在系统定位设置中允许位置访问'
  }
  return result.state === 'failed' ? `消息已发送，但位置采集失败：${result.message}` : undefined
}

const ARKME_BACKGROUND_TRANSFER_TIMEOUT_MILLIS = 20_000

async function boundedArkmeBackgroundTransfer<T>(
  operation: (signal: AbortSignal) => Promise<T>,
  timeoutMillis = ARKME_BACKGROUND_TRANSFER_TIMEOUT_MILLIS,
): Promise<T> {
  const controller = new AbortController()
  const timer = globalThis.setTimeout(() => { controller.abort(new Error('背景音处理超时')) }, timeoutMillis)
  try {
    return await operation(controller.signal)
  } finally {
    globalThis.clearTimeout(timer)
  }
}

function relatedQuickNoteReferenceExpired(error: unknown): boolean {
  return error instanceof ArkmeClientError && error.body.code === 'related-quick-note-ref-expired'
}

function qrDataUrl(content: string): string {
  const qr = qrcode(0, 'M'); qr.addData(content); qr.make(); return qr.createDataURL(6, 12)
}

export function arkmeClipboardFiles(clipboardData: Pick<DataTransfer, 'files' | 'items'>): File[] {
  const itemFiles = Array.from(clipboardData.items)
    .filter(item => item.kind === 'file')
    .map(item => item.getAsFile())
    .filter((file): file is File => file !== null)
  return itemFiles.length > 0 ? itemFiles : Array.from(clipboardData.files)
}
export function arkmeClipboardImageFiles(clipboardData: Pick<DataTransfer, 'files' | 'items'>): File[] {
  return arkmeClipboardFiles(clipboardData).filter(file => file.type.toLowerCase().startsWith('image/'))
}

export function arkmeMessageCopyText(item: ArkmeTimelineItem): string {
  const title = item.title.trim()
  const text = item.textFormat === 'markdown' ? item.textContent : item.textContent.trim()
  if (title !== '' && text !== '') return `${title}\n${text}`
  if (text !== '') return text
  if (title !== '') return title
  if (item.forwardRecords !== undefined) return item.forwardRecords.title
  if (item.sharedRecording !== undefined) return `${item.sharedRecording.title}\n${item.sharedRecording.summary}`.trim()
  if ((item.contentBlocks?.length ?? 0) > 0) return '非文本内容'
  return ''
}

export function arkmeTimelineMessageActionRef(item: ArkmeTimelineItem): string {
  return item.messageActionRef?.trim() ?? ''
}

export function arkmeTimelineOccurrenceKey(item: ArkmeTimelineItem): string {
  const opaqueKey = item.timelineItemKey?.trim() ?? ''
  return opaqueKey === '' ? `item:${item.itemUid}` : opaqueKey
}

const ARKME_MESSAGE_ACTION_MENU_LABELS = ['复制', '复制链接', '延展', '多选', '转发'] as const
const ARKME_MESSAGE_REEDIT_ACTION_LABEL = '重新编辑'
const ARKME_MESSAGE_REPORT_ACTION_LABEL = '举报'
const ARKME_MESSAGE_ACTION_DETAIL_LABEL = '详情'

export function arkmeMessageActionMenuLabels(): readonly string[] {
  return ARKME_MESSAGE_ACTION_MENU_LABELS
}

export function arkmeCanReeditTimelineMessage(item: ArkmeTimelineItem): boolean {
  if (!item.isMe || item.status !== 1 || item.awaitingTimelineProjection === true) return false
  if (item.forwardRecords !== undefined || item.sharedRecording !== undefined) return false
  if (item.displayKind === 1) return false
  return [1, 2, 3, 4].includes(item.templateKind ?? 0)
}

export function arkmeMessageActionMenuRowCount(
  item?: ArkmeTimelineItem,
  source?: ArkmeSourceItem,
  selfRole: import('../types.js').ArkmeGroupMemberRole = 'unknown',
): number {
  return ARKME_MESSAGE_ACTION_MENU_LABELS.length
    + (source !== undefined && isArkmeSelfWorkspaceSource(source) ? 1 : 0)
    + (item !== undefined && arkmeCanWithdrawTimelineMessage(source, item, selfRole) ? 1 : 0)
    + (item !== undefined && arkmeCanReeditTimelineMessage(item) ? 1 : 0)
    + (item !== undefined && arkmeCanReportTimelineMessage(source, item) ? 1 : 0)
    + (item !== undefined && arkmeCanOpenMessageSnapshot(item) ? 1 : 0)
}

const ARKME_MESSAGE_SELECT_ACTION_LABELS = ['复制文本', '复制链接', '转发', '退出多选'] as const

export function arkmeMessageSelectActionLabels(): readonly string[] {
  return ARKME_MESSAGE_SELECT_ACTION_LABELS
}

export function arkmeCanCopySelectedMessageText(selectedCount: number, actionBusy: boolean): boolean {
  return selectedCount === 1 && !actionBusy
}

export function arkmeSelectedTimelineItems(
  items: readonly ArkmeTimelineItem[],
  selectedIds: ReadonlySet<string>,
): ArkmeTimelineItem[] {
  return items.filter(item => selectedIds.has(arkmeTimelineOccurrenceKey(item)))
}

export async function arkmeCopyTextToClipboard(value: string, signal?: AbortSignal): Promise<void> {
  signal?.throwIfAborted()
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText !== undefined) {
    try {
      await navigator.clipboard.writeText(value)
      return
    } catch {
      // Fall back to the old textarea path for embedded WebViews without clipboard grants.
    }
  }
  signal?.throwIfAborted()
  if (typeof document === 'undefined') throw new Error('复制失败，请稍后重试')
  const textarea = document.createElement('textarea')
  textarea.value = value
  textarea.setAttribute('readonly', 'true')
  textarea.style.position = 'fixed'
  textarea.style.left = '-9999px'
  textarea.style.top = '0'
  document.body.appendChild(textarea)
  const selection = document.getSelection()
  const ranges = selection === null ? [] : Array.from({ length: selection.rangeCount }, (_, index) => selection.getRangeAt(index).cloneRange())
  const focused = document.activeElement
  try {
    textarea.select()
    if (!document.execCommand('copy')) throw new Error('复制失败，请稍后重试')
  } finally {
    textarea.remove()
    if (focused instanceof HTMLElement) focused.focus({ preventScroll: true })
    if (selection !== null) {
      selection.removeAllRanges()
      for (const range of ranges) selection.addRange(range)
    }
  }
}

function arkmeForwardTargetKey(source: ArkmeSourceItem): string {
  return source.kind === 'send_to_self' ? 'send_to_self' : arkmeSourceIdentityKey(source)
}

export function arkmeForwardableTargetSources(
  rootSources: readonly ArkmeSourceItem[],
  selfTarget?: ArkmeSourceItem,
  selectedTargetKeys: readonly string[] = [],
): ArkmeSourceItem[] {
  const targets = new Map<string, ArkmeSourceItem>()
  const addChatTarget = (source: ArkmeSourceItem | undefined) => {
    if (source === undefined || targets.has(arkmeForwardTargetKey(source))) return
    if (source.kind !== 'private_chat' && source.kind !== 'group_chat') return
    targets.set(arkmeForwardTargetKey(source), source)
  }
  const addSendToSelfRoot = (source: ArkmeSourceItem | undefined) => {
    if (source === undefined || source.kind !== 'send_to_self' || targets.has(arkmeForwardTargetKey(source))) return
    targets.set(arkmeForwardTargetKey(source), source)
  }
  addSendToSelfRoot(selfTarget)
  for (const source of rootSources) addChatTarget(source)
  return [...targets.values()].sort((left, right) => {
    if (left.kind === 'send_to_self') return right.kind === 'send_to_self' ? 0 : -1
    if (right.kind === 'send_to_self') return 1
    return Number(right.isPinned === true) - Number(left.isPinned === true)
      || right.activeAtMillis - left.activeAtMillis
  }).filter((target, index) => index < FORWARD_TARGET_LIMIT || selectedTargetKeys.includes(arkmeForwardTargetKey(target)))
}

export function arkmeForwardTargetVisibleSources(
  targets: readonly ArkmeSourceItem[],
  keyword: string,
): ArkmeSourceItem[] {
  const normalizedKeyword = keyword.trim().toLowerCase()
  if (normalizedKeyword === '') return targets.filter(arkmeSourceAllowsUserWrite)
  return targets.filter(arkmeSourceAllowsUserWrite).filter(target => {
    const haystack = `${target.displayName} ${arkmeForwardTargetMeta(target)}`.toLowerCase()
    return haystack.includes(normalizedKeyword)
  })
}

function arkmeForwardTargetMeta(source: ArkmeSourceItem): string {
  switch (source.kind) {
    case 'private_chat': return '私聊'
    case 'group_chat': return '群聊'
    case 'send_to_self': return '默认分类'
    case 'default_category': return '默认分类'
    case 'topic': return '主题'
  }
}

function dayKey(value: number): string {
  const date = new Date(value); return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`
}

function dayLabel(value: number): string {
  return new Intl.DateTimeFormat(arkmeIntlLocale(), { month: '2-digit', day: '2-digit' }).format(new Date(value))
}

function timeLabel(value: number): string {
  return new Intl.DateTimeFormat(arkmeIntlLocale(), { hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(value))
}

export function arkmeConversationJoinEventsInLoadedWindow(
  events: readonly ArkmeConversationMemberJoinEvent[],
  items: readonly ArkmeTimelineItem[],
  hasMore: boolean,
): ArkmeConversationMemberJoinEvent[] {
  if (!hasMore) return [...events]
  if (items.length === 0) return []
  const oldestLoadedMillis = Math.min(...items.map(item => item.sendAtMillis).filter(value => value > 0))
  if (!Number.isFinite(oldestLoadedMillis)) return []
  return events.filter(event => event.occurredAtMillis >= oldestLoadedMillis)
}

export function arkmeMemberJoinDisplayName(person: ArkmeConversationMemberJoinPerson, maxCount: number): string {
  if (person.isSelf) return '你'
  const normalized = person.displayName.replace(/[\u0000-\u001F\u007F]/g, ' ').replace(/\s+/g, ' ').trim()
  const characters = [...new Intl.Segmenter('zh-CN', { granularity: 'grapheme' }).segment(normalized)]
    .map(item => item.segment)
  if (characters.length <= maxCount) return normalized
  return `${characters.slice(0, maxCount).join('')}...`
}

export function arkmeMemberJoinTimeLabel(value: number, nowMillis = Date.now()): string {
  const date = new Date(value)
  const now = new Date(nowMillis)
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
  const target = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
  const dayOffset = Math.round((start - target) / 86_400_000)
  const time = timeLabel(value)
  if (dayOffset === 0) return time
  if (dayOffset === 1) return tr("昨天 {v0}", { v0: time })
  if (dayOffset === 2) return tr("前天 {v0}", { v0: time })
  return `${String(date.getMonth() + 1).padStart(2, '0')}月${String(date.getDate()).padStart(2, '0')}日 ${time}`
}

export function arkmeVisibleMemberJoinInvitees(
  event: ArkmeConversationMemberJoinEvent,
): ArkmeConversationMemberJoinPerson[] {
  if (event.invitees.length <= 2) return [...event.invitees]
  const visible = event.invitees.slice(0, 2)
  if (visible.some(person => person.isSelf)) return visible
  const self = event.invitees.find(person => person.isSelf)
  if (self !== undefined) visible[visible.length - 1] = self
  return visible
}

export function arkmeSourceDestinationLabel(source: ArkmeSourceItem | undefined): string {
  return source === undefined ? tr("发给自己")
    : arkmeTopicDisplayName(source.displayName, source.kind === 'topic' ? source.topicKind : undefined)
}

function arkmeComposerPlaceholderTargetForSource(
  source: ArkmeSourceItem | undefined,
  currentMemberCount: number,
): ArkmeComposerPlaceholderTarget {
  if (source === undefined || source.kind === 'send_to_self'
    || source.kind === 'default_category' || source.kind === 'topic') return { kind: 'record' }
  if (source.kind === 'private_chat') return { kind: 'private_chat', displayName: source.displayName }
  const memberCount = arkmeComposerGroupMemberCount(currentMemberCount, source.groupAvatar?.memberCount)
  return {
    kind: 'group_chat',
    displayName: source.displayName,
    ...(memberCount === undefined ? {} : { memberCount }),
  }
}

export interface ArkmeAccountSelfSourcesResolution {
  userId: number
  resolution: ArkmeSelfSourcesResolution
}

export function arkmeAggregateSourceForUser(
  userId: number | undefined,
  state: ArkmeAccountSelfSourcesResolution | undefined,
): ArkmeSourceItem | undefined {
  if (userId === undefined || state?.userId !== userId || state.resolution.status !== 'ready') return undefined
  return state.resolution.aggregateSource
}

export function arkmeSendToSelfRootSource(sources: readonly ArkmeSourceItem[]): ArkmeSourceItem | undefined {
  return sources.find(source => source.kind === 'send_to_self')
}

function mergeItems(current: ArkmeTimelineItem[], incoming: ArkmeTimelineItem[]): ArkmeTimelineItem[] {
  const map = new Map(current.map(item => [item.itemUid, item]))
  for (const item of incoming) {
    const previous = map.get(item.itemUid)
    const version = item.recordVersion ?? item.version ?? 0
    const previousVersion = previous?.recordVersion ?? previous?.version ?? 0
    // Live deltas and reads may arrive out of order. Keep the authoritative
    // record snapshot and its matching action reference together.
    if (previous?.status === 1 && item.status === 1 && version > 0 && previousVersion > 0
      && previousVersion > version) continue
    map.set(item.itemUid, {
      ...retainPartialTimelineMedia(previous, item),
      ...(previous?.aiPolish !== undefined && item.aiPolish === undefined ? { aiPolish: previous.aiPolish } : {}),
      ...(previous?.recordDurationMillis !== undefined && item.recordDurationMillis === undefined
        ? { recordDurationMillis: previous.recordDurationMillis } : {}),
      ...(previous?.editDurationMillis !== undefined && item.editDurationMillis === undefined
        ? { editDurationMillis: previous.editDurationMillis } : {}),
      ...(previous?.captureContext !== undefined && item.captureContext === undefined
        ? { captureContext: previous.captureContext } : {}),
      ...(previous?.locationCapture !== undefined && item.locationCapture === undefined
        ? { locationCapture: previous.locationCapture } : {}),
      ...(previous?.messageActionRef !== undefined && item.messageActionRef === undefined
        ? { messageActionRef: previous.messageActionRef } : {}),
      ...(previous?.mentions !== undefined && item.mentions === undefined
        ? { mentions: previous.mentions } : {}),
      ...(previous?.selfRole !== undefined && item.selfRole === undefined
        ? { selfRole: previous.selfRole } : {}),
    })
  }
  return [...map.values()].sort(compareTimelineMessages)
}

function reconcileTimelinePage(
  cachedItems: ArkmeTimelineItem[],
  delta: ArkmeChatTimelineSourceDeltaSnapshot | undefined,
  pageItems: ArkmeTimelineItem[],
  readStartDeltas: ReadonlyMap<string, ArkmeTimelineItem | undefined>,
): ArkmeTimelineItem[] {
  const removedKeys = new Set(delta?.removedItemKeys ?? [])
  const retainedPage = pageItems.filter(item => !removedKeys.has(item.timelineItemKey ?? ''))
  const ids = new Set(retainedPage.map(item => item.itemUid))
  // Newer evidence may replace page content, but cannot expand its navigation range.
  const relevant = (delta?.items ?? []).filter(item => ids.has(item.itemUid))
  const beforeRead = relevant.filter(item => item === readStartDeltas.get(item.itemUid))
  const duringRead = relevant.filter(item => item !== readStartDeltas.get(item.itemUid))
  return mergeItems(mergeItems(mergeItems(cachedItems.filter(item => ids.has(item.itemUid)), beforeRead), retainedPage), duringRead)
}

function detailExtensionTimelineItem(
  parent: ArkmeTimelineItem,
  result: ArkmeSourceMessageExtendResult,
): ArkmeTimelineItem {
  const extension = result.extension
  const avatarRef = extension.senderAvatarUrl?.trim() ?? ''
  return {
    itemUid: result.recordUid,
    senderName: extension.senderDisplayName.trim() || '我',
    isMe: true,
    sendAtMillis: extension.sendAtMillis,
    title: extension.title,
    textContent: extension.textContent,
    textFormat: extension.textFormat ?? 'plain',
    status: result.status,
    ...(result.sequence === undefined ? {} : { sequence: result.sequence }),
    ...(avatarRef === '' ? {} : { avatarRef }),
    templateKind: extension.templateKind,
    displayKind: extension.displayKind,
    extensionParentRecordUid: result.parentRecordUid,
    extensionParent: {
      itemUid: parent.itemUid,
      senderName: parent.senderName,
      title: parent.title,
      textContent: parent.textContent,
      textFormat: parent.textFormat ?? 'plain',
      ...(parent.contentBlocks === undefined ? {} : { contentBlocks: parent.contentBlocks }),
    },
    awaitingTimelineProjection: true,
  }
}

function extensionConversationPreview(extension: ArkmeMessageCopyLinkExtensionItem): string {
  return (extension.textFormat === 'markdown' ? arkmeMarkdownPlainText(extension.textContent) : extension.textContent.trim())
    || extension.title.trim()
    || extension.mediaItems.map(item => item.fileName.trim()).find(Boolean)
    || '非文本内容'
}

const ARKME_CONFIRMED_SEND_RETENTION_MILLIS = ARKME_CONVERSATION_TIMELINE_FRESH_MILLIS * 4

/**
 * Retains only bounded Host-confirmed sends while an eventually-consistent
 * first page catches up. A signed authoritative row retires its local entry.
 */
export class ArkmeConfirmedSendRetentionOwner {
  private readonly entries = new Map<string, { sourceKey: string; item: ArkmeTimelineItem; expiresAtMillis: number }>()

  constructor(private readonly maxItems = 64) {}

  retain(sourceKey: string, item: ArkmeTimelineItem, nowMillis = Date.now()): void {
    if (sourceKey === '' || item.itemUid.trim() === '' || !item.isMe || item.status < 0) return
    const key = `${sourceKey}\0${item.itemUid}`
    this.entries.delete(key)
    this.entries.set(key, {
      sourceKey,
      item: { ...item },
      expiresAtMillis: nowMillis + ARKME_CONFIRMED_SEND_RETENTION_MILLIS,
    })
    while (this.entries.size > this.maxItems) {
      const oldest = this.entries.keys().next().value as string | undefined
      if (oldest === undefined) break
      this.entries.delete(oldest)
    }
  }

  forget(sourceKey: string, itemUids: readonly string[]): void {
    for (const itemUid of itemUids) this.entries.delete(`${sourceKey}\0${itemUid}`)
  }

  merge(sourceKey: string, authoritative: ArkmeTimelineItem[], nowMillis = Date.now()): ArkmeTimelineItem[] {
    const retained: ArkmeTimelineItem[] = []
    const authoritativeById = new Map(authoritative.map(item => [item.itemUid, item]))
    for (const [key, entry] of this.entries) {
      if (entry.expiresAtMillis <= nowMillis) {
        this.entries.delete(key)
        continue
      }
      if (entry.sourceKey !== sourceKey) continue
      retained.push(entry.item)
      if (authoritativeById.get(entry.item.itemUid)?.messageActionRef !== undefined) this.entries.delete(key)
    }
    return mergeItems(retained, authoritative)
  }
}

function applySourceSendResult(
  current: ArkmeTimelineItem[],
  optimisticItemUid: string,
  result: ArkmeSourceSendResult,
): ArkmeTimelineItem[] {
  const returned = current.find(item => item.itemUid === result.itemUid)
  const optimistic = current.find(item => item.itemUid === optimisticItemUid)
  const candidate = returned ?? optimistic
  if (candidate === undefined) return current
  const { aiPolish: _optimisticAiPolish, ...candidateBase } = candidate
  const base = {
    ...candidateBase,
    ...(candidate.recordDurationMillis === undefined && optimistic?.recordDurationMillis !== undefined
      ? { recordDurationMillis: optimistic.recordDurationMillis } : {}),
    ...(candidate.editDurationMillis === undefined && optimistic?.editDurationMillis !== undefined
      ? { editDurationMillis: optimistic.editDurationMillis } : {}),
    ...(candidate.captureContext === undefined && optimistic?.captureContext !== undefined
      ? { captureContext: optimistic.captureContext } : {}),
    ...(candidate.mentions === undefined && optimistic?.mentions !== undefined
      ? { mentions: optimistic.mentions } : {}),
    ...(candidate.selfRole === undefined && optimistic?.selfRole !== undefined
      ? { selfRole: optimistic.selfRole } : {}),
  }
  const confirmed: ArkmeTimelineItem = {
    ...base,
    itemUid: result.itemUid,
    status: result.status,
    ...(result.messageActionRef === undefined ? {} : { messageActionRef: result.messageActionRef }),
    ...(result.recordTopicAssignmentRef === undefined ? {} : { recordTopicAssignmentRef: result.recordTopicAssignmentRef }),
    ...(result.sequence === undefined ? {} : { sequence: result.sequence }),
    ...(result.aiPolish === undefined ? {} : {
      aiPolish: result.aiPolish,
      ...(result.aiPolish.state === 'polished' && result.aiPolish.polishedText !== undefined
        ? { textContent: result.aiPolish.polishedText }
        : {}),
    }),
  }
  return mergeItems(
    current.filter(item => item.itemUid !== optimisticItemUid && item.itemUid !== result.itemUid),
    [confirmed],
  )
}

function optimisticTimelineMentionTargets(
  mentions: readonly ArkmeComposerMention[],
  members: readonly ArkmeConversationMemberItem[],
  rawText: string,
  text: string,
  textFormat: 'plain' | 'markdown' | undefined,
): ArkmeTimelineMentionTarget[] {
  const leadingTrim = textFormat === 'markdown' ? 0 : rawText.length - rawText.trimStart().length
  return mentions.flatMap<ArkmeTimelineMentionTarget>(mention => {
    const startIndex = Math.trunc(mention.startIndex) - leadingTrim
    const length = Math.trunc(mention.length)
    if (startIndex < 0 || length < 2 || startIndex + length > text.length
      || text.slice(startIndex, startIndex + length) !== `@${mention.displayName}`) return []
    if (mention.all === true) {
      return [{ kind: 'all', startIndex, length, displayName: mention.displayName }]
    }
    if (mention.botRef !== undefined) {
      return [{ kind: 'bot', startIndex, length, displayName: mention.displayName, botRef: mention.botRef }]
    }
    const member = members.find(item => item.status === 'active' && item.mentionRef !== undefined && item.mentionRef === mention.mentionRef)
    return member === undefined ? [] : [{
      kind: 'member',
      startIndex,
      length,
      displayName: mention.displayName,
      memberRef: member.memberRef,
    }]
  })
}

interface ArkmeTimelineViewState {
  sourceKey: string
  mode: 'latest' | 'around'
  aroundSequenceRange: ArkmeConversationTimelineSequenceRange | undefined
  items: ArkmeTimelineItem[]
  aiPolishNotices: ArkmeGroupAiPolishNotice[]
  aiPolishSettings: ArkmeGroupAiPolishSnapshot | undefined
  nextCursor: ArkmeTimelineCursor | undefined
  hasMore: boolean
  newerCursor: ArkmeTimelineCursor | undefined
  newerHasMore: boolean
}

interface ArkmeComposerAsyncScope {
  accountKey: string | undefined
  sourceKey: string
  draftKey: string | undefined
  generation: number
}

function arkmeRecordReeditFailure(caught: unknown): Pick<ArkmeRecordReeditComposerState, 'error' | 'conflict'> {
  const code = caught instanceof ArkmeClientError ? caught.body.code : undefined
  return {
    error: errorMessage(caught) || '草稿保存失败，请重试',
    conflict: code === 'record-reedit-conflict' ? 'record' : code === 'record-reedit-draft-changed' ? 'draft' : undefined,
  }
}

function arkmeRecordReeditCandidateKey(title: string, textContent: string, attachments: readonly ArkmeRecordReeditAttachmentView[],
  textFormat: ArkmeRecordReeditEditorSnapshot['textFormat'] = 'plain', mentions: readonly ArkmeComposerMention[] = []): string {
  return JSON.stringify([title.trim(), textFormat === 'markdown' ? textContent : textContent.trim(), attachments.map(attachment => attachment.selection), mentions.map(mention => ({ ...mention,
    startIndex: mention.startIndex - (textFormat === 'markdown' ? 0 : textContent.length - textContent.trimStart().length),
  }))])
}

function sameArkmeRecordReeditSession(
  left: ArkmeRecordReeditComposerState | undefined,
  right: Pick<ArkmeRecordReeditComposerState, 'generation' | 'accountKey' | 'sourceKey' | 'sourceRef' | 'item'>,
): left is ArkmeRecordReeditComposerState {
  return left !== undefined
    && left.generation === right.generation
    && left.accountKey === right.accountKey
    && left.sourceKey === right.sourceKey
    && left.sourceRef === right.sourceRef
    && left.item.itemUid === right.item.itemUid
}

function arkmeRecordReeditPreparationKey(target: ArkmeRecordReeditComposerState): string {
  return `record-reedit:${target.generation}`
}

function arkmeRecordReeditAttachmentBlock(attachment: ArkmeRecordReeditAttachmentView): ArkmeContentBlock | undefined {
  if (attachment.unavailable) return undefined
  return attachment.block ?? (attachment.localFile === undefined ? undefined : localFileBlock(attachment.localFile))
}

function resolveStateAction<Value>(action: SetStateAction<Value>, current: Value): Value {
  return typeof action === 'function' ? (action as (value: Value) => Value)(current) : action
}

function sameStateValue(left: unknown, right: unknown): boolean {
  return left === right || JSON.stringify(left) === JSON.stringify(right)
}

export function arkmeRealtimeDeltaCoversTimelineGap(
  cached: ArkmeConversationTimelineSnapshot | undefined,
  deltaItems: readonly ArkmeTimelineItem[],
  latestSequence: number | undefined,
): boolean {
  if (cached === undefined || !Number.isSafeInteger(latestSequence) || (latestSequence ?? 0) <= 0) return false
  const cachedSequence = Math.max(
    cached.latestSequence ?? 0,
    ...cached.items.map(item => item.sequence ?? 0),
  )
  const targetSequence = latestSequence ?? 0
  if (targetSequence <= cachedSequence) return false
  if (targetSequence - cachedSequence > deltaItems.length) return false
  const sequences = new Set(deltaItems.flatMap(item => Number.isSafeInteger(item.sequence) && (item.sequence ?? 0) > 0
    ? [item.sequence!]
    : []))
  for (let sequence = cachedSequence + 1; sequence <= targetSequence; sequence += 1) {
    if (!sequences.has(sequence)) return false
  }
  return true
}

export function aiPolishStatus(item: ArkmeTimelineItem): string {
  switch (item.aiPolish?.state) {
    case 'polishing': return 'AI润色中...'
    case 'polished': return '✨已润色'
    case 'kept_original': return '保持原文'
    case 'failed': return '润色失败 · 重试'
    default: return ''
  }
}

export function arkmeTimelineSenderName(item: ArkmeTimelineItem, profile?: ArkmeUserProfile): string {
  if (!item.isMe || profile === undefined) return item.senderName
  if (item.avatarSnapshot === true) {
    return item.senderNameSnapshot === true
      ? item.senderName
      : profile.nickname.trim() || profile.displayName.trim() || item.senderName
  }
  return profile.displayName.trim() || profile.nickname.trim() || item.senderName
}

export function arkmeTimelineAvatarRef(item: ArkmeTimelineItem, profile?: ArkmeUserProfile): string | undefined {
  const itemAvatarRef = item.avatarRef?.trim()
  if (itemAvatarRef !== undefined && itemAvatarRef !== '') return itemAvatarRef
  if (item.avatarSnapshot === true) return undefined
  if (!item.isMe) return undefined
  const profileAvatarRef = profile?.avatarRef.trim()
  return profileAvatarRef === '' ? undefined : profileAvatarRef
}

type ArkmeMessageSelectionAnchor = 'avatar' | 'card-center'

/** Selection placement follows the visible business object instead of assuming every row owns an avatar. */
function arkmeMessageSelectionAnchor(
  item: Pick<ArkmeTimelineItem, 'sharedRecording'>,
): ArkmeMessageSelectionAnchor {
  return item.sharedRecording === undefined ? 'avatar' : 'card-center'
}

/** Every conversation timeline shows avatars, including the aggregate and personal topic views. */
export function arkmeSourceShowsMessageAvatars(source: ArkmeSourceItem | undefined): boolean {
  if (source === undefined) return false
  return isArkmeSelfWorkspaceSource(source) || source.kind === 'private_chat' || source.kind === 'group_chat'
}

export function MessageAvatar(props: {
  senderKind?: 'human' | 'bot' | undefined
  avatarRef?: string
  fallback?: ArkmeGroupAvatarFallback
  label?: string
  member?: ArkmeConversationMemberItem
  profileEnabled: boolean
  onOpen: (member: ArkmeConversationMemberItem) => void
  onContextMenu: (member: ArkmeConversationMemberItem, anchorRect: DOMRect) => void
  onHover?: ((member: ArkmeConversationMemberItem, anchor: HTMLElement) => void) | undefined
  bot?: ArkmeBotAvatarIdentity | undefined
  onBotHover?: ((bot: ArkmeBotAvatarIdentity, anchor: HTMLElement) => void) | undefined
  onBotMenu?: ((bot: ArkmeBotAvatarIdentity, anchorRect: DOMRect) => void) | undefined
  onBotOpen?: ((bot: ArkmeBotAvatarIdentity) => void) | undefined
}) {
  const member = props.member
  const avatar = <ArkmeUserAvatar senderKind={props.senderKind} {...(props.avatarRef === undefined ? {} : { avatarRef: props.avatarRef })}
    {...(props.fallback === undefined ? {} : { fallback: props.fallback })}
    size={ARKME_MESSAGE_AVATAR_SIZE} label={props.label ?? tr("消息头像")} />
  if (member === undefined) {
    const bot = props.bot
    if (bot === undefined) return <span data-arkme-message-avatar="true" style={styles.messageAvatar} aria-hidden>{avatar}</span>
    return <button
    type="button" data-arkme-message-avatar="true" data-arkme-bot-avatar={bot.itemUid}
    style={{ ...styles.messageAvatar, padding: 0, border: 0, cursor: 'pointer' }}
    aria-label={tr('查看 {v0} 的 Bot 资料', { v0: bot.name })}
    onClick={event => {
      event.stopPropagation()
      props.onBotOpen?.(bot)
    }}
    onPointerEnter={event => {
      if (event.pointerType === 'mouse' && event.buttons === 0) props.onBotHover?.(bot, event.currentTarget)
    }}
    onKeyDown={event => {
      if (event.key !== 'ArrowDown') return
      event.preventDefault()
      props.onBotMenu?.(bot, event.currentTarget.getBoundingClientRect())
    }}
    onContextMenu={event => {
      event.preventDefault()
      event.stopPropagation()
      props.onBotMenu?.(bot, event.currentTarget.getBoundingClientRect())
    }}
  >{avatar}</button>
  }
  return <button
    type="button"
    data-arkme-message-avatar="true"
    style={{ ...styles.messageAvatar, padding: 0, border: 0, cursor: props.profileEnabled ? 'pointer' : 'default' }}
    aria-label={props.profileEnabled
      ? member.isSelf ? '查看我的用户卡片' : tr("查看 {v0}", { v0: member.displayName })
      : `${member.displayName} 的消息头像`}
    onClick={event => {
      event.stopPropagation()
      if (props.profileEnabled) props.onOpen(member)
    }}
    onPointerEnter={event => {
      if (props.profileEnabled && event.pointerType === 'mouse' && event.buttons === 0) props.onHover?.(member, event.currentTarget)
    }}
    onKeyDown={event => {
      if (event.key !== 'ArrowDown') return
      event.preventDefault()
      props.onContextMenu(member, event.currentTarget.getBoundingClientRect())
    }}
    onContextMenu={event => {
      event.preventDefault()
      event.stopPropagation()
      props.onContextMenu(member, event.currentTarget.getBoundingClientRect())
    }}
  >{avatar}</button>
}


export function ArkmeMemberJoinNotice(props: {
  rowId: string
  event: ArkmeConversationMemberJoinEvent
  membersByRef: ReadonlyMap<string, ArkmeConversationMemberItem>
  startsGroup?: boolean
  onOpenMember: (member: ArkmeConversationMemberItem) => void
}) {
  const visibleInvitees = arkmeVisibleMemberJoinInvitees(props.event)
  const inviteeMaxCount = visibleInvitees.length > 1 ? 10 : 14
  const renderPerson = (person: ArkmeConversationMemberJoinPerson, maxCount: number, key: string) => {
    const label = arkmeMemberJoinDisplayName(person, maxCount)
    const member = person.memberRef === undefined ? undefined : props.membersByRef.get(person.memberRef)
    return member === undefined
      ? <span key={key}>{label}</span>
      : <button key={key} type="button" style={styles.memberJoinLink}
        aria-label={tr("查看 {v0}", { v0: label })} onClick={() => { props.onOpenMember(member) }}>{label}</button>
  }
  return <li
    data-arkme-conversation-row={props.rowId}
    data-arkme-member-join-event={props.event.eventId}
    data-arkme-member-join-group-start={props.startsGroup === false ? 'false' : 'true'}
    style={{ ...styles.memberJoinNotice, marginTop: props.startsGroup === false ? 20 : 26 }}
  >
    <div style={styles.memberJoinTime}>{arkmeMemberJoinTimeLabel(props.event.occurredAtMillis)}</div>
    <div style={styles.memberJoinLine}>
      {renderPerson(props.event.inviter, 14, 'inviter')}
      <span>{` ${props.event.action === 'direct_add' ? tr("添加") : tr("邀请")} `}</span>
      {visibleInvitees.map((person, index) => <Fragment key={`${person.memberRef ?? person.displayName}:${index}`}>
        {index > 0 && <span>、</span>}
        {renderPerson(person, inviteeMaxCount, `invitee:${person.memberRef ?? person.displayName}:${index}`)}
      </Fragment>)}
      {props.event.invitees.length > 2 && <span>{`等${String(props.event.invitees.length)}人`}</span>}
      <span> {tr("加入群聊")}</span>
    </div>
  </li>
}

export function ArkmeMemberLeaveNotice(props: { rowId:string; event:ArkmeMemberEvent; onOpen:(event:ArkmeMemberEvent)=>void }) {
  const label = arkmeMemberJoinDisplayName({displayName:props.event.displayName,isSelf:false},14)
  return <li data-arkme-conversation-row={props.rowId} data-arkme-member-leave-event={props.event.eventId}
    style={{...styles.memberJoinNotice,marginTop:26}}>
    <div style={styles.memberJoinTime}>{arkmeMemberJoinTimeLabel(props.event.occurredAtMillis)}</div>
    <div style={styles.memberJoinLine}>
      <button type="button" style={styles.memberJoinLink} aria-label={tr("查看 {v0}", { v0: label })} onClick={() => { props.onOpen(props.event) }}>{label}</button>
      <span> {tr("退出了群聊")}</span>
    </div>
  </li>
}

export function ArkmeTimelineMessageHeader({
  item,
  profile,
  member,
  presentationIsMe = item.isMe,
}: {
  item: ArkmeTimelineItem
  profile?: ArkmeUserProfile
  member?: Pick<ArkmeConversationMemberItem, 'displayName'>
  presentationIsMe?: boolean
}) {
  const memberDisplayName = member?.displayName.trim()
  const senderName = item.selfRole?.name ?? (memberDisplayName && memberDisplayName !== '群成员' ? memberDisplayName : arkmeTimelineSenderName(item, profile))
  return <span style={styles.messageHeader}>
    {presentationIsMe && <span style={styles.meta}>{timeLabel(item.sendAtMillis)}</span>}
    {item.senderKind === 'bot' ? <ArkmeBotSenderName name={senderName} /> : <span style={styles.sender}>{senderName}</span>}
    {!presentationIsMe && <span style={styles.meta}>{timeLabel(item.sendAtMillis)}</span>}
  </span>
}

function ArkmeExtensionParentPreview({ parent, isMe, onSelect }: {
  parent: ArkmeTimelineExtensionParent
  isMe: boolean
  onSelect?: () => void
}) {
  const text = parent.textContent.trim() || parent.title.trim()
  const blocks = [...(parent.contentBlocks ?? [])].sort((left, right) => left.sortOrder - right.sortOrder)
  const visuals = blocks.filter(block => block.kind === 'image' || block.kind === 'video').slice(0, 3)
  const firstFile = blocks.find(block => block.kind !== 'image' && block.kind !== 'video')
  return <div
    data-arkme-extension-parent-preview={parent.itemUid}
    style={{
      ...styles.extensionParentPreview,
      ...(isMe ? styles.extensionParentPreviewMe : styles.extensionParentPreviewOther),
      ...(onSelect === undefined ? {} : { cursor: 'pointer' }),
    }}
    title={arkmeEmojiPlainText(text) || firstFile?.fileName}
    role={onSelect === undefined ? undefined : 'button'}
    tabIndex={onSelect === undefined ? undefined : 0}
    onClick={onSelect}
    onKeyDown={onSelect === undefined ? undefined : event => {
      if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onSelect() }
    }}
  >
    {text !== '' && <span style={styles.extensionParentText}><ArkmeRichText text={text} presentation="preview" /></span>}
    {visuals.length > 0 && <span style={styles.extensionParentMedia} aria-label={tr("原消息附件")}>
      {visuals.map(block => block.kind === 'image'
        ? <span key={block.mediaRef} style={{ position: 'relative', display: 'flex', alignSelf: 'center', flex: 'none' }}>
          <img
            src={block.localFileRef === undefined
              ? `/arkme-self/api/media?ref=${encodeURIComponent(block.mediaRef)}`
              : createArkmeSdk().localFileUrl(block.localFileRef)}
            alt={block.fileName}
            draggable={false}
            style={styles.extensionParentImage}
          />
          {block.dynamicPhoto !== undefined && <span aria-label={tr("实况照片")} style={{ position: 'absolute', display: 'flex', left: 2, bottom: 2, pointerEvents: 'none' }}>
            <ArkmeLivePhotoBadge variant="extension" />
          </span>}
        </span>
        : <span key={block.mediaRef} style={styles.extensionParentFile}>{block.fileName || tr("视频")}</span>)}
    </span>}
    {text === '' && visuals.length === 0 && firstFile !== undefined
      ? <span style={styles.extensionParentFile}>{firstFile.fileName || '附件'}</span>
      : null}
  </div>
}

function normalizedEpochMillis(value: number): number {
  return value > 0 && value < 100_000_000_000 ? value * 1000 : value
}

function forwardDateLabel(value: number): string {
  const millis = normalizedEpochMillis(value)
  if (millis <= 0) return ''
  const date = new Date(millis)
  return `${String(date.getFullYear())}年${String(date.getMonth() + 1)}月${String(date.getDate())}日`
}

function forwardShortTimeLabel(value: number): string {
  const millis = normalizedEpochMillis(value)
  if (millis <= 0) return ''
  const date = new Date(millis)
  return `${String(date.getMonth() + 1).padStart(2, '0')}月${String(date.getDate()).padStart(2, '0')}日 ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
}

function forwardFullTimeLabel(value: number): string {
  const millis = normalizedEpochMillis(value)
  if (millis <= 0) return ''
  const date = new Date(millis)
  return `${String(date.getFullYear()).padStart(4, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}`
}

function copyLinkDetailDateRange(items: readonly Pick<ArkmeMessageCopyLinkSnapshotItem, 'sendAtMillis'>[]): string {
  const timestamps = items.map(item => normalizedEpochMillis(item.sendAtMillis)).filter(value => value > 0)
  if (timestamps.length === 0) return ''
  const start = Math.min(...timestamps)
  const end = Math.max(...timestamps)
  const startText = forwardDateLabel(start)
  const endText = forwardDateLabel(end)
  return startText === endText ? startText : tr("{v0} 至 {v1}", { v0: startText, v1: endText })
}

function copyLinkShareUrl(shareWebsite: string, sid: string): string {
  return `${shareWebsite.replace(/\/+$/u, '')}/s/${sid.trim()}`
}

function copyLinkDetailExtensionTarget(detail: ArkmeMessageCopyLinkResolveResult | undefined): { itemIndex: number; parentRecordUid: string } | undefined {
  if (detail === undefined || detail.accessMode !== 'normal') return undefined
  const itemIndex = 0
  const parentRecordUid = detail.sourceAnchors?.[itemIndex]?.recordUid.trim() ?? ''
  if (detail.items[itemIndex] === undefined || parentRecordUid === '') return undefined
  return { itemIndex, parentRecordUid }
}

export function arkmeForwardTargetTimeLabel(value: number, now: number = Date.now()): string {
  const millis = normalizedEpochMillis(value)
  if (millis <= 0) return ''
  const date = new Date(millis)
  if (dayKey(millis) === dayKey(now)) {
    return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
  }
  return `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`
}

function arkmeDraftForwardRecordsTitleWithFallback(rawName: string, sourceCount: number, fallbackName: string): string {
  const name = rawName.trim() === '' ? fallbackName.trim() : rawName.trim()
  const titleName = name === '' ? '快记' : name
  if (sourceCount <= 1) return tr("{v0}的快记", { v0: titleName })
  return tr("{v0}的{v1}条快记", { v0: titleName, v1: String(sourceCount) })
}

function arkmeDraftForwardPrivateRecordsTitle(rawCurrentName: string, rawCounterpartName: string, sourceCount: number): string {
  const currentName = rawCurrentName.trim() === '' ? '我' : rawCurrentName.trim()
  const counterpartName = rawCounterpartName.trim() === '' ? '对方' : rawCounterpartName.trim()
  if (sourceCount <= 1) return tr("{v0}和{v1}的快记", { v0: currentName, v1: counterpartName })
  return tr("{v0}和{v1}的{v2}条快记", { v0: currentName, v1: counterpartName, v2: String(sourceCount) })
}

function arkmeForwardRecordPreviewLine(item: ArkmeTimelineItem): string {
  if (item.forwardRecords !== undefined) return '[转发快记]'
  if (item.sharedRecording !== undefined) return '[相关录音]'
  const text = item.textContent.trim().replace(/\s+/g, ' ')
  if (text !== '') return text
  const title = item.title.trim().replace(/\s+/g, ' ')
  if (title !== '') return title
  if ((item.contentBlocks?.length ?? 0) > 0) return '非文本内容'
  return '快记'
}

function arkmeForwardRecordOwnerLabel(item: ArkmeTimelineItem): string {
  const sender = item.senderName.trim()
  return sender === '' ? '原快记' : sender
}

export function arkmeForwardPreviewTitle(items: readonly ArkmeTimelineItem[], source?: ArkmeSourceItem): string {
  const sourceCount = items.length
  if (sourceCount <= 0) return '转发快记'
  if (source?.kind === 'private_chat') {
    return arkmeDraftForwardPrivateRecordsTitle('我', source.displayName, sourceCount)
  }
  if (source?.kind === 'group_chat') {
    return arkmeDraftForwardRecordsTitleWithFallback(source.displayName, sourceCount, '群聊')
  }
  return arkmeDraftForwardRecordsTitleWithFallback(arkmeForwardRecordOwnerLabel(items[0]!), sourceCount, '原快记')
}

export function arkmeForwardPreviewSubtitle(items: readonly ArkmeTimelineItem[]): string {
  const summaryLines = items
    .map(item => `${arkmeForwardRecordOwnerLabel(item)}：${arkmeForwardRecordPreviewLine(item)}`)
    .filter(line => line.trim() !== '')
    .slice(0, 2)
  return summaryLines[0] ?? ''
}


function ArkmeMessageActionIcon({ kind }: { kind: 'copy' | 'link' | 'select' | 'forward' | 'report' | 'withdraw' | 'edit' }) {
  if (kind === 'withdraw') return <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden>
    <path d="M4 7.5h8.25a4.25 4.25 0 0 1 0 8.5H9.5" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" />
    <path d="m6.8 4.7-3 2.8 3 2.8" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
  if (kind === 'report') return <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden>
    <path d="M4.5 17.25V3.25M5 4H13.45C14.55 4 15.16 5.27 14.48 6.14L13.26 7.7C12.91 8.15 12.91 8.78 13.26 9.23L14.48 10.79C15.16 11.66 14.55 12.93 13.45 12.93H4.5" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
  if (kind === 'edit') return <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden>
    <path d="M4 14.8 3.5 17l2.2-.5L15.8 6.4a1.7 1.7 0 0 0 0-2.4l-.8-.8a1.7 1.7 0 0 0-2.4 0L4 14.8Z" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" strokeLinejoin="round" />
    <path d="m11.7 4.2 4.1 4.1" stroke="currentColor" strokeWidth="1.45" />
  </svg>
  return <ArkmeSelectActionIcon kind={kind} size={16} />
}

function ArkmeDesktopExtensionIcon() {
  return <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden
    data-arkme-message-extension-icon="desktop">
    <circle cx="8" cy="2" r="1.5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="13" cy="6" r="1.5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="1.5" cy="10.5" r="1" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="14.5" cy="14.5" r="1" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="8" cy="10" r="2.5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M8 3.5V7.5" stroke="currentColor" />
    <path d="M12 7L10 8.5M13.5 14L10 11.5M2.5 10.5H5.5" stroke="currentColor" />
  </svg>
}

function ArkmeForwardLinearIcon({ size = 18 }: { size?: number }) {
  return <ArkmeSelectActionIcon kind="forward" size={size} />
}

function ArkmeForwardCloseBorderIcon() {
  return <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
    <circle cx="8" cy="8" r="6.5" stroke="currentColor" />
    <path d="M6 6L10 10" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M10 6L6 10" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
}

function ArkmeForwardSubmitIcon() {
  return <ArkmeSelectActionIcon kind="forward" size={22} />
}

function ArkmeSearchIcon() {
  return <svg width="17" height="17" viewBox="0 0 20 20" fill="none" aria-hidden>
    <path d="M8.75 15.5C12.4779 15.5 15.5 12.4779 15.5 8.75C15.5 5.02208 12.4779 2 8.75 2C5.02208 2 2 5.02208 2 8.75C2 12.4779 5.02208 15.5 8.75 15.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M13.75 13.75L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
}

type ArkmeCopyLinkDetailState =
  | { sid: string; status: 'loading' }
  | { sid: string; status: 'ready'; detail: ArkmeMessageCopyLinkResolveResult }
  | { sid: string; status: 'error'; message: string }

function copyLinkSnapshotSenderName(item: ArkmeMessageCopyLinkSnapshotItem): string {
  const sender = item.senderDisplayName.trim()
  if (sender !== '') return sender
  return item.sourceKind === 'agent_message' ? 'Agent' : '未知用户'
}

function CopyLinkRecordAvatar({ item, size = 46 }: { item: ArkmeMessageCopyLinkSnapshotItem; size?: number }) {
  const name = copyLinkSnapshotSenderName(item)
  const avatar = item.senderAvatarUrl?.trim() ?? ''
  const sizedStyle = { ...styles.copyLinkDetailPlaceholderAvatar, width: size, height: size }
  if (item.sourceKind === 'dsh_native') return <span role="img" aria-label="DeepSeek Harness 头像" style={sizedStyle}><DeepSeekLogoMark style={{ width: size, height: size, color: arkmeTheme.accent, opacity: 1 }} /></span>
  if (/^(https?:|data:|blob:)/iu.test(avatar)) {
    return <span style={sizedStyle} aria-hidden><img src={avatar} alt="" draggable={false} style={styles.copyLinkDetailAvatarImage} /></span>
  }
  if (avatar !== '') {
    return <ArkmeUserAvatar avatarRef={avatar} size={size} label={tr("分享快记头像")} />
  }
  return <span style={sizedStyle} aria-hidden>{[...name][0] ?? '?'}</span>
}

function copyLinkSnapshotTimelineItem(item: ArkmeMessageCopyLinkSnapshotItem, index: number): ArkmeTimelineItem {
  const avatar = item.senderAvatarUrl?.trim() ?? ''
  return {
    itemUid: `copy-link:${String(index)}:${String(item.sendAtMillis)}`,
    senderName: copyLinkSnapshotSenderName(item),
    isMe: false,
    sendAtMillis: item.sendAtMillis,
    title: item.title,
    textContent: item.textContent,
    textFormat: item.textFormat ?? 'plain',
    status: 1,
    templateKind: item.templateKind,
    displayKind: item.displayKind,
    ...(avatar !== '' && !/^(https?:|data:|blob:)/iu.test(avatar) ? { avatarRef: avatar } : {}),
  }
}

function CopyLinkDetailDrawer({
  state,
  onClose,
  onRetry,
  onShare,
  draft,
  onDraftChange,
  onSendDraft,
  sendBusy,
  sendDisabled,
  sendError,
  shareWebsite,
}: {
  state: ArkmeCopyLinkDetailState
  onClose: () => void
  onRetry: () => void
  onShare: () => void
  draft: string
  onDraftChange: (value: string) => void
  onSendDraft: () => void
  sendBusy: boolean
  sendDisabled: boolean
  sendError: string
  shareWebsite: string
}) {
  useArkmeLocale()
  const panelRef = useRef<HTMLElement>(null)
  const resize = useResizableNoteDetail(panelRef)
  const detail = state.status === 'ready' ? state.detail : undefined
  const title = detail?.displayTitle.trim() || '快记详情'
  const subtitle = detail === undefined ? '' : copyLinkDetailDateRange(detail.items)
  const generatedAt = detail === undefined ? '' : forwardFullTimeLabel(detail.generatedAtMillis)
  const shareDisabled = state.status !== 'ready'
  const renderSingleRecord = (item: ArkmeMessageCopyLinkSnapshotItem) => {
    const snapshot = copyLinkSnapshotTimelineItem(item, 0)
    return <div style={styles.copyLinkDetailRecord}>
      <CopyLinkRecordAvatar item={item} />
      <div style={styles.copyLinkDetailRecordMeta}>
        <span style={styles.copyLinkDetailRecordSender}>{copyLinkSnapshotSenderName(item)}</span>
        {forwardShortTimeLabel(item.sendAtMillis) !== '' && <span style={styles.copyLinkDetailRecordTime}>{forwardShortTimeLabel(item.sendAtMillis)}</span>}
      </div>
      <div style={styles.copyLinkDetailRecordContent}>
        <ArkmeMessageContent
          item={snapshot}
          presentation="detail"
          shareWebsite={shareWebsite}
        />
      </div>
    </div>
  }
  const renderMultiRecord = (item: ArkmeMessageCopyLinkSnapshotItem, index: number) => {
    const snapshot = copyLinkSnapshotTimelineItem(item, index)
    return <div key={`${String(index)}:${String(item.sendAtMillis)}:${copyLinkSnapshotSenderName(item)}`} style={styles.copyLinkDetailMultiRow}>
      <CopyLinkRecordAvatar item={item} size={36} />
      <div style={styles.copyLinkDetailMultiContent}>
        <span style={styles.copyLinkDetailRecordSender}>{copyLinkSnapshotSenderName(item)}</span>
        {forwardShortTimeLabel(item.sendAtMillis) !== '' && <span style={styles.copyLinkDetailRecordTime}>{forwardShortTimeLabel(item.sendAtMillis)}</span>}
        <ArkmeMessageContent
          item={snapshot}
          presentation="detail"
          shareWebsite={shareWebsite}
        />
        {item.mediaItems.length > 0 && <p style={styles.copyLinkDetailContentLabel}>
          {item.mediaItems.map(media => media.fileName.trim()).filter(value => value !== '').slice(0, 2).join('、')}
        </p>}
      </div>
    </div>
  }
  const renderExtensionItem = (item: ArkmeMessageCopyLinkExtensionItem) => {
    const snapshot = copyLinkSnapshotTimelineItem(item, 0)
    return <div key={item.recordUid} style={styles.copyLinkDetailExtensionItem}>
      <CopyLinkRecordAvatar item={item} size={36} />
      <div style={{ minWidth: 0 }}>
        <div style={styles.copyLinkDetailExtensionHead}>
          <span style={styles.copyLinkDetailExtensionSender}>{copyLinkSnapshotSenderName(item)}</span>
          {forwardShortTimeLabel(item.sendAtMillis) !== '' && <span style={styles.copyLinkDetailExtensionTime}>{forwardShortTimeLabel(item.sendAtMillis)}</span>}
        </div>
        <div style={styles.copyLinkDetailExtensionContent}>
          <ArkmeMessageContent
            item={snapshot}
            presentation="detail"
            shareWebsite={shareWebsite}
          />
        </div>
      </div>
    </div>
  }
  const extensionItems = detail?.recordContext?.extensions ?? []
  const extensionCount = Math.max(detail?.recordContext?.extensionCount ?? 0, extensionItems.length)
  const showExtensions = extensionCount > 0 || extensionItems.length > 0
  return <aside ref={panelRef} style={{ ...styles.copyLinkDetailPanel, ...resize.style }} aria-label={tr("快记分享链接详情")} data-arkme-copy-link-detail="true">
    {resize.handle}
    <ArkmeRightPanelHeader title={title} subtitle={subtitle} onClose={onClose}
      actions={<button data-arkme-feedback="neutral" type="button" className="arkme-right-panel-header-button"
        style={{ ...styles.copyLinkDetailHeaderButton, width: 30, height: 30, marginTop: -3, opacity: shareDisabled ? .38 : 1, cursor: shareDisabled ? 'default' : 'pointer' }}
        disabled={shareDisabled} aria-label={tr("分享快记链接")} onClick={onShare}><ArkmeForwardSubmitIcon /></button>} />
    {state.status === 'loading' && <div role="status" style={styles.copyLinkDetailStatus}>{tr("正在加载链接内容...")}</div>}
    {state.status === 'error' && <div role="alert" style={styles.copyLinkDetailStatus}>
      <div style={styles.copyLinkDetailError}>
        <span>{state.message}</span>
        <button data-arkme-feedback="neutral" type="button" style={styles.copyLinkDetailRetry} onClick={onRetry}>{tr("重试")}</button>
      </div>
    </div>}
    {detail !== undefined && <div style={styles.copyLinkDetailBody}>
      {detail.items.length === 1
        ? renderSingleRecord(detail.items[0]!)
        : <div style={styles.copyLinkDetailMultiList}>{detail.items.map(renderMultiRecord)}</div>}
      <div style={styles.copyLinkDetailDivider} aria-hidden />
      {showExtensions && <>
        <div style={styles.copyLinkDetailExtensionTitle}>{tr("共")}{extensionCount}{tr("条延展")}</div>
        {extensionItems.length > 0 && <div style={styles.copyLinkDetailExtensionList}>{extensionItems.map(renderExtensionItem)}</div>}
      </>}
    </div>}
    {detail !== undefined && <footer style={styles.copyLinkDetailFooter}>
      {generatedAt !== '' && <div style={styles.copyLinkDetailGeneratedAt}>{tr("此链接生成时间：")}{generatedAt}</div>}
      <div className="arkme-detail-extension-input-bar" style={styles.copyLinkDetailInputArea}>
        <div className="arkme-detail-extension-input-shell" style={styles.copyLinkDetailInputBar}>
          <span aria-hidden style={styles.copyLinkDetailInputIcon}><FileTextIcon size={18} /></span>
          <textarea
            style={styles.copyLinkDetailInput}
            value={draft}
            placeholder={tr("记录此刻想法...")}
            aria-label={tr("记录此刻想法")}
            disabled={sendBusy}
            rows={1}
            maxLength={20000}
            onChange={event => { onDraftChange(event.target.value) }}
            onKeyDown={event => {
              if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
                event.preventDefault()
                if (!sendDisabled) onSendDraft()
              }
            }}
          />
          <ArkmeComposerSendButton
            ariaLabel={sendBusy ? tr("发送中") : tr("发送延展")}
            disabled={sendDisabled}
            onClick={onSendDraft}
          />
        </div>
        {sendError !== '' && <div role="alert" style={styles.copyLinkDetailSendError}>{sendError}</div>}
      </div>
    </footer>}
  </aside>
}

export const ARKME_NOTIFICATION_BLOCKING_OVERLAY_SELECTOR = [
  '[data-arkme-notification-blocking-overlay="true"]',
  '[data-arkme-web-login-dialog="true"]',
].join(', ')
export const ARKME_NOTIFICATION_OVERLAY_OBSERVER_TIMEOUT_MILLIS = 30_000

interface ArkmeNotificationOverlayObserver {
  observe(target: Node, options: MutationObserverInit): void
  disconnect(): void
}

export function observeArkmeNotificationBlockingOverlayChanges(
  target: Node,
  onChange: () => void,
  options: {
    createObserver?(callback: () => void): ArkmeNotificationOverlayObserver
    setTimer?(callback: () => void, delay: number): ReturnType<typeof setTimeout>
    clearTimer?(timer: ReturnType<typeof setTimeout>): void
  } = {},
): () => void {
  const observer = (options.createObserver ?? (callback => new MutationObserver(callback)))(onChange)
  const schedule = options.setTimer ?? ((callback, delay) => setTimeout(callback, delay))
  const cancel = options.clearTimer ?? (timer => { clearTimeout(timer) })
  let stopped = false
  const stop = () => {
    if (stopped) return
    stopped = true
    observer.disconnect()
    cancel(timeout)
  }
  observer.observe(target, { childList: true, subtree: true })
  const timeout = schedule(stop, ARKME_NOTIFICATION_OVERLAY_OBSERVER_TIMEOUT_MILLIS)
  return stop
}

export function arkmeNotificationBlockingOverlayPresent(
  scope: Pick<Document, 'querySelector'>,
): boolean {
  return scope.querySelector(ARKME_NOTIFICATION_BLOCKING_OVERLAY_SELECTOR) !== null
}

export function arkmeNotificationConversationPanelMatches(
  panel: Pick<HTMLElement, 'getAttribute'> | null,
  activeConversation: boolean,
  sourceIdentity: string,
  targetIdentity: string,
): boolean {
  return activeConversation
    && sourceIdentity === targetIdentity
    && panel?.getAttribute('data-arkme-active-conversation-identity') === targetIdentity
}

export function takeArkmeNotificationSurfaceCommit({
  store,
  revision,
  targetIdentity,
  activeConversation,
  sourceIdentity,
  panel,
  documentScope,
}: {
  store: ArkmeNotificationActivationStore
  revision: number
  targetIdentity: string
  activeConversation: boolean
  sourceIdentity: string
  panel: Pick<HTMLElement, 'getAttribute'> | null
  documentScope: Pick<Document, 'querySelector'>
}): ArkmeNotificationActivationSnapshot | undefined {
  const current = store.getSnapshot()
  if (current.revision !== revision || current.source === undefined
    || arkmeSourceIdentityKey(current.source) !== targetIdentity
    || !arkmeNotificationConversationPanelMatches(panel, activeConversation, sourceIdentity, targetIdentity)
    || arkmeNotificationBlockingOverlayPresent(documentScope)) return undefined
  if (!current.surfaceCommitted && !store.markSurfaceCommitted(revision)) return undefined
  return store.takeReady(revision)
}

export function ArkmeSurface({
  t = defaultArkmeLoginTranslate,
  floating = false,
  initialAuth,
  currentSessionId,
  renderSlot,
  productChrome = true,
  productNavigation = productChrome,
  directoryLead,
  onCreateTask,
  onActivateSurface,
  ownsQrLogin = true,
  active = true,
}: ArkmeSurfaceProps = {}) {
  const locale = useArkmeLocale()
  const ui = useSyncExternalStore(arkmeUi.subscribe, arkmeUi.getViewSnapshot, arkmeUi.getViewSnapshot)
  const notificationActivation = useSyncExternalStore(
    arkmeNotificationActivation.subscribe,
    arkmeNotificationActivation.getSnapshot,
    arkmeNotificationActivation.getSnapshot,
  )
  const authStoreSnapshot = useSyncExternalStore(
    arkmeAuthStore.subscribe,
    arkmeAuthStore.getSnapshot,
    arkmeAuthStore.getSnapshot,
  )
  const auth = authStoreSnapshot.auth ?? initialAuth
  const authenticatedUserId = auth?.status === 'authenticated' ? auth.userId : undefined
  const authenticatedAccountKey = arkmeAuthenticatedAccountKey(auth)
  useEffect(() => {
    reactionPreview.setScope(authenticatedAccountKey)
  }, [authenticatedAccountKey, auth?.environment])
  const communityWelcome = useSyncExternalStore(betaCommunityWelcomeStore.subscribe,
    betaCommunityWelcomeStore.getSnapshot, betaCommunityWelcomeStore.getSnapshot)
  // Resolve the one legacy local welcome through the existing read API. New joins
  // already retain sourceKey; no polling, extra full-history load or server write.
  useEffect(() => {
    if (!communityWelcome || communityWelcome.sourceKey || communityWelcome.accountKey !== authenticatedAccountKey) return
    const controller = new AbortController()
    void callArkme<ArkmeTimelinePage>('source.timeline', { sourceRef: communityWelcome.sourceRef, limit: 1 }, controller.signal)
      .then(page => {
        if (!controller.signal.aborted) betaCommunityWelcomeStore.resolveLegacySource(communityWelcome, page.source)
      }).catch(() => { /* Leave the record intact; a later mount may resolve it. */ })
    return () => controller.abort()
  }, [communityWelcome, authenticatedAccountKey])
  const welcomeSuggestionRef = useRef<{ key: string; text: string }>()
  const recordingImportDialogRef = useRef<ArkmeRecordingImportDialogHandle>(null)
  const [recordingImportFeedback, setRecordingImportFeedback] = useState<{ accountKey: string | undefined; status: RecordingImportButtonStatus }>()
  const updateRecordingImportStatus = useCallback((status: RecordingImportButtonStatus) => {
    setRecordingImportFeedback(current => current?.accountKey === authenticatedAccountKey && current?.status === status
      ? current : { accountKey: authenticatedAccountKey, status })
  }, [authenticatedAccountKey])
  const recordingImportStatus = recordingImportFeedback?.accountKey === authenticatedAccountKey
    ? recordingImportFeedback?.status ?? 'idle' : 'idle'
  const [recordingImportDefaultStartAtMillis, setRecordingImportDefaultStartAtMillis] = useState(() => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return today.getTime()
  })
  const [recordingRefreshRevision, setRecordingRefreshRevision] = useState(0)
  useDirectRecordingOwner(authenticatedAccountKey, authenticatedUserId,
    authStoreSnapshot.config?.recordingImportPath ?? '/arkme-self/api/recording/import',
    authStoreSnapshot.config?.recordingWorkbenchEnabled !== false,
    () => { setRecordingRefreshRevision(value => value + 1) })
  const openRecordingImport = useCallback((defaultStartAtMillis: number) => {
    setRecordingImportDefaultStartAtMillis(defaultStartAtMillis)
    recordingImportDialogRef.current?.open()
  }, [])
  const recordingImportForeground = active && ui.mode === 'recordings'
  useEffect(() => {
    if (!recordingImportForeground) recordingImportDialogRef.current?.close()
  }, [recordingImportForeground])
  const botConversationVisible = ui.mode === 'bot' && ui.selectedBot !== undefined
  const conversationBackdropVisible = ui.mode === 'source' || ui.mode === 'contact-add' || botConversationVisible
  const selectedSource = conversationBackdropVisible ? ui.selectedSource : undefined
  const [selfSourcesResolution, setSelfSourcesResolution] = useState<ArkmeAccountSelfSourcesResolution>()
  const [selfSourcesRetryRevision, setSelfSourcesRetryRevision] = useState(0)
  useEffect(() => {
    if (auth?.status !== 'authenticated' || auth.userId === undefined) return
    const directory = selfTopicDirectory(auth.userId, auth.environment)
    let revision = arkmeUi.getRecordRevision()
    return arkmeUi.subscribe(() => {
      const next = arkmeUi.getRecordRevision()
      if (next !== revision) { revision = next; directory.invalidate() }
    })
  }, [auth?.status, auth?.userId, auth?.environment])
  const [selfTopicPreviewRequested, setSelfTopicPreviewRequested] = useState(false)
  useEffect(() => {
    if (typeof document === 'undefined') return
    const requestTopics = () => { setSelfTopicPreviewRequested(true) }
    document.addEventListener(SELF_TOPIC_MENU_OPEN, requestTopics)
    return () => { document.removeEventListener(SELF_TOPIC_MENU_OPEN, requestTopics) }
  }, [])
  useEffect(() => { setSelfTopicPreviewRequested(false) }, [authenticatedAccountKey])
  const activeSelfSourcesResolution = selfSourcesResolution === undefined
    || selfSourcesResolution.userId !== authenticatedUserId
    ? undefined
    : selfSourcesResolution.resolution
  const aggregateSource = arkmeAggregateSourceForUser(authenticatedUserId, selfSourcesResolution)
  const selfSources = activeSelfSourcesResolution?.status === 'ready'
    ? activeSelfSourcesResolution.sources
    : EMPTY_SELF_SOURCES
  const selfSourcesLoading = activeSelfSourcesResolution?.status === 'loading'
    || activeSelfSourcesResolution?.status === 'ready' && activeSelfSourcesResolution.loading
  const selfSourcesError = activeSelfSourcesResolution?.status === 'error'
    ? activeSelfSourcesResolution.message
    : activeSelfSourcesResolution?.status === 'ready'
      ? activeSelfSourcesResolution.error
      : undefined
  const source = ui.mode === 'source' || ui.mode === 'contact-add' ? selectedSource ?? aggregateSource : undefined
  const conversationKey = source === undefined ? '' : arkmeSourceIdentityKey(source)
  const notificationActivationRevision = ui.notificationActivationRevision ?? 0
  const activeConversation = active && ui.calendarOpen !== true && source !== undefined
  const activeConversationRef = useRef(activeConversation)
  activeConversationRef.current = activeConversation
  const [markdownQuickNotesEnabled, setMarkdownQuickNotesEnabled] = useState(false)
  const [backgroundSoundSupported, setBackgroundSoundSupported] = useState(false)
  const [backgroundSoundCapabilityKnown, setBackgroundSoundCapabilityKnown] = useState(false)
  const [backgroundSoundEligibilityReason, setBackgroundSoundEligibilityReason] = useState<ArkmeBackgroundSoundEligibilityReason>('membership-unavailable')
  const backgroundSoundAccountGenerationRef = useRef(0)
  useEffect(() => {
    const generation = backgroundSoundAccountGenerationRef.current + 1
    backgroundSoundAccountGenerationRef.current = generation
    setBackgroundSoundSupported(false)
    setBackgroundSoundCapabilityKnown(false)
    setBackgroundSoundEligibilityReason('membership-unavailable')
    if (!activeConversation || authenticatedUserId === undefined || authenticatedAccountKey === undefined) return
    const targetUserId = authenticatedUserId
    const targetAccountKey = authenticatedAccountKey
    let alive = true
    let controller: AbortController | undefined
    const refresh = () => {
      const readToken = arkmeBackgroundSoundServerPreferenceRuntime.beginRead(targetAccountKey)
      if (!arkmeBackgroundSoundServerPreferenceRuntime.current(readToken)) return
      controller?.abort()
      const requestController = new AbortController()
      controller = requestController
      const sameAccount = () => {
        const current = arkmeAuthStore.getSnapshot().auth
        return alive
          && !requestController.signal.aborted
          && activeConversationRef.current
          && backgroundSoundAccountGenerationRef.current === generation
          && arkmeBackgroundSoundServerPreferenceRuntime.current(readToken)
          && arkmeAuthenticatedAccountKey(current) === targetAccountKey
      }
      void Promise.all([
        retryArkmeRead(
          () => callArkme<ArkmeProviderCapabilities>('provider.capabilities', {}, requestController.signal),
          { signal: requestController.signal, retryDelays: [250, 750] },
        ).catch(() => undefined),
        retryArkmeRead(
          () => callArkme<ArkmeBackgroundSoundPreference>('settings.background-sound.get', undefined, requestController.signal),
          { signal: requestController.signal, retryDelays: [250, 750] },
        ).catch(() => undefined),
      ]).then(([capabilities, preference]) => {
        if (!sameAccount()) return
        if (capabilities === undefined) return
        setBackgroundSoundCapabilityKnown(true)
        setMarkdownQuickNotesEnabled(capabilities.features.markdownQuickNotes === true)
        const supported = capabilities.features.backgroundSound === true
        setBackgroundSoundSupported(supported)
        if (!supported) return
        if (preference === undefined) {
          return
        }
        applyArkmeBackgroundSoundOwnerSnapshot(targetAccountKey, preference, undefined, targetUserId)
        setBackgroundSoundEligibilityReason(preference.eligibilityReason)
      })
    }
    const unsubscribeRuntime = arkmeBackgroundSoundServerPreferenceRuntime.subscribe(targetAccountKey, refresh)
    refresh()
    return () => {
      alive = false
      controller?.abort()
      unsubscribeRuntime()
    }
  }, [activeConversation, authenticatedAccountKey, authenticatedUserId])
  const conversationOverlayKey = `${activeConversation ? 'active' : 'inactive'}:${conversationKey}:notification-${String(notificationActivationRevision)}`
  const conversationOverlayScopeRef = useRef({ key: conversationOverlayKey, generation: 0 })
  if (conversationOverlayScopeRef.current.key !== conversationOverlayKey) {
    conversationOverlayScopeRef.current = {
      key: conversationOverlayKey,
      generation: conversationOverlayScopeRef.current.generation + 1,
    }
  }
  const chatDelta = useSyncExternalStore(
    activeConversation ? arkmeChatTimelineDelta.subscribe : NOOP_SUBSCRIBE,
    () => activeConversation ? arkmeChatTimelineDelta.getSnapshotForSource(conversationKey) : EMPTY_CHAT_DELTA_SNAPSHOT,
    () => EMPTY_CHAT_DELTA_SNAPSHOT,
  )
  const interwovenInvalidation = useSyncExternalStore(
    activeConversation ? arkmeInterwovenInvalidation.subscribe : NOOP_SUBSCRIBE,
    () => activeConversation ? arkmeInterwovenInvalidation.getSnapshotForSource(conversationKey) : EMPTY_INTERWOVEN_INVALIDATION,
    () => EMPTY_INTERWOVEN_INVALIDATION,
  )
  const activeSourceKeyRef = useRef('')
  useEffect(() => {
    activeSourceKeyRef.current = conversationKey
  }, [conversationKey])
  const sourceIsChat = source?.kind === 'private_chat' || source?.kind === 'group_chat'
  const [foregroundReadRevision, setForegroundReadRevision] = useState(0)
  useEffect(() => {
    if (!activeConversation || !sourceIsChat || authenticatedUserId === undefined
      || typeof window === 'undefined' || typeof document === 'undefined') return
    const resumeRead = () => {
      if (arkmeVisibleReadIntentAllowed()) setForegroundReadRevision(value => value + 1)
    }
    const releaseReadAvailability = subscribeArkmeReadIntentAvailability(resumeRead)
    window.addEventListener('focus', resumeRead)
    document.addEventListener('visibilitychange', resumeRead)
    return () => {
      releaseReadAvailability()
      window.removeEventListener('focus', resumeRead)
      document.removeEventListener('visibilitychange', resumeRead)
    }
  }, [activeConversation, authenticatedUserId, sourceIsChat])
  const sourceProjectionRevision = useSyncExternalStore(
    activeConversation && !sourceIsChat ? arkmeUi.subscribe : NOOP_SUBSCRIBE,
    () => activeConversation && !sourceIsChat ? arkmeUi.getRecordRevision() : 0,
    () => 0,
  )
  const composerDraftKey = arkmeSourceComposerDraftKey(authenticatedUserId, source)
  const directAdmission = useDirectMessageAdmission(authenticatedAccountKey, source,
    activeConversation && source?.directMessageAdmissionApplicable === true)
  const composerAsyncScopeRef = useRef<ArkmeComposerAsyncScope>({
    accountKey: undefined,
    sourceKey: '',
    draftKey: undefined,
    generation: 0,
  })
  const composerAsyncScope = composerAsyncScopeRef.current
  if (composerAsyncScope.accountKey !== authenticatedAccountKey
    || composerAsyncScope.sourceKey !== conversationKey
    || composerAsyncScope.draftKey !== composerDraftKey) {
    composerAsyncScopeRef.current = {
      accountKey: authenticatedAccountKey,
      sourceKey: conversationKey,
      draftKey: composerDraftKey,
      generation: composerAsyncScope.generation + 1,
    }
  }
  const captureComposerAsyncScope = (): ArkmeComposerAsyncScope => ({ ...composerAsyncScopeRef.current })
  const renderedComposerAsyncScope = captureComposerAsyncScope()
  const sameComposerAsyncScope = (expected: ArkmeComposerAsyncScope): boolean => {
    const current = composerAsyncScopeRef.current
    return activeConversationRef.current
      && current.accountKey === expected.accountKey
      && current.sourceKey === expected.sourceKey
      && current.draftKey === expected.draftKey
      && current.generation === expected.generation
      && arkmeAuthenticatedAccountKey(arkmeAuthStore.getSnapshot().auth) === expected.accountKey
  }
  const composerDraft = useSyncExternalStore(
    activeConversation ? arkmeComposerDraftStore.subscribe : NOOP_SUBSCRIBE,
    () => activeConversation ? arkmeComposerDraftStore.get(composerDraftKey) : arkmeComposerDraftStore.get(undefined),
    () => arkmeComposerDraftStore.get(undefined),
  )
  const draft = composerDraft.text
  const attachments = composerDraft.attachments
  const [composerInputFocused, setComposerInputFocused] = useState(false)
  const [composerShowsInputTime, setComposerShowsInputTime] = useState(arkmeComposerShowsInputTime)
  const [composerExtensionTarget, setComposerExtensionTarget] = useState<{ sourceRef: string; item: ArkmeTimelineItem }>()
  const activeComposerExtensionTarget = composerExtensionTarget?.sourceRef === source?.sourceRef
    ? composerExtensionTarget
    : undefined
  const { composer: recordReeditComposer, composerRef: recordReeditComposerRef, setComposer: setRecordReeditComposer,
    updateCandidate: updateRecordReeditCandidate, activateScope: activateRecordReeditScope,
    findCandidate: findRecordReeditCandidate } = useRecordReeditEditors()
  const seenReeditCommits = useRef(new Set<string>())
  const pendingReeditHighlights = useRef(new Set<string>())
  const recordReeditGenerationRef = useRef(0)
  const [recordReeditHighlightUid, setRecordReeditHighlightUid] = useState('')
  const recordReeditHighlightTimerRef = useRef<number>()
  const activeRecordReeditComposer = recordReeditComposer?.sourceKey === conversationKey
    && recordReeditComposer?.accountKey === authenticatedAccountKey
    ? recordReeditComposer
    : undefined
  const persistRecordReeditDraft = useCallback(async (target: ArkmeRecordReeditComposerState, forCommit = false): Promise<boolean> => {
    const snapshot = target.snapshot
    if (snapshot === undefined || target.busy
      || arkmeAuthenticatedAccountKey(arkmeAuthStore.getSnapshot().auth) !== target.accountKey) return false
    const nextTitle = target.title.trim()
    const textFormat = snapshot.textFormat ?? target.item.textFormat
    const supportsTitle = snapshot.displayKind === 1
    const nextCandidateKey = arkmeRecordReeditCandidateKey(nextTitle, target.textContent, target.attachments, textFormat, target.mentions)
    const alreadyPersisted = () => nextCandidateKey === target.persisted.candidateKey
      && (!forCommit || target.persisted.draftRevision > 0)
    const operation = target.persisted.saveTail.catch(() => undefined).then(async () => {
      if ((!forCommit && target.persisted.exclusive)
        || arkmeAuthenticatedAccountKey(arkmeAuthStore.getSnapshot().auth) !== target.accountKey
        || alreadyPersisted()) return
      const result = await callArkme<{ draftRevision: number }>('source.record-reedit.draft.put', {
        sourceRef: target.sourceRef,
        itemUid: target.item.itemUid,
        newText: target.textContent,
        mentions: target.mentions ?? [],
        expectedVersion: snapshot.draft?.baseVersion ?? snapshot.version,
        expectedDraftRevision: target.persisted.draftRevision,
        attachments: target.attachments.map(attachment => attachment.selection),
        ...(supportsTitle ? { newTitle: nextTitle } : {}),
      })
      target.persisted.draftRevision = result.draftRevision
      target.persisted.candidateKey = nextCandidateKey
    })
    target.persisted.saveTail = operation.then(() => undefined, () => undefined)
    await operation
    return alreadyPersisted()
  }, [])
  const persistRecordReeditBeforeContextExit = useCallback((
    target: ArkmeRecordReeditComposerState,
    shouldClose: () => boolean,
  ) => {
    recordReeditGenerationRef.current += 1
    if (target.snapshot === undefined) {
      updateRecordReeditCandidate(target, current => sameArkmeRecordReeditSession(current, target) ? undefined : current)
      return
    }
    if (target.busy) return
    void persistRecordReeditDraft(target).then(saved => {
      if (!saved) return
      updateRecordReeditCandidate(target, current => sameArkmeRecordReeditSession(current, target)
        && arkmeRecordReeditCandidateKey(current.title, current.textContent, current.attachments, current.snapshot?.textFormat ?? current.item.textFormat, current.mentions)
          === arkmeRecordReeditCandidateKey(target.title, target.textContent, target.attachments, target.snapshot?.textFormat ?? target.item.textFormat, target.mentions)
        && shouldClose()
        ? undefined
        : current)
    }).catch(caught => {
      updateRecordReeditCandidate(target, current => sameArkmeRecordReeditSession(current, target)
        ? { ...current, busy: false, ...arkmeRecordReeditFailure(caught) }
        : current)
    })
  }, [persistRecordReeditDraft, updateRecordReeditCandidate])
  useEffect(() => {
    if (composerExtensionTarget !== undefined && composerExtensionTarget.sourceRef !== source?.sourceRef) {
      setComposerExtensionTarget(undefined)
    }
  }, [composerExtensionTarget, source?.sourceRef])
  useEffect(() => {
    if (recordReeditHighlightTimerRef.current !== undefined) {
      window.clearTimeout(recordReeditHighlightTimerRef.current)
      recordReeditHighlightTimerRef.current = undefined
    }
    setRecordReeditHighlightUid('')
    const target = recordReeditComposerRef.current
    const nextScope = { accountKey: authenticatedAccountKey, sourceKey: conversationKey, sourceRef: source?.sourceRef ?? '' }
    if (target === undefined || target.accountKey !== authenticatedAccountKey) {
      if (target !== undefined && target.snapshot === undefined) updateRecordReeditCandidate(target, () => undefined)
      activateRecordReeditScope(nextScope, ++recordReeditGenerationRef.current)
      return
    }
    if (target.sourceKey === conversationKey) {
      if (activeConversation && target.generation !== recordReeditGenerationRef.current) {
        const generation = ++recordReeditGenerationRef.current
        setRecordReeditComposer(current => sameArkmeRecordReeditSession(current, target)
          ? { ...current, generation } : current)
      }
      return
    }
    persistRecordReeditBeforeContextExit(
      target,
      () => {
        const selected = arkmeUi.getSnapshot().selectedSource
        return selected === undefined || arkmeSourceIdentityKey(selected) !== target.sourceKey
      },
    )
    activateRecordReeditScope(nextScope, ++recordReeditGenerationRef.current)
  }, [activeConversation, authenticatedAccountKey, conversationKey, persistRecordReeditBeforeContextExit, source?.sourceRef, activateRecordReeditScope, updateRecordReeditCandidate])
  useEffect(() => {
    if (activeRecordReeditComposer?.snapshot === undefined) return
    const generation = activeRecordReeditComposer.generation
    const timer = globalThis.setInterval(() => {
      const target = recordReeditComposerRef.current
      if (target?.generation === generation) void persistRecordReeditDraft(target).catch(() => undefined)
    }, ARKME_RECORD_REEDIT_AUTOSAVE_MILLIS)
    return () => { globalThis.clearInterval(timer) }
  }, [activeRecordReeditComposer?.generation, activeRecordReeditComposer?.snapshot, persistRecordReeditDraft])
  useEffect(() => () => {
    const target = recordReeditComposerRef.current
    if (target !== undefined) void persistRecordReeditDraft(target).catch(() => undefined)
  }, [persistRecordReeditDraft])
  const backgroundSoundAvailabilityRef = useRef({
    supported: backgroundSoundSupported,
    eligibilityReason: backgroundSoundEligibilityReason,
  })
  backgroundSoundAvailabilityRef.current = {
    supported: backgroundSoundSupported,
    eligibilityReason: backgroundSoundEligibilityReason,
  }
  const recordInputCaptureOwner = useMemo(() => new ArkmeRecordInputCaptureOwner({
    accountId: authenticatedAccountKey ?? 'logged-out',
    ...(activeConversation ? {} : { recorder: INACTIVE_RECORD_INPUT_RECORDER }),
    backgroundSoundEnabled: () => backgroundSoundAvailabilityRef.current.supported
      && backgroundSoundAvailabilityRef.current.eligibilityReason === 'eligible'
      && arkmeBackgroundSoundCaptureEnabled(authenticatedAccountKey),
  }), [activeConversation, authenticatedAccountKey])
  useEffect(() => {
    recordInputCaptureOwner.activate()
    return () => { recordInputCaptureOwner.deactivate() }
  }, [recordInputCaptureOwner])
  const backgroundSoundCaptureEnabled = useSyncExternalStore(
    useCallback(
      (listener: () => void) => subscribeArkmeBackgroundSoundCapturePreference(authenticatedAccountKey, listener),
      [authenticatedAccountKey],
    ),
    useCallback(() => arkmeBackgroundSoundCaptureEnabled(authenticatedAccountKey), [authenticatedAccountKey]),
    () => false,
  )
  const visibleComposerText = activeRecordReeditComposer?.textContent ?? (directAdmission.blocked ? '' : draft)
  const visibleComposerAttachments = activeRecordReeditComposer === undefined ? attachments
    : activeRecordReeditComposer.attachments.map(attachment => {
      const block = arkmeRecordReeditAttachmentBlock(attachment)
      return block === undefined ? attachment : { ...attachment, previewUrl: arkmeContentMediaUrl(block) }
    })
  const composerTextLength = Array.from(visibleComposerText).length
  const composerHasUserContent = composerTextLength > 0
    || visibleComposerAttachments.length > 0
  const composerStatsVisible = composerInputFocused || composerHasUserContent
  const composerInputStartedAtMillis = recordInputCaptureOwner.getStartedAtMillis(composerDraftKey)
  useEffect(() => {
    recordInputCaptureOwner.sync({
      draftKey: composerDraftKey,
      isActive: !directAdmission.blocked && activeRecordReeditComposer === undefined && activeConversation
        && backgroundSoundSupported && backgroundSoundEligibilityReason === 'eligible'
        && composerInputFocused && arkmeSourceSupportsRecordInputCapture(source?.kind),
      hasUserContent: composerHasUserContent,
    })
  }, [directAdmission.blocked, activeConversation, activeRecordReeditComposer, backgroundSoundEligibilityReason, backgroundSoundSupported, composerDraftKey, composerHasUserContent, composerInputFocused, recordInputCaptureOwner, source?.kind])
  const syncComposerUserInput = useCallback((hasUserContent: boolean) => {
    recordInputCaptureOwner.sync({
      draftKey: composerDraftKey,
      isActive: !directAdmission.blocked && activeConversation && backgroundSoundSupported && backgroundSoundEligibilityReason === 'eligible'
        && composerInputFocused && arkmeSourceSupportsRecordInputCapture(source?.kind),
      hasUserContent,
    })
    if (hasUserContent) recordInputCaptureOwner.beginUserInput(composerDraftKey)
  }, [directAdmission.blocked, activeConversation, backgroundSoundEligibilityReason, backgroundSoundSupported, composerDraftKey, composerInputFocused, recordInputCaptureOwner, source?.kind])
  const surfaceRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLElement>(null)
  const bodyRef = useRef<HTMLDivElement>(null)
  const recordsRef = useRef<HTMLUListElement>(null)
  const selectionStartAreaRef = useRef<HTMLDivElement>(null)
  const endAccessoryRef = useRef<HTMLDivElement>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)
  const newerSentinelRef = useRef<HTMLDivElement>(null)
  const composerRef = useRef<HTMLDivElement>(null)
  const composerResize = useResizableComposer(composerRef, activeConversation ? composerDraftKey : undefined, bodyRef)
  const textareaRef = useRef<ArkmeRichComposerHandle>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const addMenuTriggerRef = useRef<HTMLButtonElement>(null)
  const selfTopicCreateRef = useRef<ArkmeTopicCreateOpener | undefined>()
  const [activeTopicDissolve, setActiveTopicDissolveState] = useState<ArkmeTopicDissolveTask>()
  const activeTopicDissolveRef = useRef<ArkmeTopicDissolveTask>()
  const activeTopicDissolveAccountRef = useRef<string>()
  const publishActiveTopicDissolve = useCallback((next: ArkmeTopicDissolveTask | undefined) => {
    if (sameStateValue(activeTopicDissolveRef.current, next)) return
    activeTopicDissolveRef.current = next
    setActiveTopicDissolveState(next)
  }, [])
  const topicDissolveVisible = activeConversation && isArkmeSelfWorkspaceSource(source)
  useEffect(() => {
    if (activeTopicDissolveAccountRef.current !== authenticatedAccountKey) {
      activeTopicDissolveAccountRef.current = authenticatedAccountKey
      publishActiveTopicDissolve(undefined)
    }
    if (authenticatedUserId === undefined) {
      publishActiveTopicDissolve(undefined)
      return
    }
    if (!topicDissolveVisible) return
    let disposed = false
    let pending = false
    let failures = 0
    let timer: ReturnType<typeof setTimeout> | undefined
    let controller: AbortController | undefined
    const browserDocument = typeof document === 'undefined' ? undefined : document
    const foreground = () => browserDocument?.visibilityState !== 'hidden'
    const clearTimer = () => {
      if (timer !== undefined) clearTimeout(timer)
      timer = undefined
    }
    const refresh = async () => {
      if (disposed || pending || !foreground()) return
      pending = true
      const request = new AbortController()
      controller = request
      try {
        const task = await callArkme<ArkmeTopicDissolveTask | null>('topic.dissolve.active', undefined, request.signal)
        if (disposed || request.signal.aborted) return
        failures = 0
        const next = task ?? undefined
        publishActiveTopicDissolve(next)
        if (next !== undefined && next.stage !== 'completed' && next.stage !== 'failed') {
          timer = setTimeout(() => { timer = undefined; void refresh() }, 500)
        }
      } catch {
        // A transient progress lookup failure must not interrupt the conversation UI.
        if (!request.signal.aborted) {
          failures += 1
          if (failures <= 3) timer = setTimeout(() => { timer = undefined; void refresh() }, 500 * failures)
        }
      } finally {
        if (controller === request) controller = undefined
        pending = false
      }
    }
    const onVisibilityChange = () => {
      if (foreground()) void refresh()
      else { clearTimer(); controller?.abort() }
    }
    void refresh()
    browserDocument?.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      disposed = true
      clearTimer()
      controller?.abort()
      browserDocument?.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [authenticatedAccountKey, authenticatedUserId, conversationKey, publishActiveTopicDissolve, topicDissolveVisible])
  const [timelineView, setTimelineView] = useState<ArkmeTimelineViewState>({
    sourceKey: '', mode: 'latest', aroundSequenceRange: undefined, items: [], aiPolishNotices: [], aiPolishSettings: undefined, nextCursor: undefined, hasMore: false,
    newerCursor: undefined, newerHasMore: false,
  })
  const {
    sourceKey: timelineStateKey,
    mode: timelineMode,
    aroundSequenceRange,
    items,
    aiPolishNotices,
    aiPolishSettings,
    nextCursor,
    hasMore,
    newerCursor,
    newerHasMore,
  } = timelineView
  const aiPolishSettingsRef = useRef(aiPolishSettings)
  const aiPolishNoticesRef = useRef(aiPolishNotices)
  aiPolishSettingsRef.current = aiPolishSettings
  aiPolishNoticesRef.current = aiPolishNotices
  const shareWebsite = authStoreSnapshot.config?.shareWebsite ?? ARKME_DEFAULT_SHARE_WEBSITE
  const setItems = useCallback((action: SetStateAction<ArkmeTimelineItem[]>) => {
    setTimelineView(current => {
      const next = resolveStateAction(action, current.items)
      return sameStateValue(current.items, next) ? current : { ...current, items: next }
    })
  }, [])
  const setAiPolishNotices = useCallback((action: SetStateAction<ArkmeGroupAiPolishNotice[]>) => {
    setTimelineView(current => {
      const next = resolveStateAction(action, current.aiPolishNotices)
      return sameStateValue(current.aiPolishNotices, next) ? current : { ...current, aiPolishNotices: next }
    })
  }, [])
  const setAiPolishSettings = useCallback((action: SetStateAction<ArkmeGroupAiPolishSnapshot | undefined>) => {
    setTimelineView(current => {
      const next = resolveStateAction(action, current.aiPolishSettings)
      return sameStateValue(current.aiPolishSettings, next) ? current : { ...current, aiPolishSettings: next }
    })
  }, [])
  const [selfProfile, setSelfProfile] = useState<ArkmeUserProfile>()
  const [selectedSelfRole, setSelectedSelfRole] = useState<ArkmeSelfRole>()
  useEffect(() => { setSelectedSelfRole(undefined) }, [authenticatedAccountKey])
  useEffect(() => {
    if (!activeConversation || !isArkmeSelfWorkspaceSource(source)) setSelectedSelfRole(undefined)
  }, [activeConversation, source?.kind])
  const selfProfileRevision = useProfileRevision()
  const [drawer, setDrawer] = useState<'detail' | 'copyLink'>()
  const [groupMembersOpen, setGroupMembersOpen] = useState(false)
  const [detailItemUid, setDetailItemUid] = useState('')
  const [detailBackStack, setDetailBackStack] = useState<ArkmeDetailBackTarget[]>([])
  const pendingExtensionParentDetailRef = useRef<ArkmePendingExtensionParentDetail>()
  const openLocatedDetail = useCallback((itemUid: string, revision: number) => {
    const pending = pendingExtensionParentDetailRef.current
    if (pending?.revision === revision) {
      setDetailBackStack(current => pending.kind === 'push' ? [...current, pending.from] : pending.stack)
      pendingExtensionParentDetailRef.current = undefined
    }
    setDetailItemUid(itemUid)
    setShowOriginal(false)
    setDrawer('detail')
  }, [])
  const [detailCallVideoUrl, setDetailCallVideoUrl] = useState<string>()
  const [copyLinkDetail, setCopyLinkDetail] = useState<ArkmeCopyLinkDetailState>()
  const [copyLinkDetailDraft, setCopyLinkDetailDraft] = useState('')
  const [copyLinkDetailSending, setCopyLinkDetailSending] = useState(false)
  const [copyLinkDetailSendError, setCopyLinkDetailSendError] = useState('')
  const [showOriginal, setShowOriginal] = useState(false)
  const [interwovenMoments, setInterwovenMoments] = useState<ArkmeInterwovenMention[]>([])
  const [interwovenCalendarState, setInterwovenCalendarState] = useState<ConversationCalendarInteractionState>('loading')
  const [calendarMomentWindow, setCalendarMomentWindow] = useState<{ sourceKey: string; fromMillis: number }>()
  const [interwovenRefreshRevision, setInterwovenRefreshRevision] = useState(0)
  const [selectedMoment, setSelectedMoment] = useState<ArkmeInterwovenMention>()
  const [detailState, setDetailState] = useState<ArkmeInterwovenDetailViewState>()
  const [momentRelatedView, setMomentRelatedView] = useState<ArkmeRelatedDrawerView>('source-detail')
  const [momentRelatedState, setMomentRelatedState] = useState<ArkmeRelatedQuickNotesLoadState>({ kind: 'idle' })
  const [momentRelatedDetailState, setMomentRelatedDetailState] = useState<ArkmeRelatedQuickNoteDetailState>({ kind: 'idle' })
  const [timelineLoadingKey, setTimelineLoadingKey] = useState('')
  const [timelineSkeletonKey, setTimelineSkeletonKey] = useState('')
  const [timelineRevealKey, setTimelineRevealKey] = useState('')
  const [newMessageCount, setNewMessageCount] = useState(0)
  const [longArticleCreating, setLongArticleCreating] = useState(false)
  const [articlePickerScope, setArticlePickerScope] = useState<ArkmeComposerAsyncScope>()
  const articleDraftKey = sourceIsChat ? composerArticleKey(authenticatedAccountKey, composerDraftKey) : undefined
  const storedArticle = useComposerArticle(articleDraftKey)
  const pendingArticle = activeRecordReeditComposer === undefined ? storedArticle : undefined
  const articlePickerOpen = articlePickerScope !== undefined && sameComposerAsyncScope(articlePickerScope) && activeRecordReeditComposer === undefined
  const [addMenuOpen, setAddMenuOpen] = useState(false)
  const preparationJobs = useRef(new Map<string, Promise<boolean>>())
  const stageControllers = useRef(new Set<AbortController>())
  const backgroundDirectUploadCacheRef = useRef(new Map<string, ArkmeUploadedAsset[]>())
  useEffect(() => {
    backgroundDirectUploadCacheRef.current.clear()
    return () => { backgroundDirectUploadCacheRef.current.clear() }
  }, [authenticatedAccountKey])
  const [preparingKeys, setPreparingKeys] = useState<Set<string>>(() => new Set())
  const [draftPreview, setDraftPreview] = useState<ArkmeContentBlock>()
  const fileTasks = useArkmeFileSendTasks(source?.sourceRef, authenticatedUserId, activeConversation)
  const notifiedFileTasks = useRef(new Set<string>())
  useEffect(() => { setDraftPreview(undefined) }, [authenticatedUserId])
  useEffect(() => () => { for (const controller of stageControllers.current) controller.abort() }, [authenticatedUserId])
  const [busy, setBusy] = useState(false)
  const preparingFiles = composerDraftKey !== undefined && preparingKeys.has(composerDraftKey)
  const preparingReeditFiles = activeRecordReeditComposer !== undefined
    && preparingKeys.has(arkmeRecordReeditPreparationKey(activeRecordReeditComposer))
  const composerFilesDisabled = activeRecordReeditComposer === undefined
    ? preparingFiles || directAdmission.blocked
    : activeRecordReeditComposer.snapshot === undefined || preparingReeditFiles
      || activeRecordReeditComposer.loading || activeRecordReeditComposer.busy
  const composerFileAddingDisabled = composerFilesDisabled || activeRecordReeditComposer?.snapshot?.maxAttachments === 0
  const messagePreparing = useMessagePreparing({
    source, accountScope: authenticatedAccountKey,
    enabled: activeConversation && activeRecordReeditComposer === undefined && !preparingFiles && !directAdmission.blocked,
    focused: composerInputFocused,
  })
  // Transport is per message.  It must never lock the next draft while a previous
  // message waits for the server, otherwise fast keyboard input is dropped.
  const archiveReadOnly = isArkmeDSHInputTopic(source) || isArkmeDSHInputTopic(selectedSource)
  useSyncExternalStore(conversationSending.subscribe, conversationSending.getRevision, conversationSending.getRevision)
  const canSend = !conversationSending.has(composerDraftKey) && (activeRecordReeditComposer === undefined
    ? !archiveReadOnly && !directAdmission.blocked && (pendingArticle ? !pendingArticle.sending : arkmeComposerCanSend(draft, attachments.length + (composerDraftKey !== undefined && preparingKeys.has(composerDraftKey) ? 1 : 0), preparingFiles))
    : activeRecordReeditComposer.snapshot !== undefined
      && !activeRecordReeditComposer.loading
      && !activeRecordReeditComposer.busy
      && !preparingReeditFiles
      && (activeRecordReeditComposer.textContent.trim() !== ''
        || activeRecordReeditComposer.attachments.length > 0 || activeRecordReeditComposer.snapshot.hasVoice === true))
  const pendingComposerFocusDraftKeyRef = useRef<string>()
  const [compactNavigation, setCompactNavigation] = useState(false)
  const [submitBusy, setSubmitBusy] = useState(false)
  const [loadingOlder, setLoadingOlder] = useState(false)
  const olderLoadRef = useRef({ armed: true, error: '' })
  const olderLoadError = olderLoadRef.current.error
  const [loadingNewer, setLoadingNewer] = useState(false)
  const newerSentinelArmedRef = useRef(true)
  const [highlightedTargetUid, setHighlightedTargetUid] = useState('')
  const transientReactionHighlightRef = useRef(false)
  const calendarHighlightTimerRef = useRef<ReturnType<typeof setTimeout>>()
  const pendingMessageHighlightRef = useRef<() => void>()
  useEffect(() => () => {
    clearTimeout(calendarHighlightTimerRef.current)
    pendingMessageHighlightRef.current?.()
  }, [conversationKey, authenticatedAccountKey])
  const highlightLocatedMessage = useCallback((body: HTMLElement, row: HTMLElement, uid: string, transient: boolean, expires: boolean) => {
    pendingMessageHighlightRef.current?.()
    clearTimeout(calendarHighlightTimerRef.current)
    const highlight = () => {
      transientReactionHighlightRef.current = transient
      setHighlightedTargetUid(uid)
      if (expires) calendarHighlightTimerRef.current = setTimeout(() => {
        setHighlightedTargetUid(current => current === uid ? '' : current)
      }, 2_000)
    }
    if (transient) pendingMessageHighlightRef.current = afterMessageVisible(body, row, highlight)
    else highlight()
  }, [])
  const conversationTargetPagingRef = useRef({ revision: 0, pages: 0, aroundRequested: false })
  const conversationTargetLocatedRevisionRef = useRef(0)
  const conversationTargetAbortRef = useRef<AbortController>()
  const [error, setError] = useState(initialAuth?.status === 'binding-required' ? t('error.binding.required') : '')
  const createLongArticle = async () => {
    if (!source) return
    try {
      if (!await openLongArticleWindow({ ...source, sourceKey: conversationKey })) setLongArticleCreating(true)
    } catch (caught) { setError(errorMessage(caught)) }
  }
  const [agreed, setAgreed] = useState(true)
  const [loginMode, setLoginMode] = useState<ArkmeLoginMode>(initialAuth?.status === 'binding-required' ? 'phone' : 'jiwo')
  const [phone, setPhone] = useState('')
  const [smsCode, setSmsCode] = useState('')
  const [smsCountdown, setSmsCountdown] = useState(0)
  const [captchaId, setCaptchaId] = useState('')
  const [testLoginEnabled, setTestLoginEnabled] = useState(false)
  const [jiwoScanLoginEnabled, setJiwoScanLoginEnabled] = useState(false)
  const [testUserId, setTestUserId] = useState('')
  const [qr, setQr] = useState('')
  const conversationMemberSnapshot = useConversationMembers(authenticatedAccountKey, source, activeConversation)
  const conversationMembers = conversationMemberSnapshot.items
  const conversationJoinEvents = conversationMemberSnapshot.joinEvents
  const groupSelfRole = conversationMemberSnapshot.selfRole
  useEffect(() => {
    if (conversationMemberSnapshot.error !== undefined) setError(conversationMemberSnapshot.error)
  }, [conversationMemberSnapshot.error])
  const [groupMentionBots, setGroupMentionBots] = useState<ArkmeGroupBotCandidateList>()
  const [privateMentionBots, setPrivateMentionBots] = useState<ArkmeBotList>()
  const [mentionTrigger, setMentionTrigger] = useState<ArkmeComposerMentionTrigger>()
  const [mentionCandidateIndex, setMentionCandidateIndex] = useState(0)
  const [hashTagTrigger, setHashTagTrigger] = useState<ArkmeHashTagTrigger>()
  const [hashTagMemory, setHashTagMemory] = useState<{ accountKey?: string; items: ArkmeRecordTagItem[] }>({ items: [] })
  const hashTagItems = hashTagMemory.accountKey === authenticatedAccountKey ? hashTagMemory.items : []
  const [hashTagLoading, setHashTagLoading] = useState(false)
  const [hashTagError, setHashTagError] = useState(false)
  const [hashTagRemote, setHashTagRemote] = useState<{ key: string; items: ArkmeRecordTagItem[] }>({ key: '', items: [] })
  const hashTagRetryRef = useRef<() => void>()
  const [hashTagCandidateIndex, setHashTagCandidateIndex] = useState(0)
  const [hashTagRefreshRevision, setHashTagRefreshRevision] = useState(0)
  const hashTagSuggestionListRef = useRef<HTMLDivElement | null>(null)
  const activeHashTagStartRef = useRef<number>()
  const dismissedHashTagStartRef = useRef<number>()
  const hashTagActive = hashTagTrigger !== undefined
  const hashTagQuery = hashTagTrigger?.query ?? ''
  const hashTagSearchKey = JSON.stringify([authenticatedAccountKey, conversationKey, hashTagQuery])
  useEffect(() => {
    // Candidate memory belongs to an account, never to a conversation. Clear it
    // only when the authenticated account changes or logs out.
    setHashTagMemory({ ...(authenticatedAccountKey === undefined ? {} : { accountKey: authenticatedAccountKey }), items: [] })
  }, [authenticatedAccountKey])
  const [memberMenu, setMemberMenu] = useState<{
    member: ArkmeConversationMemberItem
    position: ArkmeMemberMenuPosition
    hoverAnchor?: HTMLElement
    hoverSide?: 'left' | 'right'
  }>()
  const [botMenu, setBotMenu] = useState<{
    identity: ArkmeBotAvatarIdentity
    position: { left: number; top: number }
    hoverAnchor?: HTMLElement
    hoverSide?: 'left' | 'right'
  }>()
  const [botProfile, setBotProfile] = useState<{
    identity: ArkmeBotAvatarIdentity
  }>()
  const [botRemovalTarget, setBotRemovalTarget] = useState<{ identity: ArkmeBotAvatarIdentity; bot: ArkmeGroupBotItem }>()
  const [botRecords, setBotRecords] = useState<{ identity: ArkmeBotAvatarIdentity; mode: ArkmeConversationMemberRecordMode }>()
  useEffect(() => authenticatedAccountKey ? reactionNotifications.acquire(authenticatedAccountKey) : undefined, [authenticatedAccountKey])
  const [reactionMenuRequest, setReactionMenuRequest] = useState<{ scope: string; occurrenceKey: string; toggle?: boolean }>()
  const [messageMenu, setMessageMenu] = useState<{
    occurrenceKey: string
    left: number
    top: number
    anchor: HTMLElement
  }>()
  const [snapshot, setSnapshot] = useState<{ item: ArkmeTimelineItem; actionRef: string; detail?: ArkmeMessageSnapshotDetail; loading: boolean; loadError?: string }>()
  const [messageReportItem, setMessageReportItem] = useState<ArkmeTimelineItem>()
  const [messageWithdrawalItem, setMessageWithdrawalItem] = useState<ArkmeTimelineItem>()

  const [messageActionStatus, setMessageActionStatus] = useState('')
  const messageActionStatusTimerRef = useRef<number>()
  const forwardSuccessTimerRef = useRef<number>()
  const [selectMode, setSelectMode] = useState<{ sourceKey: string; selectedIds: Set<string> }>()
  const [nativeMessageActionBusy, setMessageActionBusy] = useState<'copy-link' | 'forward'>()
  const askDsh = useAskDsh(JSON.stringify([authenticatedAccountKey, conversationKey, [...(selectMode?.selectedIds ?? [])]]), () => {
    exitMessageSelectMode()
    arkmeUi.showHarness()
    onActivateSurface?.()
  })
  const messageActionBusy = askDsh.busy ? 'ask-dsh' : nativeMessageActionBusy

  const [recordDeletion, setRecordDeletion] = useState<{ scopeKey: string; sourceRef: string; items: ArkmeTimelineItem[] }>()
  const [topicAssignment, setTopicAssignment] = useState<{
    scopeKey: string; source: ArkmeSourceItem; assignmentRefs: string[]; itemUids: string[]; anchor: HTMLElement | undefined; currentTopicKey?: string
  }>()
  const topicAssignmentScopeKey = `${authenticatedAccountKey ?? ''}:${conversationOverlayKey}`
  useEffect(() => { setRecordDeletion(undefined) }, [topicAssignmentScopeKey, activeConversation, active])
  useEffect(() => { setTopicAssignment(undefined) }, [topicAssignmentScopeKey])

  type NativeForwardRequest = {
    preview: ReturnType<typeof nativeForwardPreview>
    delivery: NativeForwardDelivery
    signal: AbortSignal
    finish(result: NativeForwardResult): void
  }
  const nativeForwardRequestRef = useRef<NativeForwardRequest>()
  const [forwardTargetPicker, setForwardTargetPicker] = useState<{
    native?: NativeForwardRequest
    sourceKey: string
    itemUids: string[]
    items: ArkmeTimelineItem[]
    selectedTargetKeys: string[]
    keyword: string
    commentText: string
    sendError: string
  }>()
  const forwardPickerOpen = forwardTargetPicker !== undefined
  const forwardDirectory = useForwardTargetDirectory(authenticatedAccountKey, forwardPickerOpen)
  const needsChatDirectory = activeConversation && detailState?.kind === 'success'
  const chatDirectory = useSyncExternalStore(
    needsChatDirectory ? arkmeChatDirectory.subscribe : NOOP_SUBSCRIBE,
    () => needsChatDirectory ? arkmeChatDirectory.getSnapshot() : EMPTY_CHAT_DIRECTORY_SNAPSHOT,
    () => EMPTY_CHAT_DIRECTORY_SNAPSHOT,
  )
  const [forwardSuccessFeedback, setForwardSuccessFeedback] = useState<{
    message: string
    targets: ArkmeSourceItem[]
  }>()
  const [memberProfile, setMemberProfile] = useState<ArkmeConversationMemberItem>()
  const [memberEventProfile, setMemberEventProfile] = useState<{ scope:string; event:ArkmeMemberEvent }>()
  const [memberEventWindowRevision, setMemberEventWindowRevision] = useState(0)
  const [memberRemovalTarget, setMemberRemovalTarget] = useState<ArkmeConversationMemberItem>()
  const [memberRecords, setMemberRecords] = useState<{
    member: ArkmeConversationMemberItem
    mode: ArkmeConversationMemberRecordMode
  }>()
  const loadedBotRecords = useMemo(() => botRecords === undefined || timelineStateKey !== conversationKey
    ? [] : arkmeLoadedBotRecords(items, botRecords.identity.directoryKey, botRecords.mode),
  [botRecords, conversationKey, items, timelineStateKey])
  const privateChatAbortRef = useRef<AbortController>()
  const [privateChatBusy, setPrivateChatBusy] = useState(false)

  useEffect(() => {
    privateChatAbortRef.current?.abort()
    privateChatAbortRef.current = undefined
    setPrivateChatBusy(false)
    return () => {
      privateChatAbortRef.current?.abort()
      privateChatAbortRef.current = undefined
    }
  }, [activeConversation, authenticatedUserId, conversationKey])
  const closeMemberProfile = useCallback(() => {
    privateChatAbortRef.current?.abort()
    privateChatAbortRef.current = undefined
    setPrivateChatBusy(false)
    setMemberProfile(undefined)
  }, [])

  useEffect(() => {
    if (!activeConversation) return
    setMemberMenu(undefined)
    setBotMenu(undefined)
    setBotProfile(undefined)
    setBotRemovalTarget(undefined)
    setBotRecords(undefined)
    setMemberProfile(undefined)
    setMemberRemovalTarget(undefined)
    setMemberRecords(undefined)
    setSnapshot(undefined)
    setMessageReportItem(undefined)
    setMessageWithdrawalItem(undefined)
  }, [activeConversation, authenticatedUserId, conversationKey, source?.kind, source?.sourceRef])

  useEffect(() => {
    if (!activeConversation) return
    setGroupMentionBots(undefined)
    setPrivateMentionBots(undefined)
    if (authenticatedUserId === undefined || source === undefined || mentionTrigger === undefined) return
    const controller = new AbortController()
    if (source.kind === 'group_chat') {
      void callArkme<ArkmeGroupBotCandidateList>('group.bots', { sourceRef: source.sourceRef }, controller.signal)
        .then(snapshot => {
          if (!controller.signal.aborted) setGroupMentionBots(snapshot)
        })
        .catch(caught => {
          if (!controller.signal.aborted) console.warn('dsh-arkme: mention bot refresh failed', errorMessage(caught))
        })
    } else if (source.kind === 'private_chat') {
      void callArkme<ArkmeBotList>('bots.list', undefined, controller.signal)
        .then(snapshot => {
          if (!controller.signal.aborted) setPrivateMentionBots(snapshot)
        })
        .catch(() => undefined)
    }
    return () => { controller.abort() }
  }, [activeConversation, authenticatedUserId, conversationKey, mentionTrigger?.startIndex, source?.kind, source?.sourceRef])

  useEffect(() => {
    if (!activeConversation || authenticatedAccountKey === undefined || authenticatedUserId === undefined || source === undefined || !hashTagActive) return
    const controller = new AbortController()
    const localItems = arkmeMergeHashTagSuggestions([], items)
    setHashTagMemory(current => ({ accountKey: authenticatedAccountKey, items: arkmeReconcileHashTagSuggestionSnapshots(
      current.accountKey === authenticatedAccountKey ? current.items : [], localItems,
    ) }))
    setHashTagRemote({ key: hashTagSearchKey, items: [] })
    setHashTagError(false)
    setHashTagLoading(true)
    let inFlight = false
    // Each query/account owns its controller. Even transports that
    // ignore abort cannot publish an old response into the new candidate list.
    const loadTags = () => {
      if (inFlight || controller.signal.aborted) return
      inFlight = true
      setHashTagLoading(true)
      setHashTagError(false)
      void callArkme<ArkmeRecordTagList>('records.tags.list', {
        query: hashTagQuery, limit: 100,
      }, controller.signal).then(snapshot => {
        if (controller.signal.aborted) return
        setHashTagRemote({ key: hashTagSearchKey, items: snapshot.items })
      }).catch(caught => {
        if (controller.signal.aborted) return
        setHashTagError(true)
        console.warn('dsh-arkme: hashtag refresh failed', errorMessage(caught))
      }).finally(() => {
        inFlight = false
        if (!controller.signal.aborted) setHashTagLoading(false)
      })
    }
    hashTagRetryRef.current = loadTags
    const timer = window.setTimeout(loadTags, 200)
    return () => {
      window.clearTimeout(timer)
      controller.abort()
      hashTagRetryRef.current = undefined
    }
  }, [activeConversation, authenticatedAccountKey, authenticatedUserId, conversationKey, hashTagActive, hashTagQuery, hashTagSearchKey, hashTagRefreshRevision, source?.sourceRef])

  useEffect(() => () => {
    if (messageActionStatusTimerRef.current !== undefined) window.clearTimeout(messageActionStatusTimerRef.current)
    if (forwardSuccessTimerRef.current !== undefined) window.clearTimeout(forwardSuccessTimerRef.current)
    if (recordReeditHighlightTimerRef.current !== undefined) window.clearTimeout(recordReeditHighlightTimerRef.current)
  }, [])

  useEffect(() => {
    if (!active || selectMode?.sourceKey === conversationKey) return
    const pendingDraftKey = pendingComposerFocusDraftKeyRef.current
    if (pendingDraftKey === undefined || busy) return
    if (pendingDraftKey !== composerDraftKey) {
      pendingComposerFocusDraftKeyRef.current = undefined
      return
    }
    pendingComposerFocusDraftKeyRef.current = undefined
    const frame = requestAnimationFrame(() => {
      const activeElement = document.activeElement
      restoreArkmeComposerFocus(
        textareaRef.current,
        activeElement,
        document.body,
        activeElement !== null && composerRef.current?.contains(activeElement) === true,
      )
    })
    return () => { cancelAnimationFrame(frame) }
  }, [active, busy, composerDraftKey, conversationKey, selectMode?.sourceKey])

  useEffect(() => {
    let active = true
    setSelfProfile(current => current?.userId === authenticatedUserId ? current : undefined)
    if (!activeConversation && ui.mode !== 'world' && ui.mode !== 'extensions') return () => { active = false }
    if (authenticatedUserId === undefined) return () => { active = false }
    void callArkme<ArkmeUserProfileSnapshot>('user.profile')
      .then(async snapshot => snapshot.profile === null
        ? await callArkme<ArkmeUserProfileSnapshot>('user.profile.refresh')
        : snapshot)
      .then(snapshot => {
        if (active && snapshot.profile?.userId === authenticatedUserId) setSelfProfile(snapshot.profile)
      })
      .catch(() => undefined)
    return () => { active = false }
  }, [activeConversation, authenticatedUserId, ui.mode, selfProfileRevision])

  useEffect(() => {
    if (ui.mode !== 'contact-add' || typeof document === 'undefined') return
    const closeFromKeyboard = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') arkmeUi.showConversations()
    }
    document.addEventListener('keydown', closeFromKeyboard)
    return () => { document.removeEventListener('keydown', closeFromKeyboard) }
  }, [ui.mode])


  useEffect(() => {
    if (highlightedTargetUid === '' || typeof document === 'undefined') return
    const clearHighlightedTargetFromOutside = (event: PointerEvent) => {
      if (event.button !== 0) return
      const eventTarget = event.target as (EventTarget & {
        closest?: (selectors: string) => { getAttribute: (name: string) => string | null } | null
      }) | null
      const clickedTargetUid = eventTarget
        ?.closest?.('[data-arkme-message-item-uid]')
        ?.getAttribute('data-arkme-message-item-uid')
      if (clickedTargetUid === highlightedTargetUid) return
      setHighlightedTargetUid('')
    }
    document.addEventListener('pointerdown', clearHighlightedTargetFromOutside, true)
    return () => {
      document.removeEventListener('pointerdown', clearHighlightedTargetFromOutside, true)
    }
  }, [highlightedTargetUid])

  const qrRequestStartedRef = useRef(false)
  const qrFlowRevisionRef = useRef(0)
  const currentJiwoAttemptRef = useRef<string>()
  const pendingLoginModeSelectionRef = useRef<ArkmeLoginMode>()
  const conversationCacheRef = useRef(new ArkmeConversationMemoryCache())
  const confirmedSendRetention = useMemo(
    () => new ArkmeConfirmedSendRetentionOwner(),
    [authenticatedAccountKey],
  )
  const cacheAccountKeyRef = useRef<string>()
  const timelineGenerationRef = useRef(0)
  const timelineWindowRevisionRef = useRef(0)
  const appliedTimelineInvalidationsRef = useRef(new Map<string, number>())
  const timelineRequestsRef = useRef(new Map<'initial' | 'older' | 'newer' | 'refresh', {
    controller: AbortController
    requestKey: string
    returnToLatest: boolean
  }>())
  const pendingViewportRestoreRef = useRef<ArkmeConversationViewportRestore>()
  useEffect(() => longArticleWindowBridge()?.onCreated(receipt => {
    if (receipt.accountKey !== authenticatedAccountKey) return
    if (!receipt.article) confirmedSendRetention.retain(receipt.sourceKey, receipt.item)
    if (receipt.sourceKey === conversationKey) {
      pendingViewportRestoreRef.current = { sourceKey: conversationKey, viewport: undefined }
      setItems(current => receipt.article?.mode === 'existing'
        ? current.map(item => item.itemUid === receipt.item.itemUid ? { ...item, ...receipt.item } : item)
        : mergeItems(current, [receipt.item]))
    }
    arkmeUi.recordChanged()
    void arkmeChatDirectory.refreshRoot({ force: true, silent: true }).catch(() => {})
  }), [authenticatedAccountKey, conversationKey, confirmedSendRetention, setItems])
  const viewportRestoreIntentRef = useRef<boolean>()
  const pendingConversationTargetLocateRef = useRef<{
    sourceKey: string
    itemUid: string
    revision: number
    momentId?: string
    transientHighlight?: boolean
  }>()
  const interwovenRequestRef = useRef<AbortController>()
  const interwovenGenerationRef = useRef(0)
  const detailRequestRef = useRef<AbortController>()
  const detailRequestMomentRef = useRef('')
  const momentRelatedRequestRef = useRef<AbortController>()
  const momentRelatedDetailRequestRef = useRef<AbortController>()
  const momentRelatedGenerationRef = useRef(0)
  const snapshotRequestRef = useRef<AbortController>()
  const copyLinkDetailRequestRef = useRef<AbortController>()
  const copyLinkDetailScopeRef = useRef({ sending: false })
  const forwardPickerScopeRef = useRef<AbortController>()
  const forwardSubmissionScopeRef = useRef({ sending: false })
  const copyLinkRefreshTimerRef = useRef<number>()
  const lastReadAckRef = useRef('')
  const bindingNotifiedUserIdRef = useRef<number | undefined>()
  const ignoreStaleBindingAuthRef = useRef(false)
  useEffect(() => {
    if (activeConversation) return
    timelineGenerationRef.current += 1
    timelineWindowRevisionRef.current += 1
    olderLoadRef.current = { armed: true, error: '' }
    setLoadingOlder(false)
    for (const request of timelineRequestsRef.current.values()) request.controller.abort()
    timelineRequestsRef.current.clear()
    conversationTargetAbortRef.current?.abort()
    conversationTargetAbortRef.current = undefined
    pendingConversationTargetLocateRef.current = undefined
    setHighlightedTargetUid('')
    interwovenGenerationRef.current += 1
    interwovenRequestRef.current?.abort()
    interwovenRequestRef.current = undefined
    detailRequestRef.current?.abort()
    detailRequestRef.current = undefined
    momentRelatedGenerationRef.current += 1
    momentRelatedRequestRef.current?.abort()
    momentRelatedRequestRef.current = undefined
    momentRelatedDetailRequestRef.current?.abort()
    momentRelatedDetailRequestRef.current = undefined
    relatedGenerationRef.current += 1
    relatedPageAbortRef.current?.abort()
    relatedPageAbortRef.current = undefined
    for (const controller of stageControllers.current) controller.abort()
    snapshotRequestRef.current?.abort()
    snapshotRequestRef.current = undefined
    copyLinkDetailRequestRef.current?.abort()
    copyLinkDetailRequestRef.current = undefined
    copyLinkDetailScopeRef.current = { sending: false }
    forwardSubmissionScopeRef.current = { sending: false }
    forwardPickerScopeRef.current?.abort()
    forwardPickerScopeRef.current = undefined
    if (copyLinkRefreshTimerRef.current !== undefined) {
      window.clearTimeout(copyLinkRefreshTimerRef.current)
      copyLinkRefreshTimerRef.current = undefined
    }
    if (messageActionStatusTimerRef.current !== undefined) {
      window.clearTimeout(messageActionStatusTimerRef.current)
      messageActionStatusTimerRef.current = undefined
    }
    if (forwardSuccessTimerRef.current !== undefined) {
      window.clearTimeout(forwardSuccessTimerRef.current)
      forwardSuccessTimerRef.current = undefined
    }
    setDraftPreview(undefined)
    setLongArticleCreating(false)
    setArticlePickerScope(undefined)
    const reeditTarget = recordReeditComposerRef.current
    if (reeditTarget !== undefined) {
      persistRecordReeditBeforeContextExit(reeditTarget, () => !activeConversationRef.current)
    }
    setAddMenuOpen(false)
    setMentionTrigger(undefined)
    setRelatedMenuOpen(false)
    setRelatedPanelOpen(false)
    setRelatedDetail(undefined)
    setGroupMembersOpen(false)
    setDrawer(undefined)
    setDetailItemUid('')
    setDetailBackStack([])
    const pendingParent = pendingExtensionParentDetailRef.current
    if (arkmeUi.getSnapshot().conversationTarget?.revision !== pendingParent?.revision) {
      pendingExtensionParentDetailRef.current = undefined
    }
    setCopyLinkDetail(undefined)
    setCopyLinkDetailDraft('')
    setCopyLinkDetailSending(false)
    setCopyLinkDetailSendError('')
    setShowOriginal(false)
    setSelectedMoment(undefined)
    setDetailState(undefined)
    setMomentRelatedView('source-detail')
    setMomentRelatedState({ kind: 'idle' })
    setMomentRelatedDetailState({ kind: 'idle' })
    setMemberMenu(undefined)
    setMessageMenu(undefined)
    setSnapshot(undefined)
    setMessageReportItem(undefined)
    setMemberProfile(undefined)
    setMemberEventProfile(undefined)
    setMemberRecords(undefined)
    setBotRecords(undefined)
    setBotRemovalTarget(undefined)
    setPrivateChatBusy(false)
    setSelectMode(undefined)
    setForwardTargetPicker(undefined)
    setForwardSuccessFeedback(undefined)
    setMessageActionStatus('')
    setMessageActionBusy(undefined)
  }, [activeConversation, persistRecordReeditBeforeContextExit])
  const authenticated = auth?.status === 'authenticated'
  const authView = arkmeAuthView(auth)
  const phoneBindingRequired = arkmeLoginNeedsPhoneBinding(auth)
  const localeId = t('locale.id')
  const localeIdRef = useRef(localeId)

  useEffect(() => {
    if (localeIdRef.current === localeId) return
    localeIdRef.current = localeId
    if (authView === 'login') setError('')
  }, [authView, localeId])
  const [relatedMenuOpen, setRelatedMenuOpen] = useState(false)
  const [selfMenuOpen, setSelfMenuOpen] = useState(false)
  const [selfCalendarOpen, setSelfCalendarOpen] = useState(false)
  const [conversationExport, setConversationExport] = useState<{
    sourceKey: string
    sourceName: string
    processed: number
    status: 'downloading' | 'success' | 'error'
    itemCount?: number
    error?: string
  }>()
  const [conversationExportToast, setConversationExportToast] = useState<{
    sequence: number
    kind: 'success' | 'error'
    text: string
  }>()
  const conversationExportAbortRef = useRef<AbortController>()
  const conversationExportToastSequenceRef = useRef(0)
  const privateActions = usePrivateChatActions(authenticatedAccountKey, authenticatedUserId, source, activeConversation, relatedMenuOpen)
  useEffect(() => { if (relatedMenuOpen) directAdmission.refresh() }, [relatedMenuOpen, directAdmission.refresh])
  const [relatedPanelOpen, setRelatedPanelOpen] = useState(false)
  const [commonGroupsOpen, setCommonGroupsOpen] = useState(false)
  useEffect(() => { setCommonGroupsOpen(false) }, [conversationKey, authenticatedAccountKey])
  const [relatedState, setRelatedState] = useState<'loading' | ArkmeRelatedRecordingPageState>('loading')
  const [relatedStateMessage, setRelatedStateMessage] = useState('')
  const [relatedError, setRelatedError] = useState('')
  const [relatedItems, setRelatedItems] = useState<ArkmeRelatedRecordingItem[]>([])
  const [relatedMonths, setRelatedMonths] = useState<ArkmeRelatedRecordingMonthBucket[]>([])
  const [relatedMonth, setRelatedMonth] = useState('')
  const [relatedHasMore, setRelatedHasMore] = useState(false)
  const [relatedNextCursor, setRelatedNextCursor] = useState<string>()
  const [relatedLoadingMore, setRelatedLoadingMore] = useState(false)
  const [relatedDetail, setRelatedDetail] = useState<ArkmeRelatedRecordingItem>()
  const relatedPageAbortRef = useRef<AbortController>()
  const relatedGenerationRef = useRef(0)
  const relatedLoadingMoreRef = useRef(false)
  const activeRelatedSourceKeyRef = useRef('')
  const relatedMenuButtonRef = useRef<HTMLButtonElement>(null)
  const selfMenuButtonRef = useRef<HTMLButtonElement>(null)
  const selfCalendarButtonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if ((!relatedMenuOpen && !selfMenuOpen && !selfCalendarOpen) || typeof document === 'undefined') return
    const closeFromKeyboard = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setRelatedMenuOpen(false)
      setSelfMenuOpen(false)
      setSelfCalendarOpen(false)
    }
    document.addEventListener('keydown', closeFromKeyboard)
    return () => {
      document.removeEventListener('keydown', closeFromKeyboard)
    }
  }, [relatedMenuOpen, selfMenuOpen, selfCalendarOpen])

  useEffect(() => { setSelfCalendarOpen(false) }, [conversationKey])

  useEffect(() => () => { conversationExportAbortRef.current?.abort() }, [])
  useEffect(() => {
    conversationExportAbortRef.current?.abort()
    conversationExportAbortRef.current = undefined
    setConversationExport(undefined)
    setConversationExportToast(undefined)
  }, [authenticatedAccountKey])

  useEffect(() => {
    if (timelineLoadingKey === '') return
    const sourceKey = timelineLoadingKey
    const timer = setTimeout(() => {
      setTimelineSkeletonKey(current => timelineLoadingKey === sourceKey ? sourceKey : current)
    }, 120)
    return () => { clearTimeout(timer) }
  }, [timelineLoadingKey])

  useEffect(() => {
    if (timelineRevealKey === '') return
    const timer = setTimeout(() => { setTimelineRevealKey('') }, 160)
    return () => { clearTimeout(timer) }
  }, [timelineRevealKey])

  useEffect(() => {
    const element = surfaceRef.current
    if (!active || element === null || typeof ResizeObserver === 'undefined') return
    const update = () => { setCompactNavigation(element.getBoundingClientRect().width < 720) }
    update()
    const observer = new ResizeObserver(update)
    observer.observe(element)
    return () => { observer.disconnect() }
  }, [active])

  const acceptAuthSnapshot = useCallback((snapshot: ArkmeAuthSnapshot) => {
    const previous = arkmeAuthStore.getSnapshot().auth
    const accountChanged = arkmeAuthenticatedAccountChanged(previous, snapshot)
    arkmeAuthStore.setAuth(snapshot)
    if (snapshot.status === 'binding-required') {
      setLoginMode('phone')
      setAgreed(true)
      setQr('')
      setSmsCode('')
      setError(t('error.binding.required'))
      if (bindingNotifiedUserIdRef.current !== snapshot.userId) {
        bindingNotifiedUserIdRef.current = snapshot.userId
        arkmeUi.authChanged(false)
      }
      return
    }
    bindingNotifiedUserIdRef.current = undefined
    if (snapshot.status !== 'authenticated') {
      if (arkmeUi.getSnapshot().mode !== 'login') arkmeUi.authChanged(false)
      return
    }
    if (accountChanged || arkmeUi.getSnapshot().mode === 'login') {
      arkmeUi.authChanged(true, accountChanged)
    }
  }, [t])

  useEffect(() => {
    if (initialAuth === undefined) return
    if (ignoreStaleBindingAuthRef.current && initialAuth.status !== 'logged-out') return
    ignoreStaleBindingAuthRef.current = false
    if (auth?.status === 'binding-required' && initialAuth.status === 'logged-out') return
    acceptAuthSnapshot(initialAuth)
  }, [acceptAuthSnapshot, auth?.status, initialAuth])
  useEffect(() => {
    if (authStoreSnapshot.auth === undefined) return
    if (ignoreStaleBindingAuthRef.current && authStoreSnapshot.auth.status !== 'logged-out') return
    ignoreStaleBindingAuthRef.current = false
    if (auth?.status === 'binding-required' && authStoreSnapshot.auth.status === 'logged-out') return
    acceptAuthSnapshot(authStoreSnapshot.auth)
  }, [acceptAuthSnapshot, auth?.status, authStoreSnapshot.auth])
  useEffect(() => {
    if (authStoreSnapshot.config === undefined) return
    setCaptchaId(authStoreSnapshot.config.captchaId)
    setTestLoginEnabled(authStoreSnapshot.config.testLoginEnabled)
    setJiwoScanLoginEnabled(authStoreSnapshot.config.jiwoScanLoginEnabled)
    if (!phoneBindingRequired && (auth?.status === 'logged-out' || auth?.status === 'expired')) {
      const selectedMode = pendingLoginModeSelectionRef.current
      pendingLoginModeSelectionRef.current = undefined
      setLoginMode(selectedMode ?? (authStoreSnapshot.config.jiwoScanLoginEnabled
        ? 'jiwo'
        : authStoreSnapshot.config.testLoginEnabled ? 'test' : 'wechat'))
    }
  }, [auth?.status, authStoreSnapshot.config, phoneBindingRequired])
  useEffect(() => {
    if (authStoreSnapshot.error !== '' && authView === 'login') {
      setError(arkmeStoredLoginErrorMessage(authStoreSnapshot.error, t))
    }
  }, [authStoreSnapshot.error, authView, t])

  const refreshAuth = useCallback(async () => {
    setBusy(true); setError('')
    try {
      const snapshot = await arkmeAuthStore.refresh()
      const config = arkmeAuthStore.getSnapshot().config
      acceptAuthSnapshot(snapshot)
      if (config !== undefined) {
        setCaptchaId(config.captchaId)
        setTestLoginEnabled(config.testLoginEnabled)
        setJiwoScanLoginEnabled(config.jiwoScanLoginEnabled)
      }
      if (!['authenticated', 'binding-required'].includes(snapshot.status)) {
        setLoginMode(config?.jiwoScanLoginEnabled === true
          ? 'jiwo'
          : config?.testLoginEnabled === true ? 'test' : 'wechat')
      }
    } catch (caught) { setError(arkmeLoginErrorMessage(caught, t)) }
    finally { setBusy(false) }
  }, [acceptAuthSnapshot, t])

  const loadRelatedPage = useCallback(async (
    month: string,
    cursor: string | undefined,
    append: boolean,
    generation: number,
  ) => {
    if (source === undefined || source.kind !== 'private_chat') return
    const sourceRef = source.sourceRef
    const sourceKey = conversationKey
    if (append && relatedLoadingMoreRef.current) return
    const controller = new AbortController()
    relatedPageAbortRef.current?.abort()
    relatedPageAbortRef.current = controller
    if (append) {
      relatedLoadingMoreRef.current = true
      setRelatedLoadingMore(true)
    } else {
      setRelatedState('loading')
      setRelatedStateMessage('')
      setRelatedError('')
      setRelatedItems([])
      setRelatedHasMore(false)
      setRelatedNextCursor(undefined)
    }
    try {
      const page = await callArkme<ArkmeRelatedRecordingPage>('related-recordings.page', {
        sourceRef,
        limit: 10,
        ...(cursor === undefined ? {} : { cursor }),
        ...(month === '' ? {} : { monthKey: month }),
        timezoneOffsetMillis: -new Date().getTimezoneOffset() * 60_000,
        includeTimeIndex: !append && month === '',
      }, controller.signal)
      if (!isCurrentRelatedRecordingRequest(
        generation, relatedGenerationRef.current, sourceKey, activeRelatedSourceKeyRef.current,
      )) return
      setRelatedItems(current => append ? mergeRelatedRecordingItems(current, page.items) : page.items)
      setRelatedState(page.state)
      setRelatedStateMessage(page.stateMessage)
      setRelatedError(page.state === 'error' ? page.stateMessage || '相关录音暂时无法读取' : '')
      setRelatedHasMore(page.hasMore)
      setRelatedNextCursor(page.nextCursor)
      if (!append && month === '') setRelatedMonths(page.timeIndexComplete ? page.monthBuckets ?? [] : [])
    } catch (caught) {
      if (controller.signal.aborted || !isCurrentRelatedRecordingRequest(
        generation, relatedGenerationRef.current, sourceKey, activeRelatedSourceKeyRef.current,
      )) return
      setRelatedState('error')
      setRelatedError(errorMessage(caught))
    } finally {
      if (append) {
        relatedLoadingMoreRef.current = false
        if (generation === relatedGenerationRef.current) setRelatedLoadingMore(false)
      }
      if (relatedPageAbortRef.current === controller) relatedPageAbortRef.current = undefined
    }
  }, [conversationKey, source])

  const reloadRelated = useCallback((month: string) => {
    relatedGenerationRef.current += 1
    const generation = relatedGenerationRef.current
    setRelatedMonth(month)
    setRelatedDetail(undefined)
    void loadRelatedPage(month, undefined, false, generation)
  }, [loadRelatedPage])

  const selectRelatedMonth = useCallback((month: string) => {
    setRelatedMonth(month)
    setRelatedDetail(undefined)
  }, [])
  const loadRelatedRecordingDetail = useCallback(async (
    item: ArkmeRelatedRecordingItem,
    signal?: AbortSignal,
  ): Promise<ArkmeRelatedRecordingItem> => {
    const detailRef = item.sharedRecordingDetailRef?.trim() ?? ''
    if (detailRef === '') return item
    const loaded = arkmeRelatedRecordingItemFromSharedRecordingPreview(
      await callArkme<ArkmeSharedRecordingPreview>(
        'source.shared-recording-detail',
        { detailRef },
        signal,
      ),
      { itemUid: item.recordingRef, isMe: !item.isSharedByOther },
    )
    const next: ArkmeRelatedRecordingItem = {
      ...item,
      sharedRecordingDetailRef: detailRef,
      startAtMillis: loaded.startAtMillis > 0 ? loaded.startAtMillis : item.startAtMillis,
      endAtMillis: loaded.endAtMillis > 0 ? loaded.endAtMillis : item.endAtMillis,
      timeRangeText: loaded.timeRangeText.trim() === '' ? item.timeRangeText : loaded.timeRangeText,
      title: loaded.title.trim() === '' ? item.title : loaded.title,
      summary: loaded.summary.trim() === '' ? item.summary : loaded.summary,
      summaryStatus: loaded.summaryStatus,
      transcriptAvailable: loaded.transcriptAvailable,
      speakers: loaded.speakers.length > 0 ? loaded.speakers : item.speakers,
      participants: loaded.participants.length > 0 ? loaded.participants : item.participants,
      isSharedByOther: item.isSharedByOther,
    }
    if (loaded.transcript !== undefined) next.transcript = loaded.transcript
    return next
  }, [])

  function activateContextPanel(kind: 'note' | 'members' | 'records' | 'moment' | 'related' | 'common-groups') {
    setCommonGroupsOpen(kind === 'common-groups')
    if (kind !== 'note') setDrawer(undefined)
    setGroupMembersOpen(kind === 'members')
    if (kind !== 'records') setMemberRecords(undefined)
    if (kind !== 'records') setBotRecords(undefined)
    if (kind !== 'moment') {
      detailRequestRef.current?.abort()
      detailRequestRef.current = undefined
      detailRequestMomentRef.current = ''
      momentRelatedGenerationRef.current += 1
      momentRelatedRequestRef.current?.abort()
      momentRelatedRequestRef.current = undefined
      momentRelatedDetailRequestRef.current?.abort()
      momentRelatedDetailRequestRef.current = undefined
      setSelectedMoment(undefined)
      setDetailState(undefined)
      setMomentRelatedView('source-detail')
      setMomentRelatedState({ kind: 'idle' })
      setMomentRelatedDetailState({ kind: 'idle' })
    }
    if (kind !== 'related') closeRelatedPanel()
  }

  function openChatMessage(item: ArkmeTimelineItem) {
    if (source && !item.forwardRecords && (item.templateKind === 8 || item.displayKind === 1)) {
      void openLongArticleWindow({ ...source, sourceKey: conversationKey }, { mode: 'existing', item })
        .then(opened => { if (!opened) openNoteDetail(item) })
        .catch(error => setError(errorMessage(error)))
      return
    }
    openNoteDetail(item)
  }

  function openNoteDetail(item: ArkmeTimelineItem, videoUrl?: string) {
    activateContextPanel('note')
    setDetailBackStack([])
    pendingExtensionParentDetailRef.current = undefined
    setDetailItemUid(item.itemUid)
    setDetailCallVideoUrl(videoUrl)
    setShowOriginal(false)
    setDrawer('detail')
  }

  const openRelatedPanel = useCallback(() => {
    if (!privateActions.relatedAllowed) return
    setRelatedMenuOpen(false)
    setSelfMenuOpen(false)
    activateContextPanel('related')
    setRelatedPanelOpen(true)
    reloadRelated('')
  }, [privateActions.relatedAllowed, reloadRelated])

  const closeRelatedPanel = useCallback(() => {
    relatedGenerationRef.current += 1
    relatedPageAbortRef.current?.abort()
    relatedPageAbortRef.current = undefined
    relatedLoadingMoreRef.current = false
    setRelatedPanelOpen(false)
    setRelatedDetail(undefined)
    setRelatedLoadingMore(false)
  }, [])

  const loadMoreRelated = useCallback(() => {
    if (relatedLoadingMoreRef.current || !relatedHasMore || relatedNextCursor === undefined) return
    void loadRelatedPage('', relatedNextCursor, true, relatedGenerationRef.current)
  }, [loadRelatedPage, relatedHasMore, relatedNextCursor])

  useEffect(() => {
    relatedPageAbortRef.current?.abort()
    relatedGenerationRef.current += 1
    activeRelatedSourceKeyRef.current = conversationKey
    relatedLoadingMoreRef.current = false
    setRelatedMenuOpen(false)
    setSelfMenuOpen(false)
    setRelatedPanelOpen(false)
    setRelatedDetail(undefined)
    setRelatedItems([])
    setRelatedMonths([])
    setRelatedMonth('')
    setRelatedHasMore(false)
    setRelatedNextCursor(undefined)
    setRelatedLoadingMore(false)
    setRelatedError('')
  }, [authenticated, conversationKey, source?.kind, ui.authRevision])

  const toggleRelatedMenu = useCallback(() => {
    if (!relatedPanelOpen) {
      setSelfMenuOpen(false)
      setRelatedMenuOpen(current => !current)
    }
  }, [relatedPanelOpen])

  const acknowledgeRead = useCallback(async (nextItems: ArkmeTimelineItem[]) => {
    if (source === undefined || nextItems.length === 0
      || (source.kind !== 'private_chat' && source.kind !== 'group_chat')) return
    const visibleLatest = nextItems.reduce((latest, item) => Math.max(latest, item.sequence ?? 0), 0)
    const readSequence = Math.max(source.latestSequence ?? 0, visibleLatest)
    const readAckKey = `${source.sourceRef}:${String(readSequence)}`
    if (readSequence <= 0 || lastReadAckRef.current === readAckKey) return
    const hasReadIntent = source.unreadCount > 0
      || arkmeChatDirectory.hasOptimisticRead(source.sourceRef, source.sourceKey, source.latestSequence ?? readSequence)
    if (!hasReadIntent) return
    const generation = timelineGenerationRef.current
    if (!await arkmeAwaitVisibleReadIntent()) return
    if (!activeConversationRef.current || generation !== timelineGenerationRef.current) return
    if (lastReadAckRef.current === readAckKey) return
    lastReadAckRef.current = readAckKey
    try {
      const result = await callArkme<ArkmeSourceReadResult>('source.mark-read', {
        sourceRef: source.sourceRef,
        readSequence,
      })
      arkmeChatDirectory.updateReadAck(source.sourceRef, source.sourceKey, result.effectiveReadSequence, result.unreadCount)
    } catch {
      if (lastReadAckRef.current === readAckKey) lastReadAckRef.current = ''
      arkmeChatDirectory.rejectOptimisticRead(source.sourceRef, source.sourceKey, readSequence)
      void arkmeChatDirectory.refreshRoot({ force: true }).catch(() => undefined)
    }
  }, [source])

  const calendarNavigation = useSelfCalendarNavigation({
    scopeKey: `${authenticatedAccountKey}:${activeConversation ? conversationKey : ''}`,
    sourceRef: activeConversation ? source?.sourceRef : undefined,
    targetRevision: ui.conversationTarget?.revision,
    locate: item => {
      if (source === undefined) throw new Error('会话已切换，请重新选择日期')
      setCalendarMomentWindow(undefined)
      arkmeUi.showConversationTarget(source, item.recordUid, item.sendAtMillis, item.recordOwnerUserId)
      return arkmeUi.getSnapshot().conversationTarget!.revision
    },
    locateMoment: item => {
      if (source?.kind !== 'private_chat' || !interwovenMoments.some(moment => moment.momentId === item.momentId)) {
        throw new Error('该互动暂不可用，请刷新日历后重试')
      }
      setCalendarMomentWindow({ sourceKey: conversationKey, fromMillis: item.occurredAtMillis })
      const context = arkmeConversationTargetRow(bodyRef.current, { itemUid: '', momentId: item.momentId })
        ? undefined : item.contextAnchor
      arkmeUi.showConversationTarget(source, context?.recordUid ?? item.momentId,
        item.occurredAtMillis, context?.recordOwnerUserId, item.momentId)
      return arkmeUi.getSnapshot().conversationTarget!.revision
    },
    cancelTarget: revision => {
      if (arkmeUi.getSnapshot().conversationTarget?.revision !== revision) return
      conversationTargetAbortRef.current?.abort()
      conversationTargetAbortRef.current = undefined
      timelineRequestsRef.current.get('older')?.controller.abort()
      timelineRequestsRef.current.delete('older')
      pendingConversationTargetLocateRef.current = undefined
      setLoadingOlder(false)
      arkmeUi.consumeConversationTarget(revision)
    },
  })
  const { finish: finishCalendarNavigation, owns: ownsCalendarNavigation } = calendarNavigation

  const prepareTimelineReactions = useCallback(async (pageItems: ArkmeTimelineItem[], signal: AbortSignal) => {
    if (!authenticatedAccountKey || !source) return
    await reactionPreview.prepare(authenticatedAccountKey, pageItems.filter(item => !!item.messageActionRef).map(item => ({
      id: `${conversationKey}:${arkmeTimelineOccurrenceKey(item)}`, sourceRef: source.sourceRef, sourceKey: source.sourceKey, itemUid: item.itemUid,
      messageActionRef: item.messageActionRef!, source: source.displayName, text: item.textContent ?? '',
    })), signal)
  }, [authenticatedAccountKey, conversationKey, source])

  const loadTimeline = useCallback(async (
    cursor?: ArkmeTimelineCursor,
    preserve = false,
    limit = 40,
    intent: 'latest' | 'return-to-latest' | 'background' | 'pagination' | 'history' | 'refresh' = cursor === undefined ? 'latest' : 'pagination',
  ): Promise<void> => {
    if (source === undefined) return
    const sourceRef = source.sourceRef
    const sourceKey = arkmeSourceIdentityKey(source)
    const generation = timelineGenerationRef.current
    const invalidationRevision = arkmeChatTimelineDelta.getSnapshotForSource(sourceKey).invalidationRevision
    const refreshWindow = intent === 'refresh' || intent === 'background'
      ? conversationCacheRef.current.getTimeline(sourceKey) : undefined
    if (intent === 'refresh' && conversationTargetAbortRef.current !== undefined
      && !conversationTargetAbortRef.current.signal.aborted) return
    if (intent === 'background'
      && (conversationCacheRef.current.getTimeline(sourceKey)?.mode === 'around'
        || conversationTargetAbortRef.current !== undefined
          && !conversationTargetAbortRef.current.signal.aborted)) return
    if (refreshWindow !== undefined && timelineRequestsRef.current.get('initial')?.controller.signal.aborted === false) return
    const direction = refreshWindow === undefined
      ? cursor === undefined ? 'initial' : (cursor.afterSequence ?? 0) > 0 ? 'newer' : 'older'
      : 'refresh'
    const requestKey = refreshWindow === undefined
      ? `${sourceKey}:${cursor === undefined ? 'initial' : JSON.stringify(cursor)}`
      : `${sourceKey}:refresh:${invalidationRevision}`
    const activeRequest = timelineRequestsRef.current.get(direction)
    if (activeRequest !== undefined && !activeRequest.controller.signal.aborted
      && activeRequest.requestKey === requestKey && intent !== 'return-to-latest') return
    if (intent === 'latest' || intent === 'return-to-latest') {
      timelineWindowRevisionRef.current += 1
      olderLoadRef.current = { armed: true, error: '' }
      setLoadingOlder(false)
      for (const [activeDirection, request] of timelineRequestsRef.current) {
        if (activeDirection !== direction) request.controller.abort()
      }
      if (intent === 'return-to-latest') {
        conversationTargetAbortRef.current?.abort()
        conversationTargetAbortRef.current = undefined
        pendingConversationTargetLocateRef.current = undefined
        const target = arkmeUi.getSnapshot().conversationTarget
        if (target !== undefined) arkmeUi.consumeConversationTarget(target.revision)
      }
    }
    const windowRevision = timelineWindowRevisionRef.current
    const hadCachedTimeline = conversationCacheRef.current.getTimeline(sourceKey) !== undefined
    const controller = new AbortController()
    activeRequest?.controller.abort()
    timelineRequestsRef.current.set(direction, { controller, requestKey, returnToLatest: intent === 'return-to-latest' })
    const readStartDeltas = new Map<string, ArkmeTimelineItem | undefined>()
    let page: ArkmeTimelinePage
    try {
      const readPage = (pageCursor?: ArkmeTimelineCursor) => withArkmeReadDeadline(signal => retryArkmeRead(async () => {
        const start = new Map((sourceIsChat ? arkmeChatTimelineDelta.getSnapshotForSource(sourceKey).items : [])
          .map(item => [item.itemUid, item]))
        const result = await callArkme<ArkmeTimelinePage>('source.timeline', {
          sourceRef, limit: refreshWindow === undefined ? limit : 100,
          ...(pageCursor === undefined ? {} : { cursor: pageCursor }),
        }, signal)
        for (const item of result.items) readStartDeltas.set(item.itemUid, start.get(item.itemUid))
        return result
      }, { signal }), controller.signal)
      page = refreshWindow === undefined ? await readPage(cursor)
        : await readConversationTimelineWindow(refreshWindow, readPage, controller.signal)
      if (refreshWindow === undefined && intent !== 'return-to-latest') await prepareTimelineReactions(page.items, controller.signal)
    } catch (caught) {
      if (isArkmeRequestAbort(caught, controller.signal)) return
      throw caught
    } finally {
      if (timelineRequestsRef.current.get(direction)?.controller === controller) {
        timelineRequestsRef.current.delete(direction)
      }
    }
    if (controller.signal.aborted || generation !== timelineGenerationRef.current
      || windowRevision !== timelineWindowRevisionRef.current) return
    if (direction === 'older' && page.hasMore
      && (page.nextCursor === undefined || sameStateValue(page.nextCursor, cursor))) {
      throw new Error('暂时无法继续加载，请重试')
    }
    const cached = conversationCacheRef.current.getTimeline(sourceKey)
    const loadingNewerPage = (cursor?.afterSequence ?? 0) > 0
    const cachedItemUids = new Set((cached?.items ?? []).map(item => item.itemUid))
    const newerPageStartItemUid = loadingNewerPage
      ? mergeItems([], page.items.filter(item => !cachedItemUids.has(item.itemUid)))[0]?.itemUid
      : undefined
    const returningToLatest = cursor === undefined || loadingNewerPage && !page.hasMore
    const nextAroundSequenceRange = !returningToLatest && (cached?.mode ?? 'latest') === 'around'
      ? arkmeConversationTimelineSequenceRange(page.items, cached?.aroundSequenceRange)
      : undefined
    const nextAiPolishSettings = cursor === undefined ? page.aiPolishSettings : cached?.aiPolishSettings
    const pendingProjectionItems = cursor === undefined
      ? (cached?.items ?? []).filter(item => item.awaitingTimelineProjection === true
        && !page.items.some(projected => projected.itemUid === item.itemUid))
      : []
    const delta = sourceIsChat ? arkmeChatTimelineDelta.getSnapshotForSource(sourceKey) : undefined
    const removedKeys = new Set(delta?.removedItemKeys ?? [])
    const authoritativeItems = mergeItems(pendingProjectionItems, reconcileTimelinePage(cached?.items ?? [], delta, page.items, readStartDeltas))
    const refreshRange = refreshWindow?.mode === 'around' ? refreshWindow.aroundSequenceRange : undefined
    const refreshedIds = new Set(refreshWindow?.items.filter(item => item.status === 1
      && (refreshRange === undefined
        || (item.sequence ?? 0) >= refreshRange.minimumSequence && (item.sequence ?? 0) <= refreshRange.maximumSequence))
      .map(item => item.itemUid))
    const snapshot: ArkmeConversationTimelineSnapshot = refreshWindow !== undefined && cached !== undefined ? {
      ...cached,
      items: confirmedSendRetention.merge(sourceKey, mergeItems(cached.items.filter(item => !refreshedIds.has(item.itemUid)
        && !removedKeys.has(item.timelineItemKey ?? '')), authoritativeItems)),
      fetchedAtMillis: Date.now(),
      aiPolishNotices: page.aiPolishNotices ?? cached.aiPolishNotices,
      ...(page.aiPolishSettings === undefined ? {} : { aiPolishSettings: page.aiPolishSettings }),
      ...(sourceIsChat
        ? { latestSequence: Math.max(cached.latestSequence ?? 0, source.latestSequence ?? 0, ...page.items.map(item => item.sequence ?? 0)) }
        : { recordRevision: sourceProjectionRevision }),
    } : {
      mode: cursor === undefined
        ? 'latest'
        : loadingNewerPage && !page.hasMore ? 'latest' : cached?.mode ?? 'latest',
      ...(nextAroundSequenceRange === undefined ? {} : { aroundSequenceRange: nextAroundSequenceRange }),
      items: cursor === undefined
        ? confirmedSendRetention.merge(sourceKey, authoritativeItems)
        : mergeItems(cached?.items ?? [], authoritativeItems),
      aiPolishNotices: cursor === undefined ? page.aiPolishNotices ?? [] : cached?.aiPolishNotices ?? [],
      hasMore: loadingNewerPage ? cached?.hasMore ?? false : page.hasMore,
      newerHasMore: loadingNewerPage ? page.hasMore : cursor === undefined ? false : cached?.newerHasMore ?? false,
      fetchedAtMillis: Date.now(),
      ...(sourceIsChat
        ? { latestSequence: Math.max(source.latestSequence ?? 0, ...page.items.map(item => item.sequence ?? 0)) }
        : { recordRevision: sourceProjectionRevision }),
      ...(nextAiPolishSettings === undefined ? {} : { aiPolishSettings: nextAiPolishSettings }),
      ...(loadingNewerPage
        ? (cached?.nextCursor === undefined ? {} : { nextCursor: cached.nextCursor })
        : (page.nextCursor === undefined ? {} : { nextCursor: page.nextCursor })),
      ...(loadingNewerPage
        ? (page.nextCursor === undefined ? {} : { newerCursor: page.nextCursor })
        : cursor === undefined || cached?.newerCursor === undefined ? {} : { newerCursor: cached.newerCursor }),
    }
    const contentChanged = !arkmeConversationTimelineContentEqual(cached, snapshot)
    const releasedMoments = conversationCacheRef.current.storeTimeline(sourceKey, snapshot)
    const readIds = new Set(page.items.map(item => item.itemUid))
    conversationCacheRef.current.consumeTimelineDeltaItems(sourceKey, (delta?.items ?? []).filter(item => readIds.has(item.itemUid)))
    if (cursor === undefined) appliedTimelineInvalidationsRef.current.set(sourceKey,
      Math.max(appliedTimelineInvalidationsRef.current.get(sourceKey) ?? 0, invalidationRevision))
    const releasedMomentsChanged = releasedMoments !== undefined
      && JSON.stringify(releasedMoments) !== JSON.stringify(interwovenMoments)
    if (contentChanged || releasedMomentsChanged || intent === 'return-to-latest') {
      const body = bodyRef.current
      pendingViewportRestoreRef.current = {
        sourceKey,
        viewport: intent === 'return-to-latest' ? { scrollTop: 0, stickToBottom: true } : preserve || hadCachedTimeline
          ? body === null ? conversationCacheRef.current.getViewport(sourceKey) : arkmeConversationViewport(body, intent === 'history')
          : undefined,
        ...(newerPageStartItemUid === undefined
          ? {}
          : { newerPageStartAnchorId: `message:${newerPageStartItemUid}` }),
      }
      if (intent === 'return-to-latest') {
        setNewMessageCount(0)
        setHighlightedTargetUid('')
      }
      if (contentChanged || intent === 'return-to-latest') setTimelineView({
        sourceKey,
        mode: snapshot.mode ?? 'latest',
        aroundSequenceRange: snapshot.aroundSequenceRange,
        items: snapshot.items,
        aiPolishNotices: snapshot.aiPolishNotices,
        aiPolishSettings: snapshot.aiPolishSettings,
        nextCursor: snapshot.nextCursor,
        hasMore: snapshot.hasMore,
        newerCursor: snapshot.newerCursor,
        newerHasMore: snapshot.newerHasMore ?? false,
      })
      if (releasedMomentsChanged && releasedMoments !== undefined) setInterwovenMoments(releasedMoments)
    }
    if (cursor === undefined) {
      setTimelineLoadingKey(current => current === sourceKey ? '' : current)
      setTimelineSkeletonKey(current => current === sourceKey ? '' : current)
      if (!hadCachedTimeline && snapshot.items.length > 0) setTimelineRevealKey(sourceKey)
      // Read acknowledgement is independent of completing an explicit navigation.
      if (intent === 'return-to-latest') void acknowledgeRead(snapshot.items)
      else await acknowledgeRead(snapshot.items)
    }
    if (generation === timelineGenerationRef.current && windowRevision === timelineWindowRevisionRef.current
      && arkmeChatTimelineDelta.getSnapshotForSource(sourceKey).invalidationRevision > invalidationRevision) {
      const refresh = loadTimeline(undefined, true, 40, 'refresh')
      if (intent === 'return-to-latest') void refresh.catch(caught => {
        if (generation === timelineGenerationRef.current && windowRevision === timelineWindowRevisionRef.current) setError(errorMessage(caught))
      })
      else await refresh
    }
  }, [acknowledgeRead, confirmedSendRetention, interwovenMoments, prepareTimelineReactions, source, sourceIsChat, sourceProjectionRevision])

  useEffect(() => {
    if (!activeConversation || source === undefined || authenticatedUserId === undefined) return
    let changed = false
    let warningText = ''
    for (const task of fileTasks.tasks) {
      if (task.state !== 'sent' || notifiedFileTasks.current.has(task.taskRef)) continue
      notifiedFileTasks.current.add(task.taskRef); changed = true
      confirmedSendRetention.retain(arkmeSourceIdentityKey(source), fileTaskTimelineItem(task))
      if ((task.result?.warningText ?? '') !== '') warningText = task.result?.warningText ?? ''
      if (notifiedFileTasks.current.size > 200) {
        const oldest = notifiedFileTasks.current.values().next().value as string | undefined
        if (oldest !== undefined) notifiedFileTasks.current.delete(oldest)
      }
    }
    if (changed) {
      if (isArkmeSelfWorkspaceSource(source)) arkmeUi.recordChanged()
      else arkmeUi.chatChanged()
      if (warningText !== '') setError(warningText)
      void loadTimeline().catch(caught => setError(errorMessage(caught)))
    }
    // Recover the acceptance boundary after a page refresh without producing a second send.
    const current = arkmeComposerDraftStore.get(composerDraftKey)
    const refs = current.attachments.map(arkmeAttachmentId)
    if ((current.fileSendIdentity !== undefined || arkmeComposerDraftStore.isRestored(composerDraftKey)) && refs.length > 0 && fileTasks.tasks.some(task =>
      (current.fileSendIdentity === undefined || current.fileSendIdentity.recordUid === task.recordUid) &&
      task.content.textContent === serializeArkmeComposerDraft(current).text.trim() && JSON.stringify(task.fileRefs) === JSON.stringify(refs))) {
      arkmeComposerDraftStore.clear(composerDraftKey)
    }
  }, [activeConversation, confirmedSendRetention, fileTasks.tasks, source, authenticatedUserId, loadTimeline, composerDraftKey])

  useEffect(() => {
    const target = ui.conversationTarget
    if (!activeConversation || !authenticated || source === undefined || target === undefined
      || timelineStateKey !== conversationKey) return
    if (conversationTargetLocatedRevisionRef.current === target.revision) return
    if (conversationTargetPagingRef.current.revision !== target.revision) {
      // A newer locate wins even when its row is already loaded (no around request).
      const returning = timelineRequestsRef.current.get('initial')
      if (returning?.returnToLatest) {
        returning.controller.abort()
        timelineRequestsRef.current.delete('initial')
        timelineWindowRevisionRef.current += 1
      }
      conversationTargetAbortRef.current?.abort()
      conversationTargetAbortRef.current = undefined
      conversationTargetPagingRef.current = { revision: target.revision, pages: 0, aroundRequested: false }
      newerSentinelArmedRef.current = true
      if (pendingConversationTargetLocateRef.current?.revision !== target.revision) {
        pendingConversationTargetLocateRef.current = undefined
      }
      clearTimeout(calendarHighlightTimerRef.current)
      pendingMessageHighlightRef.current?.()
      setHighlightedTargetUid('')
    }
    const highlightUid = target.momentId ? `moment:${target.momentId}` : target.itemUid
    if (target.momentId && !interwovenMoments.some(moment => moment.momentId === target.momentId)) {
      finishCalendarNavigation(target.revision, '该群聊互动已不可用，请刷新后重试')
      arkmeUi.consumeConversationTarget(target.revision)
      return
    }
    if (items.some(item => item.itemUid === target.itemUid)
      || target.momentId && recordOwnerId(target.recordOwnerUserId) === 0) {
      if (pendingConversationTargetLocateRef.current?.revision === target.revision) return
      let animationFrame = 0
      let remainingRenderAttempts = 3
      let cancelReactionLayout: (() => void) | undefined
      const reactionLocateController = new AbortController()
      const locateRenderedTarget = () => {
        const body = bodyRef.current
        const row = arkmeConversationTargetRow(body, target)
        if (body === null || row === undefined) {
          if (remainingRenderAttempts > 0) {
            remainingRenderAttempts -= 1
            animationFrame = requestAnimationFrame(locateRenderedTarget)
            return
          }
          if (messageActionStatusTimerRef.current !== undefined) window.clearTimeout(messageActionStatusTimerRef.current)
          if (!finishCalendarNavigation(target.revision, '已加载对应快记，但暂时无法定位到它')) {
            setMessageActionStatus('已加载对应快记，但暂时无法定位到它')
          }
          messageActionStatusTimerRef.current = window.setTimeout(() => {
            setMessageActionStatus('')
            messageActionStatusTimerRef.current = undefined
          }, MESSAGE_ACTION_NOTICE_MS)
          arkmeUi.consumeConversationTarget(target.revision)
          return
        }
        const locate = () => {
          // Explicit message navigation supersedes a cached conversation's bottom/reading restore.
          // Match the around-message path even when the target was already cached.
          pendingViewportRestoreRef.current = undefined
          viewportRestoreIntentRef.current = false
          const calendarLocate = ownsCalendarNavigation(target.revision)
          body.scrollTo({ top: arkmeConversationTargetScrollTop(body, row, calendarLocate ? 'start' : 'center'),
            behavior: calendarLocate ? 'auto' : 'smooth' })
          conversationTargetLocatedRevisionRef.current = target.revision
          const calendarFinished = finishCalendarNavigation(target.revision)
          highlightLocatedMessage(body, row, highlightUid, target.transientHighlight === true, calendarFinished || target.transientHighlight === true)
          if (target.openDetail === true) openLocatedDetail(target.itemUid, target.revision)
          arkmeUi.consumeConversationTarget(target.revision)
        }
        if (target.transientHighlight) {
          const settle = () => { if (!reactionLocateController.signal.aborted) cancelReactionLayout = afterReactionLayout(row, locate) }
          const item = items.find(item => item.itemUid === target.itemUid)
          if (item && row.querySelector('[data-arkme-reaction-ready="false"]')) {
            void prepareTimelineReactions([item], reactionLocateController.signal).then(settle, settle)
          } else settle()
        }
        else locate()
      }
      locateRenderedTarget()
      return () => { reactionLocateController.abort(); cancelReactionLayout?.(); if (animationFrame !== 0) cancelAnimationFrame(animationFrame) }
    }
    if ((source.kind === 'private_chat' || source.kind === 'group_chat')
      && recordOwnerId(target.recordOwnerUserId) !== 0
      && !conversationTargetPagingRef.current.aroundRequested) {
      conversationTargetPagingRef.current.aroundRequested = true
      const generation = timelineGenerationRef.current
      for (const request of timelineRequestsRef.current.values()) request.controller.abort()
      timelineRequestsRef.current.clear()
      const windowRevision = ++timelineWindowRevisionRef.current
      const controller = new AbortController()
      conversationTargetAbortRef.current?.abort()
      conversationTargetAbortRef.current = controller
      let readStartDeltas = new Map<string, ArkmeTimelineItem>()
      void withArkmeReadDeadline(signal => retryArkmeRead(() => {
        readStartDeltas = new Map(arkmeChatTimelineDelta.getSnapshotForSource(conversationKey).items.map(item => [item.itemUid, item]))
        return callArkme<ArkmeTimelineAroundPage>('source.timeline-around', {
          sourceRef: source.sourceRef,
          itemUid: target.itemUid,
          recordOwnerUserId: target.recordOwnerUserId,
          beforeLimit: 20,
          afterLimit: 20,
        }, signal)
      }, { signal }), controller.signal).then(async page => {
        await prepareTimelineReactions(page.items, controller.signal)
        if (controller.signal.aborted || generation !== timelineGenerationRef.current
          || windowRevision !== timelineWindowRevisionRef.current
          || arkmeUi.getSnapshot().conversationTarget?.revision !== target.revision) return
        const cached = conversationCacheRef.current.getTimeline(conversationKey)
        const delta = sourceIsChat ? arkmeChatTimelineDelta.getSnapshotForSource(conversationKey) : undefined
        const items = reconcileTimelinePage(cached?.items ?? [],
          delta, page.items, readStartDeltas)
        if (!items.some(item => item.itemUid === target.itemUid)) throw new Error('未找到要定位的快记')
        timelineRequestsRef.current.get('initial')?.controller.abort()
        timelineRequestsRef.current.delete('initial')
        timelineWindowRevisionRef.current += 1
        olderLoadRef.current = { armed: true, error: '' }
        setLoadingOlder(false)
        const nextAroundSequenceRange = arkmeConversationTimelineSequenceRange(page.items)
        const snapshot: ArkmeConversationTimelineSnapshot = {
          mode: 'around',
          ...(nextAroundSequenceRange === undefined ? {} : { aroundSequenceRange: nextAroundSequenceRange }),
          items,
          aiPolishNotices: cached?.aiPolishNotices ?? [],
          hasMore: page.olderHasMore,
          newerHasMore: page.newerHasMore,
          fetchedAtMillis: Date.now(),
          ...(sourceIsChat
            ? { latestSequence: Math.max(page.latestKnownSequence ?? 0, ...page.items.map(item => item.sequence ?? 0)) }
            : { recordRevision: sourceProjectionRevision }),
          ...(cached?.aiPolishSettings === undefined ? {} : { aiPolishSettings: cached.aiPolishSettings }),
          ...(page.olderCursor === undefined ? {} : { nextCursor: page.olderCursor }),
          ...(page.newerCursor === undefined ? {} : { newerCursor: page.newerCursor }),
        }
        conversationCacheRef.current.storeTimeline(conversationKey, snapshot)
        const readIds = new Set(page.items.map(item => item.itemUid))
        conversationCacheRef.current.consumeTimelineDeltaItems(conversationKey, (delta?.items ?? []).filter(item => readIds.has(item.itemUid)))
        conversationTargetAbortRef.current = undefined
        pendingViewportRestoreRef.current = undefined
        setMemberEventWindowRevision(value => value+1)
        pendingConversationTargetLocateRef.current = {
          sourceKey: conversationKey,
          itemUid: target.itemUid,
          revision: target.revision,
          transientHighlight: target.transientHighlight === true,
          ...(target.momentId ? { momentId: target.momentId } : {}),
        }
        setTimelineView({
          sourceKey: conversationKey,
          mode: 'around',
          aroundSequenceRange: snapshot.aroundSequenceRange,
          items: snapshot.items,
          aiPolishNotices: snapshot.aiPolishNotices,
          aiPolishSettings: snapshot.aiPolishSettings,
          nextCursor: snapshot.nextCursor,
          hasMore: snapshot.hasMore,
          newerCursor: snapshot.newerCursor,
          newerHasMore: snapshot.newerHasMore ?? false,
        })
        if (arkmeChatTimelineDelta.getSnapshotForSource(conversationKey).invalidationRevision
          > (appliedTimelineInvalidationsRef.current.get(conversationKey) ?? 0)) {
          void loadTimeline(undefined, true, 40, 'refresh').catch(caught => { setError(errorMessage(caught)) })
        }
      }).catch(caught => {
        if (isArkmeRequestAbort(caught, controller.signal)) return
        if (generation !== timelineGenerationRef.current || windowRevision !== timelineWindowRevisionRef.current) return
        conversationTargetAbortRef.current = undefined
        if (pendingConversationTargetLocateRef.current?.revision === target.revision) {
          pendingConversationTargetLocateRef.current = undefined
        }
        const message = errorMessage(caught) || '暂时无法定位这条快记'
        if (!finishCalendarNavigation(target.revision, message)) {
          if (messageActionStatusTimerRef.current !== undefined) window.clearTimeout(messageActionStatusTimerRef.current)
          setMessageActionStatus(message)
          messageActionStatusTimerRef.current = window.setTimeout(() => {
            setMessageActionStatus('')
            messageActionStatusTimerRef.current = undefined
          }, MESSAGE_ACTION_NOTICE_MS)
        }
        arkmeUi.consumeConversationTarget(target.revision)
        if (arkmeChatTimelineDelta.getSnapshotForSource(conversationKey).invalidationRevision
          > (appliedTimelineInvalidationsRef.current.get(conversationKey) ?? 0)) {
          void loadTimeline(undefined, true, 40, 'refresh').catch(error => { setError(errorMessage(error)) })
        }
      }).finally(() => {
        if (conversationTargetAbortRef.current === controller) conversationTargetAbortRef.current = undefined
      })
      return
    }
    if (sourceIsChat && recordOwnerId(target.recordOwnerUserId) !== 0) return
    if (conversationCacheRef.current.getTimeline(conversationKey) === undefined) return
    if (loadingOlder) return
    if (!hasMore || nextCursor === undefined || conversationTargetPagingRef.current.pages >= 80) {
      const message = '暂未能在当前历史中定位该条消息'
      if (!finishCalendarNavigation(target.revision, message)) setError(message)
      arkmeUi.consumeConversationTarget(target.revision)
      return
    }
    conversationTargetPagingRef.current.pages += 1
    setLoadingOlder(true)
    void loadTimeline(nextCursor, true, 100)
      .catch(caught => {
        if (arkmeUi.getSnapshot().conversationTarget?.revision !== target.revision) return
        if (!finishCalendarNavigation(target.revision, errorMessage(caught))) setError(errorMessage(caught))
        arkmeUi.consumeConversationTarget(target.revision)
      })
      .finally(() => {
        if (conversationTargetPagingRef.current.revision === target.revision) setLoadingOlder(false)
      })
  }, [activeConversation, authenticated, conversationKey, hasMore, items, interwovenMoments, loadTimeline, prepareTimelineReactions, loadingOlder, nextCursor, source, sourceIsChat, sourceProjectionRevision, timelineStateKey, ui.conversationTarget, finishCalendarNavigation, ownsCalendarNavigation, openLocatedDetail])

  useEffect(() => {
    const pending = pendingExtensionParentDetailRef.current
    if (pending !== undefined && ui.conversationTarget?.revision !== pending.revision) {
      pendingExtensionParentDetailRef.current = undefined
    }
  }, [ui.conversationTarget?.revision])

  useEffect(() => {
    if (!authStoreSnapshot.checked) void refreshAuth()
  }, [authStoreSnapshot.checked, refreshAuth, ui.authRevision])
  useLayoutEffect(() => {
    timelineGenerationRef.current += 1
    timelineWindowRevisionRef.current += 1
    newerSentinelArmedRef.current = true
    olderLoadRef.current = { armed: true, error: '' }
    setLoadingOlder(false)
    for (const request of timelineRequestsRef.current.values()) request.controller.abort()
    timelineRequestsRef.current.clear()
    conversationTargetAbortRef.current?.abort()
    conversationTargetAbortRef.current = undefined
    pendingConversationTargetLocateRef.current = undefined
    setHighlightedTargetUid('')
    const accountChanged = cacheAccountKeyRef.current !== authenticatedAccountKey
    if (accountChanged) {
      conversationCacheRef.current.clear()
      appliedTimelineInvalidationsRef.current.clear()
      cacheAccountKeyRef.current = authenticatedAccountKey
    }
    const sourceKey = authenticated && conversationKey !== '' ? conversationKey : undefined
    const cachedTimeline = sourceKey === undefined
      ? undefined
      : conversationCacheRef.current.getTimeline(sourceKey)
    if (sourceKey !== undefined && cachedTimeline !== undefined) {
      pendingViewportRestoreRef.current = {
        sourceKey,
        viewport: conversationCacheRef.current.getViewport(sourceKey),
      }
    } else {
      pendingViewportRestoreRef.current = undefined
    }
    setTimelineView({
      sourceKey: sourceKey ?? '',
      mode: cachedTimeline?.mode ?? 'latest',
      aroundSequenceRange: cachedTimeline?.aroundSequenceRange,
      items: cachedTimeline?.items ?? [],
      aiPolishNotices: cachedTimeline?.aiPolishNotices ?? [],
      aiPolishSettings: cachedTimeline?.aiPolishSettings,
      nextCursor: cachedTimeline?.nextCursor,
      hasMore: cachedTimeline?.hasMore ?? false,
      newerCursor: cachedTimeline?.newerCursor,
      newerHasMore: cachedTimeline?.newerHasMore ?? false,
    })
    setTimelineLoadingKey(sourceKey !== undefined && cachedTimeline === undefined ? sourceKey : '')
    setTimelineSkeletonKey('')
    setTimelineRevealKey('')
    setNewMessageCount(0)
    setDrawer(undefined); setGroupMembersOpen(false); setDetailItemUid(''); setDetailBackStack([]); setShowOriginal(false)
    const pendingParent = pendingExtensionParentDetailRef.current
    if (arkmeUi.getSnapshot().conversationTarget?.revision !== pendingParent?.revision) {
      pendingExtensionParentDetailRef.current = undefined
    }
    setDraftPreview(undefined)
    if (authenticated) setError('')
    setLongArticleCreating(false); setAddMenuOpen(false)
    setArticlePickerScope(undefined)
    setMentionTrigger(undefined)
    setRelatedMenuOpen(false)
    setRelatedPanelOpen(false)
    setRelatedDetail(undefined)
    interwovenRequestRef.current?.abort()
    interwovenGenerationRef.current += 1
    detailRequestRef.current?.abort()
    detailRequestMomentRef.current = ''
    momentRelatedGenerationRef.current += 1
    momentRelatedRequestRef.current?.abort()
    momentRelatedRequestRef.current = undefined
    momentRelatedDetailRequestRef.current?.abort()
    momentRelatedDetailRequestRef.current = undefined
    relatedGenerationRef.current += 1
    relatedPageAbortRef.current?.abort()
    relatedPageAbortRef.current = undefined
    snapshotRequestRef.current?.abort()
    snapshotRequestRef.current = undefined
    copyLinkDetailRequestRef.current?.abort()
    copyLinkDetailRequestRef.current = undefined
    copyLinkDetailScopeRef.current = { sending: false }
    forwardSubmissionScopeRef.current = { sending: false }
    forwardPickerScopeRef.current?.abort()
    forwardPickerScopeRef.current = undefined
    if (copyLinkRefreshTimerRef.current !== undefined) {
      window.clearTimeout(copyLinkRefreshTimerRef.current)
      copyLinkRefreshTimerRef.current = undefined
    }
    if (messageActionStatusTimerRef.current !== undefined) {
      window.clearTimeout(messageActionStatusTimerRef.current)
      messageActionStatusTimerRef.current = undefined
    }
    if (forwardSuccessTimerRef.current !== undefined) {
      window.clearTimeout(forwardSuccessTimerRef.current)
      forwardSuccessTimerRef.current = undefined
    }
    setInterwovenMoments(sourceKey === undefined
      ? []
      : conversationCacheRef.current.getInterwovenMoments(sourceKey) ?? [])
    setInterwovenCalendarState(sourceKey === undefined ? 'loading' : conversationCacheRef.current.getInterwovenState(sourceKey) ?? 'loading')
    const restoredMomentRow = sourceKey === undefined ? undefined : conversationCacheRef.current.getViewport(sourceKey)?.anchorId
    const restoredMoment = sourceKey === undefined ? undefined : conversationCacheRef.current.getInterwovenMoments(sourceKey)
      ?.find(moment => `moment:${moment.momentId}` === restoredMomentRow)
    setCalendarMomentWindow(sourceKey !== undefined && restoredMoment !== undefined
      ? { sourceKey, fromMillis: restoredMoment.occurredAtMillis } : undefined)
    setSelectedMoment(undefined)
    setDetailState(undefined)
    setMomentRelatedView('source-detail')
    setMomentRelatedState({ kind: 'idle' })
    setMomentRelatedDetailState({ kind: 'idle' })
    setCopyLinkDetail(undefined)
    setCopyLinkDetailDraft('')
    setCopyLinkDetailSending(false)
    setCopyLinkDetailSendError('')
    setMemberMenu(undefined)
    setMessageMenu(undefined)
    setSnapshot(undefined)
    setMessageReportItem(undefined)
    setMemberProfile(undefined)
    setMemberRecords(undefined)
    setPrivateChatBusy(false)
    setSelectMode(undefined)
    setForwardTargetPicker(undefined)
    setForwardSuccessFeedback(undefined)
    setMessageActionStatus('')
    setMessageActionBusy(undefined)
  }, [authenticated, authenticatedAccountKey, conversationKey, notificationActivationRevision, persistRecordReeditDraft])
  useEffect(() => {
    if (!activeConversation || !authenticated || source === undefined) return
    const generation = timelineGenerationRef.current
    const cachedTimeline = conversationCacheRef.current.getTimeline(conversationKey)
    const hasCachedTimeline = cachedTimeline !== undefined
    if (cachedTimeline?.mode === 'around') {
      // An explicit unread directory click requests current messages; a reaction locate keeps history.
      if (sourceIsChat && arkmeUi.getSnapshot().conversationTarget === undefined
        && arkmeChatDirectory.hasOptimisticRead(source.sourceRef, source.sourceKey, source.latestSequence ?? 0)) {
        if (timelineRequestsRef.current.get('initial')?.returnToLatest) return
        void loadTimeline(undefined, false, 40, 'return-to-latest').catch(caught => {
          arkmeChatDirectory.rejectOptimisticRead(source.sourceRef, source.sourceKey, source.latestSequence ?? 0)
          if (generation === timelineGenerationRef.current) setError(errorMessage(caught))
        })
        return
      }
      setTimelineLoadingKey(current => current === conversationKey ? '' : current)
      setTimelineSkeletonKey(current => current === conversationKey ? '' : current)
      return
    }
    const authoritativeLatestSequence = Math.max(source.latestSequence ?? 0, chatDelta.latestSequence ?? 0)
    const realtimeCoversLatest = sourceIsChat && arkmeRealtimeDeltaCoversTimelineGap(
      cachedTimeline,
      chatDelta.items,
      authoritativeLatestSequence,
    )
    const shouldRefresh = sourceIsChat
      ? !realtimeCoversLatest && arkmeShouldRefreshChatTimeline(cachedTimeline, Date.now(), authoritativeLatestSequence)
      : arkmeShouldRefreshRecordTimeline(cachedTimeline, Date.now(), sourceProjectionRevision)
    if (!shouldRefresh) {
      setTimelineLoadingKey(current => current === conversationKey ? '' : current)
      setTimelineSkeletonKey(current => current === conversationKey ? '' : current)
      void acknowledgeRead(cachedTimeline?.items ?? [])
      return
    }
    void loadTimeline(undefined, false, 40, 'background').catch(caught => {
      arkmeChatDirectory.rejectOptimisticRead(source.sourceRef, source.sourceKey, source.latestSequence ?? 0)
      if (generation === timelineGenerationRef.current) {
        setTimelineLoadingKey(current => current === conversationKey ? '' : current)
        setTimelineSkeletonKey(current => current === conversationKey ? '' : current)
        if (!hasCachedTimeline) setError(errorMessage(caught))
      }
    })
  }, [acknowledgeRead, activeConversation, authenticated, chatDelta.items, conversationKey, foregroundReadRevision, loadTimeline, notificationActivationRevision, source, sourceIsChat, sourceProjectionRevision])
  useEffect(() => {
    if (!authenticated || source === undefined || timelineStateKey !== conversationKey) return
    if (conversationCacheRef.current.getTimeline(conversationKey) === undefined) return
    conversationCacheRef.current.storeTimeline(conversationKey, {
      mode: timelineMode,
      ...(aroundSequenceRange === undefined ? {} : { aroundSequenceRange }),
      items,
      aiPolishNotices,
      hasMore,
      ...(aiPolishSettings === undefined ? {} : { aiPolishSettings }),
      ...(nextCursor === undefined ? {} : { nextCursor }),
      ...(newerCursor === undefined ? {} : { newerCursor }),
      newerHasMore,
    })
  }, [aiPolishNotices, aiPolishSettings, aroundSequenceRange, authenticated, conversationKey, hasMore, items, newerCursor, newerHasMore, nextCursor, source, timelineMode, timelineStateKey])
  useEffect(() => {
    if (!activeConversation || !authenticated || source?.kind !== 'private_chat') return
    const sourceRef = source.sourceRef
    const sourceKey = conversationKey
    if (conversationCacheRef.current.isInterwovenFresh(sourceKey, interwovenRefreshRevision)) return
    const generation = ++interwovenGenerationRef.current
    setInterwovenCalendarState('loading')
    const controller = new AbortController()
    interwovenRequestRef.current?.abort()
    interwovenRequestRef.current = controller
    let active = true
    void callArkme<ArkmeInterwovenBootstrap>('source.interwoven-moments', {
      sourceRef,
    }, controller.signal).then(result => {
      if (!active || generation !== interwovenGenerationRef.current) return
      const moments = result.state === 'disabled' || result.state === 'empty' ? [] : result.moments
      conversationCacheRef.current.storeInterwovenState(sourceKey, result.state)
      setInterwovenCalendarState(result.state)
      const previous = conversationCacheRef.current.getInterwovenMoments(sourceKey)
      const ready = conversationCacheRef.current.storeInterwovenMoments(sourceKey, moments, interwovenRefreshRevision)
      if (!ready || JSON.stringify(previous) === JSON.stringify(moments)) return
      const body = bodyRef.current
      pendingViewportRestoreRef.current = {
        sourceKey,
        viewport: body === null ? conversationCacheRef.current.getViewport(sourceKey) : arkmeConversationViewport(body),
      }
      setInterwovenMoments(moments)
    }).catch(() => {
      if (active && generation === interwovenGenerationRef.current) setInterwovenCalendarState('error')
    }).finally(() => {
      if (!active || generation !== interwovenGenerationRef.current) return
      if (interwovenRequestRef.current === controller) interwovenRequestRef.current = undefined
    })
    return () => {
      active = false
      controller.abort()
    }
  }, [activeConversation, authenticated, conversationKey, interwovenRefreshRevision, source?.kind])
  const appliedInterwovenInvalidationsRef = useRef(new Map<string, number>())
  useEffect(() => {
    const observationKey = `${authenticatedAccountKey ?? 'logged-out'}:${conversationKey}`
    const applied = appliedInterwovenInvalidationsRef.current.get(observationKey) ?? 0
    if (interwovenInvalidation.revision <= applied) return
    if (!activeConversation || !authenticated || typeof window === 'undefined') return
    const revision = interwovenInvalidation.revision
    const timer = setTimeout(() => {
      appliedInterwovenInvalidationsRef.current.delete(observationKey)
      appliedInterwovenInvalidationsRef.current.set(observationKey, revision)
      while (appliedInterwovenInvalidationsRef.current.size > 64) {
        const oldest = appliedInterwovenInvalidationsRef.current.keys().next().value as string | undefined
        if (oldest === undefined) break
        appliedInterwovenInvalidationsRef.current.delete(oldest)
      }
      setInterwovenRefreshRevision(value => value + 1)
    }, 250)
    return () => { clearTimeout(timer) }
  }, [activeConversation, authenticated, authenticatedAccountKey, conversationKey, interwovenInvalidation.revision])
  useEffect(() => {
    if (!activeConversation || !authenticated || typeof window === 'undefined') return
    const refreshOnFocus = () => { setInterwovenRefreshRevision(value => value + 1) }
    window.addEventListener('focus', refreshOnFocus)
    return () => { window.removeEventListener('focus', refreshOnFocus) }
  }, [activeConversation, authenticated])
  useEffect(() => {
    if (!activeConversation || !authenticated || source === undefined || timelineStateKey !== conversationKey) return
    const deltaItems = conversationCacheRef.current.unappliedTimelineDeltaItems(conversationKey, chatDelta.items)
    const removedItemKeys = new Set(chatDelta.removedItemKeys)
    const retainedItems = removedItemKeys.size === 0
      ? items
      : items.filter(item => !removedItemKeys.has(item.timelineItemKey ?? ''))
    const applicableDeltaItems = arkmeConversationTimelineDeltaItems(
      timelineMode, aroundSequenceRange, retainedItems, deltaItems,
    )
    if (applicableDeltaItems.length === 0 && retainedItems.length === items.length) return
    const nextItems = mergeItems(retainedItems, applicableDeltaItems)
    conversationCacheRef.current.consumeTimelineDeltaItems(conversationKey, applicableDeltaItems)
    if (JSON.stringify(nextItems) === JSON.stringify(items)) return
    const body = bodyRef.current
    const viewport = body === null ? undefined : arkmeConversationViewport(body)
    pendingViewportRestoreRef.current = { sourceKey: conversationKey, viewport }
    const existingIds = new Set(retainedItems.map(item => item.itemUid))
    const incomingCount = applicableDeltaItems.filter(item => !existingIds.has(item.itemUid)).length
    setTimelineView(current => {
      if (current.sourceKey !== conversationKey) return current
      const retained = removedItemKeys.size === 0
        ? current.items
        : current.items.filter(item => !removedItemKeys.has(item.timelineItemKey ?? ''))
      const mergedItems = mergeItems(retained, applicableDeltaItems)
      return sameStateValue(current.items, mergedItems) ? current : { ...current, items: mergedItems }
    })
    if (viewport?.stickToBottom === false && incomingCount > 0) {
      setNewMessageCount(current => current + incomingCount)
    } else {
      setNewMessageCount(0)
    }
    void acknowledgeRead(applicableDeltaItems)
  }, [acknowledgeRead, activeConversation, aroundSequenceRange, authenticated, chatDelta, conversationKey, items, source, timelineMode, timelineStateKey])

  useEffect(() => {
    if (!activeConversation || !authenticated || source === undefined || chatDelta.invalidationRevision <= 0) return
    const applied = appliedTimelineInvalidationsRef.current.get(conversationKey) ?? 0
    if (chatDelta.invalidationRevision <= applied) return
    void loadTimeline(undefined, true, 40, 'refresh').catch(caught => { setError(errorMessage(caught)) })
  }, [activeConversation, authenticated, chatDelta.invalidationRevision, conversationKey, loadTimeline, source])

  useEffect(() => {
    if (!activeConversation || !authenticated || source?.kind !== 'group_chat') return
    let cancelled = false
    let pending = false
    let timer: ReturnType<typeof setTimeout> | undefined
    let controller: AbortController | undefined
    const browserDocument = typeof document === 'undefined' ? undefined : document
    const foreground = () => browserDocument?.visibilityState !== 'hidden'
    const clearTimer = () => {
      if (timer !== undefined) clearTimeout(timer)
      timer = undefined
    }
    const refreshPresentation = async () => {
      if (cancelled || pending || !foreground()) return
      pending = true
      const request = new AbortController()
      controller = request
      try {
        const [settingsResult, noticesResult] = await Promise.allSettled([
          callArkme<ArkmeGroupAiPolishSnapshot>('source.ai-polish.settings', {
            sourceRef: source.sourceRef,
          }, request.signal),
          callArkme<ArkmeGroupAiPolishNotice[]>('source.ai-polish.notices', {
            sourceRef: source.sourceRef,
          }, request.signal),
        ])
        if (!cancelled && !request.signal.aborted) {
          if (settingsResult.status === 'fulfilled'
            && !sameStateValue(aiPolishSettingsRef.current, settingsResult.value)) {
            const snapshot = settingsResult.value
            aiPolishSettingsRef.current = snapshot
            setAiPolishSettings(snapshot)
          }
          if (noticesResult.status === 'fulfilled'
            && !sameStateValue(aiPolishNoticesRef.current, noticesResult.value)) {
            const notices = noticesResult.value
            aiPolishNoticesRef.current = notices
            setAiPolishNotices(notices)
          }
        }
      } catch {
        // Keep the last owner snapshot on a transient presentation read failure.
      } finally {
        if (controller === request) controller = undefined
        pending = false
        if (!cancelled && foreground()) timer = setTimeout(() => { timer = undefined; void refreshPresentation() }, 3_000)
      }
    }
    const onVisibilityChange = () => {
      if (foreground()) void refreshPresentation()
      else { clearTimer(); controller?.abort() }
    }
    void refreshPresentation()
    browserDocument?.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      cancelled = true
      clearTimer()
      controller?.abort()
      browserDocument?.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [activeConversation, authenticated, conversationKey, source?.kind, source?.sourceRef])

  const loadOlderHistory = useCallback(async () => {
    const state = olderLoadRef.current
    if (!activeConversation || !authenticated || !hasMore || nextCursor === undefined
      || ui.conversationTarget !== undefined || loadingOlder || timelineRequestsRef.current.has('older')) return
    const generation = timelineGenerationRef.current
    const windowRevision = timelineWindowRevisionRef.current
    state.armed = false
    state.error = ''
    setLoadingOlder(true)
    const current = () => state === olderLoadRef.current && generation === timelineGenerationRef.current
      && windowRevision === timelineWindowRevisionRef.current
    try {
      await loadTimeline(nextCursor, true, 40, 'history')
    } catch (caught) {
      if (current()) {
        state.error = errorMessage(caught)
      }
    } finally {
      if (state === olderLoadRef.current && generation === timelineGenerationRef.current) setLoadingOlder(false)
    }
  }, [activeConversation, authenticated, hasMore, nextCursor, loadingOlder, loadTimeline, ui.conversationTarget])

  useEffect(() => {
    const root = bodyRef.current; const sentinel = sentinelRef.current
    if (!activeConversation || !authenticated || ui.conversationTarget !== undefined
      || root === null || sentinel === null || !hasMore || nextCursor === undefined) return
    let active = true
    const observer = new IntersectionObserver(entries => {
      const state = olderLoadRef.current
      if (!active || loadingOlder || state.error !== '') return
      if (entries[0]?.isIntersecting !== true) {
        state.armed = true
        return
      }
      if (state.armed) void loadOlderHistory()
    }, { root, rootMargin: '120px 0px 0px' })
    observer.observe(sentinel); return () => { active = false; observer.disconnect() }
  }, [activeConversation, authenticated, hasMore, loadOlderHistory, loadingOlder, nextCursor, ui.conversationTarget])

  useEffect(() => {
    const root = bodyRef.current; const sentinel = newerSentinelRef.current
    if (!authenticated || root === null || sentinel === null || !newerHasMore || newerCursor === undefined) return
    const observer = new IntersectionObserver(entries => {
      if (entries[0]?.isIntersecting !== true) {
        newerSentinelArmedRef.current = true
        return
      }
      if (!newerSentinelArmedRef.current || loadingNewer) return
      newerSentinelArmedRef.current = false
      setLoadingNewer(true)
      void loadTimeline(newerCursor, true).catch(caught => { setError(errorMessage(caught)) }).finally(() => { setLoadingNewer(false) })
    }, { root, rootMargin: '0px 0px 120px' })
    observer.observe(sentinel); return () => { observer.disconnect() }
  }, [authenticated, loadTimeline, loadingNewer, newerCursor, newerHasMore])

  useEffect(() => {
    if (smsCountdown <= 0) return
    const timer = setTimeout(() => { setSmsCountdown(value => Math.max(0, value - 1)) }, 1000)
    return () => { clearTimeout(timer) }
  }, [smsCountdown])

  useEffect(() => {
    const ownsCurrentQrLogin = ownsQrLogin && ['jiwo', 'wechat'].includes(loginMode)
    if (!ownsCurrentQrLogin || !agreed || auth?.status !== 'pending' || auth.attemptId === undefined) return
    let stopped = false; let timer: ReturnType<typeof setTimeout>
    const operation = loginMode === 'jiwo' ? 'auth.app.poll' : 'auth.poll'
    const poll = async () => {
      try {
        const snapshot = await callArkme<ArkmeAuthSnapshot>(operation, { attemptId: auth.attemptId })
        if (stopped) return
        acceptAuthSnapshot(snapshot)
        if (snapshot.status === 'authenticated' || snapshot.status === 'expired') {
          if (loginMode === 'jiwo') currentJiwoAttemptRef.current = undefined
          setQr('')
          return
        }
      } catch (caught) { if (!stopped) setError(arkmeLoginErrorMessage(caught, t)) }
      if (!stopped) timer = setTimeout(() => { void poll() }, 1200)
    }
    timer = setTimeout(() => { void poll() }, 1200)
    return () => { stopped = true; clearTimeout(timer) }
  }, [agreed, auth?.attemptId, auth?.status, loginMode, ownsQrLogin, t])

  const beginWechat = async () => {
    const flowRevision = ++qrFlowRevisionRef.current
    if (!agreed) { setError(t('error.agreement.required')); return }
    setBusy(true); setError('')
    try {
      const snapshot = await callArkme<ArkmeAuthSnapshot>('auth.begin')
      if (flowRevision !== qrFlowRevisionRef.current) return
      acceptAuthSnapshot(snapshot)
      setQr(snapshot.qrContent === undefined ? '' : qrDataUrl(snapshot.qrContent))
    } catch (caught) {
      if (flowRevision === qrFlowRevisionRef.current) setError(arkmeLoginErrorMessage(caught, t))
    } finally {
      if (flowRevision === qrFlowRevisionRef.current) setBusy(false)
    }
  }

  const beginJiwo = async () => {
    const flowRevision = ++qrFlowRevisionRef.current
    if (!agreed) { setError(t('error.agreement.required')); return }
    setBusy(true); setError('')
    try {
      const snapshot = await callArkme<ArkmeAuthSnapshot>('auth.app.begin')
      if (flowRevision !== qrFlowRevisionRef.current) {
        if (snapshot.attemptId !== undefined) {
          void callArkme('auth.app.cancel', { attemptId: snapshot.attemptId }).catch(() => undefined)
        }
        return
      }
      currentJiwoAttemptRef.current = snapshot.attemptId
      acceptAuthSnapshot(snapshot)
      setQr(snapshot.qrContent === undefined ? '' : qrDataUrl(snapshot.qrContent))
    } catch (caught) {
      if (flowRevision === qrFlowRevisionRef.current) setError(arkmeLoginErrorMessage(caught, t))
    } finally {
      if (flowRevision === qrFlowRevisionRef.current) setBusy(false)
    }
  }

  const sendCode = async () => {
    if (!/^1[3-9]\d{9}$/.test(phone)) { setError(t('error.phone.eleven')); return }
    setBusy(true); setError('')
    try { const captcha = await verifyPhoneCaptcha(captchaId, phone); await callArkme('auth.phone.send', { phone, captcha }); setSmsCountdown(60) }
    catch (caught) { setError(arkmeLoginErrorMessage(caught, t)) } finally { setBusy(false) }
  }

  const verifyCode = async () => {
    if (!/^1[3-9]\d{9}$/.test(phone)) { setError(t('error.phone.invalid')); return }
    if (!/^\d{6}$/.test(smsCode)) { setError(t('error.code.invalid')); return }
    if (!agreed) { setError(t('error.agreement.required')); return }
    setBusy(true); setSubmitBusy(true); setError('')
    try {
      const snapshot = await callArkme<ArkmeAuthSnapshot>('auth.phone.verify', { phone, code: smsCode })
      acceptAuthSnapshot(snapshot)
    } catch (caught) { setError(arkmeLoginErrorMessage(caught, t)) } finally { setSubmitBusy(false); setBusy(false) }
  }

  const testLogin = async () => {
    const userId = Number(testUserId)
    if (!Number.isSafeInteger(userId) || userId <= 0) { setError(t('error.test.invalid')); return }
    if (!agreed) { setError(t('error.agreement.required')); return }
    setBusy(true); setError('')
    try {
      const snapshot = await callArkme<ArkmeAuthSnapshot>('auth.test.login', { userId })
      acceptAuthSnapshot(snapshot)
    } catch (caught) { setError(arkmeLoginErrorMessage(caught, t)) } finally { setBusy(false) }
  }

  const cancelBinding = async () => {
    setBusy(true); setError('')
    try {
      const snapshot = await callArkme<ArkmeAuthSnapshot>('auth.logout')
      ignoreStaleBindingAuthRef.current = true
      arkmeAuthStore.setAuth(snapshot)
      setPhone('')
      setSmsCode('')
      setQr('')
      setSubmitBusy(false)
      qrRequestStartedRef.current = false
      setLoginMode(jiwoScanLoginEnabled ? 'jiwo' : testLoginEnabled ? 'test' : 'wechat')
      arkmeUi.authChanged(false)
    } catch (caught) { setError(arkmeLoginErrorMessage(caught, t)) } finally { setBusy(false) }
  }

  useEffect(() => {
    qrRequestStartedRef.current = arkmeWechatRequestStartedAfterAuthStatus(
      qrRequestStartedRef.current,
      auth?.status,
    )
  }, [auth?.status])

  useEffect(() => {
    const shouldBegin = loginMode === 'jiwo'
      ? ownsQrLogin && jiwoScanLoginEnabled && authView === 'login' && auth !== undefined
        && ['logged-out', 'expired'].includes(auth.status) && agreed && qr === '' && !qrRequestStartedRef.current
      : arkmeShouldBeginWechat(auth, authView, loginMode, agreed, qr, qrRequestStartedRef.current, ownsQrLogin)
    if (!shouldBegin) return
    qrRequestStartedRef.current = true
    void (loginMode === 'jiwo' ? beginJiwo() : beginWechat())
  }, [agreed, auth, authView, jiwoScanLoginEnabled, loginMode, ownsQrLogin, qr])

  useEffect(() => () => {
    qrFlowRevisionRef.current += 1
    const attemptId = currentJiwoAttemptRef.current
    currentJiwoAttemptRef.current = undefined
    if (attemptId !== undefined) {
      void callArkme('auth.app.cancel', { attemptId }).catch(() => undefined)
    }
  }, [])

  const changeLoginMode = (mode: ArkmeLoginMode) => {
    qrFlowRevisionRef.current += 1
    const attemptId = currentJiwoAttemptRef.current
    currentJiwoAttemptRef.current = undefined
    if (attemptId !== undefined) {
      void callArkme('auth.app.cancel', { attemptId }).catch(() => undefined)
    }
    if (auth?.status === 'pending') {
      pendingLoginModeSelectionRef.current = mode
      arkmeAuthStore.setAuth({ status: 'logged-out', environment: auth.environment })
    }
    setLoginMode(mode)
    setQr('')
    setError('')
    qrRequestStartedRef.current = false
  }

  const uploadFavoriteSticker = async (file: File): Promise<ArkmeUploadedAsset> => await new Promise((resolve, reject) => {
    const request = new XMLHttpRequest()
    request.open('POST', '/arkme-self/api/upload')
    request.setRequestHeader('Content-Type', file.type || 'application/octet-stream')
    request.setRequestHeader('X-Arkme-File-Name', encodeURIComponent(file.name))
    request.onerror = () => { reject(new Error('文件上传网络错误')) }
    request.onload = () => {
      try {
        const payload = JSON.parse(request.responseText) as ArkmePluginResponse<ArkmeUploadedAsset>
        if (!payload.ok) { reject(new ArkmeClientError(payload.error)); return }
        resolve(payload.value)
      } catch (caught) { reject(caught) }
    }
    request.send(file)
  })

  const selectFiles = async (files: FileList | readonly File[] | null, pasted = false) => {
    if (activeRecordReeditComposer === undefined && directAdmission.blocked) return
    if (files === null || files.length === 0) return
    const reeditTarget = activeRecordReeditComposer
    if (reeditTarget?.snapshot?.maxAttachments === 0) return
    const targetDraftKey = composerDraftKey
    const targetUserId = authenticatedUserId
    if (targetDraftKey === undefined || targetUserId === undefined) return
    const preparationKey = reeditTarget === undefined ? targetDraftKey : arkmeRecordReeditPreparationKey(reeditTarget)
    if (reeditTarget !== undefined && (reeditTarget.snapshot === undefined || reeditTarget.loading
      || reeditTarget.busy || preparationJobs.current.has(preparationKey))) return
    const scope = captureComposerAsyncScope()
    const sameReeditTarget = () => reeditTarget !== undefined && sameComposerAsyncScope(scope)
      && recordReeditGenerationRef.current === reeditTarget.generation
      && sameArkmeRecordReeditSession(recordReeditComposerRef.current, reeditTarget)
    const showPreparationError = (message: string) => {
      if (reeditTarget === undefined) setError(message)
      else if (sameReeditTarget()) setRecordReeditComposer(current => sameArkmeRecordReeditSession(current, reeditTarget)
        ? { ...current, error: message } : current)
    }
    if (reeditTarget === undefined) syncComposerUserInput(true)
    const picked = Array.from(files)
    const controller = new AbortController(); stageControllers.current.add(controller)
    setAddMenuOpen(false); showPreparationError(''); setPreparingKeys(current => new Set([...current, preparationKey]))
    if (fileInputRef.current !== null) fileInputRef.current.value = ''
    const job = (preparationJobs.current.get(preparationKey) ?? Promise.resolve(true)).catch(() => false).then(async () => {
      const sdk = createArkmeSdk()
      const policy = await sdk.fileCapabilities()
      const maxAttachments = Math.min(policy.maxAttachments, reeditTarget?.snapshot?.maxAttachments ?? policy.maxAttachments)
      const errors: string[] = []
      let reeditAttachmentCount = reeditTarget?.attachments.length ?? 0
      let added = false
      for (const file of picked) {
        controller.signal.throwIfAborted()
        if (reeditTarget !== undefined && !sameReeditTarget()) return false
        const limit = file.type.startsWith('image/') ? policy.maxImageBytes : policy.maxFileBytes
        const attachmentCount = reeditTarget === undefined ? arkmeComposerDraftStore.get(targetDraftKey).attachments.length : reeditAttachmentCount
        if (attachmentCount >= maxAttachments) { errors.push(tr("最多添加 {v0} 个附件：{v1}", { v0: maxAttachments, v1: file.name })); continue }
        if (file.size === 0 || file.size > limit) { errors.push(tr("{v0} 为空或超过 {v1} MiB", { v0: file.name, v1: Math.floor(limit / 1024 / 1024) })); continue }
        try {
          const localFile = await sdk.stageFile(file, { signal: controller.signal, expectedUserId: targetUserId, ...(reeditTarget ? { retention: 'references' as const } : {}) })
          const currentAuth = arkmeAuthStore.getSnapshot().auth
          if (currentAuth?.status !== 'authenticated' || currentAuth.userId !== targetUserId) return false
          if (reeditTarget === undefined) arkmeComposerDraftStore.appendAttachments(targetDraftKey, [{ localFile }], policy.maxAttachments)
          else {
            if (!sameReeditTarget()) return false
            reeditAttachmentCount += 1
            setRecordReeditComposer(current => sameArkmeRecordReeditSession(current, reeditTarget)
              ? { ...current, attachments: [...current.attachments, { localFile, selection: { fileRef: localFile.fileRef } }] }
              : current)
          }
          added = true
        } catch (caught) { if (controller.signal.aborted) throw caught; errors.push(`${file.name}：${errorMessage(caught)}`) }
      }
      if (errors.length > 0) showPreparationError(errors.join('；'))
      return added
    }).catch(caught => { if (!controller.signal.aborted) showPreparationError(errorMessage(caught)); return false }).finally(() => {
      stageControllers.current.delete(controller)
      if (preparationJobs.current.get(preparationKey) === job) {
        preparationJobs.current.delete(preparationKey)
        setPreparingKeys(current => { const next = new Set(current); next.delete(preparationKey); return next })
      }
      // Paste restores its captured selection after the editable DOM commits.
      // Do not leave an unrelated tail-focus request that could replay later.
      if (!pasted) {
        if (reeditTarget === undefined) pendingComposerFocusDraftKeyRef.current = targetDraftKey
        else if (sameReeditTarget()) requestAnimationFrame(() => { textareaRef.current?.focus() })
      }
    })
    preparationJobs.current.set(preparationKey, job)
    await job
  }

  const rememberSentHashTags = useCallback((
    accountKey: string,
    itemUid: string,
    textContent: string,
    sendAtMillis: number,
    textFormat?: 'plain' | 'markdown',
  ) => {
    if (arkmeAuthenticatedAccountKey(arkmeAuthStore.getSnapshot().auth) !== accountKey) return
    setHashTagMemory(current => ({ accountKey, items: arkmeMergeHashTagSuggestions(current.accountKey === accountKey ? current.items : [], [{
      itemUid,
      textContent,
      textFormat: textFormat ?? 'plain',
      sendAtMillis,
    }]) }))
  }, [])

  const send = async () => {
    const scope = captureComposerAsyncScope()
    const roleAtSendStart = source !== undefined && isArkmeSelfWorkspaceSource(source) ? selectedSelfRole : undefined
    try { await withConversationSend(composerDraftKey, async consumed => { if (sameComposerAsyncScope(scope)) await sendCore(consumed, roleAtSendStart) }) }
    catch (caught) { setError(errorMessage(caught)) }
  }
  const sendCore = async (consumed: () => Promise<void>, roleAtSendStart?: ArkmeSelfRole) => {
    if (activeRecordReeditComposer === undefined && directAdmission.blocked) return
    if (activeRecordReeditComposer !== undefined) {
      await commitRecordReedit()
      return
    }
    if (source === undefined || composerDraftKey === undefined) return
    if (composerArticleStore.get(articleDraftKey) && articleDraftKey && authenticatedUserId !== undefined) {
      const scope = captureComposerAsyncScope()
      const auth = arkmeAuthStore.getSnapshot().auth
      if (!sameComposerAsyncScope(scope) || auth?.status !== 'authenticated') return
      messagePreparing.stop()
      const result = await composerArticleStore.send(articleDraftKey, source.sourceRef, authenticatedUserId, auth.environment, consumed)
      if (!result || !sameComposerAsyncScope(scope)) return
      // Read the canonical forwarded/published card; do not synthesize article text as a normal message.
      const page = await callArkme<ArkmeTimelinePage>('source.timeline', { sourceRef: source.sourceRef, limit: 50 }).catch(() => undefined)
      if (page && sameComposerAsyncScope(scope)) {
        setItems(current => mergeItems(current, page.items))
        const sent = page.items.find(item => item.itemUid === result.itemUid)
        if (sent) confirmedSendRetention.retain(conversationKey, sent)
      }
      if (sameComposerAsyncScope(scope)) { arkmeUi.recordChanged(); textareaRef.current?.focus() }
      return
    }
    const targetSource = source
    const extensionTarget = activeComposerExtensionTarget
    const targetDraftKey = composerDraftKey
    const targetUserId = authenticatedUserId
    const targetAccountKey = authenticatedAccountKey
    if (targetUserId === undefined || targetAccountKey === undefined) return
    const targetComposerScope = captureComposerAsyncScope()
    const sameTargetAccount = () => {
      const current = arkmeAuthStore.getSnapshot().auth
      return arkmeAuthenticatedAccountKey(current) === targetAccountKey
    }
    const sameTargetComposer = () => sameTargetAccount() && sameComposerAsyncScope(targetComposerScope)
    const preparation = preparationJobs.current.get(targetDraftKey)
    const preparationSucceeded = preparation === undefined ? true : await preparation
    if (preparationSucceeded === false) return
    if (!sameTargetAccount()) return
    const readyDraft = arkmeComposerDraftStore.get(targetDraftKey)
    const serializedDraft = serializeArkmeComposerDraft(readyDraft)
    const rawTextContent = serializedDraft.text
    const textFormat = serializedDraft.textFormat
    const textContent = textFormat === 'markdown' ? rawTextContent : rawTextContent.trim()
    if (textContent === '' && readyDraft.attachments.length === 0) return
    if (sameTargetComposer()) messagePreparing.stop()
    const inputStartedAtMillis = recordInputCaptureOwner.getStartedAtMillis(targetDraftKey)
    const locationCaptureRequested = arkmeSourceSupportsLocationCapture(targetSource.kind)
    const { recordUid, relationUid } = arkmeComposerDraftStore.beginFileSend(
      targetDraftKey,
      extensionTarget === undefined
        ? undefined
        : JSON.stringify(['source-message-extension', targetSource.sourceRef, arkmeTimelineMessageActionRef(extensionTarget.item)]),
    )
    // Take the draft before any network await.  The next keystroke now belongs to a
    // fresh draft and can be sent independently instead of being swallowed by a busy lock.
    const pendingDraft = arkmeComposerDraftStore.take(targetDraftKey)
    try { await consumed() } catch (caught) { if (sameTargetAccount()) arkmeComposerDraftStore.restore(targetDraftKey, pendingDraft); throw caught }
    // The reference belongs to the submitted draft, not the next message. Do not
    // leave it visible until the network request and attachment cleanup finish.
    if (extensionTarget !== undefined && sameTargetComposer()) {
      setComposerExtensionTarget(current => current === extensionTarget ? undefined : current)
    }
    const restorePendingDraft = () => {
      arkmeComposerDraftStore.restore(targetDraftKey, pendingDraft)
      // Only restore the reference when restore() accepted the original draft
      // identity; a newer draft or reference must keep its own destination.
      if (extensionTarget !== undefined && sameTargetComposer()
        && arkmeComposerDraftStore.get(targetDraftKey).fileSendIdentity?.recordUid === recordUid) {
        setComposerExtensionTarget(current => current ?? extensionTarget)
      }
    }
    setHashTagTrigger(undefined)
    setHashTagCandidateIndex(0)
    activeHashTagStartRef.current = undefined
    dismissedHashTagStartRef.current = undefined
    const pendingAttachments = [...pendingDraft.attachments]
    const pendingAssets = pendingAttachments.flatMap(attachment => attachment.asset === undefined ? [] : [attachment.asset])
    const pendingFileRefs = pendingAttachments.flatMap(attachment => attachment.localFile === undefined ? [] : [attachment.localFile.fileRef])
    pendingComposerFocusDraftKeyRef.current = targetDraftKey
    const now = Date.now()
    const optimisticSenderName = selfProfile?.displayName.trim() || selfProfile?.nickname.trim() || '我'
    const optimisticAvatarRef = selfProfile?.avatarRef.trim()
    const optimisticMentions = optimisticTimelineMentionTargets(
      serializedDraft.mentions,
      conversationMembers,
      rawTextContent,
      textContent,
      textFormat,
    )
    let optimistic: ArkmeTimelineItem = {
      itemUid: recordUid, senderName: optimisticSenderName, isMe: true, sendAtMillis: now,
      ...(roleAtSendStart === undefined ? {} : { selfRole: {
        roleId: roleAtSendStart.roleId,
        name: roleAtSendStart.name,
        ...(roleAtSendStart.avatarRef === undefined ? {} : { avatarRef: roleAtSendStart.avatarRef }),
      } }),
      ...(optimisticAvatarRef === undefined || optimisticAvatarRef === '' ? {} : { avatarRef: optimisticAvatarRef }),
      title: '', textContent, textFormat: textFormat ?? 'plain', status: 0,
      contentBlocks: pendingAttachments.flatMap((attachment, index) => attachment.localFile === undefined
        ? [] : [localFileBlock(attachment.localFile, index)]),
      ...(optimisticMentions.length === 0 ? {} : { mentions: optimisticMentions }),
      ...(inputStartedAtMillis === undefined ? {} : { recordDurationMillis: Math.max(0, now - inputStartedAtMillis) }),
      ...(targetSource.kind === 'group_chat' && aiPolishSettings?.enabled === true
        ? { aiPolish: { state: 'polishing' as const, originalText: textContent } }
        : {}),
      ...(extensionTarget === undefined ? {} : {
        extensionParentRecordUid: extensionTarget.item.itemUid,
        extensionParent: {
          itemUid: extensionTarget.item.itemUid,
          senderName: extensionTarget.item.senderName,
          title: extensionTarget.item.title,
          textContent: extensionTarget.item.textContent,
          textFormat: extensionTarget.item.textFormat ?? 'plain',
          ...(extensionTarget.item.contentBlocks === undefined ? {} : { contentBlocks: extensionTarget.item.contentBlocks }),
        },
        contentBlocks: pendingAttachments.flatMap((attachment, index) => attachment.localFile === undefined
          ? []
          : [localFileBlock(attachment.localFile, index)]),
      }),
      displayKind: 0,
    }
    if (sameTargetComposer()) {
      pendingViewportRestoreRef.current = { sourceKey: arkmeSourceIdentityKey(targetSource), viewport: undefined }
    }
    const pendingHumanMentions = serializedDraft.mentions.flatMap<ArkmeHumanMentionInput>(mention => {
      const base = { startIndex: mention.startIndex, length: mention.length }
      if (mention.all === true) return [{ ...base, all: true }]
      return mention.mentionRef === undefined ? [] : [{ ...base, mentionRef: mention.mentionRef }]
    })
    const pendingBotMentions = serializedDraft.mentions.flatMap<{ botRef: string; startIndex: number; length: number }>(mention => {
      if (mention.botRef === undefined) return []
      return [{ botRef: mention.botRef, startIndex: mention.startIndex, length: mention.length }]
    })
    if (sameTargetComposer()) {
      setItems(current => mergeItems(current, [optimistic]))
    }
    if (sameTargetComposer()) setError('')
    let roleBound = false
    if (roleAtSendStart !== undefined) {
      try {
        const snapshot = await callArkme<NonNullable<ArkmeTimelineItem['selfRole']>>('self-roles.bind', {
          expectedUserId: targetUserId,
          sourceRef: targetSource.sourceRef,
          recordUid,
          roleId: roleAtSendStart.roleId,
        })
        roleBound = true
        optimistic = { ...optimistic, selfRole: snapshot }
        if (sameTargetComposer()) setItems(current => current.map(item => item.itemUid === recordUid
          ? { ...item, selfRole: snapshot } : item))
      } catch (caught) {
        if (sameTargetComposer()) setItems(current => current.filter(item => item.itemUid !== recordUid))
        if (sameTargetAccount()) restorePendingDraft()
        else releaseArkmeComposerDraft(pendingDraft)
        if (sameTargetComposer()) setError(`角色尚未保存，本条内容未发送：${errorMessage(caught)}`)
        return
      }
    }
    const capturePromise = recordInputCaptureOwner.finishForSubmit(targetDraftKey)
    const locationCapturePromise: Promise<ArkmeRecordLocationForSendResult> = locationCaptureRequested
      ? captureArkmeRecordLocationForSend()
        .catch(caught => ({ state: 'failed', message: errorMessage(caught) || '位置采集失败，请稍后重试' }))
      : Promise.resolve({ state: 'disabled' })
    let inputCapture: ArkmeRecordInputCaptureResult
    try {
      inputCapture = await capturePromise
    } catch (caught) {
      let roleCleanupFailed = false
      if (roleBound && roleAtSendStart !== undefined && sameTargetAccount()) {
        try {
          await callArkme('self-roles.unbind', { expectedUserId: targetUserId, recordUid, roleId: roleAtSendStart.roleId })
        } catch { roleCleanupFailed = true }
      }
      if (sameTargetComposer()) setItems(current => current.filter(item => item.itemUid !== recordUid))
      if (sameTargetAccount()) restorePendingDraft()
      else releaseArkmeComposerDraft(pendingDraft)
      if (sameTargetComposer()) setError(roleCleanupFailed
        ? `输入快照采集失败，角色状态清理未完成；请保持原角色重试：${errorMessage(caught)}`
        : errorMessage(caught) || '输入快照采集失败，请重试')
      return
    }
    if (!sameTargetAccount()) {
      releaseArkmeComposerDraft(pendingDraft)
      return
    }
    const recordDurationMillis = inputCapture.recordDurationMillis
    const captureContext = inputCapture.captureContext
    optimistic = { ...optimistic, recordDurationMillis, captureContext }
    if (sameTargetComposer()) setItems(current => current.map(item => item.itemUid === recordUid
      ? { ...item, recordDurationMillis, captureContext } : item))
    let durableFileSendUncertain = false
    let providerWriteStarted = false
    let providerWriteSucceeded = false
    const rebindRole = async (sentRecordUid: string) => {
      if (!roleBound || sentRecordUid === recordUid || !sameTargetAccount()) return
      try {
        await callArkme('self-roles.rebind', {
          expectedUserId: targetUserId,
          recordUid,
          newRecordUid: sentRecordUid,
        })
      } catch (caught) {
        if (sameTargetComposer()) setError(`消息已发送，但角色展示暂未保存：${errorMessage(caught)}`)
      }
    }
    let stagedBackgroundFileRefs: string[] = []
    try {
      if (extensionTarget !== undefined) {
        if (pendingAssets.length > 0) throw new Error('请重新添加本地附件后再延展')
        providerWriteStarted = true
        const result = await callArkme<ArkmeSourceMessageExtendResult>('source.message-extension.extend', {
          sourceRef: targetSource.sourceRef,
          messageActionRef: arkmeTimelineMessageActionRef(extensionTarget.item),
          textContent,
          textFormat,
          humanMentions: pendingHumanMentions,
          botMentions: pendingBotMentions,
          recordUid,
          relationUid,
          fileRefs: pendingFileRefs,
        })
        providerWriteSucceeded = true
        if (!sameTargetAccount()) throw new Error('账号已切换，本次延展结果已丢弃')
        await rebindRole(result.recordUid)
        const applyExtensionResult = (current: ArkmeTimelineItem[]) => applySourceSendResult(
          current,
          recordUid,
          {
            sourceRef: targetSource.sourceRef,
            itemUid: result.recordUid,
            status: result.status,
            localState: result.localState,
            ...(result.sequence === undefined ? {} : { sequence: result.sequence }),
          },
        ).map(item => item.itemUid === result.recordUid
          ? { ...item, awaitingTimelineProjection: true as const }
          : item)
        if (sameTargetComposer()) setItems(applyExtensionResult)
        const targetSourceKey = arkmeSourceIdentityKey(targetSource)
        const cachedTimeline = conversationCacheRef.current.getTimeline(targetSourceKey)
        if (cachedTimeline !== undefined) {
          conversationCacheRef.current.storeTimeline(targetSourceKey, {
            ...cachedTimeline,
            items: applyExtensionResult(mergeItems(cachedTimeline.items, [optimistic])),
            ...(result.sequence === undefined ? {} : {
              latestSequence: Math.max(cachedTimeline.latestSequence ?? 0, result.sequence),
            }),
          })
        }
        if (result.sequence !== undefined) {
          arkmeChatDirectory.upsert({ ...targetSource,
            latestPreview: (textFormat === 'markdown' ? arkmeMarkdownPlainText(textContent) : textContent) || '非文本内容',
            activeAtMillis: now,
            latestSequence: result.sequence,
          }, targetSource.sourceKey)
        }
        rememberSentHashTags(targetAccountKey, result.recordUid, textContent, now, textFormat)
        await Promise.allSettled(pendingFileRefs.map(fileRef => callArkme('files.local.remove', { fileRef })))
        releaseArkmeComposerDraft(pendingDraft)
        arkmeUi.chatChanged()
        return
      }
      if (pendingFileRefs.length > 0 && pendingAssets.length > 0) {
        throw new Error('旧版附件与本地附件不能混合发送，请重新添加旧版附件')
      }
      const sdk = createArkmeSdk()
      const allCapturedSegments = [...inputCapture.backgroundSound.segments]
      const backgroundCaptureAvailable = backgroundSoundSupported && backgroundSoundEligibilityReason === 'eligible'
      const capturedSegments = backgroundCaptureAvailable ? allCapturedSegments : []
      let backgroundCaptureFeedback = allCapturedSegments.length > 0 && !backgroundCaptureAvailable
        ? backgroundSoundEligibilityReason === 'membership-required'
          ? '文字仍会发送，但免费版暂不支持背景音，本条录音未随消息发送'
          : backgroundSoundEligibilityReason === 'membership-unavailable'
            ? '文字仍会发送，但暂时无法确认会员权益，本条录音未随消息发送'
            : '文字仍会发送，但当前运行环境不支持背景音，本条录音未随消息发送'
        : ''
      if (allCapturedSegments.length === 0 && inputCapture.backgroundSound.enabled) {
        const failure = arkmeBackgroundSoundCaptureFailureFeedback(inputCapture.backgroundSound.failure)
        if (failure !== '') backgroundCaptureFeedback = `文字仍会发送，但背景音未录入：${failure}`
      }
      const backgroundSoundFileRefs: string[] = []
      const backgroundSoundAssets: ArkmeUploadedAsset[] = []
      const backgroundSoundAmplitudes: number[] = []
      const sendBackgroundDirectly = pendingAssets.length > 0
        || (locationCaptureRequested && pendingFileRefs.length === 0)
      if (capturedSegments.length > 0 && !sendBackgroundDirectly) {
        const maxAttachments = await boundedArkmeBackgroundTransfer(
          signal => sdk.fileCapabilities(signal).then(policy => policy.maxAttachments),
        ).catch(() => 9)
        const allowedSegments = capturedSegments.slice(0, Math.max(0, maxAttachments - pendingFileRefs.length))
        if (allowedSegments.length < capturedSegments.length) {
          backgroundCaptureFeedback = allowedSegments.length === 0
            ? '消息已保留，但附件数量已满，本条背景音未随消息发送'
            : `消息已保留；背景音片段较多，仅发送前 ${String(allowedSegments.length)} 段`
        }
        for (const segment of allowedSegments) {
          try {
            if (!sameTargetAccount()) throw new Error('账号已切换，本条背景音未暂存')
            const localFile = await boundedArkmeBackgroundTransfer(signal => sdk.stageFile(segment.blob, {
              fileName: segment.fileName,
              signal,
              expectedUserId: targetUserId,
            }))
            if (!sameTargetAccount()) throw new Error('账号已切换，本条背景音暂存结果已丢弃')
            backgroundSoundFileRefs.push(localFile.fileRef)
            stagedBackgroundFileRefs.push(localFile.fileRef)
            backgroundSoundAmplitudes.push(...segment.amplitudes)
          } catch (caught) {
            backgroundCaptureFeedback = `文字仍会发送，但部分背景音暂存失败：${errorMessage(caught) || '请检查麦克风和本地空间'}`
          }
        }
      } else if (capturedSegments.length > 0) {
        const allowedSegments = capturedSegments.slice(0, Math.max(0, 20 - pendingAssets.length))
        if (allowedSegments.length < capturedSegments.length) {
          backgroundCaptureFeedback = allowedSegments.length === 0
            ? '文字和已有附件仍会发送，但附件数量已满，本条背景音未随消息发送'
            : `文字和已有附件仍会发送；背景音片段较多，仅发送前 ${String(allowedSegments.length)} 段`
        }
        const cachedAssets = backgroundDirectUploadCacheRef.current.get(recordUid) ?? []
        for (const [index, segment] of allowedSegments.entries()) {
          try {
            const cached = cachedAssets[index]
            if (cached !== undefined) {
              backgroundSoundAssets.push(cached)
              backgroundSoundAmplitudes.push(...segment.amplitudes)
              continue
            }
            if (!sameTargetAccount()) throw new Error('账号已切换，本条背景音未上传')
            const uploaded = await boundedArkmeBackgroundTransfer(signal => sdk.upload(segment.blob, {
              fileName: segment.fileName,
              signal,
              expectedUserId: targetUserId,
            }))
            if (!sameTargetAccount()) throw new Error('账号已切换，本条背景音上传结果已丢弃')
            backgroundSoundAssets.push(uploaded)
            cachedAssets[index] = uploaded
            backgroundDirectUploadCacheRef.current.set(recordUid, cachedAssets)
            if (backgroundDirectUploadCacheRef.current.size > 8) {
              const oldest = backgroundDirectUploadCacheRef.current.keys().next().value as string | undefined
              if (oldest !== undefined && oldest !== recordUid) backgroundDirectUploadCacheRef.current.delete(oldest)
            }
            backgroundSoundAmplitudes.push(...segment.amplitudes)
          } catch (caught) {
            backgroundCaptureFeedback = `文字和已有附件仍会发送，但部分背景音上传失败：${errorMessage(caught) || '请检查网络'}`
          }
        }
      }
      const allFileRefs = [...pendingFileRefs, ...backgroundSoundFileRefs]
      if (allFileRefs.length > 0) {
        if (!sameTargetAccount()) throw new Error('账号已切换，本次发送已取消')
        const locationCapture = await locationCapturePromise
        if (!sameTargetAccount()) throw new Error('账号已切换，本次位置采集结果已丢弃')
        const fileSendParams = {
          sourceRef: targetSource.sourceRef, recordUid, relationUid, fileRefs: allFileRefs, title: '', textContent: rawTextContent, textFormat, displayKind: 0,
          expectedUserId: targetUserId,
          ...(recordDurationMillis === 0 ? {} : { recordDurationMillis }),
          captureContext,
          ...(backgroundSoundFileRefs.length === 0 ? {} : {
            backgroundSound: { fileRefs: backgroundSoundFileRefs, amplitudes: backgroundSoundAmplitudes },
          }),
          ...(locationCapture.state === 'captured' ? { location: locationCapture.location } : {}),
          ...(pendingHumanMentions.length === 0 ? {} : { humanMentions: pendingHumanMentions }),
          ...(pendingBotMentions.length === 0 ? {} : { botMentions: pendingBotMentions }),
        }
        let acceptedTask: ArkmeFileSendTask | undefined
        try {
          providerWriteStarted = true
          acceptedTask = await callArkme<ArkmeFileSendTask>('files.send', fileSendParams)
        } catch (caught) {
          let taskLookupSucceeded = false
          if (sameTargetAccount()) {
            try {
              const tasks = await callArkme<ArkmeFileSendTask[]>('files.send.tasks', { sourceRef: targetSource.sourceRef })
              taskLookupSucceeded = true
              acceptedTask = tasks.find(task => task.recordUid === recordUid && task.relationUid === relationUid)
            } catch { /* an unreadable queue leaves acceptance uncertain */ }
          }
          if (acceptedTask === undefined) {
            const nonRetryable = caught instanceof ArkmeClientError && !caught.body.retryable
            durableFileSendUncertain = sameTargetAccount() && !taskLookupSucceeded && !nonRetryable
            if (!durableFileSendUncertain && sameTargetAccount()) {
              await Promise.all(stagedBackgroundFileRefs.map(async fileRef => {
                await sdk.removeLocalFile(fileRef).catch(() => undefined)
              }))
              stagedBackgroundFileRefs = []
            }
            throw caught
          }
        }
        providerWriteSucceeded = true
        if (!sameTargetAccount()) throw new Error('账号已切换，本次发送结果已丢弃')
        await rebindRole(acceptedTask.recordUid)
        backgroundDirectUploadCacheRef.current.delete(recordUid)
        releaseArkmeComposerDraft(pendingDraft)
        fileTasks.accept(acceptedTask)
        if (sameTargetComposer()) setItems(current => current.filter(item => item.itemUid !== recordUid || item.status !== 0))
        rememberSentHashTags(targetAccountKey, acceptedTask.recordUid, rawTextContent, now, textFormat)
        const locationFeedback = locationCaptureFeedback(locationCapture)
        if (sameTargetComposer()) {
          if (locationFeedback !== undefined) setError(locationFeedback)
          else if (backgroundCaptureFeedback !== '') setError(backgroundCaptureFeedback)
        }
        return
      }
      if (!sameTargetAccount()) throw new Error('账号已切换，本次发送已取消')
      providerWriteStarted = true
      const result = textFormat === 'markdown' || pendingAssets.length > 0 || backgroundSoundAssets.length > 0
        ? await callArkme<ArkmeSourceSendResult>('source.send-rich', {
          sourceRef: targetSource.sourceRef, title: '', textContent: rawTextContent, textFormat, displayKind: 0,
          assets: pendingAssets, recordUid, relationUid, expectedUserId: targetUserId,
          ...(recordDurationMillis === 0 ? {} : { recordDurationMillis }),
          captureContext,
          ...(backgroundSoundAssets.length === 0 ? {} : {
            backgroundSound: { assets: backgroundSoundAssets, amplitudes: backgroundSoundAmplitudes },
          }),
          ...(pendingHumanMentions.length === 0 ? {} : { humanMentions: pendingHumanMentions }),
          ...(pendingBotMentions.length === 0 ? {} : { botMentions: pendingBotMentions }),
        })
        : await callArkme<ArkmeSourceSendResult>('source.send-text', {
          sourceRef: targetSource.sourceRef, textContent: rawTextContent, textFormat, recordUid, relationUid, expectedUserId: targetUserId,
          ...(recordDurationMillis === 0 ? {} : { recordDurationMillis }),
          captureContext,
          ...(pendingHumanMentions.length === 0 ? {} : { humanMentions: pendingHumanMentions }),
          ...(pendingBotMentions.length === 0 ? {} : { botMentions: pendingBotMentions }),
        })
      providerWriteSucceeded = true
      if (!sameTargetAccount()) throw new Error('账号已切换，本次发送结果已丢弃')
      await rebindRole(result.itemUid)
      backgroundDirectUploadCacheRef.current.delete(recordUid)
      if (result.localState !== 'failed') {
        rememberSentHashTags(targetAccountKey, result.itemUid, rawTextContent, now, textFormat)
      }
      const confirmedItem = applySourceSendResult([optimistic], recordUid, result)[0]
      const targetSourceKey = arkmeSourceIdentityKey(targetSource)
      if (result.localState === 'synced' && confirmedItem !== undefined) {
        confirmedSendRetention.retain(targetSourceKey, confirmedItem)
        if (targetSource.kind === 'group_chat' && pendingHumanMentions.length > 0) {
          arkmeInterwovenInvalidation.invalidate(targetSourceKey)
        }
      }
      if (sameTargetComposer()) setItems(current => applySourceSendResult(current, recordUid, result))
      if (result.localState !== 'failed' && locationCaptureRequested) {
        if (sameTargetAccount()) void locationCapturePromise.then(locationCapture => {
          if (!sameTargetAccount()) return
          const feedback = locationCaptureFeedback(locationCapture)
          if (locationCapture.state !== 'captured') {
            if (feedback !== undefined && sameTargetComposer()) setError(feedback)
            return
          }
          const selectedLocation = locationCapture.location
          return callArkme('source.message-location.set', {
            sourceRef: targetSource.sourceRef,
            itemUid: result.itemUid,
            location: selectedLocation,
          }).then(() => {
            if (!sameTargetAccount()) return
            if (sameTargetComposer()) {
              setItems(current => current.map(item => item.itemUid === result.itemUid ? { ...item, locationCapture: selectedLocation } : item))
            }
            const cachedTimeline = conversationCacheRef.current.getTimeline(targetSourceKey)
            if (cachedTimeline !== undefined) {
              conversationCacheRef.current.storeTimeline(targetSourceKey, {
                ...cachedTimeline,
                items: cachedTimeline.items.map(item => item.itemUid === result.itemUid
                  ? { ...item, locationCapture: selectedLocation }
                  : item),
              })
            }
            if (confirmedItem !== undefined) {
              confirmedSendRetention.retain(targetSourceKey, { ...confirmedItem, locationCapture: selectedLocation })
            }
            if (sameTargetComposer()) {
              setSnapshot(current => current?.item.itemUid === result.itemUid
                ? {
                    ...current,
                    item: { ...current.item, locationCapture: selectedLocation },
                    ...(current.detail === undefined ? {} : {
                      detail: {
                        ...current.detail,
                        locationCapture: selectedLocation,
                        ...(selectedLocation.altitudeMeters === undefined ? {} : { altitudeMeters: selectedLocation.altitudeMeters }),
                      },
                    }),
                  }
                : current)
            }
          }).catch(caught => {
            if (sameTargetComposer()) setError(`消息已发送，但位置快照未写入：${errorMessage(caught) || '请稍后重试'}`)
          })
        })
      }
      if (result.localState !== 'failed' && isArkmeChatDirectorySource(targetSource)
        && result.sequence !== undefined) {
        const cachedTimeline = conversationCacheRef.current.getTimeline(targetSourceKey)
        if (cachedTimeline !== undefined) {
          conversationCacheRef.current.storeTimeline(targetSourceKey, {
            ...cachedTimeline,
            items: applySourceSendResult(mergeItems(cachedTimeline.items, [optimistic]), recordUid, result),
            latestSequence: Math.max(cachedTimeline.latestSequence ?? 0, result.sequence),
          })
        }
        arkmeMessageReadReceipts.provision({
          sourceRef: targetSource.sourceRef,
          sourceKey: targetSource.sourceKey ?? targetSource.sourceRef,
          conversationKind: targetSource.kind === 'private_chat' ? 'private_chat' : 'group_chat',
          itemUid: result.itemUid,
          sequence: result.sequence,
        })
      }
      releaseArkmeComposerDraft(pendingDraft)
      if (sameTargetComposer()) {
        if (result.localState === 'failed') setError(result.error ?? '内容已保存在本地，远端同步失败')
        else if (backgroundCaptureFeedback !== '') setError(backgroundCaptureFeedback)
      }
      if (result.localState !== 'failed' && isArkmeSelfWorkspaceSource(targetSource)) arkmeUi.recordChanged()
      if (result.aiPolish?.state === 'kept_original') {
        setTimeout(() => {
          if (!sameTargetComposer()) return
          setItems(current => current.map(item => {
            if (item.itemUid !== result.itemUid || item.aiPolish?.state !== 'kept_original') return item
            const { aiPolish: _keptOriginal, ...plainItem } = item
            return plainItem
          }))
        }, 1_500)
      }
      if (pendingAssets.length > 0) await loadTimeline()
    } catch (caught) {
      // A provider may have accepted the message before a later refresh failed.
      // Never restore that draft or remove its role in that case.
      if (providerWriteSucceeded) {
        releaseArkmeComposerDraft(pendingDraft)
        if (sameTargetComposer()) setError(`消息已发送，但后续处理失败：${errorMessage(caught)}`)
        return
      }
      const definitelyNotWritten = !providerWriteStarted
        || (caught instanceof ArkmeClientError && caught.body.retryable === false && !durableFileSendUncertain)
      let roleCleanupFailed = false
      if (roleBound && roleAtSendStart !== undefined && definitelyNotWritten && sameTargetAccount()) {
        try {
          await callArkme('self-roles.unbind', { expectedUserId: targetUserId, recordUid, roleId: roleAtSendStart.roleId })
        } catch { roleCleanupFailed = true }
      }
      if (sameTargetComposer()) setItems(current => current.filter(item => item.itemUid !== recordUid))
      if (caught instanceof ArkmeClientError && caught.body.directMessageAdmission !== undefined) invalidateDirectMessageAdmission()
      if (sameTargetAccount()) {
        // restore() preserves any newer text entered after this send started.
        if (!durableFileSendUncertain) {
          restorePendingDraft()
          recordInputCaptureOwner.restoreForRetry(targetDraftKey, inputCapture)
        } else releaseArkmeComposerDraft(pendingDraft)
      } else {
        releaseArkmeComposerDraft(pendingDraft)
      }
      if (sameTargetComposer()) {
        if (roleBound && !definitelyNotWritten && roleAtSendStart !== undefined) setSelectedSelfRole(roleAtSendStart)
        setError(roleCleanupFailed
          ? `角色状态清理未完成，请保持原角色重试：${errorMessage(caught)}`
          : roleBound && !definitelyNotWritten
            ? `${errorMessage(caught)}。发送结果尚未确认，请先检查历史消息；重试时保持原角色。`
            : errorMessage(caught))
      }
    }
  }

  const updateComposerText = (text: string) => {
    if (activeRecordReeditComposer === undefined && directAdmission.blocked) return
    if (activeRecordReeditComposer !== undefined) {
      if (activeRecordReeditComposer.snapshot === undefined || activeRecordReeditComposer.loading || activeRecordReeditComposer.busy
        || preparationJobs.current.has(arkmeRecordReeditPreparationKey(activeRecordReeditComposer))) return
      setRecordReeditComposer(current => sameArkmeRecordReeditSession(current, activeRecordReeditComposer)
        ? { ...current, textContent: text, mentions: reconcileArkmeComposerMentions(current.textContent, text, current.mentions ?? [], current.snapshot?.textFormat ?? current.item.textFormat), error: '' }
        : current)
      return
    }
    const hasUserContent = text.length > 0 || attachments.length > 0
    syncComposerUserInput(hasUserContent)
    arkmeComposerDraftStore.setText(composerDraftKey, text)
  }

  const retryAiPolish = async (item: ArkmeTimelineItem) => {
    const retryRef = item.aiPolish?.retryRef
    if (retryRef === undefined) return
    setItems(current => current.map(value => value.itemUid === item.itemUid
      ? { ...value, aiPolish: { ...value.aiPolish, state: 'polishing' } }
      : value))
    try {
      const result = await callArkme<ArkmeSourceSendResult>('source.ai-polish.retry', { retryRef })
      setItems(current => current.map(value => {
        if (value.itemUid !== item.itemUid || result.aiPolish === undefined) return value
        return {
          ...value,
          ...(result.aiPolish.state === 'polished' && result.aiPolish.polishedText !== undefined
            ? { textContent: result.aiPolish.polishedText }
            : {}),
          aiPolish: result.aiPolish,
        }
      }))
    } catch (caught) {
      setItems(current => current.map(value => value.itemUid === item.itemUid && item.aiPolish !== undefined
        ? { ...value, aiPolish: item.aiPolish }
        : value))
      setError(errorMessage(caught))
    }
  }

  const personalExtensionParents = usePersonalExtensionParents(
    `${authenticatedAccountKey}:${conversationKey}:${sourceProjectionRevision}`,
    activeConversation && source !== undefined && isArkmeSelfWorkspaceSource(source) ? source.sourceRef : undefined, items,
  )
  const detailRow = items.find(item => item.itemUid === detailItemUid)
  const detailItem = detailRow === undefined ? undefined : personalExtensionParents.has(detailRow.itemUid)
    ? { ...detailRow, extensionParent: personalExtensionParents.get(detailRow.itemUid)! } : detailRow
  const detailTopicPresentation = detailItem === undefined ? undefined
    : arkmeTimelineSelfTopicPresentation(detailItem, source, selfSources)
  const detailTopicPath = detailItem === undefined ? undefined
    : detailTopicPresentation?.displayLabel
      ?? detailItem.selfTopic?.title
      ?? (source?.kind === 'topic' ? source.displayName : undefined)
  const openExtensionParentDetail = (parent: NonNullable<ArkmeTimelineItem['extensionParent']>) => {
    if (source === undefined || detailItem === undefined || parent.itemUid === detailItem.itemUid) return
    const from: ArkmeDetailBackTarget = {
      source, itemUid: detailItem.itemUid, sendAtMillis: detailItem.sendAtMillis,
    }
    if (items.some(item => item.itemUid === parent.itemUid)) {
      setDetailBackStack(current => [...current, from])
      setDetailItemUid(parent.itemUid)
      setShowOriginal(false)
      return
    }
    // A topic view can contain an extension whose parent belongs to another
    // topic. The aggregate self feed is the existing cross-topic lookup path.
    const navigate = (targetSource: ArkmeSourceItem) => {
      arkmeUi.showConversationTarget(targetSource, parent.itemUid, parent.sendAtMillis ?? 0,
        parent.recordOwnerUserId, undefined, false, true)
      const revision = arkmeUi.getSnapshot().conversationTarget?.revision
      if (revision !== undefined) pendingExtensionParentDetailRef.current = { revision, kind: 'push', from }
    }
    if (source.kind !== 'topic' && source.kind !== 'default_category') { navigate(source); return }
    const targetSource = aggregateSource ?? selfSources.find(candidate => candidate.kind === 'send_to_self')
    if (targetSource !== undefined) { navigate(targetSource); return }
    // A topic can be opened before its directory resolves. Ask the existing
    // self-target contract for the aggregate feed rather than searching only
    // inside the topic, where the source note may not exist.
    void callArkme<ArkmeSourceItem>('sources.self-target', {}).then(target => {
      const selected = arkmeUi.getSnapshot().selectedSource
      if (selected === undefined || arkmeSourceIdentityKey(selected) !== arkmeSourceIdentityKey(source)) return
      navigate(target)
    }, caught => {
      setMessageActionStatus(errorMessage(caught) || '暂时无法打开延展源')
    })
  }
  const openTimelineExtensionParent = (parent: NonNullable<ArkmeTimelineItem['extensionParent']>) => {
    if (source === undefined) return
    if (!isArkmeSelfWorkspaceSource(source)) {
      arkmeUi.showConversationTarget(source, parent.itemUid, parent.sendAtMillis ?? 0, parent.recordOwnerUserId)
      return
    }
    const navigate = (target: ArkmeSourceItem) => arkmeUi.showConversationTarget(target, parent.itemUid,
      parent.sendAtMillis ?? 0, parent.recordOwnerUserId, undefined, false, true)
    if (items.some(item => item.itemUid === parent.itemUid)) {
      navigate(source); return
    }
    const target = aggregateSource ?? selfSources.find(candidate => candidate.kind === 'send_to_self')
    if (target !== undefined) { navigate(target); return }
    void callArkme<ArkmeSourceItem>('sources.self-target', {}).then(target => {
      if (arkmeUi.getSnapshot().selectedSource?.sourceRef === source.sourceRef) navigate(target)
    }, caught => setMessageActionStatus(errorMessage(caught) || '暂时无法打开延展源'))
  }
  const detailSharedRecording = detailItem === undefined
    ? undefined
    : arkmeRelatedRecordingItemFromSharedRecording(detailItem)
  const acceptDetailExtension = useCallback((result: ArkmeSourceMessageExtendResult) => {
    const targetSource = source
    const targetSourceKey = targetSource === undefined ? '' : arkmeSourceIdentityKey(targetSource)
    const parent = items.find(item => item.itemUid === detailItemUid)
    if (targetSource === undefined || parent === undefined || result.parentRecordUid !== parent.itemUid
      || activeSourceKeyRef.current !== targetSourceKey) return
    const projected = detailExtensionTimelineItem(parent, result)
    const applyProjection = (current: ArkmeTimelineItem[]) => mergeItems(current, [projected])
    pendingViewportRestoreRef.current = { sourceKey: targetSourceKey, viewport: undefined }
    setItems(applyProjection)
    const cachedTimeline = conversationCacheRef.current.getTimeline(targetSourceKey)
    const projectedAiPolishSettings = cachedTimeline?.aiPolishSettings ?? aiPolishSettings
    const projectedNextCursor = cachedTimeline?.nextCursor ?? nextCursor
    const projectedNewerCursor = cachedTimeline?.newerCursor ?? newerCursor
    conversationCacheRef.current.storeTimeline(targetSourceKey, {
      items: applyProjection(cachedTimeline?.items ?? items),
      aiPolishNotices: cachedTimeline?.aiPolishNotices ?? aiPolishNotices,
      hasMore: cachedTimeline?.hasMore ?? hasMore,
      ...(projectedAiPolishSettings === undefined ? {} : { aiPolishSettings: projectedAiPolishSettings }),
      ...(projectedNextCursor === undefined ? {} : { nextCursor: projectedNextCursor }),
      ...(projectedNewerCursor === undefined ? {} : { newerCursor: projectedNewerCursor }),
      newerHasMore: cachedTimeline?.newerHasMore ?? newerHasMore,
      latestSequence: Math.max(cachedTimeline?.latestSequence ?? 0, result.sequence ?? 0),
      fetchedAtMillis: cachedTimeline?.fetchedAtMillis ?? Date.now(),
      ...(!sourceIsChat ? { recordRevision: cachedTimeline?.recordRevision ?? sourceProjectionRevision } : {}),
    })
    const latestPreview = extensionConversationPreview(result.extension)
    if (result.sequence !== undefined && isArkmeChatDirectorySource(targetSource)) {
      arkmeChatDirectory.upsert({ ...targetSource,
        latestPreview,
        activeAtMillis: result.extension.sendAtMillis,
        latestSequence: result.sequence,
      }, targetSource.sourceKey)
    }
    arkmeUi.chatChanged()
    void loadTimeline(undefined, true).catch(caught => {
      if (activeSourceKeyRef.current === targetSourceKey) setError(errorMessage(caught))
    })
  }, [aiPolishNotices, aiPolishSettings, detailItemUid, hasMore, items, loadTimeline, newerCursor, newerHasMore, nextCursor, source, sourceIsChat, sourceProjectionRevision])
  const activateSource = useCallback((nextSource: ArkmeTimelinePage['source']) => {
    if (conversationWindowRequested()) {
      void navigateConversationWindow(nextSource).then(result => {
        if (result.destination === 'main') setError(`独立窗口打开失败（${result.reason}），已在主窗口打开该会话`)
      }).catch(caught => setError(errorMessage(caught)))
      return
    }

    // The directory owns the middle conversation list. Update it before selecting
    // the source so sources opened outside that list (for example, from World)
    // have an entry to select immediately instead of waiting for its cached refresh.
    // Personal categories and topics belong only to the send-to-self workspace;
    // selecting one must not promote it into the left conversation directory.
    if (isArkmeChatDirectorySource(nextSource)) arkmeChatDirectory.upsert(nextSource)
    arkmeUi.selectSource(nextSource)
    arkmeUi.chatChanged()
  }, [])
  const updateSourceProjection = useCallback((nextSource: ArkmeTimelinePage['source']) => {
    if (isArkmeChatDirectorySource(nextSource)) arkmeChatDirectory.upsert(nextSource)
    arkmeUi.updateSelectedSourceProjection(nextSource)
  }, [])
  const applyGroupMembershipChange = useCallback((targetSource: ArkmeSourceIdentityFacts) => {
    arkmeConversationMembers.clear(authenticatedAccountKey, targetSource)
    const selectedSource = arkmeUi.getSnapshot().selectedSource
    if (selectedSource !== undefined
      && arkmeSourceIdentityKey(selectedSource) === arkmeSourceIdentityKey(targetSource)) {
      arkmeUi.chatChanged()
    }
    void arkmeChatDirectory.refreshRoot({ force: true }).catch(() => undefined)
  }, [authenticatedAccountKey])
  const updateSourceMessageDndProjection = useCallback((targetSource: ArkmeSourceIdentityFacts, result: ArkmeGroupNotificationResult) => {
    const sourceIdentity = arkmeSourceIdentityKey(targetSource)
    const selectedSource = arkmeUi.getSnapshot().selectedSource
    const directorySource = arkmeChatDirectory.getSnapshot().sources.find(item => arkmeSourceIdentityKey(item) === sourceIdentity)
    const selected = selectedSource !== undefined && arkmeSourceIdentityKey(selectedSource) === sourceIdentity
      ? selectedSource : undefined
    const currentSource = selected === undefined ? directorySource : retainNewerArkmeChatPolicy(directorySource, selected)
    if (currentSource === undefined) return result.messageDnd
    const projected = retainNewerArkmeChatPolicy(currentSource, {
      ...currentSource,
      ...projectArkmeChatAttentionFromMuted(currentSource.unreadCount, result.messageDnd),
      chatNotificationPolicyUpdatedAtMillis: result.chatNotificationPolicyUpdatedAtMillis,
    })
    updateSourceProjection(projected)
    return projected.isMuted === true
  }, [updateSourceProjection])
  const conversationMemberByRef = useMemo(
    () => new Map(conversationMembers.map(member => [member.memberRef, member])),
    [conversationMembers],
  )
  const composerMentionsEnabled = source?.kind === 'group_chat' || source?.kind === 'private_chat'
  const mentionCandidates = useMemo(
    (): ArkmeMentionCandidate[] => {
      if (!composerMentionsEnabled || mentionTrigger === undefined) return []
      if (source?.kind === 'group_chat') {
        return arkmeGroupMentionCandidates(
          mentionTrigger.query,
          groupMentionBots?.items ?? [],
          conversationMembers,
        )
      } else if (source?.kind === 'private_chat') {
        return arkmePrivateMentionCandidates(mentionTrigger.query, privateMentionBots?.items ?? [])
      }
      return []
    },
    [composerMentionsEnabled, conversationMembers, groupMentionBots?.items, mentionTrigger, privateMentionBots?.items, source?.kind],
  )
  const hashTagCandidates = useMemo(() => {
    if (hashTagTrigger === undefined) return []
    const query = hashTagTrigger.query.toLocaleLowerCase()
    const remote = hashTagRemote.key === hashTagSearchKey ? hashTagRemote.items : []
    return arkmeReconcileHashTagSuggestionSnapshots(remote, hashTagItems)
      .filter(item => arkmeHashTagMatches(item.tagText, hashTagTrigger.query))
      .sort((left, right) => Number(right.tagText.toLocaleLowerCase() === query) - Number(left.tagText.toLocaleLowerCase() === query)
        || right.latestSendAtMillis - left.latestSendAtMillis || left.normalizedTag.localeCompare(right.normalizedTag))
      .slice(0, 100)
  }, [hashTagItems, hashTagRemote, hashTagSearchKey, hashTagTrigger])
  const selfConversationMember = useMemo(
    () => conversationMembers.find(member => member.isSelf),
    [conversationMembers],
  )
  useEffect(() => { setMentionCandidateIndex(0) }, [mentionTrigger?.startIndex, mentionTrigger?.endIndex, mentionTrigger?.query])
  useEffect(() => { setHashTagCandidateIndex(0) }, [hashTagTrigger?.startIndex, hashTagTrigger?.endIndex, hashTagTrigger?.query])
  useLayoutEffect(() => {
    if (hashTagTrigger === undefined || hashTagCandidates.length === 0) return
    hashTagSuggestionListRef.current
      ?.querySelector<HTMLElement>('[role="option"][aria-selected="true"]')
      ?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }, [hashTagCandidateIndex, hashTagCandidates.length, hashTagTrigger?.endIndex, hashTagTrigger?.startIndex])
  useEffect(() => {
    setMentionTrigger(undefined)
    setMentionCandidateIndex(0)
    setHashTagTrigger(undefined)
    setHashTagCandidateIndex(0)
    activeHashTagStartRef.current = undefined
    dismissedHashTagStartRef.current = undefined
  }, [conversationKey, source?.kind, activeRecordReeditComposer?.generation])
  const closeMemberMenu = useCallback(() => { setMemberMenu(undefined) }, [])
  const closeBotMenu = useCallback(() => { setBotMenu(undefined) }, [])
  const openBotHover = useCallback((identity: ArkmeBotAvatarIdentity, anchor: HTMLElement, preferredSide?: 'left') => {
    if (source?.kind !== 'group_chat' || !anchor.isConnected || document.querySelector('[aria-modal="true"]')) return
    const rect = anchor.getBoundingClientRect()
    const host = panelRef.current?.getBoundingClientRect()
    const side = preferredSide ?? (host && rect.left > host.left + host.width / 2 ? 'left' : 'right')
    setMemberMenu(undefined)
    setBotMenu(current => current !== undefined && current.hoverAnchor === undefined ? current : {
      identity, hoverAnchor: anchor, hoverSide: side,
      position: { left: side === 'left' ? rect.left - 4 : rect.right + 4, top: rect.top - 4 },
    })
  }, [source?.kind])
  const openBotMenu = useCallback((identity: ArkmeBotAvatarIdentity, anchorRect: DOMRect) => {
    if (source?.kind !== 'group_chat') return
    setMemberMenu(undefined)
    setBotMenu({ identity, position: { left: anchorRect.left, top: anchorRect.bottom } })
  }, [source?.kind])
  const openBotProfile = useCallback((identity: ArkmeBotAvatarIdentity) => {
    setBotMenu(undefined)
    setBotProfile({ identity })
  }, [])
  const openBotRemoval = useCallback((identity: ArkmeBotAvatarIdentity, bot: ArkmeGroupBotItem) => {
    setBotMenu(undefined)
    setBotRemovalTarget({ identity, bot })
  }, [])
  const openBotChat = useCallback((bot: ArkmeBotSummary) => {
    setBotMenu(undefined)
    setBotProfile(undefined)
    void callArkme('conversation.directory.visibility.set', {
      entryKind: 'bot', entryRef: bot.botRef, hidden: false,
    }).then(() => { arkmeUi.openBotConversation(bot) }, caught => {
      setMessageActionStatus(errorMessage(caught) || '暂时无法打开 Bot 对话')
    })
  }, [])
  const openMemberHover = useCallback((member: ArkmeConversationMemberItem, anchor: HTMLElement, preferredSide?: 'left') => {
    if (source?.kind !== 'group_chat' || !anchor.isConnected || document.querySelector('[aria-modal="true"]')) return
    const rect = anchor.getBoundingClientRect()
    const host = panelRef.current?.getBoundingClientRect()
    const side = preferredSide ?? (host && rect.left > host.left + host.width / 2 ? 'left' : 'right')
    // Merely hovering must not close a profile/records panel or steal a pinned right-click menu.
    setBotMenu(undefined)
    setMemberMenu(current => current !== undefined && current.hoverAnchor === undefined ? current : {
      member, hoverAnchor: anchor, hoverSide: side,
      position: { left: side === 'left' ? rect.left - 4 : rect.right + 4, top: rect.top - 4, placement: 'below' },
    })
  }, [source?.kind])
  const openMemberMenu = useCallback((member: ArkmeConversationMemberItem, anchorRect: DOMRect) => {
    const host = panelRef.current
    if (host === null || source === undefined) return
    setMemberProfile(undefined)
    setBotMenu(undefined)
    setMemberEventProfile(undefined)
    setMemberRecords(undefined)
    setMemberMenu({
      member,
      position: { left: anchorRect.left, top: anchorRect.bottom, placement: 'below' },
    })
  }, [groupSelfRole, source])
  const openMemberProfile = useCallback((member: ArkmeConversationMemberItem) => {
    if (source?.kind !== 'group_chat') return
    setMemberMenu(undefined)
    setBotMenu(undefined)
    setMemberEventProfile(undefined)
    setMemberRecords(undefined)
    setMemberProfile(member)
  }, [source?.kind])
  const mentionOpensMemberProfile = useCallback((mentionText: string, mentionTarget?: ArkmeTimelineMentionTarget): boolean => (
    source?.kind === 'group_chat'
    && arkmeMemberForMention(mentionText, conversationMembers, mentionTarget) !== undefined
  ), [conversationMembers, source?.kind])
  const openMentionMemberProfile = useCallback((mentionText: string, mentionTarget?: ArkmeTimelineMentionTarget) => {
    if (source?.kind !== 'group_chat') return
    const member = arkmeMemberForMention(mentionText, conversationMembers, mentionTarget)
    if (member === undefined) return
    openMemberProfile(member)
  }, [conversationMembers, openMemberProfile, source?.kind])
  const openMemberRecords = useCallback((member: ArkmeConversationMemberItem, mode: ArkmeConversationMemberRecordMode) => {
    setMemberMenu(undefined)
    setMemberEventProfile(undefined)
    setMemberProfile(undefined)
    activateContextPanel('records')
    setBotRecords(undefined)
    setMemberRecords({ member, mode })
  }, [])
  const openBotRecords = useCallback((identity: ArkmeBotAvatarIdentity, mode: ArkmeConversationMemberRecordMode) => {
    setBotMenu(undefined)
    setBotProfile(undefined)
    activateContextPanel('records')
    setMemberRecords(undefined)
    setBotRecords({ identity, mode })
  }, [])
  const [editedComposerSelection, setEditedComposerSelection] = useState<{
    scope: ArkmeComposerAsyncScope
    reeditGeneration?: number | undefined
    request: ArkmeComposerSelectionRequest
  }>()
  const beginPasteFocus = useComposerPasteFocus({
    scope: composerAsyncScopeRef.current,
    generation: activeRecordReeditComposer?.generation,
    active: activeConversation,
    editor: textareaRef,
    container: composerRef,
    onReady: request => setEditedComposerSelection({
      scope: composerAsyncScopeRef.current,
      reeditGeneration: activeRecordReeditComposer?.generation,
      request,
    }),
  })
  const focusEditedComposer = useCallback((cursor: number) => {
    if (composerDraftKey === undefined) return
    const scope = composerAsyncScopeRef.current
    const reeditGeneration = recordReeditComposerRef.current?.generation
    if (reeditGeneration !== undefined) {
      requestAnimationFrame(() => {
        if (recordReeditComposerRef.current?.generation !== reeditGeneration || composerAsyncScopeRef.current !== scope || !activeConversationRef.current
          || arkmeAuthenticatedAccountKey(arkmeAuthStore.getSnapshot().auth) !== scope.accountKey) return
        const editor = textareaRef.current
        if (editor === null || editor.disabled) return
        editor.focus()
        editor.setSelectionRange(cursor, cursor)
        messagePreparing.input(arkmeComposerDraftStore.get(composerDraftKey).text)
      })
      return
    }
    const text = arkmeComposerDraftStore.get(composerDraftKey).text
    // The editor consumes this once, after its document has committed. A single
    // animation frame can run before a newly initialized editor is ready.
    setEditedComposerSelection({
      scope: composerAsyncScopeRef.current,
      request: { text, start: cursor, end: cursor },
    })
    messagePreparing.input(text)
  }, [composerDraftKey, messagePreparing.input])
  const insertReeditMentionAt = useCallback((identity: Pick<ArkmeComposerMention, 'mentionRef' | 'botRef' | 'all'>,
    displayName: string, start: number, end: number) => {
    const target = activeRecordReeditComposer
    if (!target || target.loading || target.busy || !target.snapshot
      || preparationJobs.current.has(arkmeRecordReeditPreparationKey(target))) return
    const inserted = insertArkmeComposerMentionToken({ text: target.textContent, mentions: target.mentions ?? [], emojis: [] }, identity, displayName, start, end, target.snapshot.textFormat ?? target.item.textFormat)
    if (!inserted) return
    setRecordReeditComposer(current => sameArkmeRecordReeditSession(current, target)
      ? { ...current, textContent: inserted.text, mentions: inserted.mentions, error: '' } : current)
    setMentionTrigger(undefined)
    setMemberMenu(undefined)
    focusEditedComposer(inserted.caretIndex)
  }, [activeRecordReeditComposer, setRecordReeditComposer, focusEditedComposer])
  const insertMemberMentionAt = useCallback((
    member: ArkmeConversationMemberItem,
    selectionStart: number,
    selectionEnd = selectionStart,
  ) => {
    if (composerDraftKey === undefined
      || member.isSelf
      || member.mentionRef === undefined
      || member.mentionDisplayName === undefined
      || !composerMentionsEnabled) return
    if (activeRecordReeditComposer !== undefined) {
      insertReeditMentionAt({ mentionRef: member.mentionRef }, member.mentionDisplayName, selectionStart, selectionEnd)
      return
    }
    syncComposerUserInput(true)
    const cursor = arkmeComposerDraftStore.insertMention(
      composerDraftKey,
      member.mentionRef,
      member.mentionDisplayName,
      selectionStart,
      selectionEnd,
    )
    setMemberMenu(undefined)
    setMentionTrigger(undefined)
    if (cursor === undefined) return
    focusEditedComposer(cursor)
  }, [activeRecordReeditComposer, insertReeditMentionAt, composerDraftKey, composerMentionsEnabled, focusEditedComposer, syncComposerUserInput])

  const insertEmoji = useCallback((emoji: ArkmeEmoji) => {
    if (composerDraftKey === undefined || busy) return
    syncComposerUserInput(true)
    const textarea = textareaRef.current
    const start = textarea?.selectionStart ?? draft.length
    const end = textarea?.selectionEnd ?? start
    const caretIndex = arkmeComposerDraftStore.insertEmoji(composerDraftKey, emoji, start, end)
    if (caretIndex === undefined) return
    focusEditedComposer(caretIndex)
  }, [busy, composerDraftKey, draft, focusEditedComposer, syncComposerUserInput])
  const insertMemberMention = useCallback((member: ArkmeConversationMemberItem) => {
    const textarea = textareaRef.current
    const start = textarea?.selectionStart ?? draft.length
    const end = textarea?.selectionEnd ?? start
    insertMemberMentionAt(member, start, end)
  }, [draft.length, insertMemberMentionAt])
  const insertBotMention = useCallback((botRef: string, displayName: string) => {
    const textarea = textareaRef.current
    const start = textarea?.selectionStart ?? draft.length
    const end = textarea?.selectionEnd ?? start
    if (activeRecordReeditComposer !== undefined) {
      insertReeditMentionAt({ botRef }, displayName, start, end)
      setBotMenu(undefined)
      return
    }
    if (composerDraftKey === undefined || !composerMentionsEnabled) return
    syncComposerUserInput(true)
    const cursor = arkmeComposerDraftStore.insertBotMention(composerDraftKey, botRef, displayName, start, end)
    setBotMenu(undefined)
    if (cursor !== undefined) focusEditedComposer(cursor)
  }, [activeRecordReeditComposer, composerDraftKey, composerMentionsEnabled, draft.length,
    focusEditedComposer, insertReeditMentionAt, syncComposerUserInput])
  const insertMentionCandidate = useCallback((member: ArkmeMentionCandidate) => {
    if (mentionTrigger === undefined) return
    if (activeRecordReeditComposer !== undefined && member.kind !== 'member') {
      if (member.kind === 'all') insertReeditMentionAt({ all: true }, '所有人', mentionTrigger.startIndex, mentionTrigger.endIndex)
      else if (member.kind === 'bot') insertReeditMentionAt({ botRef: member.botRef }, member.displayName, mentionTrigger.startIndex, mentionTrigger.endIndex)
      return
    }
    if (member.kind === 'all') {
      if (composerDraftKey === undefined || !composerMentionsEnabled) return
      syncComposerUserInput(true)
      const cursor = arkmeComposerDraftStore.insertAllMention(
        composerDraftKey,
        mentionTrigger.startIndex,
        mentionTrigger.endIndex,
      )
      setMentionTrigger(undefined)
      if (cursor === undefined) return
      focusEditedComposer(cursor)
      return
    }
    if (member.kind === 'bot') {
      if (composerDraftKey === undefined || !composerMentionsEnabled) return
      syncComposerUserInput(true)
      const cursor = arkmeComposerDraftStore.insertBotMention(
        composerDraftKey,
        member.botRef,
        member.displayName,
        mentionTrigger.startIndex,
        mentionTrigger.endIndex,
      )
      setMentionTrigger(undefined)
      if (cursor === undefined) return
      focusEditedComposer(cursor)
      return
    }
    insertMemberMentionAt(member, mentionTrigger.startIndex, mentionTrigger.endIndex)
  }, [activeRecordReeditComposer, insertReeditMentionAt, composerDraftKey, composerMentionsEnabled, focusEditedComposer, insertMemberMentionAt, mentionTrigger, syncComposerUserInput])
  const insertHashTagCandidate = useCallback((item: ArkmeRecordTagItem) => {
    if (activeRecordReeditComposer !== undefined || hashTagTrigger === undefined || composerDraftKey === undefined) return
    syncComposerUserInput(true)
    const cursor = arkmeComposerDraftStore.insertHashTag(
      composerDraftKey,
      item.tagText,
      hashTagTrigger.startIndex,
      hashTagTrigger.endIndex,
    )
    setHashTagTrigger(undefined)
    activeHashTagStartRef.current = undefined
    dismissedHashTagStartRef.current = undefined
    if (cursor === undefined) return
    focusEditedComposer(cursor)
  }, [activeRecordReeditComposer, composerDraftKey, focusEditedComposer, hashTagTrigger, syncComposerUserInput])
  const updateComposerRichTrigger = useCallback((text: string, selectionStart: number, selectionEnd: number) => {
    if (activeRecordReeditComposer !== undefined) {
      setHashTagTrigger(undefined)
      activeHashTagStartRef.current = undefined
      dismissedHashTagStartRef.current = undefined
      const trigger = composerMentionsEnabled ? arkmeComposerMentionTrigger(text, selectionStart, selectionEnd) : undefined
      const textFormat = activeRecordReeditComposer.snapshot?.textFormat ?? activeRecordReeditComposer.item.textFormat
      setMentionTrigger(trigger && textFormat === 'markdown'
        && !arkmeMarkdownTextRanges(text).some(range => trigger.startIndex >= range.start && trigger.endIndex <= range.end) ? undefined : trigger)
      return
    }
    const nextHashTagTrigger = arkmeHashTagTrigger(text, selectionStart, selectionEnd)
    if (nextHashTagTrigger === undefined) {
      activeHashTagStartRef.current = undefined
      dismissedHashTagStartRef.current = undefined
    }
    if (nextHashTagTrigger !== undefined && dismissedHashTagStartRef.current === nextHashTagTrigger.startIndex) {
      setHashTagTrigger(undefined)
      return
    }
    if (nextHashTagTrigger !== undefined && activeHashTagStartRef.current !== nextHashTagTrigger.startIndex) {
      activeHashTagStartRef.current = nextHashTagTrigger.startIndex
      setHashTagRefreshRevision(value => value + 1)
    }
    setHashTagTrigger(nextHashTagTrigger)
    if (nextHashTagTrigger !== undefined) {
      setMentionTrigger(undefined)
      return
    }
    if (!composerMentionsEnabled) {
      setMentionTrigger(undefined)
      return
    }
    setMentionTrigger(arkmeComposerMentionTrigger(text, selectionStart, selectionEnd))
  }, [activeRecordReeditComposer, composerMentionsEnabled])
  const resolveSendToSelfSource = useCallback(async (signal: AbortSignal): Promise<ArkmeSourceItem> => {
    if (aggregateSource !== undefined) return aggregateSource
    if (authenticatedUserId === undefined) {
      throw new Error('发给自己暂不可用，请稍后重试')
    }
    const page = await callArkme<ArkmeSourceList>('sources.list', {
      directory: 'send_to_self',
      limit: 100,
    }, signal)
    if (signal.aborted) throw new Error('发给自己已取消')
    const sendToSelfSource = arkmeSendToSelfRootSource(page.items)
    if (sendToSelfSource === undefined) {
      throw new Error('发给自己暂不可用，请稍后重试')
    }
    const defaultCategorySource = page.items.find(item => item.kind === 'default_category')
    if (defaultCategorySource !== undefined) {
      setSelfSourcesResolution({
        userId: authenticatedUserId,
        resolution: {
          status: 'ready',
          aggregateSource: sendToSelfSource,
          defaultCategorySource,
          sources: page.items,
          loading: false,
        },
      })
    }
    return sendToSelfSource
  }, [aggregateSource, authenticatedUserId])
  const openPrivateChatForMember = useCallback((member: ArkmeConversationMemberItem) => {
    if (source === undefined || privateChatBusy || privateChatAbortRef.current !== undefined) return
    setPrivateChatBusy(true)
    setError('')
    if (arkmeMemberConversationAction(member) === 'send_to_self') {
      const controller = new AbortController()
      privateChatAbortRef.current = controller
      void resolveSendToSelfSource(controller.signal)
        .then(result => {
          if (controller.signal.aborted) return
          setMemberProfile(undefined)
          setMemberRecords(undefined)
          activateSource(result)
        })
        .catch(caught => { if (!controller.signal.aborted) setError(errorMessage(caught)) })
        .finally(() => {
          if (privateChatAbortRef.current !== controller) return
          privateChatAbortRef.current = undefined
          setPrivateChatBusy(false)
        })
      return
    }
    const controller = new AbortController()
    privateChatAbortRef.current = controller
    void callArkme<ArkmeOpenPrivateChatResult>('chat.member.private.open', {
      sourceRef: source.sourceRef,
      memberRef: member.memberRef,
    }, controller.signal)
      .then(result => {
        if (controller.signal.aborted) return
        setMemberProfile(undefined)
        setMemberRecords(undefined)
        activateSource(result.source)
      })
      .catch(caught => { if (!controller.signal.aborted) setError(errorMessage(caught)) })
      .finally(() => {
        if (privateChatAbortRef.current !== controller) return
        privateChatAbortRef.current = undefined
        setPrivateChatBusy(false)
      })
  }, [activateSource, privateChatBusy, resolveSendToSelfSource, source])
  useEffect(() => {
    if (memberMenu === undefined) return
    const close = () => { setMemberMenu(undefined) }
    const body = bodyRef.current
    body?.addEventListener('scroll', close, { passive: true })
    window.addEventListener('resize', close)
    return () => {
      body?.removeEventListener('scroll', close)
      window.removeEventListener('resize', close)
    }
  }, [memberMenu])
  const activateSelfSource = useCallback((nextSource: ArkmeTimelinePage['source']) => {
    activateSource(nextSource)
  }, [activateSource])
  const activateSendToSelf = useCallback(() => {
    arkmeUi.focusSendToSelf()
    arkmeUi.chatChanged()
  }, [])
  const acceptSelfSourcesResolution = useCallback((
    userId: number,
    resolution: ArkmeSelfSourcesResolution,
  ) => {
    setSelfSourcesResolution({ userId, resolution })
  }, [])
  const invalidateTopicSelection = useCallback(() => {
    arkmeUi.focusSendToSelf()
  }, [])

  const loadMomentRelated = async (moment: ArkmeInterwovenMention) => {
    if (source === undefined || source.kind !== 'private_chat') return
    momentRelatedRequestRef.current?.abort()
    const controller = new AbortController()
    const generation = ++momentRelatedGenerationRef.current
    momentRelatedRequestRef.current = controller
    setMomentRelatedState({ kind: 'loading' })
    try {
      const list = await callArkme<ArkmeRelatedQuickNoteList>('source.related-quick-notes.from-moment', {
        sourceRef: source.sourceRef,
        momentRef: moment.momentRef,
      }, controller.signal)
      if (controller.signal.aborted || momentRelatedRequestRef.current !== controller
        || generation !== momentRelatedGenerationRef.current) return
      setMomentRelatedState(list.items.length === 0 ? { kind: 'empty' } : { kind: 'success', list })
    } catch (caught) {
      if (controller.signal.aborted || momentRelatedRequestRef.current !== controller
        || generation !== momentRelatedGenerationRef.current) return
      setMomentRelatedState({ kind: 'error', message: errorMessage(caught) })
    } finally {
      if (momentRelatedRequestRef.current === controller) momentRelatedRequestRef.current = undefined
    }
  }

  const loadMomentRelatedDetail = async (relatedItem: ArkmeRelatedQuickNoteItem) => {
    if (source === undefined || source.kind !== 'private_chat') return
    momentRelatedDetailRequestRef.current?.abort()
    const controller = new AbortController()
    momentRelatedDetailRequestRef.current = controller
    setMomentRelatedView('related-detail')
    setMomentRelatedDetailState({ kind: 'loading' })
    try {
      const detail = await callArkme<ArkmeRelatedQuickNoteDetail>('source.related-quick-note.detail', {
        sourceRef: source.sourceRef,
        relatedRef: relatedItem.relatedRef,
      }, controller.signal)
      if (controller.signal.aborted || momentRelatedDetailRequestRef.current !== controller) return
      setMomentRelatedDetailState({ kind: 'success', item: relatedItem, detail })
    } catch (caught) {
      if (controller.signal.aborted || momentRelatedDetailRequestRef.current !== controller) return
      if (relatedQuickNoteReferenceExpired(caught)) {
        setMomentRelatedDetailState({ kind: 'idle' })
        setMomentRelatedView('related-list')
        if (selectedMoment !== undefined) await loadMomentRelated(selectedMoment)
        return
      }
      setMomentRelatedDetailState({ kind: 'error', item: relatedItem, message: errorMessage(caught) })
    } finally {
      if (momentRelatedDetailRequestRef.current === controller) momentRelatedDetailRequestRef.current = undefined
    }
  }

  const loadMomentDetail = async (moment: ArkmeInterwovenMention, force = false) => {
    if (source === undefined || source.kind !== 'private_chat') return
    if (!force && detailRequestMomentRef.current === moment.momentId) return
    detailRequestRef.current?.abort()
    momentRelatedGenerationRef.current += 1
    momentRelatedRequestRef.current?.abort()
    momentRelatedRequestRef.current = undefined
    momentRelatedDetailRequestRef.current?.abort()
    momentRelatedDetailRequestRef.current = undefined
    const controller = new AbortController()
    detailRequestRef.current = controller
    detailRequestMomentRef.current = moment.momentId
    setSelectedMoment(moment)
    setDetailState({ kind: 'loading' })
    setMomentRelatedView('source-detail')
    setMomentRelatedState({ kind: 'idle' })
    setMomentRelatedDetailState({ kind: 'idle' })
    try {
      const detail = await callArkme<ArkmeInterwovenDetail>('source.interwoven-detail', {
        sourceRef: source.sourceRef,
        momentRef: moment.momentRef,
      }, controller.signal)
      if (detailRequestRef.current !== controller) return
      setDetailState({ kind: 'success', detail })
      void loadMomentRelated(moment)
    } catch (caught) {
      if (detailRequestRef.current !== controller) return
      setDetailState({ kind: 'error', message: errorMessage(caught) })
    } finally {
      if (detailRequestRef.current === controller) {
        detailRequestRef.current = undefined
        detailRequestMomentRef.current = ''
      }
    }
  }

  const openMomentDetail = (moment: ArkmeInterwovenMention) => {
    if (selectedMoment?.momentId === moment.momentId
      && detailState !== undefined && detailState.kind !== 'error') return
    activateContextPanel('moment')
    void loadMomentDetail(moment)
  }

  const closeMomentDetail = () => {
    detailRequestRef.current?.abort()
    detailRequestRef.current = undefined
    detailRequestMomentRef.current = ''
    momentRelatedGenerationRef.current += 1
    momentRelatedRequestRef.current?.abort()
    momentRelatedRequestRef.current = undefined
    momentRelatedDetailRequestRef.current?.abort()
    momentRelatedDetailRequestRef.current = undefined
    setSelectedMoment(undefined)
    setDetailState(undefined)
    setMomentRelatedView('source-detail')
    setMomentRelatedState({ kind: 'idle' })
    setMomentRelatedDetailState({ kind: 'idle' })
  }

  const backMomentRelated = () => {
    if (momentRelatedView === 'related-detail') {
      momentRelatedDetailRequestRef.current?.abort()
      momentRelatedDetailRequestRef.current = undefined
    }
    setMomentRelatedView(relatedDrawerBackTarget(momentRelatedView))
  }

  const refreshCurrentWindow = useCallback(() => loadTimeline(undefined, true, 40, 'refresh'), [loadTimeline])
  const reeditSubmissions = useRecordReeditSubmissions(
    source?.sourceRef, authenticatedAccountKey, activeConversation,
    timelineStateKey === conversationKey ? items : [], refreshCurrentWindow, conversationKey,
  )
  const { displayItems, reeditItems } = useMemo(() => {
    const remoteIds = new Set(items.map(item => item.itemUid))
    const reeditItems = new Set<ArkmeTimelineItem>()
    const remoteItems = items.map(item => {
      const bound = bindSentFileTaskLocals(item, fileTasks.tasks)
      const projected = projectRecordReedit(bound, reeditSubmissions.jobs)
      if (projected !== bound) reeditItems.add(projected)
      return projected
    })
    const displayItems = [...remoteItems, ...fileTasks.tasks.filter(task => !remoteIds.has(task.result?.itemUid ?? task.recordUid)).map(fileTaskTimelineItem)]
      .sort(compareTimelineMessages)
    return { displayItems, reeditItems }
  }, [items, fileTasks.tasks, reeditSubmissions.jobs])
  useEffect(() => {
    for (const job of reeditSubmissions.jobs) {
      if (job.state === 'pending' || job.state === 'committing') pendingReeditHighlights.current.add(job.submissionId)
    }
    const committed = reeditSubmissions.jobs.filter(job => job.state === 'committed'
      && pendingReeditHighlights.current.has(job.submissionId) && !seenReeditCommits.current.has(job.submissionId))
    if (committed.length === 0) return
    for (const job of committed) seenReeditCommits.current.add(job.submissionId)
    const latest = committed[committed.length - 1]!
    if (recordReeditHighlightTimerRef.current !== undefined) window.clearTimeout(recordReeditHighlightTimerRef.current)
    setHighlightedTargetUid('')
    setRecordReeditHighlightUid(latest.itemUid)
    recordReeditHighlightTimerRef.current = window.setTimeout(() => {
      recordReeditHighlightTimerRef.current = undefined
      setRecordReeditHighlightUid('')
    }, ARKME_RECORD_REEDIT_HIGHLIGHT_MILLIS)
  }, [reeditSubmissions.jobs])
  const visibleConversationJoinEvents = useMemo(
    () => source?.kind === 'group_chat'
      ? arkmeConversationJoinEventsInLoadedWindow(conversationJoinEvents, displayItems, hasMore)
      : [],
    [conversationJoinEvents, displayItems, hasMore, source?.kind],
  )
  const memberEventScope = `${authenticatedAccountKey ?? ''}:${conversationKey}:${timelineMode}:${memberEventWindowRevision}`
  useEffect(() => { setMemberEventProfile(undefined) }, [activeConversation, authenticated, groupSelfRole, memberEventScope])
  const memberEventTimeline = useMemberEventTimeline({
    enabled: activeConversation && authenticated && source?.kind === 'group_chat' && groupSelfRole === 'owner',
    restoreCached: activeConversation && authenticated && source?.kind === 'group_chat',
    accessRevoked: source?.kind === 'group_chat' && groupSelfRole !== 'unknown' && groupSelfRole !== 'owner',
    accountKey:authenticatedAccountKey, sourceKey:conversationKey, sourceRef:source?.sourceRef ?? '',
    mode:timelineMode, windowRevision:memberEventWindowRevision,
    ready:timelineStateKey === conversationKey && timelineLoadingKey !== conversationKey,
    windowReady: timelineStateKey === conversationKey && (timelineLoadingKey !== conversationKey
      || (cacheAccountKeyRef.current === authenticatedAccountKey && conversationCacheRef.current.getTimeline(conversationKey) !== undefined)),
    items, hasMoreMessages:hasMore,
    paginationKey:JSON.stringify([nextCursor,newerCursor,hasMore,newerHasMore]),
    bodyRef,
    beforeChange:() => {
      const body=bodyRef.current
      if (body !== null && pendingViewportRestoreRef.current === undefined) {
        pendingViewportRestoreRef.current={sourceKey:conversationKey,viewport:arkmeConversationViewport(body)}
      }
    },
  })
  const openMemberEventProfile = (event:ArkmeMemberEvent) => {
    setMemberProfile(undefined)
    setMemberRecords(undefined)
    setMemberMenu(undefined)
    setMemberEventProfile({scope:memberEventScope,event})
  }
  const interwovenWindow = useMemo(
    () => projectInterwovenWindow(displayItems, interwovenMoments, hasMore,
      calendarMomentWindow?.sourceKey === conversationKey ? calendarMomentWindow.fromMillis : undefined),
    [displayItems, interwovenMoments, hasMore, calendarMomentWindow, conversationKey],
  )
  const displayRows = useMemo<Array<ArkmeConversationRow | {
    kind: 'community-welcome'; id: string; occurredAtMillis: number; title: string
  } | {
    kind: 'notice'; id: string; occurredAtMillis: number; item: ArkmeGroupAiPolishNotice
  } | {
    kind: 'member-join'; id: string; occurredAtMillis: number; item: ArkmeConversationMemberJoinEvent
  } | {
    kind:'member-event-gap'; id:string; occurredAtMillis:number; gapId:string
  }>>(
    () => [
      ...(communityWelcome !== undefined && source !== undefined && welcomeMatchesSource(communityWelcome, authenticatedAccountKey, source)
        && timelineMode !== 'around'
        ? [{ kind: 'community-welcome' as const, id: `community-welcome:${communityWelcome.occurredAtMillis}`,
          occurredAtMillis: communityWelcome.occurredAtMillis, title: source.displayName }] : []),
      ...mergeConversationRows(displayItems, interwovenWindow.inline, memberEventTimeline.events),
      ...memberEventTimeline.gaps.map(gap => ({kind:'member-event-gap' as const,id:`member-event-gap:${gap.id}`,occurredAtMillis:gap.at,gapId:gap.id})),
      ...aiPolishNotices.map(notice => ({
        kind: 'notice' as const,
        id: `notice:${notice.noticeUid}`,
        occurredAtMillis: notice.createdAtMillis,
        item: notice,
      })),
      ...visibleConversationJoinEvents.map(event => ({
        kind: 'member-join' as const,
        id: `member-join:${event.eventId}`,
        occurredAtMillis: event.occurredAtMillis,
        item: event,
      })),
    ].sort((left, right) => left.kind === 'message' && right.kind === 'message'
      ? compareTimelineMessages(left.item, right.item)
      : left.occurredAtMillis - right.occurredAtMillis || left.id.localeCompare(right.id)),
    [aiPolishNotices, displayItems, interwovenWindow, visibleConversationJoinEvents,memberEventTimeline.events,memberEventTimeline.gaps,
      communityWelcome, authenticatedAccountKey, source?.sourceRef, source?.sourceKey, source?.kind, source?.displayName, timelineMode],
  )
  useLayoutEffect(() => {
    const pending = pendingConversationTargetLocateRef.current
    const body = bodyRef.current
    if (pending === undefined || body === null || pending.sourceKey !== timelineStateKey) return
    const row = arkmeConversationTargetRow(body, pending)
    if (row === undefined) return
    const calendarLocate = ownsCalendarNavigation(pending.revision)
    pendingConversationTargetLocateRef.current = undefined
    pendingViewportRestoreRef.current = undefined
    viewportRestoreIntentRef.current = false
    body.scrollTo({ top: arkmeConversationTargetScrollTop(body, row, calendarLocate ? 'start' : 'center'), behavior: 'auto' })
    const highlightUid = pending.momentId ? `moment:${pending.momentId}` : pending.itemUid
    conversationTargetLocatedRevisionRef.current = pending.revision
    const calendarFinished = finishCalendarNavigation(pending.revision)
    highlightLocatedMessage(body, row, highlightUid, pending.transientHighlight === true, calendarFinished || pending.transientHighlight === true)
    if (arkmeUi.getSnapshot().conversationTarget?.openDetail === true) openLocatedDetail(pending.itemUid, pending.revision)
    arkmeUi.consumeConversationTarget(pending.revision)
  }, [displayRows, timelineStateKey, finishCalendarNavigation, ownsCalendarNavigation, openLocatedDetail])
  const activeSelectMode = selectMode?.sourceKey === conversationKey ? selectMode : undefined
  const exitMessageSelectMode = useCallback(() => {
    const body = bodyRef.current
    if (activeSelectMode !== undefined && body !== null) {
      // Exit at the current reading position, including after scrolling in select
      // mode. Never restore the message that originally opened selection.
      pendingViewportRestoreRef.current = {
        sourceKey: timelineStateKey,
        viewport: arkmeConversationViewport(body, true),
      }
    }
    setSelectMode(undefined)
  }, [activeSelectMode, timelineStateKey])
  const selectedMessageItems = useMemo(
    () => activeSelectMode === undefined ? [] : arkmeSelectedTimelineItems(displayItems, activeSelectMode.selectedIds),
    [activeSelectMode, displayItems],
  )
  const selectedMessageCount = activeSelectMode?.selectedIds.size ?? 0
  const selectedMessagesWithinBatchLimit = selectedMessageCount > 0 && selectedMessageCount <= 100
  const selectedMessagesCanDelete = selectedMessagesWithinBatchLimit && selectedMessageItems.length === selectedMessageCount
    && selectedMessageItems.every(item => Boolean(item.recordDeletionRef))
  const selectedMessagesHaveSnapshots = selectedMessageCount > 0 && selectedMessageItems.length === selectedMessageCount
    && selectedMessageItems.every(item => arkmeTimelineMessageActionRef(item) !== '')
  const selectedMessagesSupportSnapshotBatch = selectedMessagesWithinBatchLimit && selectedMessagesHaveSnapshots
  const selectedMessagesCanAskDsh = selectedMessagesSupportSnapshotBatch && selectedMessageItems.every(item => item.quickNoteDetailsSupported !== false)
  const forwardTargets = useMemo(() => arkmeForwardableTargetSources(
    forwardDirectory.chats, forwardDirectory.self, forwardTargetPicker?.selectedTargetKeys,
  ), [forwardDirectory.chats, forwardDirectory.self, forwardTargetPicker?.selectedTargetKeys])
  const forwardTargetsRef = useRef(forwardTargets)
  forwardTargetsRef.current = forwardTargets
  useEffect(() => {
    setForwardTargetPicker(current => {
      if (current === undefined) return current
      const keys = current.selectedTargetKeys.filter(key => forwardTargets.some(target => arkmeForwardTargetKey(target) === key))
      return keys.length === current.selectedTargetKeys.length ? current : { ...current, selectedTargetKeys: keys }
    })
  }, [forwardTargets])
  const forwardVisibleTargets = useMemo(
    () => forwardTargetPicker === undefined
      ? []
      : arkmeForwardTargetVisibleSources(forwardTargets, forwardTargetPicker.keyword),
    [forwardTargetPicker, forwardTargets],
  )
  const forwardSelectedTargets = useMemo(
    () => forwardTargetPicker === undefined
      ? []
      : forwardTargetPicker.selectedTargetKeys
        .map(key => forwardTargets.find(target => arkmeForwardTargetKey(target) === key))
        .filter((target): target is ArkmeSourceItem => target !== undefined),
    [forwardTargetPicker, forwardTargets],
  )
  const forwardSelfTarget = forwardVisibleTargets.find(target => target.kind === 'send_to_self')
  const renderForwardTargetButton = (target: ArkmeSourceItem) => {
    const selected = forwardTargetPicker?.selectedTargetKeys.includes(arkmeForwardTargetKey(target)) === true
    return (
      <button data-arkme-feedback="neutral"
        type="button"
        style={{ ...styles.forwardTargetRow, ...(selected ? styles.forwardTargetRowSelected : {}) }}
        aria-pressed={selected}
        onClick={() => { toggleForwardTarget(target) }}
      >
        {target.isPinned === true && <ArkmePinnedCorner />}
        <span
          style={{ ...styles.forwardTargetCheck, ...(selected ? styles.forwardTargetCheckSelected : {}) }}
          aria-hidden
        >✓</span>
        <ArkmeDirectorySourceAvatar source={target} size={38} />
        <span style={styles.forwardTargetText}>
          <span style={styles.forwardTargetName}><ArkmeRichText text={target.displayName} presentation="preview" /></span>
          <span style={styles.forwardTargetMeta}><ArkmeRichText text={target.latestPreview?.trim() || arkmeForwardTargetMeta(target)} presentation="preview" /></span>
        </span>
        <span style={styles.forwardTargetTime}>
          {arkmeForwardTargetTimeLabel(target.activeAtMillis)}
        </span>
      </button>
    )
  }
  const forwardPickerMessageItems = forwardTargetPicker?.items ?? []
  const messageMenuItem = useMemo(
    () => messageMenu === undefined
      ? undefined
      : displayItems.find(item => arkmeTimelineOccurrenceKey(item) === messageMenu.occurrenceKey),
    [displayItems, messageMenu],
  )
  useEffect(() => {
    const visibleOccurrenceKeys = new Set(displayItems.map(arkmeTimelineOccurrenceKey))
    setMessageMenu(current => current === undefined || visibleOccurrenceKeys.has(current.occurrenceKey) ? current : undefined)
    setSnapshot(current => current === undefined || visibleOccurrenceKeys.has(arkmeTimelineOccurrenceKey(current.item)) ? current : undefined)
    setMessageReportItem(current => current === undefined || visibleOccurrenceKeys.has(arkmeTimelineOccurrenceKey(current)) ? current : undefined)
    setMessageWithdrawalItem(current => current === undefined || visibleOccurrenceKeys.has(arkmeTimelineOccurrenceKey(current)) ? current : undefined)
    if (activeSelectMode !== undefined && activeSelectMode.selectedIds.size > 0
      && ![...activeSelectMode.selectedIds].some(key => visibleOccurrenceKeys.has(key))) {
      exitMessageSelectMode()
    } else setSelectMode(current => {
      if (current === undefined || current.sourceKey !== conversationKey) return current
      const selectedIds = new Set([...current.selectedIds].filter(key => visibleOccurrenceKeys.has(key)))
      if (selectedIds.size === current.selectedIds.size) return current
      return selectedIds.size === 0 ? undefined : { ...current, selectedIds }
    })
    setForwardTargetPicker(current => {
      if (current === undefined || current.native || current.sourceKey !== conversationKey) return current
      const items = current.items.filter(item => visibleOccurrenceKeys.has(arkmeTimelineOccurrenceKey(item)))
      if (items.length === current.items.length) return current
      return items.length === 0 ? undefined : { ...current, items, itemUids: items.map(item => item.itemUid) }
    })
  }, [activeSelectMode, conversationKey, displayItems, exitMessageSelectMode])
  const showMessageActionStatus = useCallback((message: string, autoClear = true) => {
    if (messageActionStatusTimerRef.current !== undefined) {
      window.clearTimeout(messageActionStatusTimerRef.current)
      messageActionStatusTimerRef.current = undefined
    }
    setMessageActionStatus(message)
    if (message === '' || !autoClear) return
    messageActionStatusTimerRef.current = window.setTimeout(() => {
      setMessageActionStatus('')
      messageActionStatusTimerRef.current = undefined
    }, MESSAGE_ACTION_NOTICE_MS)
  }, [])
  const startConversationExport = useCallback((target: ArkmeSourceItem | undefined) => {
    if (target === undefined) return
    if (conversationExportAbortRef.current !== undefined) return
    const controller = new AbortController()
    const targetKey = arkmeSourceIdentityKey(target)
    const targetName = target.displayName.trim() || '当前对话'
    const exportedAt = new Date()
    conversationExportAbortRef.current = controller
    setConversationExportToast(undefined)
    setConversationExport({ sourceKey: targetKey, sourceName: targetName, processed: 0, status: 'downloading' })
    setRelatedMenuOpen(false)
    setSelfMenuOpen(false)
    void Promise.all([
      collectArkmeConversationExportItems(cursor => callArkme<ArkmeTimelinePage>('source.timeline', {
        sourceRef: target.sourceRef,
        limit: 100,
        ...(cursor === undefined ? {} : { cursor }),
      }, controller.signal), controller.signal, progress => {
        if (conversationExportAbortRef.current !== controller) return
        setConversationExport(current => current?.status === 'downloading'
          ? { ...current, sourceKey: targetKey, sourceName: targetName, processed: progress.itemCount }
          : current)
      }),
      isArkmeSelfWorkspaceSource(target)
        ? collectArkmeConversationExportTopics(cursor => callArkme<ArkmeSourceList>('sources.list', {
          directory: 'send_to_self', limit: 100,
          ...(cursor === undefined ? {} : { cursor }),
        }, controller.signal), controller.signal)
        : Promise.resolve([] as ArkmeSourceItem[]),
    ]).then(([exportItems, topics]) => {
      if (controller.signal.aborted || conversationExportAbortRef.current !== controller) return
      const markdown = arkmeConversationExportMarkdown({ source: target, items: exportItems, topics, exportedAt })
      downloadArkmeConversationMarkdown(arkmeConversationExportFileName(target, exportedAt, topics), markdown)
      setConversationExport({
        sourceKey: targetKey,
        sourceName: targetName,
        processed: exportItems.length,
        itemCount: exportItems.length,
        status: 'success',
      })
      conversationExportToastSequenceRef.current += 1
      setConversationExportToast({
        sequence: conversationExportToastSequenceRef.current,
        kind: 'success',
        text: tr("已导出 {v0} 条内容，Markdown 文件已开始下载", { v0: String(exportItems.length) }),
      })
    }).catch(caught => {
      if (!isArkmeRequestAbort(caught, controller.signal)) {
        const failureMessage = errorMessage(caught) || '导出失败，请重试'
        setConversationExport({
          sourceKey: targetKey,
          sourceName: targetName,
          processed: 0,
          status: 'error',
          error: failureMessage,
        })
        conversationExportToastSequenceRef.current += 1
        setConversationExportToast({
          sequence: conversationExportToastSequenceRef.current,
          kind: 'error',
          text: failureMessage,
        })
      }
    }).finally(() => {
      if (conversationExportAbortRef.current !== controller) return
      conversationExportAbortRef.current = undefined
    })
  }, [])
  const showForwardSuccessFeedback = useCallback((targets: readonly ArkmeSourceItem[], successCount: number, failureCount: number) => {
    if (forwardSuccessTimerRef.current !== undefined) {
      window.clearTimeout(forwardSuccessTimerRef.current)
      forwardSuccessTimerRef.current = undefined
    }
    const message = failureCount > 0
      ? tr("已转发到 {v0} 个对象，{v1} 个失败", { v0: String(successCount), v1: String(failureCount) })
      : tr("已转发到 {v0} 个对象", { v0: String(successCount) })
    setForwardSuccessFeedback({ message, targets: [...targets] })
    forwardSuccessTimerRef.current = window.setTimeout(() => {
      setForwardSuccessFeedback(undefined)
      forwardSuccessTimerRef.current = undefined
    }, 4_000)
  }, [])
  const closeMessageMenu = useCallback(() => {
    setMessageMenu(undefined)
  }, [])
  const canAssignRecordTopics = (itemsToAssign: readonly ArkmeTimelineItem[]) => source !== undefined
    && isArkmeSelfWorkspaceSource(source) && messageActionBusy === undefined
    && itemsToAssign.length > 0 && itemsToAssign.length <= 100
    && itemsToAssign.every(item => Boolean(item.recordTopicAssignmentRef))
  const openRecordTopicAssignment = (itemsToAssign: readonly ArkmeTimelineItem[], anchor?: HTMLElement) => {
    if (!activeConversationRef.current || source === undefined || !canAssignRecordTopics(itemsToAssign)) return
    const currentTopicKey = source.kind === 'topic' ? source.topicHierarchyKey
      : itemsToAssign[0]?.recordTopicAssignmentTopicKey
    const allInCurrentTopic = currentTopicKey !== undefined && (source.kind === 'topic'
      || itemsToAssign.every(item => item.recordTopicAssignmentTopicKey === currentTopicKey))
    setTopicAssignment({ scopeKey: topicAssignmentScopeKey, source, anchor,
      ...(allInCurrentTopic ? { currentTopicKey } : {}),
      assignmentRefs: itemsToAssign.map(item => item.recordTopicAssignmentRef!),
      itemUids: itemsToAssign.map(item => item.itemUid),
    })
    closeMessageMenu()
  }
  const startMessageExtension = useCallback((item: ArkmeTimelineItem) => {
    const actionRef = arkmeTimelineMessageActionRef(item)
    closeMessageMenu()
    if (source === undefined || actionRef === '') return
    if (activeRecordReeditComposer !== undefined) {
      showMessageActionStatus('请先完成编辑后再进行延展')
      return
    }
    setComposerExtensionTarget({ sourceRef: source.sourceRef, item })
    setDrawer(undefined)
    requestAnimationFrame(() => { textareaRef.current?.focus() })
  }, [activeRecordReeditComposer, closeMessageMenu, showMessageActionStatus, source])
  const defaultExtensionMentionTargetRef = useRef<typeof composerExtensionTarget>()
  useEffect(() => {
    if (composerExtensionTarget === undefined) {
      defaultExtensionMentionTargetRef.current = undefined
      return
    }
    if (defaultExtensionMentionTargetRef.current === composerExtensionTarget
      || source?.kind !== 'group_chat' || source.sourceRef !== composerExtensionTarget.sourceRef
      || !activeConversation || activeRecordReeditComposer !== undefined) return
    const item = composerExtensionTarget.item
    const member = item.memberRef === undefined ? undefined : conversationMemberByRef.get(item.memberRef)
    if (member === undefined && !conversationMemberSnapshot.complete && conversationMemberSnapshot.error === undefined) return
    defaultExtensionMentionTargetRef.current = composerExtensionTarget
    if (item.isMe || member === undefined || member.isSelf || member.status !== 'active'
      || member.mentionRef === undefined || member.mentionDisplayName === undefined) return
    const current = arkmeComposerDraftStore.get(composerDraftKey)
    if (current.attachments.length > 0 || current.emojis.length > 0) return
    const mention = current.mentions.length === 1 ? current.mentions[0] : undefined
    const onlyMention = mention !== undefined && mention.all !== true
      && current.text.trim() === current.text.slice(mention.startIndex, mention.startIndex + mention.length).trim()
    if (current.text.trim() !== '' && !onlyMention) return
    insertMemberMentionAt(member, 0, current.text.length)
  }, [activeConversation, activeRecordReeditComposer, composerDraftKey, composerExtensionTarget,
    conversationMemberByRef, conversationMemberSnapshot.complete, conversationMemberSnapshot.error,
    insertMemberMentionAt, source])
  const closeRecordReedit = useCallback(async () => {
    const target = recordReeditComposerRef.current
    if (target === undefined || target.busy || preparationJobs.current.has(arkmeRecordReeditPreparationKey(target))) return
    setRecordReeditComposer(current => sameArkmeRecordReeditSession(current, target)
      ? { ...current, busy: true, error: '' }
      : current)
    try {
      if (target.snapshot !== undefined && !await persistRecordReeditDraft(target)) {
        updateRecordReeditCandidate(target, current => ({ ...current, busy: false }))
        return
      }
      const active = sameArkmeRecordReeditSession(recordReeditComposerRef.current, target)
      updateRecordReeditCandidate(target, () => undefined)
      if (active) {
        recordReeditGenerationRef.current += 1
        requestAnimationFrame(() => { textareaRef.current?.focus() })
      }
    } catch (caught) {
      updateRecordReeditCandidate(target, current => ({ ...current, busy: false, ...arkmeRecordReeditFailure(caught) }))
    }
  }, [persistRecordReeditDraft, updateRecordReeditCandidate])
  const commitRecordReedit = useCallback(async () => {
    const target = recordReeditComposerRef.current
    const targetSource = source
    if (target === undefined || target.snapshot === undefined || target.loading || target.busy || targetSource === undefined
      || preparationJobs.current.has(arkmeRecordReeditPreparationKey(target))
      || target.persisted.exclusive
      || target.sourceKey !== arkmeSourceIdentityKey(targetSource)
      || target.accountKey !== arkmeAuthenticatedAccountKey(arkmeAuthStore.getSnapshot().auth)) return
    const nextTitle = target.title.trim()
    const textFormat = target.snapshot.textFormat ?? target.item.textFormat
    const nextText = textFormat === 'markdown' ? target.textContent : target.textContent.trim()
    const supportsTitle = target.snapshot.displayKind === 1
    if ((nextText.trim() === '' && target.attachments.length === 0 && !target.snapshot.hasVoice) || (supportsTitle && nextTitle === '')) {
      setRecordReeditComposer(current => sameArkmeRecordReeditSession(current, target)
        ? { ...current, error: '请输入完整内容' }
        : current)
      return
    }
    if (nextTitle.length > 100 || nextText.length > target.snapshot.maxTextLength) {
      setRecordReeditComposer(current => sameArkmeRecordReeditSession(current, target)
        ? { ...current, error: '标题或正文长度超出限制' }
        : current)
      return
    }
    target.persisted.exclusive = true
    setRecordReeditComposer(current => sameArkmeRecordReeditSession(current, target)
      ? { ...current, busy: true, error: '' }
      : current)
    try {
      await persistRecordReeditDraft(target, true)
      await target.persisted.saveTail
      if (target.accountKey !== arkmeAuthenticatedAccountKey(arkmeAuthStore.getSnapshot().auth)) return
      const result = await callArkme<ArkmeRecordReeditSubmissionView>('source.record-reedit.submit', {
        sourceRef: target.sourceRef,
        itemUid: target.item.itemUid,
        newText: nextText,
        expectedVersion: target.snapshot.draft?.baseVersion ?? target.snapshot.version,
        expectedDraftRevision: target.persisted.draftRevision,
        attachments: target.attachments.map(attachment => attachment.selection),
        ...(supportsTitle ? { newTitle: nextTitle } : {}),
      })
      const active = sameArkmeRecordReeditSession(recordReeditComposerRef.current, target)
        && target.accountKey === arkmeAuthenticatedAccountKey(arkmeAuthStore.getSnapshot().auth)
        && recordReeditGenerationRef.current === target.generation
      reeditSubmissions.accepted(result)
      updateRecordReeditCandidate(target, () => undefined)
      if (active) {
        recordReeditGenerationRef.current += 1
        requestAnimationFrame(() => { textareaRef.current?.focus() })
      }
    } catch (caught) {
      updateRecordReeditCandidate(target, current => ({ ...current, busy: false, ...arkmeRecordReeditFailure(caught) }))
    } finally {
      target.persisted.exclusive = false
      updateRecordReeditCandidate(target, current => current.busy ? { ...current, busy: false } : current)
    }
  }, [persistRecordReeditDraft, source, reeditSubmissions.accepted, updateRecordReeditCandidate])
  const recoverRecordReedit = useCallback(async () => {
    const target = recordReeditComposerRef.current
    if (target === undefined || target.busy || !target.recoveryConfirmation
      || preparationJobs.current.has(arkmeRecordReeditPreparationKey(target))
      || target.persisted.exclusive) return
    const sameTarget = () => {
      const selected = arkmeUi.getSnapshot().selectedSource
      return selected !== undefined && arkmeSourceIdentityKey(selected) === target.sourceKey
        && arkmeAuthenticatedAccountKey(arkmeAuthStore.getSnapshot().auth) === target.accountKey
        && sameArkmeRecordReeditSession(recordReeditComposerRef.current, target)
        && recordReeditGenerationRef.current === target.generation
    }
    if (!sameTarget()) return
    target.persisted.exclusive = true
    setRecordReeditComposer(current => sameArkmeRecordReeditSession(current, target)
      ? { ...current, busy: true, error: '' } : current)
    try {
      await target.persisted.saveTail
      if (!sameTarget()) return
      let snapshot = await callArkme<ArkmeRecordReeditEditorSnapshot>('source.record-reedit.detail', {
        sourceRef: target.sourceRef, itemUid: target.item.itemUid,
      })
      if (!sameTarget()) return
      const draftChanged = (snapshot.draft?.draftRevision ?? 0) !== target.persisted.draftRevision
      if (target.conflict !== 'draft' && !draftChanged && snapshot.draft !== undefined) {
        await callArkme('source.record-reedit.draft.delete', {
          sourceRef: target.sourceRef, itemUid: target.item.itemUid,
          expectedDraftRevision: target.persisted.draftRevision,
        })
        if (!sameTarget()) return
        snapshot = await callArkme<ArkmeRecordReeditEditorSnapshot>('source.record-reedit.detail', {
          sourceRef: target.sourceRef, itemUid: target.item.itemUid,
        })
      }
      if (!sameTarget()) return
      const title = snapshot.draft?.title ?? snapshot.title
      const textContent = snapshot.draft?.textContent ?? snapshot.textContent
      const mentions = snapshot.draft?.mentions ?? snapshot.mentions ?? []
      const attachments = snapshot.draft?.attachments ?? snapshot.attachments ?? []
      setRecordReeditComposer(current => sameArkmeRecordReeditSession(current, target)
        ? {
          ...current, snapshot, title, textContent, mentions, attachments,
          persisted: { candidateKey: arkmeRecordReeditCandidateKey(title, textContent, attachments, snapshot.textFormat ?? current.item.textFormat, mentions), draftRevision: snapshot.draft?.draftRevision ?? 0, saveTail: Promise.resolve() },
          busy: false, conflict: undefined, recoveryConfirmation: false,
          error: draftChanged ? '已重新载入其他入口更新的草稿，请检查后再决定是否放弃' : '',
        } : current)
      setDraftPreview(undefined)
    } catch (caught) {
      if (sameTarget()) setRecordReeditComposer(current => sameArkmeRecordReeditSession(current, target)
        ? { ...current, busy: false, ...arkmeRecordReeditFailure(caught) } : current)
    } finally {
      target.persisted.exclusive = false
      updateRecordReeditCandidate(target, current => current.busy ? { ...current, busy: false } : current)
    }
  }, [updateRecordReeditCandidate])
  const openRecordReedit = useCallback(async (item: ArkmeTimelineItem) => {
    closeMessageMenu()
    if (source === undefined || !arkmeCanReeditTimelineMessage(item)) return
    const submission = reeditSubmissions.jobs.find(job => job.itemUid === item.itemUid)
    if (submission && ['pending', 'committing', 'uncertain'].includes(submission.state)) {
      showMessageActionStatus('这条快记仍在保存或核对，请稍后再编辑；其他消息不受影响')
      return
    }
    const previous = recordReeditComposerRef.current
    if (previous !== undefined) {
      if (previous.accountKey === authenticatedAccountKey && previous.sourceKey === arkmeSourceIdentityKey(source)
        && previous.item.itemUid === item.itemUid) {
        requestAnimationFrame(() => { textareaRef.current?.focus() })
        return
      }
      persistRecordReeditBeforeContextExit(previous, () => true)
    }
    const generation = ++recordReeditGenerationRef.current
    const retained = findRecordReeditCandidate({ accountKey: authenticatedAccountKey,
      sourceKey: arkmeSourceIdentityKey(source), sourceRef: source.sourceRef }, item.itemUid)
    const target: ArkmeRecordReeditComposerState = retained === undefined ? {
      generation,
      accountKey: authenticatedAccountKey,
      sourceKey: arkmeSourceIdentityKey(source),
      sourceRef: source.sourceRef,
      item,
      snapshot: undefined,
      title: item.title,
      textContent: item.textContent,
      mentions: [],
      attachments: [],
      persisted: { candidateKey: arkmeRecordReeditCandidateKey(item.title, item.textContent, [], item.textFormat), draftRevision: 0, saveTail: Promise.resolve() },
      loading: true,
      busy: false,
      error: '',
    } : { ...retained, generation }
    setComposerExtensionTarget(undefined)
    setAddMenuOpen(false)
    setHashTagTrigger(undefined)
    activeHashTagStartRef.current = undefined
    dismissedHashTagStartRef.current = undefined
    setMentionTrigger(undefined)
    setDraftPreview(undefined)
    setDrawer(undefined)
    setRecordReeditComposer(target)
    if (retained !== undefined) {
      requestAnimationFrame(() => { textareaRef.current?.focus() })
      return
    }
    try {
      const snapshot = await callArkme<ArkmeRecordReeditEditorSnapshot>('source.record-reedit.detail', {
        sourceRef: source.sourceRef,
        itemUid: item.itemUid,
      })
      if (recordReeditGenerationRef.current !== generation) return
      const title = snapshot.draft?.title ?? snapshot.title
      const textContent = snapshot.draft?.textContent ?? snapshot.textContent
      const mentions = snapshot.draft?.mentions ?? snapshot.mentions ?? []
      const attachments = snapshot.draft?.attachments ?? snapshot.attachments ?? []
      setRecordReeditComposer(current => sameArkmeRecordReeditSession(current, target)
        ? {
          ...current,
          snapshot,
          title,
          textContent,
          mentions,
          attachments,
          persisted: { candidateKey: arkmeRecordReeditCandidateKey(title, textContent, attachments, snapshot.textFormat ?? item.textFormat, mentions), draftRevision: snapshot.draft?.draftRevision ?? 0, saveTail: Promise.resolve() },
          loading: false,
        }
        : current)
      if (snapshot.draft !== undefined) showMessageActionStatus('已恢复本机重新编辑草稿')
      requestAnimationFrame(() => { textareaRef.current?.focus() })
    } catch (caught) {
      if (recordReeditGenerationRef.current !== generation) return
      setRecordReeditComposer(current => sameArkmeRecordReeditSession(current, target)
        ? { ...current, loading: false, error: errorMessage(caught) }
        : current)
    }
  }, [authenticatedAccountKey, closeMessageMenu, persistRecordReeditBeforeContextExit, findRecordReeditCandidate, showMessageActionStatus, source, reeditSubmissions.jobs])
  const closeMessageSnapshot = useCallback(() => {
    snapshotRequestRef.current?.abort()
    snapshotRequestRef.current = undefined
    setSnapshot(undefined)
  }, [])
  const openMessageSnapshot = useCallback((item: ArkmeTimelineItem) => {
    if (!activeConversationRef.current) return
    closeMessageMenu()
    snapshotRequestRef.current?.abort()
    snapshotRequestRef.current = undefined
    const actionRef = arkmeTimelineMessageActionRef(item)
    setSnapshot({ item, actionRef, loading: true })
    if (source === undefined) {
      setSnapshot(current => current?.actionRef === actionRef
        ? { ...current, loading: false, loadError: '当前对话不可用，请刷新后重试' }
        : current)
      return
    }
    if (actionRef === '') {
      setSnapshot(current => current?.actionRef === actionRef
        ? { ...current, loading: false, loadError: '快记详情引用无效，请刷新后重试' }
        : current)
      return
    }
    const controller = new AbortController()
    snapshotRequestRef.current = controller
    void callArkme<ArkmeMessageSnapshotDetail>('source.message-snapshot.detail', {
      sourceRef: source.sourceRef,
      actionRef,
    }, controller.signal).then(detail => {
      if (controller.signal.aborted || snapshotRequestRef.current !== controller || !activeConversationRef.current) return
      setSnapshot(current => current?.actionRef === actionRef ? { ...current, detail, loading: false } : current)
    }).catch(caught => {
      if (controller.signal.aborted || snapshotRequestRef.current !== controller || !activeConversationRef.current) return
      setSnapshot(current => current?.actionRef === actionRef
        ? { ...current, loading: false, loadError: errorMessage(caught) || '请稍后重试' }
        : current)
    }).finally(() => {
      if (snapshotRequestRef.current === controller) snapshotRequestRef.current = undefined
    })
  }, [closeMessageMenu, source])
  const openMessageReport = useCallback((item: ArkmeTimelineItem) => {
    if (!activeConversationRef.current) return
    closeMessageMenu()
    if (!arkmeCanReportTimelineMessage(source, item)) return
    setMessageReportItem(item)
  }, [closeMessageMenu, source])
  const openMessageWithdrawal = useCallback((item: ArkmeTimelineItem) => {
    if (!activeConversationRef.current) return
    closeMessageMenu()
    if (!arkmeCanWithdrawTimelineMessage(source, item, groupSelfRole)) return
    setMessageWithdrawalItem(item)
  }, [closeMessageMenu, groupSelfRole, source])
  const openMessageMenu = useCallback((item: ArkmeTimelineItem, event: Pick<MouseEvent, 'preventDefault' | 'stopPropagation' | 'clientX' | 'clientY'>, anchor?: HTMLElement) => {
    event.preventDefault()
    event.stopPropagation()
    const host = panelRef.current
    if (host === null || source === undefined) return

    setMemberMenu(undefined)
    setMessageMenu({
      occurrenceKey: arkmeTimelineOccurrenceKey(item),
      // Keep a mounted bubble as the picker anchor after its context menu closes.
      anchor: anchor ?? host,
      left: event.clientX,
      top: event.clientY,
    })
  }, [groupSelfRole, source])
  const copyMessageText = useCallback(async (item: ArkmeTimelineItem) => {
    const text = arkmeMessageCopyText(item)
    closeMessageMenu()
    if (text === '') {
      showMessageActionStatus('暂无可复制文本')
      return
    }
    try {
      await arkmeCopyTextToClipboard(text)
      showMessageActionStatus('已复制')
    } catch (caught) {
      showMessageActionStatus(errorMessage(caught) || '复制失败，请稍后重试')
    }
  }, [closeMessageMenu, showMessageActionStatus])
  const copySelectedMessageText = useCallback(async () => {
    if (messageActionBusy !== undefined) return
    const item = selectedMessageItems[0]
    if (selectedMessageItems.length !== 1 || item === undefined) {
      showMessageActionStatus('请选择 1 条消息复制文本')
      return
    }
    const text = arkmeMessageCopyText(item)
    if (text === '') {
      showMessageActionStatus('暂无可复制文本')
      return
    }
    try {
      await arkmeCopyTextToClipboard(text)
      showMessageActionStatus('已复制')
    } catch (caught) {
      showMessageActionStatus(errorMessage(caught) || '复制失败，请稍后重试')
    }
  }, [messageActionBusy, selectedMessageItems, showMessageActionStatus])
  const copyMessageLink = useCallback(async (itemsToCopy: readonly ArkmeTimelineItem[]) => {
    if (source === undefined) return
    closeMessageMenu()
    if (messageActionBusy !== undefined) {
      showMessageActionStatus(messageActionBusy === 'copy-link' ? '正在生成链接' : '转发中', false)
      return
    }
    const sourceRef = source.sourceRef
    const sourceKey = conversationKey
    const actionRefs = itemsToCopy.map(arkmeTimelineMessageActionRef).filter(value => value !== '')
    if (actionRefs.length === 0 || actionRefs.length !== itemsToCopy.length) {
      showMessageActionStatus('当前消息暂不支持复制链接')
      return
    }
    if (itemsToCopy.length > 100) {
      showMessageActionStatus('最多选择 100 条消息执行此操作')
      return
    }
    const controller = new AbortController()
    const timeout = window.setTimeout(() => {
      controller.abort()
    }, MESSAGE_ACTION_REQUEST_TIMEOUT_MS)
    setMessageActionBusy('copy-link')
    showMessageActionStatus('正在生成链接', false)
    setError('')
    try {
      const result = await callArkme<ArkmeMessageCopyLinkResult>('source.message-copy-link', {
        sourceRef,
        actionRefs,
      }, controller.signal)
      if (activeSourceKeyRef.current !== sourceKey) return
      await arkmeCopyTextToClipboard(result.url)
      if (activeSourceKeyRef.current !== sourceKey) return
      showMessageActionStatus('复制链接成功')
    } catch (caught) {
      if (activeSourceKeyRef.current === sourceKey) {
        showMessageActionStatus(controller.signal.aborted ? '复制链接失败，请稍后重试' : errorMessage(caught) || '复制链接失败，请稍后重试')
      }
    } finally {
      window.clearTimeout(timeout)
      setMessageActionBusy(undefined)
    }
  }, [closeMessageMenu, conversationKey, messageActionBusy, showMessageActionStatus, source])
  const loadMessageCopyLinkDetail = useCallback((sid: string, options: { preserveReady?: boolean } = {}) => {
    if (!activeConversationRef.current) return
    const normalizedSid = sid.trim()
    if (!/^[0-9A-Za-z]{16}$/.test(normalizedSid)) {
      setCopyLinkDetail({ sid: normalizedSid, status: 'error', message: '链接暂不可用' })
      return
    }
    copyLinkDetailRequestRef.current?.abort()
    const controller = new AbortController()
    copyLinkDetailRequestRef.current = controller
    const timeout = window.setTimeout(() => {
      if (copyLinkDetailRequestRef.current !== controller || !activeConversationRef.current) return
      setCopyLinkDetail(current => current?.sid === normalizedSid
        ? options.preserveReady === true && current.status === 'ready'
          ? current
          : { sid: normalizedSid, status: 'error', message: '链接暂不可用' }
        : current)
      controller.abort()
    }, MESSAGE_ACTION_REQUEST_TIMEOUT_MS)
    if (options.preserveReady !== true) {
      setCopyLinkDetail({ sid: normalizedSid, status: 'loading' })
    } else {
      setCopyLinkDetail(current => current?.sid === normalizedSid && current.status === 'ready'
        ? current
        : { sid: normalizedSid, status: 'loading' })
    }
    void callArkme<ArkmeMessageCopyLinkResolveResult>('source.message-copy-link.resolve', { sid: normalizedSid }, controller.signal)
      .then(detail => {
        if (controller.signal.aborted || copyLinkDetailRequestRef.current !== controller || !activeConversationRef.current) return
        setCopyLinkDetail(current => current?.sid === normalizedSid
          ? { sid: normalizedSid, status: 'ready', detail }
          : current)
      })
      .catch(caught => {
        if (controller.signal.aborted || copyLinkDetailRequestRef.current !== controller || !activeConversationRef.current) return
        setCopyLinkDetail(current => current?.sid === normalizedSid
          ? options.preserveReady === true && current.status === 'ready'
            ? current
            : { sid: normalizedSid, status: 'error', message: errorMessage(caught) || '链接暂不可用' }
          : current)
      })
      .finally(() => {
        window.clearTimeout(timeout)
        if (copyLinkDetailRequestRef.current === controller) copyLinkDetailRequestRef.current = undefined
      })
  }, [])
  const closeMessageCopyLinkDetail = useCallback(() => {
    copyLinkDetailRequestRef.current?.abort()
    copyLinkDetailRequestRef.current = undefined
    copyLinkDetailScopeRef.current = { sending: false }
    setCopyLinkDetailSending(false)
    if (copyLinkRefreshTimerRef.current !== undefined) {
      window.clearTimeout(copyLinkRefreshTimerRef.current)
      copyLinkRefreshTimerRef.current = undefined
    }
    setDrawer(undefined)
    setCopyLinkDetail(undefined)
    setCopyLinkDetailDraft('')
    setCopyLinkDetailSendError('')
  }, [])
  const openMessageCopyLinkDetail = useCallback((sid: string) => {
    if (!activeConversationRef.current) return
    copyLinkDetailScopeRef.current = { sending: false }
    setCopyLinkDetailSending(false)
    if (copyLinkRefreshTimerRef.current !== undefined) {
      window.clearTimeout(copyLinkRefreshTimerRef.current)
      copyLinkRefreshTimerRef.current = undefined
    }
    closeMessageMenu()
    setMemberMenu(undefined)
    setCopyLinkDetailDraft('')
    setCopyLinkDetailSendError('')
    setDrawer('copyLink')
    loadMessageCopyLinkDetail(sid)
  }, [closeMessageMenu, loadMessageCopyLinkDetail])
  const shareCurrentCopyLinkDetail = useCallback(async () => {
    if (copyLinkDetail?.status !== 'ready') return
    try {
      await arkmeCopyTextToClipboard(copyLinkShareUrl(shareWebsite, copyLinkDetail.sid))
      showMessageActionStatus('已复制链接')
    } catch (caught) {
      showMessageActionStatus(errorMessage(caught) || '分享失败，请重试')
    }
  }, [copyLinkDetail, shareWebsite, showMessageActionStatus])
  const sendCopyLinkDetailThought = useCallback(async () => {
    const scope = copyLinkDetailScopeRef.current
    if (!activeConversationRef.current || copyLinkDetail?.status !== 'ready' || scope.sending) return
    const overlayGeneration = conversationOverlayScopeRef.current.generation
    const isCurrent = () => activeConversationRef.current
      && conversationOverlayScopeRef.current.generation === overlayGeneration
      && copyLinkDetailScopeRef.current === scope
    const target = copyLinkDetailExtensionTarget(copyLinkDetail.detail)
    if (target === undefined) {
      setCopyLinkDetailSendError('链接暂不可延展')
      return
    }
    const textContent = copyLinkDetailDraft.trim()
    if (textContent === '') return
    const recordUid = crypto.randomUUID()
    scope.sending = true
    setCopyLinkDetailSending(true)
    setCopyLinkDetailSendError('')
    try {
      await callArkme<ArkmeMessageCopyLinkExtendResult>('source.message-copy-link.extend', {
        sid: copyLinkDetail.sid,
        itemIndex: target.itemIndex,
        textContent,
        recordUid,
      })
      if (!isCurrent()) return
      setCopyLinkDetailDraft('')
      arkmeUi.chatChanged()
      copyLinkRefreshTimerRef.current = window.setTimeout(() => {
        copyLinkRefreshTimerRef.current = undefined
        if (!isCurrent()) return
        loadMessageCopyLinkDetail(copyLinkDetail.sid, { preserveReady: true })
      }, 550)
    } catch (caught) {
      if (isCurrent()) {
        setCopyLinkDetailSendError(errorMessage(caught) || '发送失败，请重试')
      }
    } finally {
      scope.sending = false
      if (isCurrent()) {
        setCopyLinkDetailSending(false)
      }
    }
  }, [
    copyLinkDetail,
    copyLinkDetailDraft,
    loadMessageCopyLinkDetail,
  ])
  const enterMessageSelectMode = useCallback((item: ArkmeTimelineItem) => {
    closeMessageMenu()
    if (source === undefined) {
      showMessageActionStatus('当前消息暂不支持多选')
      return
    }
    setAddMenuOpen(false)
    pendingComposerFocusDraftKeyRef.current = undefined
    const focused = typeof document === 'undefined' ? null : document.activeElement
    if (typeof HTMLElement !== 'undefined' && focused instanceof HTMLElement && composerRef.current?.contains(focused)) focused.blur()
    const body = bodyRef.current
    const anchorId = displayRows.find(row => row.kind === 'message' && arkmeTimelineOccurrenceKey(row.item) === arkmeTimelineOccurrenceKey(item))?.id
    const anchorOffset = body === null ? undefined : arkmeConversationAnchorOffset(body, anchorId)
    // Selection controls can reflow earlier messages. Let the timeline's viewport
    // owner restore the clicked occurrence instead of centering it or following the bottom.
    if (body !== null) pendingViewportRestoreRef.current = {
      sourceKey: timelineStateKey,
      viewport: {
        scrollTop: body.scrollTop,
        stickToBottom: false,
        ...(anchorId === undefined ? {} : { anchorId }),
        ...(anchorOffset === undefined ? {} : { anchorOffset }),
      },
    }
    setSelectMode({ sourceKey: conversationKey, selectedIds: new Set([arkmeTimelineOccurrenceKey(item)]) })
  }, [closeMessageMenu, conversationKey, displayRows, showMessageActionStatus, source, timelineStateKey])
  const commitMarqueeSelection = useCallback((keys: ReadonlySet<string>) => {
    if (source === undefined || messageActionBusy !== undefined) return
    const availableKeys = new Set(displayItems.map(arkmeTimelineOccurrenceKey))
    const covered = [...keys].filter(key => availableKeys.has(key))
    if (covered.length === 0) return
    closeMessageMenu()
    setAddMenuOpen(false)
    pendingComposerFocusDraftKeyRef.current = undefined
    const focused = typeof document === 'undefined' ? null : document.activeElement
    if (typeof HTMLElement !== 'undefined' && focused instanceof HTMLElement && composerRef.current?.contains(focused)) focused.blur()
    const body = bodyRef.current
    if (body !== null) pendingViewportRestoreRef.current = {
      sourceKey: timelineStateKey, viewport: arkmeConversationViewport(body, true),
    }
    setSelectMode(current => ({ sourceKey: conversationKey,
      selectedIds: new Set([...(current?.sourceKey === conversationKey ? current.selectedIds : []), ...covered]),
    }))
  }, [closeMessageMenu, conversationKey, displayItems, messageActionBusy, source, timelineStateKey])
  const toggleSelectedMessage = useCallback((item: ArkmeTimelineItem) => {
    if (source === undefined || messageActionBusy !== undefined) return
    setSelectMode(current => {
      const occurrenceKey = arkmeTimelineOccurrenceKey(item)
      if (current === undefined || current.sourceKey !== conversationKey) return { sourceKey: conversationKey, selectedIds: new Set([occurrenceKey]) }
      const selectedIds = new Set(current.selectedIds)
      if (selectedIds.has(occurrenceKey)) selectedIds.delete(occurrenceKey)
      else selectedIds.add(occurrenceKey)
      return { sourceKey: current.sourceKey, selectedIds }
    })
  }, [conversationKey, messageActionBusy, source])
  const openForwardTargetPicker = useCallback((itemsToForward: readonly ArkmeTimelineItem[] = selectedMessageItems) => {
    if (!activeConversationRef.current) return
    closeMessageMenu()
    // A local edit preview is not the saved content carried by its action ref.
    const savedItems = itemsToForward.map(item => {
      const saved = items.find(value => arkmeTimelineOccurrenceKey(value) === arkmeTimelineOccurrenceKey(item))
      return saved && projectRecordReedit(saved, reeditSubmissions.jobs) !== saved ? saved : item
    })
    const forwardableItems = savedItems.filter(item => arkmeTimelineMessageActionRef(item) !== '')
    if (source === undefined || forwardableItems.length === 0 || forwardableItems.length !== savedItems.length) {
      showMessageActionStatus('请选择要转发的快记')
      return
    }
    if (forwardableItems.length > 100) {
      showMessageActionStatus('最多选择 100 条快记')
      return
    }
    if (savedItems.some((saved, index) => saved !== itemsToForward[index])) {
      showMessageActionStatus('转发使用已保存内容，请核对预览；尚未同步的编辑不参与转发')
    }
    const sourceKey = conversationKey
    setForwardTargetPicker({
      sourceKey,
      itemUids: forwardableItems.map(item => item.itemUid),
      items: [...forwardableItems],
      selectedTargetKeys: [],
      keyword: '',
      commentText: '',
      sendError: '',
    })
    forwardPickerScopeRef.current?.abort()
    const controller = new AbortController()
    forwardPickerScopeRef.current = controller
  }, [closeMessageMenu, conversationKey, items, reeditSubmissions.jobs, selectedMessageItems, showMessageActionStatus, source])
  useEffect(() => {
    if (forwardPickerOpen) return
    forwardPickerScopeRef.current?.abort()
    forwardPickerScopeRef.current = undefined
  }, [forwardPickerOpen])
  const toggleForwardTarget = useCallback(async (target: ArkmeSourceItem) => {
    if (forwardTargetPicker === undefined || messageActionBusy === 'forward') return
    const request = forwardPickerScopeRef.current
    const isCurrent = () => request !== undefined && !request.signal.aborted
      && forwardPickerScopeRef.current === request
      && arkmeAuthenticatedAccountKey(arkmeAuthStore.getSnapshot().auth) === authenticatedAccountKey
    const selected = forwardTargetPicker.selectedTargetKeys.includes(arkmeForwardTargetKey(target))
    if (!selected && target.directMessageAdmissionApplicable === true) {
      try { await requireDirectMessageSendAllowed(target, request?.signal) }
      catch (error) { if (isCurrent()) showMessageActionStatus(errorMessage(error)); return }
    }
    if (!isCurrent()) return
    if (!selected && forwardTargetPicker.selectedTargetKeys.length >= MAX_FORWARD_TARGET_SELECTION) {
      showMessageActionStatus(tr("最多选择 {v0} 个转发对象", { v0: String(MAX_FORWARD_TARGET_SELECTION) }))
      return
    }
    setForwardTargetPicker(current => {
      if (current === undefined || current.items !== forwardTargetPicker.items) return current
      const key = arkmeForwardTargetKey(target)
      if (!forwardTargetsRef.current.some(item => arkmeForwardTargetKey(item) === key)) return current
      if (!selected && (current.selectedTargetKeys.includes(key) || current.selectedTargetKeys.length >= MAX_FORWARD_TARGET_SELECTION)) return current
      return { ...current, selectedTargetKeys: selected
        ? current.selectedTargetKeys.filter(value => value !== key)
        : [...current.selectedTargetKeys, key] }
    })
  }, [authenticatedAccountKey, forwardTargetPicker, forwardTargets, messageActionBusy, showMessageActionStatus])
  const forwardMessageItems = useCallback(async (
    itemsToForward: readonly ArkmeTimelineItem[],
    targetSources: readonly ArkmeSourceItem[],
    commentText = '',
  ) => {
    const native = forwardTargetPicker?.native
    if ((!native && (source === undefined || itemsToForward.length === 0)) || messageActionBusy !== undefined || forwardSubmissionScopeRef.current.sending) return
    const sourceRef = source?.sourceRef
    const sourceKey = conversationKey
    const actionRefs = itemsToForward.map(arkmeTimelineMessageActionRef).filter(value => value !== '')
    if ((!native && actionRefs.length === 0) || targetSources.length === 0) return
    const submissionScope = forwardSubmissionScopeRef.current
    submissionScope.sending = true
    const isCurrent = () => forwardSubmissionScopeRef.current === submissionScope
      && arkmeAuthenticatedAccountKey(arkmeAuthStore.getSnapshot().auth) === authenticatedAccountKey
      && (native ? nativeForwardRequestRef.current === native && !native.signal.aborted : activeSourceKeyRef.current === sourceKey)
    const normalizedCommentText = commentText.trim()
    const controller = new AbortController()
    const timeout = window.setTimeout(() => {
      controller.abort()
    }, MESSAGE_FORWARD_REQUEST_TIMEOUT_MS)
    const cancelNative = () => controller.abort()
    native?.signal.addEventListener('abort', cancelNative, { once: true })
    setMessageActionBusy('forward')
    showMessageActionStatus('转发中', false)
    setError('')
    setForwardTargetPicker(current => current === undefined ? current : { ...current, sendError: '' })
    try {
      const results = await Promise.allSettled(targetSources.map(async targetSource => {
        await requireDirectMessageSendAllowed(targetSource, controller.signal)
        if (!isCurrent()) controller.abort()
        controller.signal.throwIfAborted()
        return {
          targetSource,
          result: native ? await native.delivery.send(targetSource, normalizedCommentText, controller.signal) : await callArkme<ArkmeSourceSendResult>('source.forward-messages', {
            sourceRef,
            targetSourceRef: targetSource.sourceRef,
            actionRefs,
            recordUid: crypto.randomUUID(),
            relationUid: crypto.randomUUID(),
            ...(normalizedCommentText === '' ? {} : { commentText: normalizedCommentText }),
          }, controller.signal),
        }
      }))
      if (!isCurrent()) return
      let successCount = 0
      let failureCount = 0
      let warningText = ''
      let needsTimelineRefresh = false
      for (const outcome of results) {
        if (outcome.status === 'rejected') {
          failureCount += 1
          continue
        }
        successCount += 1
        const { targetSource, result } = outcome.value
        if ((result.warningText ?? '') !== '') warningText = result.warningText ?? ''
        if (arkmeSourceIdentityKey(targetSource) === sourceKey) needsTimelineRefresh = true
      }
      if (successCount === 0) {
        const firstFailureMessage = results
          .find((outcome): outcome is PromiseRejectedResult => outcome.status === 'rejected')
          ?.reason
        const message = controller.signal.aborted ? '转发失败，请稍后重试' : errorMessage(firstFailureMessage) || '发送失败，请重试'
        setForwardTargetPicker(current => current === undefined ? current : { ...current, sendError: message })
        showMessageActionStatus(message)
        return
      }
      const successfulTargets = results
        .filter((outcome): outcome is PromiseFulfilledResult<{ targetSource: ArkmeSourceItem; result: ArkmeSourceSendResult }> => outcome.status === 'fulfilled')
        .map(outcome => outcome.value.targetSource)
      if (native) {
        const retryTargets = results.flatMap((outcome, index) => outcome.status === 'rejected' || outcome.value.result.warningText
          ? [arkmeForwardTargetKey(targetSources[index]!)] : [])
        if (retryTargets.length) {
          const message = warningText || `已转发 ${successCount} 个目标，${failureCount} 个失败，可重试`
          setForwardTargetPicker(current => current?.native === native ? { ...current, selectedTargetKeys: retryTargets, sendError: message } : current)
          return
        }
        native.finish({ completed: true })
        setForwardTargetPicker(undefined)
        return
      }
      exitMessageSelectMode()
      setForwardTargetPicker(undefined)
      closeMessageMenu()
      showForwardSuccessFeedback(successfulTargets, successCount, failureCount)
      showMessageActionStatus(warningText)
      if (needsTimelineRefresh) await loadTimeline()
    } catch (caught) {
      if (isCurrent()) {
        const message = controller.signal.aborted ? '转发失败，请稍后重试' : errorMessage(caught) || '转发失败，请稍后重试'
        setForwardTargetPicker(current => current === undefined ? current : { ...current, sendError: message })
        showMessageActionStatus(message)
      }
    } finally {
      window.clearTimeout(timeout)
      native?.signal.removeEventListener('abort', cancelNative)
      submissionScope.sending = false
      if (forwardSubmissionScopeRef.current === submissionScope) setMessageActionBusy(undefined)
    }
  }, [authenticatedAccountKey, closeMessageMenu, conversationKey, exitMessageSelectMode, forwardTargetPicker, loadTimeline, messageActionBusy, showForwardSuccessFeedback, showMessageActionStatus, source])
  const confirmForwardTargets = useCallback(async () => {
    if (forwardTargetPicker === undefined) return
    if (!forwardTargetPicker.native && forwardTargetPicker.items.length === 0) {
      setForwardTargetPicker({ ...forwardTargetPicker, sendError: '请选择要转发的快记' })
      showMessageActionStatus('请选择要转发的快记')
      return
    }
    const targets = forwardTargetPicker.selectedTargetKeys
      .map(key => forwardTargets.find(item => arkmeForwardTargetKey(item) === key))
      .filter((item): item is ArkmeSourceItem => item !== undefined)
    if (targets.length === 0) {
      setForwardTargetPicker({ ...forwardTargetPicker, sendError: '请选择转发对象' })
      showMessageActionStatus('请选择转发对象')
      return
    }
    await forwardMessageItems(forwardPickerMessageItems, targets, forwardTargetPicker.commentText)
  }, [forwardMessageItems, forwardPickerMessageItems, forwardTargetPicker, forwardTargets, showMessageActionStatus])
  useEffect(() => {
    if (typeof window === 'undefined') return
    const host = window as NativeCopyLinkWindow
    const entry = createNativeCopyLinkEntry(host, {
      isCurrentAccount: userId => Boolean(authenticatedAccountKey && authenticatedUserId === userId && arkmeAuthenticatedAccountKey(arkmeAuthStore.getSnapshot().auth) === authenticatedAccountKey),
      generate: (attempt, signal) => callArkme<ArkmeMessageCopyLinkResult>('native-chat.copy-link', { snapshot: attempt.snapshot, expectedUserId: attempt.userId }, signal),
      copyText: arkmeCopyTextToClipboard,
    })
    host[NATIVE_COPY_LINK_ENTRY] = entry
    return () => { if (host[NATIVE_COPY_LINK_ENTRY] === entry) delete host[NATIVE_COPY_LINK_ENTRY] }
  }, [authenticatedAccountKey, authenticatedUserId])
  useEffect(() => {
    if (typeof window === 'undefined') return
    const host = window as NativeForwardWindow
    const entry: NativeForwardEntry = {
      async open(content, signal, caller) {
        const { snapshot, userId } = content
        if (host[NATIVE_FORWARD_ENTRY] !== entry || !isNativeForwardCaller(host, caller) || signal.aborted) throw new Error('DSH 对话已切换，请重新选择')
        if (!authenticatedAccountKey || authenticatedUserId !== userId || arkmeAuthenticatedAccountKey(arkmeAuthStore.getSnapshot().auth) !== authenticatedAccountKey) throw new Error('账号已变化，请重新选择')
        if (nativeForwardRequestRef.current || forwardPickerScopeRef.current || forwardSubmissionScopeRef.current.sending) throw new Error('请先完成当前转发')
        if (!snapshot.sessionId || !snapshot.messages.length || snapshot.messages.length > 100) throw new Error('请选择 1 至 100 条消息')
        const frozen = structuredClone(snapshot)
        return await new Promise<NativeForwardResult>(resolve => {
          let finished = false
          const cancel = () => {
            request.finish({ completed: false })
            setForwardTargetPicker(current => current?.native === request ? undefined : current)
          }
          const request: NativeForwardRequest = {
            preview: nativeForwardPreview(frozen), signal,
            delivery: content.delivery,
            finish(result) {
              if (finished) return
              finished = true
              signal.removeEventListener('abort', cancel)
              if (nativeForwardRequestRef.current === request) nativeForwardRequestRef.current = undefined
              resolve(result)
            },
          }
          nativeForwardRequestRef.current = request
          signal.addEventListener('abort', cancel, { once: true })
          forwardPickerScopeRef.current = new AbortController()
          setForwardTargetPicker({ native: request, sourceKey: '', items: [], itemUids: [], selectedTargetKeys: [], keyword: '', commentText: content.delivery.comment ?? '', sendError: '' })
        })
      },
    }
    host[NATIVE_FORWARD_ENTRY] = entry
    return () => {
      if (host[NATIVE_FORWARD_ENTRY] === entry) delete host[NATIVE_FORWARD_ENTRY]
      nativeForwardRequestRef.current?.finish({ completed: false })
    }
  }, [authenticatedAccountKey, authenticatedUserId])
  useEffect(() => {
    const request = nativeForwardRequestRef.current
    if (request && forwardTargetPicker?.native !== request) request.finish({ completed: false })
  }, [forwardTargetPicker])
  const rememberConversationViewport = useConversationViewport({
    active: activeConversation,
    sourceKey: conversationKey,
    renderedSourceKey: timelineStateKey,
    bodyRef,
    store: conversationCacheRef.current,
    pendingRestore: pendingViewportRestoreRef,
    restoreIntent: viewportRestoreIntentRef,
  })
  useConversationResizeAnchor(bodyRef, active && activeConversation ? conversationKey : undefined, endAccessoryRef, activeSelectMode !== undefined, recordsRef, viewportRestoreIntentRef)
  const bottomVisibility = useScrollBottomVisibility(bodyRef, recordsRef, active && activeConversation, displayRows)
  const getCalendarReadingDate = useCallback(() => {
    if (!activeConversation || timelineStateKey !== conversationKey) return undefined
    return arkmeConversationReadingDate(bodyRef.current, [
      ...interwovenWindow.prelude.map(item => ({ id: `moment:${item.momentId}`, kind: 'moment', occurredAtMillis: item.occurredAtMillis })),
      ...displayRows,
    ])
  }, [activeConversation, timelineStateKey, conversationKey, displayRows, interwovenWindow.prelude])
  const handleConversationScroll = useCallback(() => {
    if (rememberConversationViewport()?.stickToBottom) setNewMessageCount(0)
    bottomVisibility.measure()
  }, [bottomVisibility.measure, rememberConversationViewport])
  const returnToLatest = useCallback(async () => {
    setCalendarMomentWindow(undefined)
    const body = bodyRef.current
    if (body === null || !activeConversation || timelineStateKey !== conversationKey) return
    if (timelineMode === 'around' || newerHasMore || conversationTargetAbortRef.current !== undefined) {
      await loadTimeline(undefined, false, 40, 'return-to-latest')
      return
    }
    const target = arkmeUi.getSnapshot().conversationTarget
    if (target !== undefined) {
      timelineWindowRevisionRef.current += 1
      for (const request of timelineRequestsRef.current.values()) request.controller.abort()
      timelineRequestsRef.current.clear()
      pendingConversationTargetLocateRef.current = undefined
      olderLoadRef.current = { armed: true, error: '' }
      setLoadingOlder(false)
      arkmeUi.consumeConversationTarget(target.revision)
    }
    // Loaded latest window: no read request and no change to the 80px auto-follow policy.
    body.scrollTo({ top: body.scrollHeight, behavior: 'instant' })
    conversationCacheRef.current.storeViewport(timelineStateKey, arkmeConversationViewport(body))
    setNewMessageCount(0)
    setHighlightedTargetUid('')
    bottomVisibility.measure()
  }, [activeConversation, bottomVisibility.measure, conversationKey, loadTimeline, newerHasMore, timelineMode, timelineStateKey])
  const detailGroupTarget = useMemo(
    () => detailState?.kind === 'success'
      ? resolveInterwovenGroupTarget(chatDirectory.sources, detailState.detail.groupName)
      : undefined,
    [chatDirectory, detailState],
  )
  const showMessageAvatars = arkmeSourceShowsMessageAvatars(source)
  const surfaceTitle = ui.mode === 'recordings' ? '全天候录音'
    : ui.mode === 'recognized-speakers' ? '已识别说话人'
    : ui.mode === 'calls' ? '通话'
    : ui.mode === 'world' ? '世界'
    : ui.mode === 'search' ? '搜索'
    : ui.mode === 'extensions' ? '市集'
    : ui.mode === 'voiceprint' ? '声纹管理'
    : ui.mode === 'arko' ? 'Arko'
    : conversationBackdropVisible ? arkmeSourceDestinationLabel(selectedSource)
    : 'Arkme'
  const arkoContentVisible = authView === 'content' && ui.mode === 'arko'
  const utilityContentVisible = authView === 'content'
    && (ui.mode === 'recordings' || ui.mode === 'recognized-speakers' || ui.mode === 'world' || ui.mode === 'search' || ui.mode === 'extensions'
      || ui.mode === 'voiceprint' || ui.mode === 'calls')
  const composerPlaceholder = arkmeComposerPlaceholderText(
    arkmeComposerPlaceholderTargetForSource(selectedSource, conversationMemberSnapshot.complete && conversationMemberSnapshot.error === undefined ? conversationMembers.length : 0),
  )
  const effectiveComposerPlaceholder = activeRecordReeditComposer !== undefined
    ? activeRecordReeditComposer.loading ? '正在读取快记和草稿…' : '重新编辑快记…'
    : directAdmission.blocked ? directAdmission.message
    : activeComposerExtensionTarget === undefined
      ? composerPlaceholder
      : tr("发送到「{v0}」…", { v0: source?.displayName ?? '' })
  const activeComposerTargetItem = activeRecordReeditComposer === undefined
    ? activeComposerExtensionTarget?.item
    : {
      ...activeRecordReeditComposer.item,
      title: activeRecordReeditComposer.snapshot?.title ?? activeRecordReeditComposer.item.title,
      textContent: activeRecordReeditComposer.snapshot?.textContent ?? activeRecordReeditComposer.item.textContent,
    }
  const composerHasTarget = activeComposerTargetItem !== undefined
  // The desktop hint sits above the bordered input card. Keep its reserved
  // space outside that card so fading it never adds blank space above the text.
  const destinationName = source?.displayName ?? ''
  const destinationMemberCount = source?.kind === 'group_chat'
    ? arkmeComposerGroupMemberCount(conversationMemberSnapshot.complete && conversationMemberSnapshot.error === undefined
      ? conversationMembers.length : 0, source.groupAvatar?.memberCount)
    : undefined
  const composerDestinationHint = sourceIsChat && <div
    style={{ ...styles.composerDestinationHint, height: source?.kind === 'private_chat' ? 30 : 20,
      opacity: composerInputFocused ? 1 : 0 }}
    data-arkme-composer-destination-hint="true"
    aria-hidden={!composerInputFocused}
  >
    <span style={{ ...styles.composerDestinationHintIcon, fontSize: source?.kind === 'private_chat' ? 16 : 14 }} aria-hidden>💡</span>
    <span style={styles.composerDestinationHintText} title={tr("正在给 {v0} 发消息", { v0: destinationName })}><span>{tr("正在给")} </span>
    <span style={styles.composerDestinationHintName}>{source?.kind === 'group_chat' && destinationName.length > 10
      ? `${destinationName.slice(0, 10)}...` : destinationName}</span>
    {destinationMemberCount !== undefined && destinationMemberCount > 1 && <span>{tr(" ({v0}人)", { v0: destinationMemberCount })}</span>}<span> {tr("发消息")}</span></span>
  </div>
  // Reserve the stats line even for an empty, unfocused self/topic draft so
  // focus changes cannot resize the message viewport and shift its scroll anchor.
  const composerInfoRow = <div data-arkme-composer-info-row="true"
    style={{ ...styles.composerInfoRow, minHeight: source?.kind === 'private_chat' ? 30 : 20 }}>
    {composerDestinationHint}
    <ArkmeComposerInputStats
      active={activeConversation}
      visible={composerStatsVisible}
      textLength={composerTextLength}
      {...(composerInputStartedAtMillis === undefined ? {} : { startedAtMillis: composerInputStartedAtMillis })}
      showsInputTime={composerShowsInputTime}
      onToggle={() => {
        const next = !composerShowsInputTime
        setComposerShowsInputTime(next)
        setArkmeComposerShowsInputTime(next)
        textareaRef.current?.focus()
      }}
    />
  </div>
  const localNotificationBlockingOverlayOpen = addMenuOpen
    || longArticleCreating
    || articlePickerOpen
    || groupMembersOpen
    || drawer !== undefined
    || selectedMoment !== undefined
    || relatedMenuOpen
    || selfMenuOpen
    || relatedPanelOpen
    || commonGroupsOpen
    || relatedDetail !== undefined
    || memberMenu !== undefined
    || messageMenu !== undefined
    || snapshot !== undefined
    || messageReportItem !== undefined
    || memberProfile !== undefined
    || memberEventProfile?.scope === memberEventScope
    || memberRecords !== undefined
    || forwardTargetPicker !== undefined

  const selfTour = useSendToSelfTour({
    root: panelRef,
    composer: textareaRef,
    auth,
    active: activeConversation && ui.mode === 'source' && isArkmeSelfWorkspaceSource(selectedSource),
    blocked: authView !== 'content' || busy || ui.webLoginDialogOpen === true
      || localNotificationBlockingOverlayOpen || activeSelectMode !== undefined || activeRecordReeditComposer !== undefined,
    deepLink: ui.conversationTarget !== undefined,
    notificationRevision: notificationActivationRevision,
  })

  useLayoutEffect(() => {
    const target = notificationActivation.source
    if (target === undefined || typeof document === 'undefined') return
    const revision = notificationActivation.revision
    const targetIdentity = arkmeSourceIdentityKey(target)
    let resolved = false
    let stopWatching: () => void = () => undefined
    const tryCommit = () => {
      const current = arkmeNotificationActivation.getSnapshot()
      if (resolved || current.revision !== revision || current.source === undefined
        || arkmeSourceIdentityKey(current.source) !== targetIdentity) {
        stopWatching()
        return
      }
      if (localNotificationBlockingOverlayOpen) return
      const ready = takeArkmeNotificationSurfaceCommit({
        store: arkmeNotificationActivation,
        revision,
        targetIdentity,
        activeConversation,
        sourceIdentity: conversationKey,
        panel: panelRef.current,
        documentScope: document,
      })
      if (ready === undefined) return
      resolved = true
      stopWatching()
      if (ready.activationId !== undefined) {
        void arkmeDesktopNotifications.completeActivationV2(ready.activationId, 'resolved')
      }
    }
    tryCommit()
    if (!resolved && typeof MutationObserver !== 'undefined' && document.body !== null) {
      stopWatching = observeArkmeNotificationBlockingOverlayChanges(document.body, tryCommit)
      // Close the race where a portal was removed between the first query and observer registration.
      tryCommit()
    }
    return stopWatching
  }, [
    activeConversation, conversationKey, localNotificationBlockingOverlayOpen,
    notificationActivation.navigationApplied, notificationActivation.revision,
    notificationActivation.source, notificationActivation.surfaceCommitted,
  ])

  const selfWorkspaceSelected = conversationBackdropVisible && isArkmeSelfWorkspaceSource(selectedSource)
  const selfTopicDirectoryOwner = authenticated && auth?.userId !== undefined
    && (selfWorkspaceSelected || selfTopicPreviewRequested)
    ? <ArkmeTopicDirectoryPopover
      key={`self-topic-directory:${auth.environment}:${String(auth.userId)}`}
      userId={auth.userId}
      environment={auth.environment}
      selectedSource={active && selfWorkspaceSelected ? selectedSource : undefined}
      trigger="none"
      onSelect={activateSelfSource}
      onSelectionRefreshed={updateSourceProjection}
      onSelectionInvalidated={invalidateTopicSelection}
      onSelfSourcesResolution={acceptSelfSourcesResolution}
      onCreateWarning={message => { showMessageActionStatus(message, false) }}
      onCreateTopicReady={open => { selfTopicCreateRef.current = open }}
      retryRevision={selfSourcesRetryRevision}
    />
    : null
  const selfTopicMenuOwner = authenticated && auth?.userId !== undefined
    ? <ArkmeSourceBreadcrumb
      key={`source-breadcrumb:${auth.environment}:${String(auth.userId)}`}
      userId={auth.userId}
      environment={auth.environment}
      selectedSource={selfWorkspaceSelected ? selectedSource : undefined}
      sources={selfSources}
      trigger={active && selfWorkspaceSelected ? 'visible' : 'none'}
      showRootTitle={false}
      tourOpen={active && selfWorkspaceSelected ? selfTour.topicMenuOpen : undefined}
      loading={selfSourcesLoading || (selfTopicPreviewRequested && activeSelfSourcesResolution === undefined)}
      countsReady={activeSelfSourcesResolution?.status === 'ready' ? activeSelfSourcesResolution.complete : false}
      {...(selfSourcesError === undefined ? {} : { error: selfSourcesError })}
      onSelect={activateSelfSource}
      onSelectAggregate={activateSendToSelf}
      onOpen={() => { if (auth.userId !== undefined) void selfTopicDirectory(auth.userId, auth.environment).ensure() }}
      onCreateTopic={() => { selfTopicCreateRef.current?.() }}
      onCreateChildTopic={(parent, parentLevel) => { selfTopicCreateRef.current?.(parent, parentLevel) }}
      onRenameTopic={async (topic, title) => {
        const result = await callArkme<{ sourceRef: string; displayName: string }>('topic.rename', {
          sourceRef: topic.sourceRef,
          title,
        })
        if (auth.userId !== undefined) selfTopicDirectory(auth.userId, auth.environment).upsert({ ...topic, sourceRef: result.sourceRef, displayName: result.displayName })
        setSelfSourcesRetryRevision(value => value + 1)
        return { ...topic, sourceRef: result.sourceRef, displayName: result.displayName }
      }}
      onDissolveTopic={async (topic, parent, children, onProgress) => {
        const requestId = globalThis.crypto?.randomUUID?.() ?? `topic-dissolve-${String(Date.now())}`
        let polling = true
        const reportProgress = async () => {
          if (!polling) return
          const progress = await callArkme<ArkmeTopicDissolveTask | null>('topic.dissolve.status', { requestId })
          if (progress !== null) {
            onProgress(progress)
            publishActiveTopicDissolve(progress)
          }
        }
        const timer = globalThis.setInterval(() => { void reportProgress().catch(() => undefined) }, 250)
        try {
          await callArkme('topic.dissolve', {
            sourceRef: topic.sourceRef,
            ...(parent === undefined ? {} : { parentSourceRef: parent.sourceRef }),
            childSourceRefs: children.map(child => child.sourceRef),
            requestId,
            expectedRecordCount: Math.max(0, topic.recordCount ?? 0),
          })
          await reportProgress()
          setSelfSourcesRetryRevision(value => value + 1)
        } finally {
          polling = false
          globalThis.clearInterval(timer)
        }
      }}
      {...(activeTopicDissolve === undefined ? {} : { activeDissolve: activeTopicDissolve })}
      onRetry={() => { setSelfSourcesRetryRevision(value => value + 1) }}
      onMoveTopic={async (topic, currentParent, nextParent, insertBefore) => {
        await callArkme<ArkmeTopicHierarchyMoveResult>('topic.hierarchy.move', {
          sourceRef: topic.sourceRef,
          ...(currentParent === undefined ? {} : { currentParentSourceRef: currentParent.sourceRef }),
          ...(nextParent === undefined ? {} : { nextParentSourceRef: nextParent.sourceRef }),
          ...(insertBefore === undefined ? {} : { insertBeforeSourceRef: insertBefore.sourceRef }),
        })
        setSelfSourcesRetryRevision(value => value + 1)
      }}
    />
    : null

  const recordingImportOwner = authView === 'content' && authenticatedUserId !== undefined
    && authStoreSnapshot.config?.recordingWorkbenchEnabled !== false
    ? <ArkmeRecordingImportDialog
      key={`recording-import:${authenticatedAccountKey ?? 'unknown'}`}
      ref={recordingImportDialogRef}
      importPath={authStoreSnapshot.config?.recordingImportPath ?? '/arkme-self/api/recording/import'}
      defaultStartAtMillis={recordingImportDefaultStartAtMillis}
      currentUserId={authenticatedUserId}
      foreground={recordingImportForeground}
      onStatusChange={updateRecordingImportStatus}
      onAccepted={() => { setRecordingRefreshRevision(value => value + 1) }}
    />
    : null

  // Draft updates must not rebuild every loaded message. Event handlers read the
  // latest committed actions; render dependencies below contain only timeline facts.
  const timelineActionValues = { activateSelfSource, openChatMessage, openMemberEventProfile, openMemberHover, openMemberMenu, openMemberProfile, openBotHover, openBotMenu, openBotProfile, openMentionMemberProfile, openMessageCopyLinkDetail, openMessageMenu, openMomentDetail, openNoteDetail, openRecordReedit, retryAiPolish, toggleSelectedMessage, updateComposerText,
    openTimelineExtensionParent, refreshFiles: fileTasks.refresh, refreshReedits: reeditSubmissions.refresh }
  const timelineActions = useRef(timelineActionValues)
  useLayoutEffect(() => { timelineActions.current = timelineActionValues })
  const timelineReediting = activeRecordReeditComposer !== undefined
  const timelineFileTasks = fileTasks.tasks
  const timelineReeditJobs = reeditSubmissions.jobs
  const renderedMessageRows = useMemo(() => {
    if (source === undefined) return null
    return displayRows.map((row, index) => {
                const previous = index === 0 ? undefined : displayRows[index - 1]
                const startsDay = previous === undefined
                  || dayKey(previous.occurredAtMillis) !== dayKey(row.occurredAtMillis)
                if (row.kind === 'community-welcome') return <Fragment key={row.id}>
                  {startsDay && <li style={styles.date}>{dayLabel(row.occurredAtMillis)}</li>}
                  <li data-arkme-conversation-row={row.id}>
                  <ArkmeBetaCommunityWelcome title={row.title}
                    disabled={composerFilesDisabled || timelineReediting || archiveReadOnly}
                    onChoose={text => {
                      if (!composerDraftKey || composerFilesDisabled || timelineReediting || archiveReadOnly) return
                      const current = arkmeComposerDraftStore.get(composerDraftKey)
                      const previous = welcomeSuggestionRef.current
                      const suggestionKey = `${authenticatedAccountKey}:${composerDraftKey}`
                      const next = welcomeDraft(current.text, previous?.key === suggestionKey ? previous.text : undefined, text)
                      if (current.attachments.length === 0 && next !== current.text) {
                        timelineActions.current.updateComposerText(next)
                        welcomeSuggestionRef.current = { key: suggestionKey, text: next }
                      }
                      textareaRef.current?.focus()
                      const focusedEditor = document.activeElement
                      // The controlled editor renders the new draft after this click.
                      // Position the caret after that render, without stealing focus
                      // if the user has switched conversations or focused elsewhere.
                      requestAnimationFrame(() => {
                        const editor = textareaRef.current
                        if (!focusedEditor?.isConnected || document.activeElement !== focusedEditor || !editor || editor.disabled) return
                        editor.setSelectionRange(editor.value.length, editor.value.length)
                      })
                    }} />
                  </li>
                </Fragment>
                if (row.kind === 'member-event-gap') return <li key={row.id} data-arkme-member-event-gap={row.gapId} aria-hidden style={{height:1,width:'100%'}}/>
                if (row.kind === 'member-event') return <ArkmeMemberLeaveNotice key={row.id} rowId={row.id} event={row.item} onOpen={(...args) => timelineActions.current.openMemberEventProfile(...args)}/>
                if (row.kind === 'member-join') return <ArkmeMemberJoinNotice
                  key={row.id}
                  rowId={row.id}
                  event={row.item}
                  membersByRef={conversationMemberByRef}
                  startsGroup={previous?.kind !== 'member-join'}
                  onOpenMember={(...args) => timelineActions.current.openMemberProfile(...args)}
                />
                if (row.kind === 'notice') return <Fragment key={row.id}>
                  {startsDay && <li style={styles.date}>{dayLabel(row.occurredAtMillis)}</li>}
                  <li data-arkme-conversation-row={row.id} style={styles.notice}>{row.item.message}</li>
                </Fragment>
                if (row.kind === 'moment') {
                  return <Fragment key={row.id}>
                    {startsDay && <li style={styles.date}>{dayLabel(row.occurredAtMillis)}</li>}
                    <ArkmeInterwovenMentionCard moment={row.item} rowId={row.id} onOpen={(...args) => timelineActions.current.openMomentDetail(...args)}
                      highlighted={highlightedTargetUid === row.id} />
                  </Fragment>
                }
                const item = personalExtensionParents.has(row.item.itemUid)
                  ? { ...row.item, extensionParent: personalExtensionParents.get(row.item.itemUid)! } : row.item
                const selfTopicPresentation = arkmeTimelineSelfTopicPresentation(item, source, selfSources)
                const selfWorkspace = isArkmeSelfWorkspaceSource(source)
                // A self-role is only a presentation identity. Keep item.isMe as
                // the real author/permission fact, and choose the left lane here.
                const role = arkmeSelfRoleForPresentation(item, source.kind)
                const presentationIsMe = item.isMe && role === undefined
                const roleFallback = role === undefined ? undefined : arkmeSelfRoleAvatarFallback(role)
                const avatarRef = selfWorkspace
                  ? arkmePersonalAvatarRef(item, selfProfile)
                  : arkmeTimelineAvatarRef(item, selfProfile)
                const messageMember = item.memberRef === undefined
                  ? (item.isMe ? selfConversationMember : undefined)
                  : conversationMemberByRef.get(item.memberRef)
                const messageHeader = presentationIsMe || source.kind === 'private_chat' ? null : <ArkmeTimelineMessageHeader
                  item={item}
                  presentationIsMe={presentationIsMe}
                  {...(selfProfile === undefined ? {} : { profile: selfProfile })}
                  {...(source.kind === 'group_chat' && messageMember !== undefined ? { member: messageMember } : {})}
                />
                const polishStatus = aiPolishStatus(item)
                const selectedForAction = activeSelectMode?.selectedIds.has(arkmeTimelineOccurrenceKey(item)) === true
                const isForwardMessageCard = item.forwardRecords !== undefined
                const isSharedRecordingCard = item.sharedRecording !== undefined
                const isStructuredMessageCard = isForwardMessageCard || isSharedRecordingCard
                const isExtensionMessage = !isSharedRecordingCard && item.extensionParent !== undefined
                const messageAvatar = showMessageAvatars && !isSharedRecordingCard
                  ? <MessageAvatar senderKind={item.senderKind}
                    {...(avatarRef === undefined ? {} : { avatarRef })}
                    {...(roleFallback === undefined ? {} : { fallback: roleFallback, label: `${role?.name ?? ''}的角色头像` })}
                    {...(messageMember === undefined ? {} : { member: messageMember })}
                    {...(source?.kind !== 'group_chat' || item.senderKind !== 'bot' ? {} : { bot: {
                      itemUid: item.itemUid, name: item.senderName,
                      ...(item.senderBotDirectoryKey === undefined ? {} : { directoryKey: item.senderBotDirectoryKey }),
                      ...(avatarRef === undefined ? {} : { avatarRef }),
                    } })}
                    profileEnabled={source?.kind === 'group_chat'}
                    onOpen={(...args) => timelineActions.current.openMemberProfile(...args)}
                    onContextMenu={(...args) => timelineActions.current.openMemberMenu(...args)}
                    onHover={(...args) => timelineActions.current.openMemberHover(...args)}
                    onBotHover={(...args) => timelineActions.current.openBotHover(...args)}
                    onBotMenu={(...args) => timelineActions.current.openBotMenu(...args)}
                    onBotOpen={(...args) => timelineActions.current.openBotProfile(...args)}
                  />
                  : null
                const selectionAnchor = arkmeMessageSelectionAnchor(item)
                const isHighlighted = highlightedTargetUid === item.itemUid || recordReeditHighlightUid === item.itemUid
                const messageLineBottomGap = index === displayRows.length - 1 || activeSelectMode !== undefined ? 0 : isSharedRecordingCard ? 42 : 18
                return <Fragment key={row.id}>
                  {startsDay && <li style={styles.date}>{dayLabel(item.sendAtMillis)}</li>}
                  <li data-arkme-width-anchor={row.id} data-arkme-width-preview={(item.conversationPreview || item.textContent || item.title).slice(0, 320)}
                    data-arkme-width-avatar={avatarRef}
                    data-arkme-width-sender={role?.name ?? arkmeTimelineSenderName(item, selfProfile)}
                    data-arkme-width-sender-kind={item.senderKind ?? 'human'}
                    data-arkme-selection-key={arkmeTimelineOccurrenceKey(item)} data-arkme-conversation-row={row.id} data-arkme-message-item-uid={item.itemUid} style={{
                    ...styles.row,
                    ...(activeSelectMode === undefined
                      ? isSharedRecordingCard
                        ? styles.sharedRecordingRow
                        : presentationIsMe ? styles.rowMe : styles.rowOther
                      : selectionAnchor === 'avatar'
                        ? styles.rowSelectAvatarMode
                        : styles.rowSelectCardCenterMode),
                    ...(selectedForAction ? styles.rowSelectedForAction : {}),
                    ...(isHighlighted ? styles.rowSearchTarget : {}),
                  }}
                    onClickCapture={event => {
                      if (activeSelectMode === undefined) return
                      if (!arkmeShouldToggleMessageSelectFromRowClick(event.target)) return
                      event.preventDefault()
                      event.stopPropagation()
                      timelineActions.current.toggleSelectedMessage(item)
                    }}
                  >
                    {isHighlighted && <span
                      key={`highlight:${conversationTargetLocatedRevisionRef.current}`}
                      aria-hidden="true"
                      data-arkme-highlight-backdrop="true"
                      style={{ ...styles.rowSearchTargetBackdrop, bottom: messageLineBottomGap - 6, ...(highlightedTargetUid === item.itemUid && transientReactionHighlightRef.current ? { animation: 'arkme-reaction-highlight 2s ease-out both' } : {}) }}
                    />}
                    {activeSelectMode !== undefined && <ArkmeMessageSelectionControl
                      anchor={selectionAnchor}
                      checked={selectedForAction}
                      disabled={messageActionBusy !== undefined}
                      onToggle={() => { timelineActions.current.toggleSelectedMessage(item) }}
                    />}
                    <div style={{
                      ...styles.messageLine,
                      ...(activeSelectMode !== undefined && selectionAnchor === 'avatar' ? styles.messageLineSelectAvatarMode : {}),
                      ...(presentationIsMe && !isSharedRecordingCard ? styles.messageLineMe : {}),
                      ...(isForwardMessageCard ? styles.forwardMessageLine : {}),
                      ...(isSharedRecordingCard ? styles.sharedRecordingMessageLine : {}),
                      ...(activeSelectMode !== undefined && selectionAnchor === 'card-center' ? styles.messageLineSelectCardCenterMode : {}),
                      // The viewport already supplies the gap above the composer.
                      marginBottom: messageLineBottomGap,
                    }}>
                      {!isExtensionMessage && messageAvatar}
                      <div style={{
                        ...styles.messageBody,
                        ...(presentationIsMe && !isSharedRecordingCard ? styles.messageBodyMe : {}),
                        ...(isForwardMessageCard ? styles.forwardMessageBody : {}),
                        ...(isSharedRecordingCard ? styles.sharedRecordingMessageBody : {}),
                      }}>
                        {!isSharedRecordingCard && !isExtensionMessage && messageHeader}
                        {(() => {
                          const reactionTarget = { sourceKey: source.sourceKey, itemUid: item.itemUid, id: `${conversationKey}:${arkmeTimelineOccurrenceKey(item)}`, sourceRef: source.sourceRef, messageActionRef: item.messageActionRef ?? '', source: source.displayName, sourceKind: source.kind, text: item.textContent ?? '' }
                          const canReact = !!item.messageActionRef
                          const messageBubble = <div
                            role="button"
                            tabIndex={0}
                            style={{
                              ...styles.bubble,
                              ...(isSharedRecordingCard ? styles.sharedRecordingBubble : presentationIsMe ? styles.bubbleMe : styles.bubbleOther),
                              ...(isForwardMessageCard ? styles.forwardBubble : {}),
                              ...(item.callRecord ? arkmeCallRecordBubbleStyle(presentationIsMe) : {}),
                            }}
                            onClick={event => {
                              // Portal clicks follow the React tree but do not belong to this bubble's DOM.
                              if (!(event.target instanceof Element) || !event.currentTarget.contains(event.target)) return
                              if (event.target.closest('button,a,audio,video,input,select,textarea,[contenteditable],[role=link],[role=slider]')) return
                              if (window.getSelection()?.toString()) return
                              event.currentTarget.focus({ preventScroll: true })
                              timelineActions.current.openChatMessage(item)
                            }}
                            onKeyDown={event => {
                              if (event.target !== event.currentTarget) return
                              if (event.key !== 'Enter' && event.key !== ' ') return
                              event.preventDefault(); timelineActions.current.openChatMessage(item)
                            }}
                            onContextMenu={event => { timelineActions.current.openMessageMenu(item, event, event.currentTarget) }}
                            data-arkme-message-direction={role === undefined ? item.isMe ? 'self' : 'other' : 'self-role'}
                            aria-label={item.callRecord ? '打开通话详情' : isSharedRecordingCard ? '打开录音详情' : tr("打开快记详情")}
                          >{polishStatus !== '' && <span style={styles.polishMeta}>
                            {item.aiPolish?.state === 'failed' ? <button data-arkme-feedback="neutral"
                              type="button" style={styles.retry} onClick={event => { event.stopPropagation(); void timelineActions.current.retryAiPolish(item) }}
                            >{polishStatus}</button> : polishStatus}
                          </span>}
                            <ArkmeMessageContent
                              key={`message-content:${conversationOverlayKey}`}
                              sessionAttachmentPreview
                              item={item}
                              mediaSelectionIsExplicit={reeditItems.has(item)}
                              onCallDetailOpen={videoUrl => { timelineActions.current.openNoteDetail(item, videoUrl) }}
                              sourceRef={source.sourceRef}
                              sourceIdentityKey={conversationKey}
                              sourceDisplayName={source.displayName}
                              highlightMentions
                              shareWebsite={shareWebsite}
                              onMessageCopyLinkOpen={(...args) => timelineActions.current.openMessageCopyLinkDetail(...args)}
                              onMentionClick={(...args) => timelineActions.current.openMentionMemberProfile(...args)}
                              isMentionClickable={mentionOpensMemberProfile}
                              onLongArticleUpdated={detail => {
                                setItems(current => current.map(candidate => candidate.itemUid === detail.itemUid
                                  ? {
                                    ...candidate,
                                    title: detail.title,
                                    textContent: detail.textContent,
                                    textFormat: detail.textFormat ?? 'plain',
                                    sendAtMillis: detail.sendAtMillis,
                                    updateAtMillis: detail.updateAtMillis,
                                    templateKind: 1,
                                    displayKind: 1,
                                    version: detail.version,
                                    recordDurationMillis: detail.recordDurationMillis,
                                    editDurationMillis: detail.editDurationMillis,
                                  }
                                  : candidate))
                              }}
                            />
                            {!isSharedRecordingCard && <ArkmeTimelineAgentSourceBadge item={item} />}
                            {canReact && authenticatedAccountKey && activeSelectMode === undefined && <ArkmeReactionSelections scope={authenticatedAccountKey} target={reactionTarget}
                              actorName={selfProfile?.displayName.trim() || selfProfile?.nickname.trim() || '我'}
                              actorGroupNickname={source.kind === 'group_chat' ? selfConversationMember?.memberName : undefined}
                              actorAvatarRef={selfProfile?.avatarRef}
                              onAdd={() => setReactionMenuRequest({ scope: authenticatedAccountKey, occurrenceKey: arkmeTimelineOccurrenceKey(item), toggle: true })} />}
                            {timelineReeditJobs.filter(job => job.itemUid === item.itemUid && (job.state === 'failed' || job.state === 'uncertain')).map(job => <div key={job.submissionId} role="status" aria-label={tr("重新编辑保存状态")} style={styles.polishMeta}>
                              {job.state === 'failed' ? tr("保存失败：{v0}", { v0: job.error ?? '请恢复编辑后重试' }) : '保存结果待确认'}
                              {job.state === 'failed' && <button data-arkme-feedback="neutral" type="button" style={styles.retry} onClick={event => { event.stopPropagation(); void timelineActions.current.openRecordReedit(item) }}>{tr("恢复编辑")}</button>}
                              {job.state === 'uncertain' && <button data-arkme-feedback="neutral" type="button" style={styles.retry} onClick={event => { event.stopPropagation(); void timelineActions.current.refreshReedits(true).catch(caught => setError(errorMessage(caught))) }}>{tr("核对结果")}</button>}
                            </div>)}
                          </div>
                          const fileSendStatus = timelineFileTasks.filter(task => (task.result?.itemUid ?? task.recordUid) === item.itemUid && task.state !== 'sent' && fileTaskShowsInlineStatus(task)).map(task => <div key={task.taskRef} role="status" aria-label={tr("附件发送状态")} data-arkme-file-send-status={task.state} style={{ fontSize: 12, lineHeight: 1.5, color: arkmeTheme.secondary, maxWidth: '100%', overflowWrap: 'anywhere', textAlign: presentationIsMe ? 'right' : 'left' }}>
                              {task.error ?? (task.state === 'sending' ? '正在发送…' : task.state === 'queued' ? '等待上传' : '正在上传')}
                              {task.state === 'failed' && task.retryable !== false && !directAdmission.blocked && <button data-arkme-feedback="neutral" type="button" onClick={event => { event.stopPropagation(); void callArkme('files.send.retry', { taskRef: task.taskRef }).then(timelineActions.current.refreshFiles).catch(caught => setError(errorMessage(caught))) }}>{tr("重试")}</button>}
                              {task.state === 'uncertain' && <button data-arkme-feedback="neutral" type="button" onClick={event => { event.stopPropagation(); void callArkme<ArkmeFileSendTask>('files.send.reconcile', { taskRef: task.taskRef }).then(value => { timelineActions.current.refreshFiles(); if (value.state === 'uncertain') setError('最近的会话记录还无法确认发送结果，请先核对原会话，不要重复发送') }).catch(caught => setError(errorMessage(caught))) }}>{tr("核对发送结果")}</button>}
                              {(task.state === 'failed' || task.state === 'uncertain') && <>{' '}<button data-arkme-feedback="danger" type="button" aria-label={tr("清除发送记录")} onClick={event => {
                                event.stopPropagation()
                                if (task.state === 'uncertain' && !window.confirm('发送结果仍未确认。清除记录不会撤回可能已发送的消息，是否继续？')) return
                                void callArkme('files.send.discard', { taskRef: task.taskRef })
                                  .then(timelineActions.current.refreshFiles)
                                  .catch(caught => setError(errorMessage(caught)))
                              }}>{tr("清除")}</button></>}
                            </div>)
                          const messageContentLine = isSharedRecordingCard
                            ? messageBubble
                            : <ArkmeMessageReadReceiptLine
                              key={`read-receipt:${conversationOverlayKey}`}
                              source={source}
                              item={item}
                              wide={isStructuredMessageCard}
                            >
                              {messageBubble}
                            </ArkmeMessageReadReceiptLine>
                          const topicBadge = selfTopicPresentation === undefined
                            ? null
                            : <ArkmeTimelineSelfTopicBadge
                                topic={selfTopicPresentation.topic}
                                {...(selfTopicPresentation.displayLabel === undefined ? {} : { displayLabel: selfTopicPresentation.displayLabel })}
                                onSelect={(...args) => timelineActions.current.activateSelfSource(...args)}
                              />
                          const reactionMessageLine = canReact && authenticatedAccountKey && activeSelectMode === undefined
                              ? <ArkmeReactionPreview
                                key={`reaction:${conversationKey}:${item.itemUid}`}
                                isMe={presentationIsMe}
                                scope={authenticatedAccountKey}
                                openRequested={reactionMenuRequest?.scope === authenticatedAccountKey && reactionMenuRequest?.occurrenceKey === arkmeTimelineOccurrenceKey(item)}
                                toggleRequested={reactionMenuRequest?.toggle === true}
                                onOpenHandled={() => setReactionMenuRequest(undefined)}
                                target={reactionTarget}>{messageContentLine}</ArkmeReactionPreview> : messageContentLine
                          if (!isExtensionMessage || item.extensionParent === undefined) return <>
                            {reactionMessageLine}
                            {fileSendStatus}
                            {topicBadge}
                          </>
                          return <div
                            data-arkme-extension-message-cluster="true"
                            style={{
                              ...styles.extensionMessageGroup,
                              ...(presentationIsMe ? styles.extensionMessageGroupMe : {}),
                            }}
                          >
                            <ArkmeExtensionParentPreview
                              parent={item.extensionParent}
                              isMe={presentationIsMe}
                              onSelect={() => {
                                if (item.extensionParent !== undefined) timelineActions.current.openTimelineExtensionParent(item.extensionParent)
                              }}
                            />
                            <div
                              data-arkme-extension-child-line="true"
                              style={{
                                ...styles.extensionChildLine,
                                ...(presentationIsMe ? styles.extensionChildLineMe : {}),
                              }}
                            >
                              {messageAvatar}
                              <div style={{
                                ...styles.extensionChildBody,
                                ...(presentationIsMe ? styles.extensionChildBodyMe : {}),
                              }}>
                                {messageHeader}
                                {reactionMessageLine}
                                {fileSendStatus}
                                {topicBadge}
                              </div>
                            </div>
                          </div>
                        })()}
                      </div>
                    </div>
                  </li>
                </Fragment>
              })
  }, [displayRows, personalExtensionParents, source, selfSources, selfProfile, selfConversationMember, conversationMemberByRef,
    activeSelectMode, archiveReadOnly, authenticatedAccountKey, composerDraftKey, composerFilesDisabled,
    timelineReediting, conversationKey, conversationOverlayKey, directAdmission.blocked, timelineFileTasks,
    highlightedTargetUid, mentionOpensMemberProfile, messageActionBusy, recordReeditHighlightUid,
    reeditItems, timelineReeditJobs, shareWebsite, showMessageAvatars, locale, reactionMenuRequest])

  const directRecordingNotice = ui.mode !== 'recordings' || !active ? <ArkmeDirectRecordingStatus floating /> : null

  const forwardDialogContent = forwardTargetPicker !== undefined && (activeConversation || forwardTargetPicker.native !== undefined) ? (<div
          data-arkme-notification-blocking-overlay="true"
          style={{ ...styles.forwardTargetBackdrop, ...(forwardTargetPicker.native ? { position: 'fixed' as const, zIndex: 10000 } : {}) }}
          role="presentation"
          onMouseDown={event => { if (event.target === event.currentTarget && messageActionBusy !== 'forward') setForwardTargetPicker(undefined) }}
        >
          <section
            style={styles.forwardTargetDialog}
            role="dialog"
            aria-modal="true"
            aria-labelledby="arkme-forward-target-title"
          >
            <header style={styles.forwardTargetHeader}>
              <span aria-hidden />
              <h3 id="arkme-forward-target-title" style={styles.forwardTargetTitle}>{tr("转发给")}</h3>
              <button data-arkme-feedback="neutral"
                type="button"
                style={styles.forwardTargetClose}
                disabled={messageActionBusy === 'forward'}
                aria-label={tr("关闭转发对象选择")}
                onClick={() => { if (messageActionBusy !== 'forward') setForwardTargetPicker(undefined) }}
              >×</button>
            </header>
            <div style={styles.forwardTargetSearchWrap}>
              <ArkmeForwardSearch
                value={forwardTargetPicker.keyword}
                disabled={messageActionBusy === 'forward'}
                onChange={event => {
                  const keyword = event.currentTarget.value
                  setForwardTargetPicker(current => current === undefined
                    ? current
                    : { ...current, keyword })
                }}
              />
            </div>
            <div style={styles.forwardTargetBody}>
              <ul style={styles.forwardTargetList} aria-label={tr("转发对象列表")}>
                {(forwardSelfTarget !== undefined || forwardTargetPicker.keyword.trim() === '') && <li key="send_to_self">
                  {forwardSelfTarget !== undefined ? renderForwardTargetButton(forwardSelfTarget) : <div
                    aria-disabled="true" aria-label={tr("发给自己暂不可用")} style={{ ...styles.forwardTargetRow, cursor: 'default' }}>
                    <span style={styles.forwardTargetCheck} aria-hidden />
                    <ArkmeDirectorySourceAvatar source={{ kind: 'send_to_self' }} size={38} />
                    <span style={styles.forwardTargetText}>
                      <span style={styles.forwardTargetName}>{tr("发给自己")}</span>
                      <span style={styles.forwardTargetMeta}>{tr("默认分类")}</span>
                    </span>
                  </div>}
                </li>}
                {forwardVisibleTargets.filter(target => target.kind !== 'send_to_self').map(target =>
                  <li key={arkmeForwardTargetKey(target)}>{renderForwardTargetButton(target)}</li>)}
                {forwardVisibleTargets.length === 0 && <li role="status" style={styles.forwardTargetStatus}>
                  {forwardDirectory.loading ? '正在加载转发对象...' : '暂无可转发对象'}
                </li>}
              </ul>
              {forwardDirectory.error !== '' && <div role="alert" style={styles.forwardTargetStatus}>{forwardDirectory.error}</div>}
            </div>
            {forwardSelectedTargets.length > 0 && <footer style={styles.forwardTargetFooter}>
              <div style={styles.forwardTargetRecipients}>
                <span>{tr("发送给：")}</span>
                <span style={styles.forwardTargetAvatarStack}>
                  {forwardSelectedTargets.slice(0, 6).map(target => <ArkmeDirectorySourceAvatar
                    key={target.sourceRef}
                    source={target}
                    size={26}
                  />)}
                </span>
              </div>
              <div style={styles.forwardTargetFooterDivider} />
              <div style={styles.forwardTargetPreview}>
                <span style={styles.forwardTargetPreviewIcon}><ArkmeForwardLinearIcon size={18} /></span>
                <span style={styles.forwardTargetPreviewText}>
                  <span style={styles.forwardTargetPreviewTitle}><ArkmeRichText text={forwardTargetPicker.native?.preview.title ?? arkmeForwardPreviewTitle(forwardPickerMessageItems, source)} presentation="preview" /></span>
                  <span style={styles.forwardTargetPreviewSubtitle}><ArkmeRichText text={forwardTargetPicker.native?.preview.subtitle ?? arkmeForwardPreviewSubtitle(forwardPickerMessageItems)} presentation="preview" /></span>
                </span>
                <button data-arkme-feedback="neutral"
                  type="button"
                  style={styles.forwardTargetPreviewClose}
                  disabled={messageActionBusy === 'forward'}
                  aria-label={tr("关闭转发对象选择")}
                  onClick={() => { if (messageActionBusy !== 'forward') setForwardTargetPicker(undefined) }}
                ><ArkmeForwardCloseBorderIcon /></button>
              </div>
              {forwardTargetPicker.sendError !== '' && <div role="alert" style={styles.forwardTargetSendError}>{forwardTargetPicker.sendError}</div>}
              <div style={styles.forwardTargetComposer}>
                <textarea
                  style={styles.forwardTargetCommentInput}
                  value={forwardTargetPicker.native?.delivery.comment ?? forwardTargetPicker.commentText}
                  placeholder={tr("说点什么...")}
                  aria-label={tr("转发附言")}
                  disabled={messageActionBusy === 'forward' || forwardTargetPicker.native?.delivery.comment !== undefined}
                  onChange={event => {
                    const commentText = event.currentTarget.value
                    setForwardTargetPicker(current => current === undefined
                      ? current
                      : { ...current, commentText })
                  }}
                  onKeyDown={event => {
                    if (event.key === 'Enter' && !event.shiftKey) {
                      event.preventDefault()
                      void confirmForwardTargets()
                    }
                  }}
                />
                <button data-arkme-feedback="primary"
                  type="button"
                  style={{
                    ...styles.forwardTargetSend,
                    ...(messageActionBusy === 'forward' ? styles.forwardTargetSendDisabled : {}),
                  }}
                  disabled={messageActionBusy === 'forward'}
                  aria-label={messageActionBusy === 'forward' ? '转发中' : '发送转发'}
                  onClick={() => { void confirmForwardTargets() }}
                >
                  <ArkmeForwardSubmitIcon />
                </button>
              </div>
            </footer>}
          </section>
        </div>) : null
  const forwardDialog = forwardTargetPicker?.native && forwardDialogContent ? createPortal(forwardDialogContent, document.body) : forwardDialogContent
  const retainedCallPage = authView === 'content' && <ArkmeRetainedCallPage
    key={`calls:${auth?.status}:${auth?.environment}:${auth?.userId}`}
    active={active && ui.mode === 'calls'}
  />

  if (!active) return <>
    {forwardDialog}
    <div
      key="conversation-surface"
      className="arkme-conversation-surface"
      ref={surfaceRef}
      data-arkme-owned="product-surface"
      data-arkme-surface-suspended="true"
      style={{
        ...styles.surface,
        ...(floating ? styles.floatingSurface : {}),
        ...(productChrome && compactNavigation ? styles.compactSurface : {}),
      }}
      aria-hidden
    >
      <section key="conversation-panel" className="arkme-conversation-panel" style={styles.panel}>
        {retainedCallPage}
      </section>
    </div>
    {selfTopicDirectoryOwner}
    {selfTopicMenuOwner}
    {recordingImportOwner}
    {directRecordingNotice}
  </>

  return (
    <>
    <ArkmeBotIdentityStyles />
    {selfTour.panel}
    {activeConversation && recordDeletion?.scopeKey === topicAssignmentScopeKey && <ArkmeRecordDeletionDialog
      key={recordDeletion.scopeKey} sourceRef={recordDeletion.sourceRef}
      deletionRefs={recordDeletion.items.map(item => item.recordDeletionRef!)}
      onCancel={() => { setRecordDeletion(undefined) }}
      onRefresh={() => {
        confirmedSendRetention.forget(conversationKey, recordDeletion.items.map(item => item.itemUid))
        conversationCacheRef.current.invalidateTimelines()
        setRecordDeletion(undefined)
        exitMessageSelectMode()
        void loadTimeline(undefined, true, 40, 'refresh').catch(caught => {
          if (activeSourceKeyRef.current === conversationKey) showMessageActionStatus(errorMessage(caught))
        })
      }}
      onResult={result => {
        // 已完成的 owner 写入使旧窗口读取失效，避免迟到分页重新插入删除前的快照。
        timelineWindowRevisionRef.current += 1
        for (const request of timelineRequestsRef.current.values()) request.controller.abort()
        timelineRequestsRef.current.clear()
        setLoadingOlder(false)
        const deleted = new Set(result.items.filter(item => item.result === 'deleted').map(item => item.recordUid))
        confirmedSendRetention.forget(conversationKey, [...deleted])
        const confirmedVersions = new Map(result.items.filter(item => deleted.has(item.recordUid)).map(item => [item.recordUid, item.version]))
        const keep = (item: ArkmeTimelineItem) => !deleted.has(item.itemUid) || (item.recordVersion ?? 0) > (confirmedVersions.get(item.itemUid) ?? 0)
        setItems(current => current.filter(keep))
        conversationCacheRef.current.invalidateTimelines()
        const cached = conversationCacheRef.current.getTimeline(conversationKey)
        if (cached) conversationCacheRef.current.storeTimeline(conversationKey, { ...cached, items: cached.items.filter(keep), fetchedAtMillis: 0 })
        setRecordDeletion(undefined)
        if (deleted.size === result.items.length) exitMessageSelectMode()
        else setSelectMode(current => current?.sourceKey !== conversationKey ? current : { ...current,
          selectedIds: new Set(recordDeletion.items.filter(keep).map(arkmeTimelineOccurrenceKey)) })
        const rejection = result.items.find(item => item.result === 'rejected')
        showMessageActionStatus(deleted.size === result.items.length
          ? tr("成功删除 {v0} 条内容", { v0: String(deleted.size) })
          : rejection?.result === 'rejected'
            ? `已确认删除 ${String(deleted.size)} 条；${rejection.message}；其余内容未继续删除`
            : `已确认删除 ${String(deleted.size)} 条，其余内容请刷新核对后再操作`)
      }}
    />}
    {activeConversation && auth?.userId !== undefined && topicAssignment?.scopeKey === topicAssignmentScopeKey && <ArkmeRecordTopicAssignmentDialog
      key={topicAssignmentScopeKey}
      userId={auth.userId} environment={auth.environment} anchor={topicAssignment.anchor}
      source={topicAssignment.source} assignmentRefs={topicAssignment.assignmentRefs}
      {...(topicAssignment.currentTopicKey === undefined ? {} : { currentTopicKey: topicAssignment.currentTopicKey })}
      onCancel={() => { setTopicAssignment(undefined) }}
      onRefresh={() => {
        if (topicAssignment.source.kind !== 'send_to_self') confirmedSendRetention.forget(conversationKey, topicAssignment.itemUids)
        exitMessageSelectMode()
        setTopicAssignment(undefined)
        setSelfSourcesRetryRevision(value => value + 1)
        arkmeUi.recordChanged()
      }}
      onAssigned={(result, target) => {
        if (topicAssignment.source.kind !== 'send_to_self') confirmedSendRetention.forget(conversationKey, result.movedRecordUids)
        setTopicAssignment(undefined)
        exitMessageSelectMode()
        setSelfSourcesRetryRevision(value => value + 1)
        arkmeUi.recordChanged()
        showMessageActionStatus(`${target ? '已指定主题' : '已移出主题'}${result.projectionRefreshPending ? '，列表正在刷新' : ''}`)
      }}
    />}
    <div
      key="conversation-surface"
      className="arkme-conversation-surface"
      ref={surfaceRef}
      data-arkme-owned="product-surface"
      style={{
        ...styles.surface,
        ...(floating ? styles.floatingSurface : {}),
        ...(productChrome && compactNavigation ? styles.compactSurface : {}),
      }}
    >
      {productChrome && productNavigation && authView === 'content' && <ArkmeProductNavigation
        compact={compactNavigation}
        currentSessionId={currentSessionId}
      />}
      {productChrome && authView === 'content' && (conversationBackdropVisible || ui.mode === 'arko') && <aside
        data-arkme-owned="directory-pane"
        style={{ ...styles.directoryPane, ...(compactNavigation ? styles.compactDirectoryPane : {}) }}
        aria-label={tr("Arkme 对话目录")}
      >
        <ArkmeNavigation
          currentSessionId={currentSessionId}
          embeddedProductShell
          {...(aggregateSource === undefined ? {} : { sendToSelfSource: aggregateSource })}
          {...(directoryLead === undefined ? {} : { directoryLead })}
          {...(onCreateTask === undefined ? {} : { onCreateTask })}
          {...(onActivateSurface === undefined ? {} : { onActivateSurface })}
          {...(renderSlot === undefined ? {} : { renderSlot })}
        />
      </aside>}
      <section
        key="conversation-panel"
        className="arkme-conversation-panel"
        ref={panelRef}
        data-arkme-active-conversation-identity={activeConversation ? conversationKey : undefined}
        style={styles.panel}
        role="region"
        aria-label={surfaceTitle}
      >
        {selfTopicDirectoryOwner}
        {!selfWorkspaceSelected && selfTopicMenuOwner}
        {authView !== 'login' && !arkoContentVisible && !utilityContentVisible && !botConversationVisible && <header data-arkme-window-drag-region="conversation" className="arkme-conversation-header"
          data-arkme-centered-header={selfWorkspaceSelected ? 'self' : undefined}
          style={{ ...styles.header, ...(selfWorkspaceSelected ? {
            display: 'grid', gridTemplateColumns: CONVERSATION_HEADER_COLUMNS, paddingLeft: 20, paddingRight: 20,
          } : {}) }}>
          {authenticated && conversationBackdropVisible && source?.kind === 'group_chat' && <span style={styles.headerAvatar}>
            <ArkmeDirectorySourceAvatar source={source} size={34} />
          </span>}
          <div data-arkme-window-drag-region="conversation" style={styles.titleGroup}>
            {selfWorkspaceSelected
              ? <h2 style={styles.title}>{tr("发给自己")}</h2>
              : <div style={styles.titleBlock}>
                <span style={styles.titleLine}>
                  <h2 style={styles.title}>{surfaceTitle}</h2>
                  {authenticated && conversationBackdropVisible && source?.kind === 'private_chat'
                    && <ArkmeMembershipBadge memberType={source.peerMemberType} />}
                  {isArkmeOfficialAuthor(source) && <ArkmeTopicTagBadge label={tr("官方")} />}
                  {source?.isMuted === true && <span style={styles.titleMuteIcon}><ArkmeMuteIcon size={16} /></span>}
                </span>
                {authenticated && conversationBackdropVisible && source?.kind === 'group_chat'
                  && aiPolishSettings?.enabled === true
                  && <span style={styles.headerSubtitle}>{tr("AI润色已开启")}{aiPolishSettings.activeRuleName.trim() === '' ? '' : ` · ${aiPolishSettings.activeRuleName}`}</span>}
              </div>}
            {authenticated && selfWorkspaceSelected
              && source?.isMuted === true && <span style={styles.titleMuteIcon}><ArkmeMuteIcon size={16} /></span>}
          </div>
          {selfWorkspaceSelected && <div data-arkme-header-selector="self" style={{
            gridColumn: 2, gridRow: 1, justifySelf: 'center', minWidth: 0, maxWidth: '100%',
          }}>{selfTopicMenuOwner}</div>}
          {authenticated && activeConversation && sourceIsChat && source !== undefined && <ArkmeChatCalendar
            key={`chat-calendar:${authenticatedAccountKey}:${conversationKey}`}
            sourceRef={source.sourceRef} scopeKey={conversationKey} accountScope={authenticatedAccountKey}
            getReadingDate={getCalendarReadingDate}
            interactions={source.kind === 'private_chat' ? { moments: interwovenMoments, state: interwovenCalendarState,
              retry: () => setInterwovenRefreshRevision(value => value + 1) } : undefined}
            onSelect={calendarNavigation.select} onOpen={calendarNavigation.cancel} />}
          {authenticated && activeConversation && sourceIsChat && source !== undefined && <ArkmeConversationSearch
            key={`chat-search:${authenticatedAccountKey}:${conversationOverlayKey}`} source={source} host={panelRef} accountKey={authenticatedAccountKey ?? ''} />}
          {authenticated && conversationBackdropVisible && source?.kind === 'private_chat' && <ArkmePrivateCallMenu
            key={`private-call:${conversationOverlayKey}`}
            sourceRef={source.sourceRef}
            displayName={source.displayName}
            assetBasePath={authStoreSnapshot.config?.callAssetBasePath ?? '/arkme-self/api/call'}
          />}
          {authenticated && conversationBackdropVisible && source?.kind === 'group_chat' && <ArkmeGroupChatControls
            key={`group-controls:${conversationOverlayKey}`}
            source={source}
            accountScope={authenticatedAccountKey}
            overlayHostRef={panelRef}
            aiPolishSettings={aiPolishSettings}
            onAiPolishSettingsChanged={setAiPolishSettings}
            onSourceProjectionUpdated={updateSourceProjection}
            onMembershipChanged={applyGroupMembershipChange}
            onMessageDndUpdated={updateSourceMessageDndProjection}
            membersOpen={groupMembersOpen}
            onMembersOpenChange={open => { if (open) activateContextPanel('members'); else setGroupMembersOpen(false) }}
            onMemberOpen={openMemberProfile}
            onMemberContextMenu={openMemberMenu}
            onMemberHover={openMemberHover}
            onBotOpen={openBotProfile}
            onBotContextMenu={openBotMenu}
            onBotHover={openBotHover}
            onStatus={showMessageActionStatus}
            onError={setError}
            onExport={() => { startConversationExport(source) }}
            exportBusy={conversationExport?.status === 'downloading'}
            exportProcessed={conversationExport?.processed ?? 0}
          />}
          {authenticated && activeConversation && isArkmeSelfWorkspaceSource(source) && <div style={{ ...ARKME_CONVERSATION_HEADER_ACTIONS_STYLE, gridColumn: 3, gridRow: 1, justifySelf: 'end' }}>
            <ArkmeConversationHeaderIconButton
              label={tr("按日期查看发给自己")}
              buttonRef={selfCalendarButtonRef}
              hasPopup="dialog"
              expanded={selfCalendarOpen}
              onClick={() => {
                setRelatedMenuOpen(false)
                setSelfMenuOpen(false)
                if (!selfCalendarOpen) calendarNavigation.cancel()
                setSelfCalendarOpen(value => !value)
              }}
            ><CalendarBlank size={24} aria-hidden /></ArkmeConversationHeaderIconButton>
            <ArkmeSelfCalendarPopover
              key={`self-calendar:${authenticatedAccountKey}:${conversationKey}`}
              accountScope={authenticatedAccountKey}
              scopeKey={source.kind === 'topic' ? conversationKey : source.kind}
              sourceRef={source.sourceRef}
              open={selfCalendarOpen}
              getReadingDate={getCalendarReadingDate}
              anchor={selfCalendarButtonRef}
              onClose={() => { setSelfCalendarOpen(false) }}
              onSelectDate={calendarNavigation.select}
              onSelectRecord={(item: ArkmeCalendarRecordItem) => {
                arkmeUi.showConversationTarget(source, item.recordUid, item.sendAtMillis)
              }}
            />
            <ConversationActionsMenu
              items={[conversationExportActionItem({
                busy: conversationExport?.status === 'downloading',
                processed: conversationExport?.processed ?? 0,
                invoke: () => { startConversationExport(source) },
              })]}
              anchor={selfMenuButtonRef}
              label={tr("更多发给自己操作")}
              onClose={() => { setSelfMenuOpen(false) }}
              trigger={{
                open: selfMenuOpen,
                busy: conversationExport?.status === 'downloading',
                onOpenChange: open => {
                  setRelatedMenuOpen(false)
                  setSelfCalendarOpen(false)
                  setSelfMenuOpen(open)
                },
              }}
            />
          </div>}
          {shouldShowPrivateChatActions(authenticated, source?.kind) && <div style={{
            ...ARKME_CONVERSATION_HEADER_ACTIONS_STYLE,
            visibility: relatedPanelOpen ? 'hidden' : 'visible',
          }}>
            <ConversationActionsMenu items={privateChatActionItems(privateActions, directAdmission, openRelatedPanel, {
              busy: conversationExport?.status === 'downloading',
              processed: conversationExport?.processed ?? 0,
              invoke: () => { startConversationExport(source) },
            }, source?.kind === 'private_chat' && source.peerUserId !== undefined && source.peerUserId > 0
              ? () => activateContextPanel('common-groups') : undefined)}
              anchor={relatedMenuButtonRef}
              onClose={() => { setRelatedMenuOpen(false) }}
              trigger={{
                open: relatedMenuOpen,
                busy: conversationExport?.status === 'downloading',
                onOpenChange: open => {
                  setSelfMenuOpen(false)
                  setSelfCalendarOpen(false)
                  if (open === relatedMenuOpen) return
                  if (open) toggleRelatedMenu()
                  else setRelatedMenuOpen(false)
                },
              }}
            />
          </div>}
        </header>}
        {activeConversation && calendarNavigation.status !== undefined && <ArkmeCalendarNavigationStatus
          status={calendarNavigation.status} onCancel={calendarNavigation.cancel} onRetry={calendarNavigation.retry}
        />}
        {conversationExport?.status === 'downloading' && <div
          role="status"
          aria-live="polite"
          data-arkme-conversation-export-progress="true"
          style={styles.conversationExportProgress}
        >
          <IconLoadingOutline16 className="arkme-icon-spin" />
          <span style={styles.conversationExportProgressName}>{conversationExport.sourceName}</span>
          <span style={styles.conversationExportProgressCount}>{conversationExport.processed > 0
            ? tr("已处理 {v0} 条", { v0: String(conversationExport.processed) })
            : '正在准备'}</span>
        </div>}
        {conversationExportToast !== undefined && <Toast
          key={conversationExportToast.sequence}
          text={conversationExportToast.text}
          anchor={panelRef.current}
          icon={conversationExportToast.kind === 'success'
            ? <span style={{ color: 'var(--dsw-alias-state-success-primary, #16a34a)' }}><IconCheckOutline16 /></span>
            : <span style={{ color: 'var(--dsw-alias-state-error-primary, #ef4444)' }}><IconWarningOutline16 /></span>}
          onDone={() => {
            setConversationExportToast(current => current?.sequence === conversationExportToast.sequence ? undefined : current)
          }}
        />}
        {retainedCallPage}
        {authView === 'login' ? <div style={styles.loginBody}><ArkmeLogin
          t={t}
          mode={loginMode}
          phoneBindingRequired={phoneBindingRequired}
          agreed={agreed}
          busy={busy}
          submitBusy={submitBusy}
          error={error}
          phone={phone}
          smsCode={smsCode}
          smsCountdown={smsCountdown}
          testLoginEnabled={testLoginEnabled}
          jiwoScanLoginEnabled={jiwoScanLoginEnabled}
          testUserId={testUserId}
          qrDataUrl={qr}
          onModeChange={changeLoginMode}
          onAgreementChange={setAgreed}
          onPhoneChange={setPhone}
          onSmsCodeChange={setSmsCode}
          onTestUserIdChange={setTestUserId}
          onSendCode={() => { void sendCode() }}
          onVerifyCode={() => { void verifyCode() }}
          onTestLogin={() => { void testLogin() }}
          onWechatLogin={() => { void beginWechat() }}
          onJiwoLogin={() => { void beginJiwo() }}
          onCancelBinding={() => { void cancelBinding() }}
        /></div> : ui.mode === 'calls' ? null
          : ui.mode === 'recordings' ? <ArkmeRecordingSurface
            active={active}
            key={`recordings:${auth?.status ?? 'unknown'}:${auth?.environment ?? 'unknown'}:${String(auth?.userId ?? 0)}`}
            onOpenRecordingImport={openRecordingImport}
            recordingRefreshRevision={recordingRefreshRevision}
            recordingImportStatus={recordingImportStatus}
          />
          : ui.mode === 'recognized-speakers' ? <ArkmeRecognizedSpeakersSurface
            key={`recognized-speakers:${auth?.environment ?? 'unknown'}:${String(auth?.userId ?? 0)}`}
            accountKey={`${auth?.environment ?? 'unknown'}:${String(auth?.userId ?? 0)}`}
            onBack={() => { const dateStamp = ui.recordingReturnDateStamp; if (dateStamp === undefined) arkmeUi.showRecordings(); else arkmeUi.showRecordingTarget(dateStamp, dateStamp) }}
          />
          : ui.mode === 'world' ? <ArkmeWorldSurface
            key={`world:${auth?.environment}:${auth?.userId}:${ui.worldNavigationRevision ?? 0}`}
            initialScope={ui.worldInitialScope ?? 'all'}
            {...(ui.worldTarget === undefined ? {} : { target: ui.worldTarget })}
            {...(auth?.status !== 'authenticated' ? {} : { currentUserId: auth.userId })}
            onBackToWorld={() => { arkmeUi.backFromWorld() }}
            onSourceActivated={activateSource}
          />
          : ui.mode === 'search' ? <div style={styles.utilityBody}><ArkmeSearchSurface
            {...(ui.searchTarget === undefined ? {} : {
              initialQuery: ui.searchTarget.query,
              initialQueryRevision: ui.searchTarget.revision,
            })}
          /></div>
          : ui.mode === 'extensions' ? <ArkmeMarketplace
            displayMode="page"
            {...(currentSessionId === undefined ? {} : { currentSessionId })}
            {...(auth?.status !== 'authenticated' ? {} : { currentUserId: auth.userId })}
            {...(selfProfile?.avatarRef.trim() ? { currentUserAvatarRef: selfProfile.avatarRef.trim() } : {})}
            {...(ui.extensionShareRef === undefined ? {} : { shareRef: ui.extensionShareRef })}
            {...(ui.extensionShareAction === undefined ? {} : { shareAction: ui.extensionShareAction })}
            {...(ui.extensionDetailId === undefined ? {} : { initialExtensionId: ui.extensionDetailId })}
            {...(ui.extensionAuthorFilter === undefined ? {} : { initialAuthorFilter: ui.extensionAuthorFilter })}
            onShareResolved={extensionId => { arkmeUi.showExtensionDetail(extensionId) }}
            onShareExit={() => { arkmeUi.dismissExtensionShare() }}
            onPrivateChatOpened={activateSource}
          />
          : ui.mode === 'voiceprint' ? <ArkmeVoiceprintSurface onBack={() => { arkmeUi.showRecordings() }} />
          : ui.mode === 'arko' ? <ArkmeArkoSurface key={arkmeArkoSurfaceKey(auth)} />
          : botConversationVisible && ui.selectedBot !== undefined ? <ArkmeBotConversationSurface
            key={ui.selectedBot.botRef} bot={ui.selectedBot} onConversationActivity={bot => { arkmeUi.openBotConversation(bot) }} onDeleted={() => { arkmeUi.showHarness() }}
          />
          : source === undefined ? <div className="arkme-conversation-body" style={styles.body}>
            {activeSelfSourcesResolution?.status === 'error'
              ? <div role="alert" style={styles.loading}>
                <div style={styles.error}>{activeSelfSourcesResolution.message}</div>
                <button data-arkme-feedback="neutral" type="button" style={{ ...styles.retry, marginTop: 8, fontSize: 12 }}
                  onClick={() => { setSelfSourcesRetryRevision(value => value + 1) }}>{tr("重试")}</button>
              </div>
              : <div role="status" style={styles.loading}>{tr("正在加载发给自己的内容…")}</div>}
          </div> : <ArkmeWideConversation
            enabled={active && activeConversation && (source.kind === 'private_chat' || source.kind === 'group_chat')}
            scopeKey={conversationKey} viewportRef={bodyRef} controlsHidden={activeSelectMode !== undefined}>
          <ArkmeInterwovenReadProvider sourceRef={source.sourceRef} scope={`${authenticatedAccountKey}:${conversationKey}`}
            enabled={activeConversation && authenticated && source.kind === 'private_chat'}>
          <div style={{ position: 'relative', display: 'flex', flex: 1, minHeight: 0 }}>
          <div className="arkme-conversation-body" data-arkme-width-viewport ref={bodyRef} style={{
            ...styles.body,
            ...(displayRows.length === 0 && interwovenWindow.prelude.length === 0 ? { display: 'flex', flexDirection: 'column' as const } : {}),
          }} onScroll={handleConversationScroll}>
            {error !== '' && <div style={styles.error}>{error}</div>}
            {error !== '' && displayRows.length === 0 && timelineLoadingKey !== conversationKey && <button data-arkme-feedback="neutral"
              type="button" style={styles.retry} onClick={() => {
                setError('')
                setTimelineLoadingKey(conversationKey)
                setForegroundReadRevision(value => value + 1)
              }}>{tr("重新加载")}</button>}
            {interwovenWindow.prelude.length > 0 && <ArkmeInterwovenPrelude
              key={conversationKey} moments={interwovenWindow.prelude} onOpen={openMomentDetail} />}
            <div ref={sentinelRef} style={styles.sentinel} />
            {hasMore && <div style={styles.loading}>
              {(olderLoadError !== '' || nextCursor === undefined) && <div role="alert">
                {olderLoadError || '暂时无法继续加载，请刷新会话后重试'}
              </div>}
              {nextCursor !== undefined && <button data-arkme-feedback="neutral" type="button" style={styles.retry} disabled={loadingOlder}
                onClick={() => { void loadOlderHistory() }}>
                {loadingOlder ? tr("正在加载更早内容…") : olderLoadError === '' ? '继续加载更早消息' : '重试加载更早消息'}
              </button>}
            </div>}
            {timelineSkeletonKey === conversationKey && displayRows.length === 0 && <div
              role="status"
              aria-label={tr("正在加载会话内容")}
              style={styles.timelineSkeleton}
            >{[58, 44, 66, 50, 61].map((width, index) => <div
              key={`${width}:${index}`}
              aria-hidden
              style={{ ...styles.timelineSkeletonRow, ...(index % 3 === 1 ? styles.timelineSkeletonRowMe : {}) }}
            >
              <span style={styles.timelineSkeletonAvatar} />
              <span style={{ ...styles.timelineSkeletonBubble, width: `${width}%` }} />
            </div>)}</div>}
            {displayRows.length > 0 && <ul ref={recordsRef} className={`arkme-conversation-records${timelineRevealKey === conversationKey
              ? ' arkme-conversation-records-reveal'
              : ''}`} style={styles.records}>

              {renderedMessageRows}

            </ul>}
            <div ref={newerSentinelRef} style={styles.sentinel} />
            <div ref={endAccessoryRef} data-arkme-conversation-end-accessory aria-hidden={activeSelectMode !== undefined || undefined}
              style={{ flexShrink: 0, visibility: activeSelectMode === undefined ? 'visible' : 'hidden', ...(displayRows.length === 0 ? { marginTop: 'auto' } : {}) }}>
              {activeConversation
                && (source?.kind === 'private_chat' || source?.kind === 'group_chat')
                && source.sourceKey !== undefined && authenticatedAccountKey !== undefined
                && <ArkmeMessagePreparingIndicator sourceKey={source.sourceKey} accountScope={authenticatedAccountKey} />}
            </div>
          </div>
          <RegionMarquee getStartArea={() => selectionStartAreaRef.current && composerRef.current
            ? { element: selectionStartAreaRef.current, boundary: composerRef.current } : undefined} viewportRef={bodyRef} scopeKey={`${authenticatedAccountKey}:${conversationKey}`}
            enabled={active && activeConversation && timelineStateKey === conversationKey && messageActionBusy === undefined
              && forwardTargetPicker === undefined && topicAssignment === undefined && drawer === undefined}
            getItems={() => [...(bodyRef.current?.querySelectorAll<HTMLElement>('[data-arkme-selection-key]') ?? [])]
              .map(element => ({ key: element.dataset.arkmeSelectionKey!, element }))}
            onCommit={commitMarqueeSelection}
            style={{ border: `1px solid ${arkmeTheme.accent}`, background: `color-mix(in srgb, ${arkmeTheme.accent} 16%, transparent)` }}
          />
          {active && activeConversation && timelineStateKey === conversationKey && <ArkmeConversationBottomControl
            key={`${authenticatedAccountKey}:${conversationKey}`}
            showBackToBottom={bottomVisibility.visible || timelineMode === 'around' || newerHasMore}
            newMessageCount={newMessageCount}
            onReturnToLatest={returnToLatest}
          />}
          </div>
          {isArkmeOfficialAuthor(source) && timelineStateKey === conversationKey
            && timelineLoadingKey !== conversationKey && error === ''
            && conversationCacheRef.current.getTimeline(conversationKey) !== undefined
            && displayRows.length === 0 && !hasMore && !newerHasMore
            && (source?.latestSequence ?? 0) === 0
            && <ArkmeOfficialAuthorGuide />}
          {activeConversation && forwardSuccessFeedback !== undefined && <div style={styles.forwardSuccessBannerWrap}>
            <button
              type="button"
              style={styles.forwardSuccessBanner}
              aria-label={forwardSuccessFeedback.message}
              onClick={() => {
                if (forwardSuccessTimerRef.current !== undefined) {
                  window.clearTimeout(forwardSuccessTimerRef.current)
                  forwardSuccessTimerRef.current = undefined
                }
                const target = forwardSuccessFeedback.targets[0]
                setForwardSuccessFeedback(undefined)
                if (target !== undefined) activateSource(target)
              }}
            >
              <span
                style={{
                  ...styles.forwardSuccessAvatarStack,
                  width: Math.min(30 + Math.max(0, forwardSuccessFeedback.targets.length - 1) * 15, 75),
                }}
              >
                {forwardSuccessFeedback.targets.slice(0, 4).map((target, index) => <span
                  key={target.sourceRef}
                  style={{ position: 'absolute', left: index * 15, top: 0 }}
                >
                  <ArkmeDirectorySourceAvatar source={target} size={30} />
                </span>)}
              </span>
              <span style={styles.forwardSuccessText}>{forwardSuccessFeedback.message}</span>
              <span style={styles.forwardSuccessAction}>{tr("去看看")}</span>
            </button>
          </div>}
          {((activeSelectMode === undefined && askDsh.status) || messageActionStatus) && <div role="status" aria-live="polite" style={styles.messageActionToast}>{(activeSelectMode === undefined && askDsh.status) || messageActionStatus}</div>}
          {/* Match the desktop input Stack: the editor stays mounted beneath the selection overlay. */}
          <div ref={selectionStartAreaRef} className="arkme-conversation-input-slot" style={{ position: 'relative', flex: 'none', ...(activeSelectMode === undefined ? {} : { minHeight: askDsh.status ? 160 : 130 }) }}>
          {archiveReadOnly && source !== undefined && <div
            aria-hidden={activeSelectMode !== undefined || undefined}
            {...(activeSelectMode === undefined ? {} : { inert: '' })}
            style={{ visibility: activeSelectMode === undefined ? 'visible' : 'hidden' }}>
            <ArkmeTopicReadOnlyNotice />
          </div>}
          {(!archiveReadOnly || activeRecordReeditComposer !== undefined) && <footer data-arkme-width-composer className="arkme-conversation-composer"
            aria-hidden={activeSelectMode !== undefined || undefined}
            {...(activeSelectMode === undefined ? {} : { inert: '' })}
            style={{ ...styles.composer, ...(activeSelectMode === undefined ? {} : { visibility: 'hidden', pointerEvents: 'none' }) }}
            onClick={event => { focusArkmeComposerFromClick(textareaRef.current, event) }}
            onDragOver={event => { if (!composerFileAddingDisabled && Array.from(event.dataTransfer.types).includes('Files')) { event.preventDefault(); event.dataTransfer.dropEffect = 'copy' } }}
            onDrop={event => { if (!composerFileAddingDisabled && event.dataTransfer.files.length > 0) { event.preventDefault(); void selectFiles(event.dataTransfer.files) } }}
          ><div style={styles.composerStack}>
            {activeRecordReeditComposer !== undefined && activeRecordReeditComposer.error !== ''
              && <div role="alert" style={styles.error}>{activeRecordReeditComposer.error}</div>}
            {activeRecordReeditComposer !== undefined && (activeRecordReeditComposer.conflict !== undefined
              || (activeRecordReeditComposer.snapshot?.draft !== undefined
                && activeRecordReeditComposer.snapshot.draft.baseVersion !== undefined
                && activeRecordReeditComposer.snapshot.draft.baseVersion !== activeRecordReeditComposer.snapshot.version)) && <button data-arkme-feedback="neutral"
              type="button" style={styles.retry} disabled={composerFilesDisabled}
              aria-label={activeRecordReeditComposer.conflict === 'draft' ? '重新载入草稿' : '放弃草稿并重新载入'}
              onClick={() => setRecordReeditComposer(current => sameArkmeRecordReeditSession(current, activeRecordReeditComposer)
                ? { ...current, recoveryConfirmation: true } : current)}
            >{activeRecordReeditComposer.conflict === 'draft' ? '重新载入草稿' : '放弃草稿并重新载入'}</button>}
            {activeRecordReeditComposer?.recoveryConfirmation && <ArkmeConfirmDialog
              titleId="arkme-reedit-recovery-title"
              title={activeRecordReeditComposer.conflict === 'draft' ? '重新载入最新草稿？' : '放弃草稿并重新载入？'}
              description={activeRecordReeditComposer.conflict === 'draft'
                ? '当前输入区将替换为其他入口保存的最新草稿。不会删除草稿或修改已保存的快记。'
                : '本机这份重新编辑草稿中的正文和附件改动将被放弃，重新载入已保存的快记。此操作不会删除线上附件。'}
              busy={activeRecordReeditComposer.busy}
              error={activeRecordReeditComposer.error}
              confirmTone={activeRecordReeditComposer.conflict === 'draft' ? 'primary' : 'danger'}
              confirmLabel={activeRecordReeditComposer.conflict === 'draft' ? '重新载入' : '放弃并重新载入'}
              busyLabel={tr("正在重新载入…")}
              onClose={() => setRecordReeditComposer(current => sameArkmeRecordReeditSession(current, activeRecordReeditComposer) && !current.busy
                ? { ...current, recoveryConfirmation: false } : current)}
              onConfirm={() => { void recoverRecordReedit() }}
            />}
            {composerInfoRow}
            {activeComposerTargetItem !== undefined && <div
              style={styles.composerExtensionTarget}
              {...(activeRecordReeditComposer === undefined
                ? { 'data-arkme-composer-extension-target': 'true' }
                : { 'data-arkme-composer-reedit-target': 'true' })}
            >
              <div style={styles.composerExtensionTargetBody}>
                {activeRecordReeditComposer !== undefined && <div style={styles.composerReeditLabel}>{tr("重新编辑:")}</div>}
                {(activeComposerTargetItem.textContent.trim() || activeComposerTargetItem.title.trim()) !== ''
                  && <div style={styles.composerExtensionTargetText}><ArkmeRichText text={activeComposerTargetItem.textContent.trim() || activeComposerTargetItem.title.trim()} presentation="preview" /></div>}
                {(activeComposerTargetItem.contentBlocks?.length ?? 0) > 0 && <div style={styles.composerExtensionTargetFiles}>
                  {activeComposerTargetItem.contentBlocks?.slice(0, 3).map((block, index) => <span key={`${block.mediaRef}:${String(index)}`} style={styles.composerExtensionTargetFile} title={block.fileName || '附件'}>
                    {block.kind === 'image'
                      ? <><img style={styles.composerExtensionTargetImage} src={createArkmeSdk().mediaUrl(block.mediaRef)} alt={block.fileName || '图片附件'} /><span hidden>{block.fileName || '附件'}</span></>
                      : block.fileName || '附件'}
                  </span>)}
                </div>}
              </div>
              <button data-arkme-feedback="neutral"
                type="button"
                style={styles.composerExtensionTargetCancel}
                aria-label={activeRecordReeditComposer === undefined ? '取消延展' : '关闭重新编辑'}
                disabled={activeRecordReeditComposer?.busy === true || preparingReeditFiles}
                onClick={() => {
                  if (activeRecordReeditComposer === undefined) {
                    setComposerExtensionTarget(undefined)
                    textareaRef.current?.focus()
                  } else {
                    void closeRecordReedit()
                  }
                }}
              >×</button>
            </div>}
            <div
              ref={composerRef}
              className="arkme-conversation-composer-inner"
              data-arkme-primary-composer="true"
              data-arkme-composer-focused={composerInputFocused ? 'true' : 'false'}
              data-arkme-composer-resize-highlighted={composerResize.highlighted ? 'true' : undefined}
              style={{
              ...styles.composerInner,
              ...(composerInputFocused ? styles.composerInnerFocused : {}),
              ...arkmeConversationComposerBorder(composerInputFocused || composerHasTarget ? colors.border : 'transparent', composerHasTarget, composerResize.highlighted),
              }}
            >
            {composerResize.handle}
            {addMenuOpen && <ArkmeActionMenu label={tr("添加内容")} side="top"
              getAnchorRect={() => addMenuTriggerRef.current?.getBoundingClientRect() ?? null}
              onClose={() => setAddMenuOpen(false)} actions={[
                { id: 'files', label: '添加附件', icon: <IconPaperclipOutline16 />, onSelect: () => { setAddMenuOpen(false); fileInputRef.current?.click() } },
                activeRecordReeditComposer === undefined && { id: 'article', label: sourceIsChat ? '添加长文' : '写长文', icon: <IconEditOutline16 />, disabled: pendingArticle?.sending === true, onSelect: () => { if (pendingArticle?.sending) return; if (sourceIsChat) setArticlePickerScope(captureComposerAsyncScope()); else void createLongArticle(); setAddMenuOpen(false) } },
              ]} />}
            <input ref={fileInputRef} type="file" multiple hidden onChange={event => { void selectFiles(event.currentTarget.files) }} />
            <div className="arkme-conversation-input-card">
            {pendingArticle && <div data-arkme-pending-article="true" style={{ display: 'flex', gap: 12, alignItems: 'center', margin: '8px 12px', padding: '10px 12px', border: `1px solid ${arkmeTheme.border}`, borderRadius: 10, background: arkmeTheme.layer1, color: arkmeTheme.text }}>
              <IconEditOutline16 />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 600 }}>{pendingArticle.article.kind === 'existing' ? pendingArticle.article.detail.title : pendingArticle.article.draft.title}</div>
                <div style={{ color: arkmeTheme.secondary, fontSize: 12, marginTop: 4 }}>{pendingArticle.sending ? '长文发送中…' : '长文将单独发送，原文字和附件保留'}</div>
                {pendingArticle.error && <div role="alert" style={{ color: arkmeTheme.danger, fontSize: 12 }}>{pendingArticle.error} {tr("· 点击发送重试")}</div>}
              </div>
              <button type="button" aria-label={tr("移除待发送长文")} disabled={pendingArticle.sending} style={{ color: arkmeTheme.secondary, background: 'transparent', border: 0, cursor: 'pointer', fontSize: 20 }} onClick={() => { if (articleDraftKey) composerArticleStore.remove(articleDraftKey) }}>×</button>
            </div>}
            {visibleComposerAttachments.length > 0 && <ArkmeAttachmentStrip attachments={visibleComposerAttachments} disabled={composerFilesDisabled}
              onMove={(from, to) => {
                if (composerFilesDisabled) return
                if (activeRecordReeditComposer === undefined) arkmeComposerDraftStore.moveAttachment(composerDraftKey, from, to)
                else setRecordReeditComposer(current => {
                  if (!sameArkmeRecordReeditSession(current, activeRecordReeditComposer)) return current
                  const next = [...current.attachments]
                  const attachment = next[from]
                  if (attachment === undefined || to < 0 || to >= next.length) return current
                  next.splice(from, 1); next.splice(to, 0, attachment)
                  return { ...current, attachments: next, error: '' }
                })
              }}
              canPreview={attachment => activeRecordReeditComposer === undefined
                ? attachment.localFile !== undefined
                : activeRecordReeditComposer.attachments.some(view => arkmeAttachmentId(view) === arkmeAttachmentId(attachment)
                  && arkmeRecordReeditAttachmentBlock(view) !== undefined)}
              onPreview={attachment => {
                if (activeRecordReeditComposer === undefined) {
                  if (attachment.localFile !== undefined) {
                    const block = localFileBlock(attachment.localFile)
                    if (attachmentPreviewBridge()?.version === 1) {
                      try { openSessionAttachmentPreview(attachments.flatMap(value => value.localFile === undefined ? [] : [localFileBlock(value.localFile)]), block, 'composer') }
                      catch { setError('无法创建预览窗口，请再次点击附件重试') }
                    } else setDraftPreview(block)
                  }
                } else {
                  const view = activeRecordReeditComposer.attachments.find(view => arkmeAttachmentId(view) === arkmeAttachmentId(attachment))
                  if (view !== undefined) {
                    const block = arkmeRecordReeditAttachmentBlock(view)
                    if (block !== undefined && attachmentPreviewBridge()?.version === 1) {
                      try { openSessionAttachmentPreview(activeRecordReeditComposer.attachments.flatMap(value => { const item = arkmeRecordReeditAttachmentBlock(value); return item === undefined ? [] : [item] }), block, 'composer-reedit') }
                      catch { setError('无法创建预览窗口，请再次点击附件重试') }
                    } else setDraftPreview(block)
                  }
                }
              }}
              onRemove={attachment => {
                if (composerFilesDisabled) return
                if (activeRecordReeditComposer !== undefined) {
                  setRecordReeditComposer(current => sameArkmeRecordReeditSession(current, activeRecordReeditComposer)
                    ? { ...current, attachments: current.attachments.filter(view => arkmeAttachmentId(view) !== arkmeAttachmentId(attachment)), error: '' }
                    : current)
                  setDraftPreview(undefined)
                  return
                }
                arkmeComposerDraftStore.removeAttachment(composerDraftKey, arkmeAttachmentId(attachment))
                if (attachment.localFile !== undefined) void callArkme('files.local.remove', { fileRef: attachment.localFile.fileRef }).catch(caught => setError(errorMessage(caught)))
              }}
            />}
            {activeRecordReeditComposer?.attachments.some(attachment => attachment.unavailable) && <div role="status" style={styles.error}>{tr("部分附件不可用，请移除后重新添加")}</div>}
            {activeConversation && draftPreview !== undefined && typeof document !== 'undefined' && createPortal(<ArkmeMediaPreview selected={draftPreview} blocks={activeRecordReeditComposer === undefined
              ? attachments.flatMap(attachment => attachment.localFile === undefined ? [] : [localFileBlock(attachment.localFile)])
              : activeRecordReeditComposer.attachments.flatMap(attachment => { const block = arkmeRecordReeditAttachmentBlock(attachment); return block === undefined ? [] : [block] })} onSelect={setDraftPreview} onClose={() => setDraftPreview(undefined)} openLocalFile={false} />, document.body)}
            {activeRecordReeditComposer === undefined && hashTagTrigger !== undefined && <div ref={hashTagSuggestionListRef} style={styles.mentionSuggestions} role="listbox" aria-label={tr("选择标签")}>
              {hashTagCandidates.length === 0 && !hashTagError
                ? <div style={styles.mentionSuggestionsEmpty}>{hashTagLoading ? '正在加载标签…' : '暂无匹配标签，可继续输入创建新标签'}</div>
                : hashTagCandidates.map((item, index) => <button data-arkme-feedback="neutral"
                  key={`${item.normalizedTag}:${item.tagText}`}
                  type="button"
                  role="option"
                  aria-selected={index === hashTagCandidateIndex}
                  style={{
                    ...styles.mentionSuggestionRow,
                    ...(index === hashTagCandidateIndex ? styles.mentionSuggestionRowActive : {}),
                  }}
                  onMouseEnter={() => { setHashTagCandidateIndex(index) }}
                  onMouseDown={event => {
                    event.preventDefault()
                    insertHashTagCandidate(item)
                  }}
                >
                  <span style={{ ...styles.mentionSuggestionBotAvatar, flex: 'none', fontWeight: 600 }} aria-hidden>#</span>
                  <span style={styles.mentionSuggestionText}>
                    <span style={styles.mentionSuggestionName}>{item.tagText}</span>
                    <span style={styles.mentionSuggestionSecondary}>{tr("使用")} {item.recordCount} {tr("次")}</span>
                  </span>
                </button>)}
              {hashTagError && <div role="status" style={styles.mentionSuggestionsEmpty}>{tr("标签加载失败，")}<button type="button" onMouseDown={event => event.preventDefault()} onClick={() => hashTagRetryRef.current?.()}>{tr("重试")}</button>
              </div>}
            </div>}
            {mentionTrigger !== undefined && <div style={styles.mentionSuggestions} role="listbox" aria-label={tr("选择要 @ 的对象")}>
              <ArkmeMentionSuggestionThemeStyles />
              {mentionCandidates.length === 0
                ? <div style={styles.mentionSuggestionsEmpty}>{tr("暂无可 @ 的对象")}</div>
                : mentionCandidates.map((member, index) => <ArkmeMentionSuggestionRow
                  key={arkmeMentionCandidateKey(member)}
                  candidate={member}
                  active={index === mentionCandidateIndex}
                  styles={{
                    row: styles.mentionSuggestionRow!,
                    rowActive: styles.mentionSuggestionRowActive!,
                    avatar: styles.mentionSuggestionAvatar!,
                    botAvatar: styles.mentionSuggestionBotAvatar!,
                    text: styles.mentionSuggestionText!,
                    name: styles.mentionSuggestionName!,
                    secondary: styles.mentionSuggestionSecondary!,
                  }}
                  onActive={() => { setMentionCandidateIndex(index) }}
                  onSelect={() => { insertMentionCandidate(member) }}
                />)}
            </div>}
            <ArkmeRichComposerInput textFormat={activeRecordReeditComposer?.snapshot?.textFormat ?? activeRecordReeditComposer?.item.textFormat ?? 'plain'} markdownEnabled={activeRecordReeditComposer === undefined && markdownQuickNotesEnabled} key={activeRecordReeditComposer === undefined ? composerDraftKey : `record-reedit:${activeRecordReeditComposer.generation}`} className="arkme-conversation-textarea" ref={textareaRef} style={{ ...styles.textarea!, ...composerResize.editorStyle }} value={visibleComposerText} mentions={activeRecordReeditComposer === undefined ? composerDraft.mentions : activeRecordReeditComposer.mentions ?? []} emojis={activeRecordReeditComposer === undefined ? composerDraft.emojis : []} maxLength={activeRecordReeditComposer?.snapshot?.maxTextLength ?? 20000} placeholder={effectiveComposerPlaceholder} ariaLabel={activeRecordReeditComposer === undefined ? effectiveComposerPlaceholder : '重新编辑快记'} disabled={composerFilesDisabled}
              selectionRequest={activeConversation && !composerFilesDisabled
                && editedComposerSelection?.scope === composerAsyncScopeRef.current
                && authenticatedAccountKey === editedComposerSelection.scope.accountKey
                && activeRecordReeditComposer?.generation === editedComposerSelection.reeditGeneration
                ? editedComposerSelection.request : undefined}
              markdown={activeRecordReeditComposer === undefined && !directAdmission.blocked ? composerDraft.markdown : undefined}
              onMarkdownChange={(markdown, text, mentions, emojis) => {
                if (activeRecordReeditComposer === undefined && !directAdmission.blocked) arkmeComposerDraftStore.setMarkdown(composerDraftKey, markdown, text, mentions, emojis)
              }}
              onTextChange={updateComposerText}
              onInputActivity={messagePreparing.input}
              onFocus={() => { messagePreparing.focus(true); setComposerInputFocused(true) }}
              onBlur={() => { messagePreparing.focus(false); setComposerInputFocused(false) }}
              onSelectionChange={updateComposerRichTrigger}
              onPaste={event => {
                if (composerFileAddingDisabled) return
                const files = arkmeClipboardFiles(event.clipboardData)
                if (files.length === 0) return
                event.preventDefault()
                const restoreFocus = beginPasteFocus()
                setEditedComposerSelection(undefined)
                void selectFiles(files, true).finally(restoreFocus)
              }}
              onKeyDown={event => {
                if (event.nativeEvent.isComposing) return
                if (activeRecordReeditComposer === undefined && hashTagTrigger !== undefined) {
                  if (event.key === 'Escape') {
                    event.preventDefault()
                    dismissedHashTagStartRef.current = hashTagTrigger.startIndex
                    setHashTagTrigger(undefined)
                    return
                  }
                  if (event.key === 'ArrowDown' && hashTagCandidates.length > 0) {
                    event.preventDefault()
                    setHashTagCandidateIndex(index => (index + 1) % hashTagCandidates.length)
                    return
                  }
                  if (event.key === 'ArrowUp' && hashTagCandidates.length > 0) {
                    event.preventDefault()
                    setHashTagCandidateIndex(index => (index + hashTagCandidates.length - 1) % hashTagCandidates.length)
                    return
                  }
                  if ((event.key === 'Enter' || event.key === 'Tab') && hashTagCandidates.length > 0) {
                    event.preventDefault()
                    const selectedCandidate = hashTagCandidates[Math.min(hashTagCandidateIndex, hashTagCandidates.length - 1)]
                    if (selectedCandidate !== undefined) insertHashTagCandidate(selectedCandidate)
                    return
                  }
                }
                if (mentionTrigger !== undefined) {
                  if (event.key === 'Escape') {
                    event.preventDefault()
                    setMentionTrigger(undefined)
                    return
                  }
                  if (event.key === 'ArrowDown' && mentionCandidates.length > 0) {
                    event.preventDefault()
                    setMentionCandidateIndex(index => (index + 1) % mentionCandidates.length)
                    return
                  }
                  if (event.key === 'ArrowUp' && mentionCandidates.length > 0) {
                    event.preventDefault()
                    setMentionCandidateIndex(index => (index + mentionCandidates.length - 1) % mentionCandidates.length)
                    return
                  }
                  if ((event.key === 'Enter' || event.key === 'Tab') && mentionCandidates.length > 0) {
                    event.preventDefault()
                    const selectedCandidate = mentionCandidates[Math.min(mentionCandidateIndex, mentionCandidates.length - 1)]
                    if (selectedCandidate !== undefined) insertMentionCandidate(selectedCandidate)
                    return
                  }
                }
                if (activeRecordReeditComposer !== undefined && !event.nativeEvent.isComposing && (event.key === 'Backspace' || event.key === 'Delete')) {
                  const deletion = arkmeComposerAtomicDeletion(visibleComposerText, activeRecordReeditComposer.mentions ?? [],
                    textareaRef.current?.selectionStart ?? visibleComposerText.length,
                    textareaRef.current?.selectionEnd ?? visibleComposerText.length,
                    event.key === 'Backspace' ? 'backward' : 'forward')
                  if (deletion) {
                    event.preventDefault()
                    updateComposerText(deletion.text)
                    focusEditedComposer(deletion.caretIndex)
                    return
                  }
                }
                if (activeRecordReeditComposer === undefined && !event.nativeEvent.isComposing && (event.key === 'Backspace' || event.key === 'Delete')) {
                  const caret = arkmeComposerDraftStore.deleteMentionAtSelection(
                    composerDraftKey,
                    textareaRef.current?.selectionStart ?? draft.length,
                    textareaRef.current?.selectionEnd ?? draft.length,
                    event.key === 'Backspace' ? 'backward' : 'forward',
                  )
                  if (caret !== undefined) {
                    event.preventDefault()
                    focusEditedComposer(caret)
                    return
                  }
                }
                if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
                  event.preventDefault()
                  if (canSend) void send()
                }
              }} />
            </div>
            <div data-arkme-composer-footer="tools" style={styles.tools}><div style={styles.toolGroup}><ArkmeComposerToolButton ref={addMenuTriggerRef} onPointerDown={event => event.stopPropagation()} aria-label={tr("添加内容")} aria-haspopup="menu" aria-expanded={addMenuOpen} disabled={composerFileAddingDisabled} onClick={() => { setAddMenuOpen(value => !value) }}>{(activeRecordReeditComposer === undefined ? preparingFiles : preparingReeditFiles) ? <ArkmeFilePreparingIndicator /> : <ArkmeComposerPlusIcon />}</ArkmeComposerToolButton><ArkmeEmojiPicker
              key={`emoji-picker:${authenticatedAccountKey}:${conversationOverlayKey}`}
              accountKey={authenticatedAccountKey}
              disabled={activeSelectMode !== undefined || preparingFiles || directAdmission.blocked || activeRecordReeditComposer !== undefined}
              scopeKey={composerDraftKey}
              {...(source?.kind === 'private_chat' || source?.kind === 'group_chat' ? { sourceRef: source.sourceRef } : {})}
              getCaretGeometry={() => textareaRef.current?.getCaretGeometry()}
              getEditorGeometry={() => textareaRef.current?.getEditorGeometry()}
              onSelect={insertEmoji}
              onUploadSticker={async file => {
                if (composerDraftKey === undefined) throw new Error('请先选择聊天')
                return await uploadFavoriteSticker(file)
              }}
              onStickerSent={async () => {
                if (!sameComposerAsyncScope(renderedComposerAsyncScope)) return
                await loadTimeline(undefined, false, 40, 'return-to-latest')
              }}
              onError={message => { setError(message) }}
            />{authenticatedUserId !== undefined && activeRecordReeditComposer === undefined && arkmeSourceSupportsRecordInputCapture(source?.kind) && <ArkmeComposerScreenshotButton
              userId={authenticatedUserId}
              scope={composerAsyncScopeRef.current}
              active={activeConversation}
              disabled={composerFileAddingDisabled || activeSelectMode !== undefined || !activeConversation}
              isCurrent={() => sameComposerAsyncScope(renderedComposerAsyncScope) && recordReeditComposerRef.current === undefined}
              onBegin={options => {
                syncComposerUserInput(true)
                textareaRef.current?.focus({ preventScroll: true })
                return beginPasteFocus({ nativeDialog: true, ...options })
              }}
              onFile={file => selectFiles([file], true)}
              onError={message => setError(message)}
            />}</div><div style={styles.composerSendArea}>
              {authenticatedUserId !== undefined && authenticatedAccountKey !== undefined
                && source !== undefined && isArkmeSelfWorkspaceSource(source)
                && activeRecordReeditComposer === undefined && <ArkmeSelfRolePicker
                  accountKey={authenticatedAccountKey}
                  userId={authenticatedUserId}
                  selectedRole={selectedSelfRole}
                  onSelect={setSelectedSelfRole}
                  selfAvatarRef={selfProfile?.avatarRef}
                  disabled={composerFilesDisabled || activeSelectMode !== undefined}
                />}
              <ArkmeComposerSendButton
                disabled={!canSend}
                ariaLabel={activeRecordReeditComposer === undefined ? pendingArticle ? '发送长文' : tr("发送消息") : '保存重新编辑'}
                shortcutHint={tr("Enter发送 / Shift+Enter换行")}
                onClick={() => { void send() }}
              />
            </div></div>
            {activeRecordReeditComposer === undefined && arkmeSourceSupportsRecordInputCapture(source?.kind) && <ArkmeBackgroundSoundWaveform
              owner={recordInputCaptureOwner}
              draftKey={composerDraftKey}
            />}
          </div>
          </div></footer>}
          {activeSelectMode !== undefined && selectedMessageCount > 0 && !selectedMessagesSupportSnapshotBatch && <div
            role="status" style={{ padding: '6px 16px', color: arkmeTheme.secondary, fontSize: 12 }}
          >{selectedMessageCount > 100 ? `已选择 ${selectedMessageCount} 条消息，批量操作最多支持 100 条，请减少选择后操作`
            : '选中的消息包含暂不支持复制链接或转发的内容，可取消这些消息后操作'}</div>}
          {activeSelectMode !== undefined && <div style={{ ...styles.selectBar, flexWrap: 'wrap' }} role="toolbar" aria-label={tr("已选择 {v0} 条消息", { v0: selectedMessageCount })}>
            {askDsh.status && <div role="status" aria-live="polite" style={{ flexBasis: '100%', textAlign: 'center', color: arkmeTheme.secondary, fontSize: 12, overflowWrap: 'anywhere' }}>{askDsh.status}</div>}
            {source !== undefined && isArkmeSelfWorkspaceSource(source) && <button data-arkme-feedback="neutral"
              type="button" aria-label={tr("指定主题")} style={styles.selectBarButton}
              disabled={!canAssignRecordTopics(selectedMessageItems) || selectedMessageItems.length !== selectedMessageCount}
              onClick={event => {
                if (selectedMessageItems.length !== selectedMessageCount) return
                openRecordTopicAssignment(selectedMessageItems, event.currentTarget)
              }}
            ><span style={styles.selectBarIconTile}><ArkmeSelectActionIcon kind="assign" size={20} /></span><span style={styles.selectBarLabel}>{tr("指定主题")}</span></button>}
            {(() => {
              const copyTextEnabled = arkmeCanCopySelectedMessageText(selectedMessageItems.length, messageActionBusy !== undefined)
              return <button data-arkme-feedback="neutral"
                type="button"
                style={{
                  ...styles.selectBarButton,
                  ...(!copyTextEnabled ? styles.selectBarButtonDisabled : {}),
                }}
                disabled={!copyTextEnabled}
                onClick={() => { void copySelectedMessageText() }}
              ><span style={styles.selectBarIconTile}><ArkmeSelectActionIcon kind="copy" /></span><span style={styles.selectBarLabel}>{ARKME_MESSAGE_SELECT_ACTION_LABELS[0]}</span></button>
            })()}
            <button data-arkme-feedback="neutral"
              type="button"
              style={{
                ...styles.selectBarButton,
                ...(!selectedMessagesSupportSnapshotBatch || messageActionBusy !== undefined ? styles.selectBarButtonDisabled : {}),
              }}
              disabled={!selectedMessagesSupportSnapshotBatch || messageActionBusy !== undefined}
              onClick={() => { void copyMessageLink(selectedMessageItems) }}
            ><span style={styles.selectBarIconTile}><ArkmeSelectActionIcon kind="link" /></span><span style={styles.selectBarLabel}>{ARKME_MESSAGE_SELECT_ACTION_LABELS[1]}</span></button>
            <button data-arkme-feedback="neutral"
              type="button"
              style={{
                ...styles.selectBarButton,
                ...(!selectedMessagesSupportSnapshotBatch || messageActionBusy !== undefined ? styles.selectBarButtonDisabled : {}),
              }}
              disabled={!selectedMessagesSupportSnapshotBatch || messageActionBusy !== undefined}
              onClick={() => { openForwardTargetPicker() }}
            ><span style={styles.selectBarIconTile}><ArkmeSelectActionIcon kind="forward" /></span><span style={styles.selectBarLabel}>{messageActionBusy === 'forward' ? '转发中' : ARKME_MESSAGE_SELECT_ACTION_LABELS[2]}</span></button>
            <button data-arkme-feedback="neutral" type="button" aria-label="问 DSH" title={selectedMessagesCanAskDsh ? '将所选快记及原件添加到 DSH 新对话，等待手动发送' : '请选择 1 至 100 条可读取的快记'}
              style={{ ...styles.selectBarButton, ...(!selectedMessagesCanAskDsh || messageActionBusy !== undefined ? styles.selectBarButtonDisabled : {}) }}
              disabled={!selectedMessagesCanAskDsh || messageActionBusy !== undefined}
              onClick={() => { if (source) void askDsh.run(source.sourceRef, askDshNotesWithLocalNames(selectedMessageItems, source, conversationMemberByRef, selfProfile)) }}
            ><span style={styles.selectBarIconTile}><AskDshIcon size={22} /></span><span style={styles.selectBarLabel}>{askDsh.busy ? '准备中' : '问 DSH'}</span></button>
            <button data-arkme-feedback="danger" type="button" aria-label={tr("删除")} title={selectedMessagesCanDelete ? tr("删除") : '包含他人或暂不可删除的快记'}
              style={{ ...styles.selectBarButton, ...(!selectedMessagesCanDelete || messageActionBusy !== undefined ? styles.selectBarButtonDisabled : {}) }}
              disabled={!selectedMessagesCanDelete || messageActionBusy !== undefined}
              onClick={() => { if (source && selectedMessagesCanDelete && messageActionBusy === undefined) setRecordDeletion({
                scopeKey: topicAssignmentScopeKey, sourceRef: source.sourceRef, items: [...selectedMessageItems],
              }) }}
            ><span style={styles.selectBarIconTile}><Trash size={22} /></span><span style={styles.selectBarLabel}>{tr("删除")}</span></button>
            <button data-arkme-feedback="neutral"
              type="button"
              style={{
                ...styles.selectBarButton,
                ...(messageActionBusy !== undefined ? styles.selectBarButtonDisabled : {}),
              }}
              disabled={messageActionBusy !== undefined}
              aria-label={ARKME_MESSAGE_SELECT_ACTION_LABELS[3]}
              onClick={exitMessageSelectMode}
            ><span style={styles.selectBarIconTile}><ArkmeSelectActionIcon kind="close" size={18} /></span><span style={styles.selectBarLabel}>{ARKME_MESSAGE_SELECT_ACTION_LABELS[3]}</span></button>
          </div>}
          </div>
        </ArkmeInterwovenReadProvider>
        </ArkmeWideConversation>}
        {forwardDialog}
        {activeConversation && messageMenu !== undefined && messageMenuItem !== undefined && <ArkmeActionMenu
          label={tr("消息操作")} point={{ x: messageMenu.left, y: messageMenu.top }} onClose={closeMessageMenu}
          actions={[
            !!messageMenuItem.messageActionRef && authenticatedAccountKey !== undefined && activeSelectMode === undefined && { id: 'reaction', label: '表态', icon: <ReactionAddIcon />, onSelect: () => {
              setReactionMenuRequest({ scope: authenticatedAccountKey, occurrenceKey: arkmeTimelineOccurrenceKey(messageMenuItem) })
              closeMessageMenu()
            } },
            { id: 'copy', label: ARKME_MESSAGE_ACTION_MENU_LABELS[0], icon: <ArkmeMessageActionIcon kind="copy" />, onSelect: () => { void copyMessageText(messageMenuItem) } },
            { id: 'link', label: ARKME_MESSAGE_ACTION_MENU_LABELS[1], icon: <ArkmeMessageActionIcon kind="link" />, disabled: arkmeTimelineMessageActionRef(messageMenuItem) === '', onSelect: () => { void copyMessageLink([messageMenuItem]) } },
            !archiveReadOnly && { id: 'extend', label: ARKME_MESSAGE_ACTION_MENU_LABELS[2], icon: <ArkmeDesktopExtensionIcon />, disabled: arkmeTimelineMessageActionRef(messageMenuItem) === '', onSelect: () => startMessageExtension(messageMenuItem) },
            arkmeCanReeditTimelineMessage(messageMenuItem) && { id: 'edit', label: ARKME_MESSAGE_REEDIT_ACTION_LABEL, icon: <ArkmeMessageActionIcon kind="edit" />, onSelect: () => openRecordReedit(messageMenuItem) },
            source !== undefined && isArkmeSelfWorkspaceSource(source) && { id: 'assign', label: '指定主题', icon: <ArkmeSelectActionIcon kind="assign" size={16} />, disabled: !canAssignRecordTopics([messageMenuItem]), onSelect: () => openRecordTopicAssignment([messageMenuItem], messageMenu.anchor) },
            { id: 'select', label: ARKME_MESSAGE_ACTION_MENU_LABELS[3], icon: <ArkmeMessageActionIcon kind="select" />, onSelect: () => enterMessageSelectMode(messageMenuItem) },
            { id: 'forward', label: ARKME_MESSAGE_ACTION_MENU_LABELS[4], icon: <ArkmeMessageActionIcon kind="forward" />, disabled: arkmeTimelineMessageActionRef(messageMenuItem) === '', onSelect: () => openForwardTargetPicker([messageMenuItem]) },
            { id: 'ask-dsh', label: askDsh.busy ? '准备中' : '问 DSH', icon: <AskDshIcon size={20} />,
              disabled: source === undefined || arkmeTimelineMessageActionRef(messageMenuItem) === '' || messageMenuItem.quickNoteDetailsSupported === false || messageActionBusy !== undefined,
              onSelect: () => {
                if (!source || messageActionBusy !== undefined || arkmeTimelineMessageActionRef(messageMenuItem) === '' || messageMenuItem.quickNoteDetailsSupported === false) return
                closeMessageMenu()
                void askDsh.run(source.sourceRef, askDshNotesWithLocalNames([messageMenuItem], source, conversationMemberByRef, selfProfile))
              },
            },
            arkmeCanReportTimelineMessage(source, messageMenuItem) && { id: 'report', label: ARKME_MESSAGE_REPORT_ACTION_LABEL, icon: <ArkmeMessageActionIcon kind="report" />, onSelect: () => openMessageReport(messageMenuItem) },
            arkmeCanOpenMessageSnapshot(messageMenuItem) && { id: 'detail', label: ARKME_MESSAGE_ACTION_DETAIL_LABEL, onSelect: () => openMessageSnapshot(messageMenuItem) },
            arkmeCanWithdrawTimelineMessage(source, messageMenuItem, groupSelfRole) && { type: 'separator', id: 'withdraw-divider' },
            arkmeCanWithdrawTimelineMessage(source, messageMenuItem, groupSelfRole) && { id: 'withdraw', label: '撤回', icon: <ArkmeMessageActionIcon kind="withdraw" />, danger: true, onSelect: () => openMessageWithdrawal(messageMenuItem) },
          ]}
        />}
        {activeConversation && messageReportItem !== undefined && <ArkmeMessageReportDialog
          item={messageReportItem}
          onClose={() => { setMessageReportItem(undefined) }}
          onSubmitted={() => {
            setMessageReportItem(undefined)
            showMessageActionStatus('举报已提交')
          }}
        />}
        {activeConversation && messageWithdrawalItem !== undefined && <ArkmeMessageWithdrawalDialog
          item={messageWithdrawalItem}
          onClose={() => { setMessageWithdrawalItem(undefined) }}
          onWithdrawn={result => {
            arkmeChatTimelineDelta.applyTimelineChange({
              sourceKey: conversationKey,
              timelineItemKey: result.timelineItemKey,
              changeKind: 'deleted',
              changeVersion: result.withdrawnAtMillis,
              relationTerminal: true,
              throughSequence: messageWithdrawalItem.sequence ?? source?.latestSequence ?? 0,
            })
            setMessageWithdrawalItem(undefined)
            showMessageActionStatus(result.alreadyWithdrawn ? '消息已撤回' : '已撤回')
          }}
        />}
        {activeConversation && snapshot !== undefined && <ArkmeMessageSnapshotDialog
          item={snapshot.item}
          {...(snapshot.detail === undefined ? {} : { detail: snapshot.detail })}
          loading={snapshot.loading}
          {...(snapshot.loadError === undefined ? {} : { loadError: snapshot.loadError })}
          backgroundSoundEnabled={backgroundSoundSupported && backgroundSoundEligibilityReason === 'eligible'
            && backgroundSoundCaptureEnabled && arkmeMicrophoneCaptureReady()}
          backgroundSoundSupported={backgroundSoundCapabilityKnown ? backgroundSoundSupported : true}
          backgroundSoundEligibilityReason={backgroundSoundEligibilityReason}
          onEnableBackgroundSound={() => {
            closeMessageSnapshot()
            arkmeUi.openDshSettings()
          }}
          onClose={closeMessageSnapshot}
        />}
        {activeConversation && source !== undefined && memberMenu !== undefined && <ArkmeMemberActionMenu
          member={memberMenu.member}
          sourceKind={source.kind}
          position={memberMenu.position}
          hoverAnchor={memberMenu.hoverAnchor}
          hoverSide={memberMenu.hoverSide}
          onMention={() => { insertMemberMention(memberMenu.member) }}
          onRecords={mode => { openMemberRecords(memberMenu.member, mode) }}
          canRemove={source.kind === 'group_chat' && groupSelfRole === 'owner' && !memberMenu.member.isSelf && !memberMenu.member.isOwner}
          onRemove={() => { setMemberRemovalTarget(memberMenu.member); closeMemberMenu() }}
          onClose={closeMemberMenu}
        />}
        {activeConversation && source?.kind === 'group_chat' && authenticatedAccountKey !== undefined && botMenu !== undefined && <ArkmeBotAvatarActionMenu
          key={`${authenticatedAccountKey}:${source.sourceRef}:${botMenu.identity.itemUid}`}
          accountKey={authenticatedAccountKey}
          sourceRef={source.sourceRef}
          identity={botMenu.identity}
          hoverAnchor={botMenu.hoverAnchor}
          hoverSide={botMenu.hoverSide}
          position={botMenu.position}
          onMention={insertBotMention}
          onRecords={mode => { openBotRecords(botMenu.identity, mode) }}
          canRemove={groupSelfRole === 'owner'}
          onRemove={bot => { openBotRemoval(botMenu.identity, bot) }}
          onClose={closeBotMenu}
        />}
        {activeConversation && source?.kind === 'group_chat' && botRemovalTarget !== undefined && <ArkmeBotRemoveDialog
          sourceRef={source.sourceRef}
          bot={botRemovalTarget.bot}
          onClose={() => { setBotRemovalTarget(undefined) }}
          onRemoved={() => {
            if (authenticatedAccountKey && botRemovalTarget.identity.directoryKey) {
              arkmeInvalidateBotAvatarResolution(authenticatedAccountKey, source.sourceRef, botRemovalTarget.identity.directoryKey)
            }
            arkmeGroupBotsChanged(authenticatedAccountKey, source.sourceRef, botRemovalTarget.bot.botRef)
            setBotRemovalTarget(undefined)
            setGroupMentionBots(undefined)
            showMessageActionStatus(tr('Bot 已移出群聊'))
          }}
        />}
        {activeConversation && source?.kind === 'group_chat' && memberRemovalTarget !== undefined && <ArkmeGroupMemberRemoveDialog
          sourceRef={source.sourceRef}
          member={memberRemovalTarget}
          onClose={() => { setMemberRemovalTarget(undefined) }}
          onRemoved={result => {
            setMemberRemovalTarget(undefined)
            arkmeConversationMembers.remove(authenticatedAccountKey, source, result.memberRef)
            showMessageActionStatus(result.joinRestricted ? '已移出群聊，并禁止再次加入' : '已移出群聊')
          }}
        />}
        {activeConversation && memberProfile !== undefined && <ArkmeMemberProfileCard
          member={memberProfile}
          showTopicNickname={source?.kind === 'group_chat'}
          busy={privateChatBusy}
          onClose={closeMemberProfile}
          onSend={() => { openPrivateChatForMember(memberProfile) }}
        />}
        {activeConversation && source !== undefined && authenticatedAccountKey !== undefined && botProfile !== undefined && <ArkmeBotAvatarProfileCard
          key={`${authenticatedAccountKey}:${source.sourceRef}:${botProfile.identity.itemUid}`}
          accountKey={authenticatedAccountKey}
          sourceRef={source.sourceRef}
          identity={botProfile.identity}
          onChat={openBotChat}
          onClose={() => { setBotProfile(undefined) }}
        />}
        {activeConversation && source?.kind === 'group_chat' && groupSelfRole === 'owner' && !memberEventTimeline.unavailable
          && memberEventProfile?.scope === memberEventScope && <ArkmeMemberEventProfileDialog
            key={`${memberEventScope}:${memberEventProfile.event.eventId}`}
            sourceRef={source.sourceRef} event={memberEventProfile.event}
            onClose={() => { setMemberEventProfile(undefined) }}
            onUnavailable={() => { memberEventTimeline.revoke() }}
            onError={setError}
            onOpen={result => { setMemberEventProfile(undefined); activateSource(result.source) }}
          />}
        {activeConversation && source !== undefined && memberRecords !== undefined && <ArkmeMemberRecordsPanel
          sourceRef={source.sourceRef}
          sourceIdentityKey={conversationKey}
          member={memberRecords.member}
          mode={memberRecords.mode}
          onClose={() => { setMemberRecords(undefined) }}
        />}
        {activeConversation && source?.kind === 'group_chat' && botRecords !== undefined && <ArkmeLoadedBotRecordsPanel
          sourceRef={source.sourceRef}
          sourceIdentityKey={conversationKey}
          botName={botRecords.identity.name}
          mode={botRecords.mode}
          items={loadedBotRecords}
          onClose={() => { setBotRecords(undefined) }}
        />}
        {activeConversation && drawer === 'detail' && detailItem !== undefined && <>
          <div
            style={styles.forwardDrawerDismiss}
            aria-hidden="true"
            onClick={() => { setDrawer(undefined) }}
          />
          {detailItem.forwardRecords !== undefined && <ForwardRecordsDetail
            key={detailItem.itemUid}
            item={detailItem}
            onPrivateChatOpened={activateSource}
            onClose={() => { setDrawer(undefined) }}
          />}
          {detailSharedRecording !== undefined && <RelatedRecordingDetail
            key={detailItem.itemUid}
            item={detailSharedRecording}
            showReadOnlyLabel={false}
            {...(detailSharedRecording.sharedRecordingDetailRef === undefined ? {} : {
              loadDetail: async (_item: ArkmeRelatedRecordingItem, signal?: AbortSignal) => arkmeRelatedRecordingItemFromSharedRecordingPreview(
                await callArkme<ArkmeSharedRecordingPreview>(
                  'source.shared-recording-detail',
                  { detailRef: detailSharedRecording.sharedRecordingDetailRef },
                  signal,
                ),
                detailItem,
              ),
            })}
            onClose={() => { setDrawer(undefined) }}
          />}
        </>}
        {activeConversation && drawer === 'detail' && detailItem?.callRecord !== undefined && <ArkmeCallDetailDrawer
          key={`${authenticatedAccountKey}:${conversationOverlayKey}:${detailItem.itemUid}`}
          item={detailItem}
          initialVideoUrl={detailCallVideoUrl}
          onClose={() => { setDrawer(undefined) }}
        />}
        {activeConversation && drawer === 'detail' && detailItem !== undefined && detailItem.callRecord === undefined && detailItem.forwardRecords === undefined && detailSharedRecording === undefined && <ArkmeTimelineDetailDrawer
          key={`${authenticatedAccountKey ?? 'signed-out'}:${detailItem.itemUid}`}
          item={detailItem}
          sourceRef={source?.sourceRef}
          sourceIdentityKey={conversationKey}
          canExtend={!archiveReadOnly}
          sourceKind={source?.kind}
          {...(detailTopicPath === undefined ? {} : { selfTopicPath: detailTopicPath })}
          {...(detailTopicPresentation !== undefined ? { selfTopicSource: detailTopicPresentation.topic }
            : source?.kind === 'topic' ? { selfTopicSource: source } : {})}
          onOpenSelfTopic={topic => { setDrawer(undefined); activateSelfSource(topic) }}
          onOpenExtensionParent={openExtensionParentDetail}
          {...(detailBackStack.length === 0 ? {} : { onBackToExtension: () => {
            const previous = detailBackStack[detailBackStack.length - 1]
            if (previous === undefined) return
            if (arkmeSourceIdentityKey(previous.source) === conversationKey
              && items.some(item => item.itemUid === previous.itemUid)) {
              setDetailBackStack(current => current.slice(0, -1))
              setDetailItemUid(previous.itemUid)
              setShowOriginal(false)
            } else {
              arkmeUi.showConversationTarget(previous.source, previous.itemUid, previous.sendAtMillis,
                undefined, undefined, false, true)
              const revision = arkmeUi.getSnapshot().conversationTarget?.revision
              if (revision !== undefined) pendingExtensionParentDetailRef.current = {
                revision, kind: 'restore', stack: detailBackStack.slice(0, -1),
              }
            }
          } })}
          {...(selfProfile === undefined ? {} : { currentSelfProfile: selfProfile })}
          {...(authenticatedUserId === undefined || authenticatedAccountKey === undefined || source === undefined || !isArkmeSelfWorkspaceSource(source)
            ? {} : { selfRoleSelection: {
              accountKey: authenticatedAccountKey,
              userId: authenticatedUserId,
              selectedRole: selectedSelfRole,
              selfAvatarRef: selfProfile?.avatarRef,
              onSelect: setSelectedSelfRole,
            } })}
          conversationMembers={conversationMembers}
          showOriginal={showOriginal}
          onClose={() => { setDrawer(undefined); setDetailBackStack([]); pendingExtensionParentDetailRef.current = undefined }}
          onToggleOriginal={() => { setShowOriginal(value => !value) }}
          shareWebsite={shareWebsite}
          onMessageCopyLinkOpen={openMessageCopyLinkDetail}
          onExtensionSent={acceptDetailExtension}
          onOpenPrivateChatMember={openPrivateChatForMember}
          messageCreationBlocked={directAdmission.blocked}
          messageCreationRestriction={directAdmission.blocked ? directAdmission.message : ''}
          onToast={showMessageActionStatus}
        />}
        {activeConversation && drawer === 'copyLink' && copyLinkDetail !== undefined && <>
          <div
            style={styles.forwardDrawerDismiss}
            aria-hidden="true"
            onClick={closeMessageCopyLinkDetail}
          />
          <CopyLinkDetailDrawer
            state={copyLinkDetail}
            onClose={closeMessageCopyLinkDetail}
            onRetry={() => { loadMessageCopyLinkDetail(copyLinkDetail.sid) }}
            onShare={() => { void shareCurrentCopyLinkDetail() }}
            draft={copyLinkDetailDraft}
            onDraftChange={value => {
              setCopyLinkDetailDraft(value)
              if (copyLinkDetailSendError !== '') setCopyLinkDetailSendError('')
            }}
            onSendDraft={() => { void sendCopyLinkDetailThought() }}
            sendBusy={copyLinkDetailSending}
            sendDisabled={copyLinkDetailSending || copyLinkDetailDraft.trim() === '' || copyLinkDetail.status !== 'ready' || copyLinkDetailExtensionTarget(copyLinkDetail.detail) === undefined}
            sendError={copyLinkDetailSendError}
            shareWebsite={shareWebsite}
          />
        </>}
        {authView === 'content' && ui.mode === 'contact-add' && <div
          style={styles.contactBackdrop}
          role="presentation"
          onMouseDown={event => { if (event.target === event.currentTarget) arkmeUi.showConversations() }}
        >
          <section style={styles.contactDialog} role="dialog" aria-modal="true" aria-labelledby="arkme-contact-add-title">
            <header style={styles.contactDialogHeader}>
              <h2 id="arkme-contact-add-title" style={styles.contactDialogTitle}>{tr("添加联系人")}</h2>
              <button data-arkme-feedback="neutral" type="button" style={styles.contactDialogClose} aria-label={tr("关闭添加联系人")} onClick={() => { arkmeUi.showConversations() }}>×</button>
            </header>
            <div style={styles.contactDialogBody}>
              <ArkmeContactAddSurface
                compact
                shareWebsite={shareWebsite}
                onSourceActivated={activateSource}
              />
            </div>
          </section>
        </div>}
      </section>
      {activeConversation && commonGroupsOpen && source?.kind === 'private_chat' && <ArkmeCommonGroupsPanel
        key={`${authenticatedAccountKey}:${conversationKey}`} source={source} returnFocusRef={relatedMenuButtonRef}
        onClose={() => setCommonGroupsOpen(false)} onOpen={target => { activateSource(target); setCommonGroupsOpen(false) }} />}
      {activeConversation && relatedPanelOpen && source?.kind === 'private_chat' && <RelatedRecordingsPanel
        contactName={source.displayName}
        {...(source.avatarRef === undefined ? {} : { contactAvatarRef: source.avatarRef })}
        state={relatedState}
        stateMessage={relatedStateMessage}
        error={relatedError}
        items={relatedItems}
        hasMore={relatedHasMore}
        loadingMore={relatedLoadingMore}
        monthBuckets={relatedMonths}
        selectedMonth={relatedMonth}
        onClose={closeRelatedPanel}
        onRetry={() => { reloadRelated('') }}
        onLoadMore={loadMoreRelated}
        onMonthChange={selectRelatedMonth}
        onSelect={setRelatedDetail}
      />}
      {activeConversation && relatedDetail !== undefined && <RelatedRecordingDetail
        item={relatedDetail}
        loadDetail={loadRelatedRecordingDetail}
        onClose={() => { setRelatedDetail(undefined) }}
      />}
      {activeConversation && selectedMoment !== undefined && detailState !== undefined && <ArkmeInterwovenDetailAside
        state={detailState}
        relatedView={momentRelatedView}
        relatedState={momentRelatedState}
        relatedDetailState={momentRelatedDetailState}
        shareWebsite={shareWebsite}
        {...(source === undefined ? {} : { sourceRef: source.sourceRef })}
        onClose={closeMomentDetail}
        onRetry={() => { void loadMomentDetail(selectedMoment, true) }}
        onOpenRelated={() => { setMomentRelatedView('related-list') }}
        onSelectRelated={item => { void loadMomentRelatedDetail(item) }}
        onBackRelated={backMomentRelated}
        onRetryRelated={() => { void loadMomentRelated(selectedMoment) }}
        onRetryRelatedDetail={item => { void loadMomentRelatedDetail(item) }}
        {...(detailGroupTarget === undefined ? {} : { onOpenGroup: () => {
          closeMomentDetail()
          arkmeUi.selectSource(detailGroupTarget)
        } })}
      />}
      {articlePickerOpen && source !== undefined && authenticatedUserId !== undefined && typeof document !== 'undefined' && createPortal(
        <ArkmeArticlePicker key={`${articleDraftKey}:${articlePickerScope.generation}`} sourceRef={source.sourceRef} userId={authenticatedUserId}
          onCreate={() => openLongArticleWindow({ ...source, sourceKey: conversationKey })}
          onClose={() => { setArticlePickerScope(undefined); if (sameComposerAsyncScope(articlePickerScope)) textareaRef.current?.focus() }}
          onSelect={article => { if (articleDraftKey && sameComposerAsyncScope(articlePickerScope)) composerArticleStore.set(articleDraftKey, article) }} />,
        document.body,
      )}
      {activeConversation && longArticleCreating && source !== undefined && typeof document !== 'undefined' && createPortal(
        <ArkmeLongArticleDialog
          sourceRef={source.sourceRef}
          onClose={() => { setLongArticleCreating(false) }}
          onCreated={item => {
            pendingViewportRestoreRef.current = { sourceKey: conversationKey, viewport: undefined }
            confirmedSendRetention.retain(conversationKey, item)
            setItems(current => mergeItems(current, [item]))
            if (isArkmeSelfWorkspaceSource(source)) arkmeUi.recordChanged()
          }}
        />,
        document.body,
      )}
    </div>
    {recordingImportOwner}
    {directRecordingNotice}
    </>
  )
}
